import React, { useState, useEffect, useCallback, useRef } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { QRCodeSVG } from 'qrcode.react';
import emailjs from '@emailjs/browser';

import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import {
  Users,
  LayoutDashboard,
  MapPin,
  Clock,
  DollarSign,
  BarChart3,
  Settings,
  Search,
  Plus,
  ChevronDown,
  ChevronRight,
  Mail,
  Phone,
  Edit,
  Save,
  FileText,
  CheckCircle,
  AlertCircle,
  Receipt,
  Loader2,
  X,
  Trash2,
  Briefcase,
  Building2,
  Shield,
  Download,
  Camera,
  User,
  Upload,
  Globe,
  Database,

  Menu, // Hamburger Menu
  LogOut, // Logout Icon
  Calculator, // General Accounts
  ShoppingCart, // Sales & Purchases
  Package, // Warehouses
  CreditCard,
  Printer,
  Scan,
  QrCode,
  Zap,
  ArrowLeft,
  RefreshCw,
  Coffee,
  Monitor,
  Disc,
  Lock,
  Minus,
  History,
  Play,
  Wrench,
  Smartphone,
  Laptop,
  CheckSquare,
  AlertTriangle,
  Tag,
  UserPlus,
  Activity,
  ShoppingBag,
  Banknote,
  ChevronLeft,
  Tablet,
} from 'lucide-react';
import { auth, db, storage } from './firebase'; // Firebase
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { translations } from './i18n/translations';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  linkWithPopup,
  signInAnonymously,
  setPersistence,
  indexedDBLocalPersistence
} from 'firebase/auth';

import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
  runTransaction,

  onSnapshot,
  writeBatch,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';

// SILENCED: Global Error Handler no longer interrupts user
window.onerror = function (message, source, lineno, colno, error) {
  console.error('App Error:', message, 'Line:', lineno);
};

// --- Subcomponents ---
function SidebarItem({ icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3.5 px-5 py-4 rounded-2xl mb-2 transition-all duration-300 group active-haptic relative overflow-hidden ${active
        ? 'bg-blue-600 text-white shadow-xl shadow-blue-500/20 translate-x-1 font-black'
        : 'text-slate-400 hover:text-white hover:bg-slate-800/40'
        }`}
    >
      {active && <div className="absolute inset-0 bg-white/10 shimmer"></div>}
      <div className={`transition-all duration-500 ${active ? 'scale-110 rotate-6' : 'group-hover:scale-110 group-hover:-rotate-3'}`}>{icon}</div>
      <span className={`text-[12px] uppercase tracking-widest transition-all ${active ? 'opacity-100 font-black' : 'opacity-60 group-hover:opacity-100 font-bold'}`}>{label}</span>
      {active && (
        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white shadow-[0_0_12px_rgba(255,255,255,0.9)] animate-pulse"></div>
      )}
    </button>
  );
}

function StatusBadge({ status, lateHours, t }) {
  const styles = {
    'On Time': 'bg-green-100 text-green-700',
    'Late': 'bg-amber-100 text-amber-700',
    'Absent': 'bg-red-100 text-red-700',
    'Pending': 'bg-gray-100 text-gray-600'
  };

  // Clean status string to matching translation key if possible, or leave as is
  const getStatusLabel = (s) => {
    // Safety check: if t is undefined (not passed), return original string
    if (typeof t !== 'function') return s;

    if (s === 'On Time') return t('onTime');
    if (s === 'Late') {
      const base = t('late');
      return lateHours ? `${base} (${lateHours})` : base;
    }
    if (s === 'Absent') return t('absent');
    return s;
  };

  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${styles[status] || styles['Pending']}`}>
      {getStatusLabel(status)}
    </span>
  );
}


export default function App() {
  // Initialize Auth Persistence and EmailJS for Electron
  useEffect(() => {
    setPersistence(auth, indexedDBLocalPersistence).catch(err => console.error("Persistence Error:", err));
    emailjs.init("IgVOMU-SebZmlIgQk"); // Initialized with user's Public Key
  }, []);

  // --- Auth & UI State (Moved to top to fix TDZ) ---
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const isRegistered = user && !user.isAnonymous;
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024); // Default open on Desktop
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [authForm, setAuthForm] = useState({ email: '', password: '', apiKey: '' });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState(null); 
  const [otpSent, setOtpSent] = useState(false);
  const [otpInput, setOtpInput] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [printFormat, setPrintFormat] = useState('Thermal'); // 'Thermal' or 'A4'
  const [printDual, setPrintDual] = useState(false);
  const [printBigOrderNumber, setPrintBigOrderNumber] = useState(true);
  const [borrowRecords, setBorrowRecords] = useState([]);
  const [borrowForm, setBorrowForm] = useState({ borrowerName: '', itemName: '', borrowDate: new Date().toISOString().split('T')[0], borrowTime: new Date().toTimeString().slice(0,5), returnDate: '', returnTime: '', returned: false, info: '' });
  const [isBorrowModalOpen, setIsBorrowModalOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [securityPin, setSecurityPin] = useState('1234');
  const [managerPin, setManagerPin] = useState('8888');
  const [ownerPin, setOwnerPin] = useState('0000');
  const [currentMode, setCurrentMode] = useState('Cashier'); // 'Cashier' | 'Manager' | 'Owner'
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [pinInput, setPinInput] = useState('');
  const [showSensitiveData, setShowSensitiveData] = useState(false);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);

  const handlePinAuth = (inputPin) => {
    let authorized = false;
    let newMode = currentMode;

    if (inputPin === ownerPin) {
      newMode = 'Owner';
      setShowSensitiveData(true);
      authorized = true;
    } else if (inputPin === managerPin) {
      newMode = 'Manager';
      authorized = true;
    } else if (inputPin === securityPin) {
      authorized = true;
    }

    if (authorized) {
      setIsPinModalOpen(false);
      setPinInput('');
      setCurrentMode(newMode);

      // Trigger respective actions
      if (pinAction === 'showCosts') setShowSensitiveData(true);
      if (pinAction === 'changeSalesEmployee') setIsSelectSalesEmployeeModalOpen(true);
      if (pinAction === 'accessReports' || pinAction === 'accessAccounts') {
        // Only allow Manager or Owner to access these modules
        if (newMode === 'Owner' || newMode === 'Manager') {
          setActiveTab(pinAction === 'accessReports' ? 'reports' : 'accounts');
          if (window.innerWidth < 768) setIsSidebarOpen(false);
        } else {
          alert(t('adminPinRequired'));
        }
      }
    } else {
      setTimeout(() => {
        setPinInput('');
        alert(t('incorrectPin'));
      }, 200);
    }
  };
  const [shopSettings, setShopSettings] = useState({
    name: '',
    address: '',
    phone: '',
    upiId: '',
    instapayId: '',
    logo: '', // Base64 or URL
    gstEnabled: false,
    gstNumber: '',
    gstPercent: 0
  });
  const [cameras, setCameras] = useState([]);
  const [activeCameraId, setActiveCameraId] = useState(null);
  const [heldCarts, setHeldCarts] = useState([]); // Multiple selling sessions
  const [scannerMode, setScannerMode] = useState('sell'); // 'sell' or 'buy'
  const [showUpiQr, setShowUpiQr] = useState(false);
  const [isMobileCartOpen, setIsMobileCartOpen] = useState(false);
  const [upiQrTimer, setUpiQrTimer] = useState(15);
  const [digitalSubMethod, setDigitalSubMethod] = useState('UPI');
  const [currency, setCurrency] = useState(() => localStorage.getItem('currency') || 'EGP');
  const [barcodePrintMode, setBarcodePrintMode] = useState('sticker'); // 'sticker' or 'a4'

  // --- State with Firestore Integration ---
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [payrolls, setPayrolls] = useState([]); // [New] Monthly Payroll Records

  const [accounts, setAccounts] = useState([]);
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [selectedInventoryItems, setSelectedInventoryItems] = useState([]);
  const [pendingRegistrations, setPendingRegistrations] = useState([]); // [New] For Admin Approval

  // --- Sales UI & Filter States ---
  const [salesEmployee, setSalesEmployee] = useState(null);

  const [cart, setCart] = useState([]);
  const [cartDiscount, setCartDiscount] = useState(0);

  const [isKiosk, setIsKiosk] = useState(() => localStorage.getItem('isKiosk') === 'true');
  const [isSelectSalesEmployeeModalOpen, setIsSelectSalesEmployeeModalOpen] = useState(false);
  const [pinAction, setPinAction] = useState('showCosts'); // 'showCosts' | 'changeSalesEmployee'
  const [paymentMethod, setPaymentMethod] = useState('Cash'); // 'Cash' | 'Visa' | 'Online'
  const [posLocationFilter, setPosLocationFilter] = useState('');
  const [warehouseLocationFilter, setWarehouseLocationFilter] = useState('');
  const [homeLocationFilter, setHomeLocationFilter] = useState('');
  const [historyLocationFilter, setHistoryLocationFilter] = useState('');
  const [reportLocationFilter, setReportLocationFilter] = useState('');
  const [historyFilter, setHistoryFilter] = useState('All');
  const [historyDateFilter, setHistoryDateFilter] = useState(new Date().toISOString().split('T')[0]);

  const [kioskTab, setKioskTab] = useState('home');
  const [kioskTime, setKioskTime] = useState(new Date());
  useEffect(() => {
    localStorage.setItem('isKiosk', isKiosk);
    if (!isKiosk) setKioskTab('home');
  }, [isKiosk]);
  useEffect(() => {
    const timer = setInterval(() => setKioskTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  const [externalPayment, setExternalPayment] = useState(null); // { invoiceId, amount, upiId, expiry }
  useEffect(() => { localStorage.setItem('currency', currency); }, [currency]);


  // --- Barcode / Scanner Buffering (Hardware Scanners) ---
  const barcodeBuffer = useRef('');
  const lastKeyTime = useRef(0);


  // Handle Incoming Payment QR Codes from Prints
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payId = params.get('pay');
    const amt = params.get('amt');
    const upi = params.get('upi') || params.get('id'); // Support both legacy and new 'id'
    const method = params.get('method') || 'UPI'; // Default to UPI for legacy
    const pn = params.get('pn');
    const exp = params.get('exp');

    if (payId && amt && upi && exp) {
      const now = Date.now();
      const expiryTime = parseInt(exp);
      if (now < expiryTime) {
        setExternalPayment({
          invoiceId: payId,
          amount: parseFloat(amt),
          upiId: upi,
          paymentMethod: method,
          merchantName: pn || 'Merchant',
          expiry: expiryTime,
          expired: false
        });
        setActiveTab('payment_portal');
      } else {
        setExternalPayment({ expired: true });
        setActiveTab('payment_portal');
      }
    }
  }, []);



  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'en');
  useEffect(() => { localStorage.setItem('language', language); }, [language]);

  const [orderType, setOrderType] = useState('Walk-in'); // 'Walk-in' or 'Takeaway'

  const t = useCallback((key) => {
    const lang = translations[language] ? language : 'en';
    return (translations[lang] && translations[lang][key]) || key;
  }, [language]);

  const [newSaleForm, setNewSaleForm] = useState({ customer: '', customerId: '', amount: 0, status: 'Completed', items: '' });
  const [isEditHistoryModalOpen, setIsEditHistoryModalOpen] = useState(false);
  const [editingHistoryItem, setEditingHistoryItem] = useState(null);

  // Sync document direction and language for RTL support (Arabic)
  useEffect(() => {
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  // Reactive default customer names for POS (fixes untranslated defaults on language switch)
  useEffect(() => {
    const defaults = {
      en: { walkIn: 'Walk-in Customer', takeaway: 'Takeaway Customer' },
      ar: { walkIn: 'عميل محلي', takeaway: 'عميل تيك أواي' },
      hi: { walkIn: 'वॉक-इन ग्राहक', takeaway: 'टेकअवे ग्राहक' },
      zh: { walkIn: '散客', takeaway: '外带客户' }
    };
    const isCurrentDefault = Object.values(defaults).some(d =>
      newSaleForm.customer === d.walkIn || newSaleForm.customer === d.takeaway || !newSaleForm.customer
    );
    if (isCurrentDefault) {
      setNewSaleForm(prev => ({
        ...prev,
        customer: orderType === 'Walk-in' ? t('walkInCustomer') : t('takeawayCustomer')
      }));
    }
  }, [language, orderType, t, newSaleForm.customer]);
  // Sync Settings from Firestore
  useEffect(() => {
    if (user) {
      const fetchSettings = async () => {
        try {
          const docRef = doc(db, 'settings', 'user_prefs_' + user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.securityPin) setSecurityPin(data.securityPin);
            if (data.managerPin) setManagerPin(data.managerPin);
            if (data.ownerPin) setOwnerPin(data.ownerPin);
            if (data.securityQuestion) setSecurityQuestion(data.securityQuestion);
            if (data.securityAnswer) setSecurityAnswer(data.securityAnswer);
            if (data.language) setLanguage(data.language);
            if (data.currency) setCurrency(data.currency);
          }
        } catch (error) {
          console.error("Error fetching settings:", error);
        }
      };
      fetchSettings();
    }
  }, [user]);

  // Helper to save settings
  const saveUserSettings = async (newSettings) => {
    if (!user) return;
    try {
      const docRef = doc(db, 'settings', 'user_prefs_' + user.uid);
      await setDoc(docRef, newSettings, { merge: true });
    } catch (error) {
      console.error("Error saving settings:", error);
      alert((translations[language]?.saveError || 'Error saving') + ': ' + error.message);
    }
  };



  const formatCurrency = useCallback((val) => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(Number(val) || 0);
    } catch (e) {
      return currency + ' ' + (Number(val) || 0).toLocaleString();
    }
  }, [currency]);

  const handlePrintInvoice = useCallback((invoiceData, type = 'Invoice', formatOverride = null) => {
    const printWindow = window.open('', '', 'width=800,height=600');
    if (!printWindow) return;

    const currentFormat = formatOverride || printFormat;

    const styles = currentFormat === 'Thermal' ? `
      @page { margin: 0; }
      body { font-family: 'Courier New', monospace; width: 80mm; padding: 10px; margin: 0 auto; color: #000; background: #fff; }
      .page { padding-bottom: 20px; display: block; position: relative; }
      .page-break { page-break-after: always; }
      
      /* Header */
      .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 12px; }
      .title { font-size: 1.1em; font-weight: bold; text-transform: uppercase; margin-bottom: 3px; }
      .subtitle { font-size: 0.75em; line-height: 1.3; margin: 2px 0; }
      .invoice-title { font-size: 1em; font-weight: bold; margin: 8px 0; text-decoration: underline; }
      .seller-info { font-size: 0.75em; border: 1px dashed #000; padding: 4px; margin: 5px 0; text-align: center; }
      
      /* Details */
      .details { font-size: 0.8em; margin: 10px 0; line-height: 1.4; }
      .details p { margin: 3px 0; }
      .details strong { font-weight: bold; }
      
      /* Table */
      table { width: 100%; font-size: 0.75em; border-collapse: collapse; margin: 10px 0; }
      thead { border-bottom: 1px dashed #000; }
      th { text-align: left; padding: 5px 2px; font-weight: bold; }
      th.right { text-align: right; }
      td { padding: 5px 2px; border-bottom: 1px dotted #ccc; }
      td.right { text-align: right; }
      tbody tr:last-child td { border-bottom: none; }
      
      /* Totals */
      .totals { margin-top: 10px; border-top: 1px dashed #000; padding-top: 8px; }
      .totals-row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 0.8em; }
      .totals-row.subtotal { font-weight: normal; }
      .totals-row.total { font-weight: bold; font-size: 1em; border-top: 1px solid #000; margin-top: 5px; padding-top: 5px; }
      
      /* Footer */
      .footer { text-align: center; font-size: 0.7em; margin-top: 15px; border-top: 1px dashed #000; padding-top: 8px; font-style: italic; }
      .copy-label { text-align: center; font-weight: bold; margin-bottom: 8px; text-transform: uppercase; font-size: 0.75em; border: 1px solid #000; display: inline-block; padding: 3px 8px; }
      .big-id { font-size: 3.5em; font-weight: 900; text-align: center; margin: 8px 0; border: 3px solid #000; padding: 5px; background: #fff; letter-spacing: 1px; line-height: 1; }
      .big-id-label { font-size: 0.8rem; font-weight: 900; text-transform: uppercase; display: block; margin-bottom: 2px; letter-spacing: 1px; color: #000; }
    ` : `
      @page { margin: 10mm; size: A4; }
      body { font-family: 'Arial', sans-serif; color: #000; background: #fff; line-height: 1.4; }
      .page { padding: 40px; position: relative; border: 1px solid #000; border-bottom: none; }
      .page:last-child { border-bottom: 2px solid #000; }
      .page-break { page-break-after: always; }

      /* Header */
      .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
      .brand-info { flex: 1; }
      .brand-info h1 { font-size: 32px; font-weight: 900; margin: 0; text-transform: uppercase; }
      .brand-info p { margin: 2px 0; font-size: 14px; font-weight: bold; }

      .invoice-title-box { text-align: right; }
      .invoice-title-box h2 { font-size: 48px; font-weight: 900; margin: 0 0 10px 0; text-transform: uppercase; }
      .info-table { border-collapse: collapse; min-width: 200px; }
      .info-table td { border: 2px solid #000; padding: 6px 12px; font-size: 12px; font-weight: bold; text-transform: uppercase; }
      .info-table td:first-child { background: #fff; text-align: right; width: 40%; }
      .info-table td:last-child { background: #fff; text-align: left; }

      /* Sections */
      .section-header { background: #000; color: #fff; padding: 10px 15px; font-weight: 900; text-transform: uppercase; font-size: 14px; margin: 20px 0 0 0; }
      .section-content { border: 2px solid #000; padding: 15px; font-weight: bold; font-size: 14px; margin-bottom: 20px; }

      /* Items Table */
      .items-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
      .items-table th { background: #000; color: #fff; padding: 12px 15px; text-align: left; font-size: 14px; font-weight: 900; text-transform: uppercase; }
      .items-table td { border: 2px solid #000; padding: 12px 15px; font-size: 14px; font-weight: bold; }
      .center { text-align: center; }
      .right { text-align: right; }

      /* Totals & Comments */
      .bottom-layout { display: flex; justify-content: space-between; margin-top: 30px; }
      .comments-box { width: 60%; }
      .comments-box p { font-size: 12px; font-style: italic; margin: 5px 0; font-weight: bold; }
      
      .totals-box { width: 35%; }
      .totals-table { width: 100%; border-collapse: collapse; }
      .totals-table td { padding: 8px 10px; font-size: 14px; font-weight: bold; }
      .totals-table tr.total-row td { border: 3px solid #000; font-size: 18px; font-weight: 900; text-transform: uppercase; }
      .totals-table .label { color: #666; }

      .footer { margin-top: 50px; border-top: 2px solid #000; padding-top: 10px; text-align: center; font-size: 10px; font-weight: bold; }
      .copy-label { position: absolute; top: 10px; right: 10px; font-size: 10px; font-weight: bold; text-transform: uppercase; border: 1px solid #000; padding: 2px 5px; }
    `;

    const getPageContent = (copyLabel, isFirstPage) => {
      // Resolve Site Details
      const site = sites.find(s => s.name === invoiceData.location) || {};
      const printName = 'FINN ERP';
      const printAddress = site.address || shopSettings.address;
      const printPhone = site.phone || shopSettings.phone;

      // Resolve Time
      let printTime = new Date().toLocaleTimeString();
      if (invoiceData.createdAt && invoiceData.createdAt.seconds) {
        printTime = new Date(invoiceData.createdAt.seconds * 1000).toLocaleTimeString();
      } else if (invoiceData.time) {
        printTime = invoiceData.time;
      }

      // Resolve Translated Payment Method
      const paymentMethodKey = (invoiceData.paymentMethod || 'Cash').toLowerCase().includes('visa') ? 'visa' :
        (invoiceData.paymentMethod || 'Cash').toLowerCase().includes('online') ? 'onlinePayment' : 'cash';
      const printPaymentMethod = t(paymentMethodKey);

      // Enhanced Calculation to handle different data structures
      const subtotal = Array.isArray(invoiceData.items)
        ? invoiceData.items.reduce((sum, item) => {
          const price = Number(item.price || item.sellPrice || 0);
          const qty = Number(item.qty || item.quantity || 1);
          return sum + (price * qty);
        }, 0)
        : Number(invoiceData.amount || 0);

      const total = subtotal - Number(invoiceData.discount || 0);

      const gstRate = shopSettings.gstEnabled ? (Number(shopSettings.gstPercent) || 0) : 0;
      let taxableAmount = total;
      let taxAmount = 0;
      if (gstRate > 0) {
        taxableAmount = total / (1 + (gstRate / 100));
        taxAmount = total - taxableAmount;
      }


      if (printFormat === 'Thermal') {
        return `
      <div class="page ${isFirstPage && printDual ? 'page-break' : ''}">
        ${copyLabel ? `<div class="copy-label">${copyLabel}</div>` : ''}
        
        <div class="header">
          ${shopSettings.logo ? `<img src="${shopSettings.logo}" style="height: 80px; width: auto; max-width: 100%; margin-bottom: 12px; object-fit: contain;" alt="Logo" />` : ''}
          <div class="title">${printName}</div>
          <div class="subtitle">${printAddress} | ${t('phone')}: ${printPhone}</div>
          ${shopSettings.gstEnabled && shopSettings.gstNumber ? `<div class="subtitle" style="font-weight:bold; margin-top:3px;">${t('gstNumber')}: ${shopSettings.gstNumber}</div>` : ''}
        </div>
        <div class="seller-info">${t('soldBy')}: ${invoiceData.soldBy || 'Admin'}</div>
        
        ${(invoiceData.type === 'sale' || Boolean(invoiceData.invoiceId)) && printBigOrderNumber ? `
        <div class="big-id">
          <span class="big-id-label">${t('orderNumber')}</span>
          ${invoiceData.invoiceId ? (!isNaN(invoiceData.invoiceId.split('-').pop()) ? Number(invoiceData.invoiceId.split('-').pop()) : invoiceData.invoiceId.split('-').pop() || invoiceData.invoiceId) : 'N/A'}
        </div>
        ` : ''}

        <div class="invoice-title">${t('retailInvoice')}</div>

        <div class="details">
          <p><strong>${t('date')}:</strong> ${invoiceData.date || new Date().toLocaleDateString()}</p>
          <p><strong>${t('time')}:</strong> ${printTime}</p>
          <p><strong>${invoiceData.client || invoiceData.customer || t('customer')}</strong></p>
          <p><strong>ID:</strong> ${invoiceData.invoiceId || 'N/A'}</p>
          <p><strong>${t('paymentMode')}:</strong> ${printPaymentMethod}</p>
        </div>

        <table>
          <thead>
            <tr>
              <th>${t('item')}</th>
              <th class="right">${t('qty')}</th>
              <th class="right">${t('amt')}</th>
            </tr>
          </thead>
          <tbody>
            ${Array.isArray(invoiceData.items) ? invoiceData.items.map(item => `
            <tr>
              <td>${item.name}</td>
              <td class="right">${item.qty || item.quantity || 1}</td>
              <td class="right">${formatCurrency(Number(item.price || item.sellPrice || 0) * Number(item.qty || item.quantity || 1))}</td>
            </tr>
            `).join('') : `<tr><td colspan="3">${invoiceData.items}</td></tr>`}
          </tbody>
        </table>

        <div class="totals">
          <div class="totals-row subtotal">
            <span>${t('subtotal')}</span>
            <span>${formatCurrency(subtotal)}</span>
          </div>
          ${(invoiceData.discount > 0) ? `
          <div class="totals-row discount">
             <span>${t('discount') || 'Discount'}</span>
             <span>-${formatCurrency(invoiceData.discount)}</span>
          </div>` : ''}
          ${gstRate > 0 ? `
          <div class="totals-row subtotal" style="font-size: 0.8em; margin-top: 5px; color: #555;">
             <span>Taxable Amt.</span>
             <span>${formatCurrency(taxableAmount)}</span>
          </div>
          <div class="totals-row subtotal" style="font-size: 0.8em; margin-bottom: 5px; color: #555;">
             <span>GST (${gstRate}%)</span>
             <span>${formatCurrency(taxAmount)}</span>
          </div>` : ''}
          <div class="totals-row total">
            <span>${t('total')}</span>
            <span>${formatCurrency(total)}</span>
          </div>
        </div>

        <div class="footer">
          <p>${t('thankYou')}</p>
        </div>

        ${(invoiceData.paymentMethod === 'Online') ? (() => {
            const subMethod = invoiceData.digitalSubMethod || digitalSubMethod || 'UPI';
            const payId = subMethod === 'InstaPay' ? shopSettings.instapayId : shopSettings.upiId;

            if (!payId) return '';

            const label = subMethod === 'UPI' ? t('payWithUPI') : t('payWithInstapay');
            const directPayUrl = subMethod === 'InstaPay'
              ? payId
              : `upi://pay?pa=${payId}&pn=${shopSettings.name}&am=${total.toFixed(2)}&cu=INR`;

            return `
          <div style="text-align: center; margin-top: 15px; border-top: 1px dashed #eee; padding-top: 10px;">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(directPayUrl)}" style="width: 120px; height: 120px;" />
            <p style="font-size: 0.6em; margin-top: 5px; font-weight: bold;">${label || 'Scan to Pay'}</p>
            <p style="font-size: 0.5em; color: #666; margin-top: 2px;">(Valid for 10 minutes)</p>
          </div>
          `;
          })() : ''}
      </div>
        `;
      } else {
        return `
      <div class="page ${isFirstPage && printDual ? 'page-break' : ''}">
        ${copyLabel ? `<div class="copy-label">${copyLabel}</div>` : ''}
        
        <div class="header">
          <div class="brand-info">
            <div style="display: flex; align-items: center; gap: 20px; margin-bottom: 25px;">
               ${shopSettings.logo ? `<img src="${shopSettings.logo}" style="height: 80px; width: auto; max-height: 80px; object-fit: contain;" />` : ''}
               <div>
                  <h1 style="font-size: 38px; color: #1e293b; margin: 0;">${printName}</h1>
                  <p style="color: #64748b; font-size: 14px; margin-top: 5px;">${printAddress}</p>
                  <p style="color: #64748b; font-size: 14px;">${t('phone')}: ${printPhone}</p>
                   ${shopSettings.gstEnabled && shopSettings.gstNumber ? `<p style="color: #1e293b; font-size: 14px; font-weight: bold; margin-top: 4px;">${t('gstNumber')}: ${shopSettings.gstNumber}</p>` : ''}
               </div>
            </div>
          </div>
          <div class="invoice-title-box">
            <h2>INVOICE</h2>
            <table class="info-table">
              <tr><td>DATE</td><td>${invoiceData.date || new Date().toISOString().split('T')[0]}</td></tr>
              <tr><td>TIME</td><td>${printTime}</td></tr>
              <tr><td>INVOICE #</td><td>${invoiceData.invoiceId || 'N/A'}</td></tr>
              <tr><td>CUSTOMER ID</td><td>${invoiceData.customerId || '123'}</td></tr>
              <tr><td>PAYMENT MODE</td><td>${printPaymentMethod.toUpperCase()}</td></tr>
            </table>
          </div>
        </div>

        <div class="section-header">BILL TO</div>
        <div class="section-content">
          ${invoiceData.client || invoiceData.customer || 'Walk-in Customer'}
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 60%">DESCRIPTION</th>
              <th class="center">TAXED</th>
              <th class="right">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            ${Array.isArray(invoiceData.items) ? invoiceData.items.map(item => `
            <tr>
              <td>${item.name}</td>
              <td class="center">X</td>
              <td class="right">${formatCurrency(Number(item.price || item.sellPrice || 0) * Number(item.qty || item.quantity || 1))}</td>
            </tr>
            `).join('') : `<tr><td>${invoiceData.items}</td><td class="center">-</td><td class="right">${formatCurrency(subtotal)}</td></tr>`}
          </tbody>
        </table>

        <div class="bottom-layout">
          <div class="comments-box">
            <div class="section-header" style="margin-top: 0">COMMENTS</div>
            <div class="section-content" style="min-height: 100px;">
              <p>1. Total payment due in 30 days</p>
              <p>2. Please include the invoice number on your check</p>
              ${invoiceData.notes ? `<p>${invoiceData.notes}</p>` : ''}
            </div>
          </div>
          <div class="totals-box">
            <table class="totals-table">
              <tr><td class="label">Subtotal</td><td class="right">${formatCurrency(subtotal)}</td></tr>
              ${gstRate > 0 ? `
              <tr><td class="label">Taxable Amt</td><td class="right">${formatCurrency(taxableAmount)}</td></tr>
              <tr><td class="label">GST (${gstRate}%)</td><td class="right">${formatCurrency(taxAmount)}</td></tr>
              ` : ''}
              <tr class="total-row"><td>TOTAL</td><td class="right">${formatCurrency(total)}</td></tr>
            </table>
          </div>
        </div>

        <div style="text-align: right; margin-top: 20px;">
          ${(invoiceData.paymentMethod === 'Online') ? (() => {
            const subMethod = invoiceData.digitalSubMethod || digitalSubMethod || 'UPI';
            const payId = subMethod === 'InstaPay' ? shopSettings.instapayId : shopSettings.upiId;
            if (!payId) return '';
            const directPayUrl = subMethod === 'InstaPay'
              ? payId
              : `upi://pay?pa=${payId}&pn=${shopSettings.name}&am=${total.toFixed(2)}&cu=INR`;
            return `<img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(directPayUrl)}" style="width: 100px; height: 100px; border: 1px solid #000; padding: 5px;" />
                    <p style="font-size: 8px; font-weight: bold; margin-top: 4px; text-transform: uppercase;">Scan to Pay Online</p>`;
          })() : ''}
        </div>

        <div class="footer">
          THANK YOU FOR YOUR BUSINESS!
        </div>
      </div>
        `;
      }
    };

    let finalHtml = getPageContent(printDual ? t('customerCopy') : '', true);
    if (printDual) {
      finalHtml += getPageContent(t('shopCopy'), false);
    }

    const content = `
      <html>
        <head>
          <title>Print ${type}</title>
          <style>${styles}</style>
        </head>
        <body>
          ${finalHtml}
        </body>
      </html>
    `;

    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  }, [printFormat, sites, shopSettings, digitalSubMethod, t, formatCurrency, printDual, printBigOrderNumber]);




  // State Persistence for Tabs
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('activeTab') || 'dashboard');

  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddSiteModalOpen, setIsAddSiteModalOpen] = useState(false);

  const [attendanceSiteFilter, setAttendanceSiteFilter] = useState('All');
  const [employeeLocationFilter, setEmployeeLocationFilter] = useState('');
  const [payrollLocationFilter, setPayrollLocationFilter] = useState('');
  const [payrollMonthFilter, setPayrollMonthFilter] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [employeeMonthFilter, setEmployeeMonthFilter] = useState(new Date().toISOString().slice(0, 7)); // For Employees tab
  const [profitMonthFilter, setProfitMonthFilter] = useState(new Date().toISOString().slice(0, 7));
  const [profitPeriod, setProfitPeriod] = useState('Monthly');
  const [profitDateFilter, setProfitDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [profitYearFilter, setProfitYearFilter] = useState(new Date().getFullYear().toString());
  const [attendanceDateFilter, setAttendanceDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [isAddAttendanceModalOpen, setIsAddAttendanceModalOpen] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null); // For edit modal




  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);

      if (currentUser) {
        // Force PIN check on refresh/login
        setCurrentMode('Cashier');
        setShowSensitiveData(false);
        setPinAction('switchMode');
        setIsPinModalOpen(true);
      } else {
        // Clear all state data on logout to prevent leakage
        setEmployees([]);
        setSites([]);
        setAttendance([]);
        setPayrolls([]);
        setAccounts([]);
        setSales([]);
        setPurchases([]);
        setInventory([]);
        setHeldCarts([]);
        setCart([]);
        setShopSettings({
          name: '',
          address: '',
          phone: '',
          upiId: '',
          instapayId: '',
          gstEnabled: false,
          gstNumber: '',
          gstPercent: 0
        });
        setSecurityPin('1234');
        setSecurityQuestion('');
        setSecurityAnswer('');
        setShowSensitiveData(false);
        setPinInput('');
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [cafeSessions, setCafeSessions] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [cafeRooms, setCafeRooms] = useState([]);
  const [isStartSessionModalOpen, setIsStartSessionModalOpen] = useState(false);
  const [sessionStartForm, setSessionStartForm] = useState({ customerName: '', customerPhone: '', sessionType: 'Open' });
  const [pendingRoom, setPendingRoom] = useState(null);
  const [isCafeOrderModalOpen, setIsCafeOrderModalOpen] = useState(false);
  const [activeCafeSession, setActiveCafeSession] = useState(null);
  const [activeCafeCategory, setActiveCafeCategory] = useState('Hot Drinks');
  const [cafeSubTab, setCafeSubTab] = useState('board'); // 'board' | 'rooms' | 'recipes'

  // --- Service / Repair Shop State ---
  const [serviceTickets, setServiceTickets] = useState([]);
  const [serviceSubTab, setServiceSubTab] = useState('board'); // 'board' | 'active' | 'new' | 'history' | 'customers' | 'inventory' | 'sales' | 'reports'
  const [serviceForm, setServiceForm] = useState({
    customerName: '', customerPhone: '', customerEmail: '', customerAddress: '',
    deviceType: 'Mobile', brand: '', model: '', serialNo: '',
    issue: '', priority: 'Normal', technician: '', estimatedCost: '', status: 'Received',
    notes: '', diagnostics: '', partsUsed: [], paymentStatus: 'Unpaid', amountPaid: 0, paymentMethod: 'Cash', laborCost: 0,
    photos: []
  });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceStatusFilter, setServiceStatusFilter] = useState('');
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [partSearchResults, setPartSearchResults] = useState([]);
  const [serviceCustomers, setServiceCustomers] = useState([]);
  const [selectedServiceCustomer, setSelectedServiceCustomer] = useState(null);
  const [serviceCustomerForm, setServiceCustomerForm] = useState({ name: '', phone: '', email: '', address: '', notes: '' });
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [serviceInventory] = useState([]);
  const [isServiceInventoryModalOpen, setIsServiceInventoryModalOpen] = useState(false);
  const [editingServiceInventory, setEditingServiceInventory] = useState(null); // Added this
  const [serviceInventoryForm, setServiceInventoryForm] = useState({ name: '', category: 'Phone Parts', stock: 0, minStock: 5, buyPrice: 0, sellPrice: 0 });
  const [serviceCart, setServiceCart] = useState([]);
  const [serviceInventorySearch, setServiceInventorySearch] = useState('');
  const [serviceHistoryDateFilter, setServiceHistoryDateFilter] = useState(new Date().toISOString().split('T')[0]);

  async function handleUploadRepairPhoto(file, ticketId) {
    if (!file) return null;
    const storageRef = ref(storage, `repairs/${ticketId}/${Date.now()}_${file.name}`);
    try {
      setPhotoUploading(true);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      setPhotoUploading(false);
      return url;
    } catch (e) {
      console.error("Upload error:", e);
      setPhotoUploading(false);
      alert("Photo upload failed. Please check connection.");
      return null;
    }
  }

  const [roomForm, setRoomForm] = useState({ name: '', type: 'Cafe', hourlyPrice: 0 });
  const [recipeForm, setRecipeForm] = useState({ name: '', category: 'Hot Drinks', sellPrice: '', ingredients: [] });

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second for a real running clock
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (user) {
      const q = query(collection(db, 'cafeSessions'), where('userId', '==', user.uid));
      return onSnapshot(q, (snapshot) => {
        setCafeSessions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      });
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const q = query(collection(db, 'recipes'), where('userId', '==', user.uid));
      return onSnapshot(q, (snapshot) => {
        setRecipes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      });
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const q = query(collection(db, 'cafeRooms'), where('userId', '==', user.uid));
      return onSnapshot(q, (snapshot) => {
        const rooms = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (rooms.length === 0) {
          // One-time seed for new users
          const defaults = [
            { name: 'Room 1', type: 'PlayStation', userId: user.uid },
            { name: 'Table 1', type: 'Billiards', userId: user.uid },
            { name: 'Lounge 1', type: 'Cafe', userId: user.uid },
          ];
          defaults.forEach(d => addDoc(collection(db, 'cafeRooms'), d));
        }
        setCafeRooms(rooms);
      });
    }
  }, [user]);

  // Firestore: Service Tickets (Real-time)
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'serviceTickets'), where('userId', '==', user.uid));
    const unsub1 = onSnapshot(q, (snapshot) => {
      setServiceTickets(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    
    const qBorrow = query(collection(db, 'borrowRecords'), where('userId', '==', user.uid));
    const unsub2 = onSnapshot(qBorrow, (snapshot) => {
      setBorrowRecords(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    
    return () => {
      unsub1();
      unsub2();
    };
  }, [user]);

  // Firestore: Service Customers (Real-time)
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'serviceCustomers'), where('userId', '==', user.uid));
    return onSnapshot(q, (snapshot) => {
      setServiceCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
  }, [user]);

  // --- PIN Modal Keyboard Support ---
  useEffect(() => {
    if (!isPinModalOpen) return;
    const handleKeyDown = (e) => {
      if (e.key >= '0' && e.key <= '9') {
        if (pinInput.length < 4) {
          const newPin = pinInput + e.key;
          setPinInput(newPin);
        }
      } else if (e.key === 'Backspace') {
        setPinInput(prev => prev.slice(0, -1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPinModalOpen, pinInput]);

  // Auto-submit PIN when length is 4 (for keyboard support & clicks)
  useEffect(() => {
    if (isPinModalOpen && pinInput.length === 4) {
      handlePinAuth(pinInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinInput, isPinModalOpen]);

  // --- Auth Handlers ---
  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (authMode === 'login') {
        await signInWithEmailAndPassword(auth, authForm.email, authForm.password);
      } else {
        if (!otpSent) {
          // Generate 6-digit OTP and unique Registration ID
          const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
          setGeneratedOtp(newOtp);
          
          const regId = 'FINN-' + Math.random().toString(36).substr(2, 6).toUpperCase() + '-' + Date.now().toString().slice(-4);
          setAuthForm(prev => ({ ...prev, apiKey: regId }));
          
          // Actually sending OTP via EmailJS
          try {
            await emailjs.send(
              'service_omt8tsa', // User provided Service ID
              'template_yct5kru', // User provided Template ID
              {
                email: 'shoaibwwe01@outlook.com',
                passcode: newOtp,
                time: new Date().toLocaleTimeString(),
                user_email: authForm.email,
                registration_id: regId,
                app_name: 'Finn ERP'
              }
            );
            console.log("Email successfully sent to shoaibwwe01@outlook.com via EmailJS");
          } catch (error) {
            console.error("Email sending failed:", error);
            alert("EmailJS Error: " + (error.text || JSON.stringify(error)));
          }

          // BACKUP: Store registration request in Firestore so Admin can see it in-app
          try {
            await addDoc(collection(db, 'registrations'), {
              userEmail: authForm.email,
              otp: newOtp,
              registrationId: regId,
              status: 'Pending',
              timestamp: serverTimestamp()
            });
            console.log("Registration request backed up to Firestore");
          } catch (e) {
            console.error("Firestore Backup Error:", e);
          }
          
          setOtpSent(true);
          alert(t('otpSent') || "OTP sent to admin (shoaibwwe01@outlook.com). Please get the code from him to complete registration.");
          setLoading(false);
          return;
        }

        // Verify OTP
        if (otpInput !== generatedOtp) {
          alert(t('invalidOtp') || "Invalid OTP code. Please try again.");
          setLoading(false);
          return;
        }

        await createUserWithEmailAndPassword(auth, authForm.email, authForm.password);
      }
      setAuthForm({ email: '', password: '' });
      setOtpSent(false);
      setOtpInput('');
    } catch (error) {
      alert(t('authError') + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!authForm.email) return;
    setLoading(true);
    try {
      const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
      setGeneratedOtp(newOtp);
      
      await emailjs.send(
        'service_omt8tsa',
        'template_yct5kru',
        {
          email: 'shoaibwwe01@outlook.com',
          passcode: newOtp,
          time: new Date().toLocaleTimeString(),
          user_email: authForm.email,
          registration_id: authForm.apiKey,
          app_name: 'Finn ERP'
        }
      );
      
      // Update Firestore with new OTP
      const q = query(collection(db, 'registrations'), where('registrationId', '==', authForm.apiKey));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        await updateDoc(doc(db, 'registrations', snapshot.docs[0].id), { otp: newOtp, timestamp: serverTimestamp() });
      }

      alert(t('otpResent') || "A new OTP code has been sent successfully!");
    } catch (e) {
      console.error(e);
      alert("Resend failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error(error);
      alert(t('googleSignInError') + error.message);
    }
  };

  const handleAnonymousLogin = async () => {
    setLoading(true);
    try {
      await signInAnonymously(auth);
    } catch (error) {
      console.error(error);
      alert("Anonymous login failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      alert(t('enterEmail'));
      return;
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      alert(t('passwordResetSent'));
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      console.error(error);
      alert("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error(error);
    }
  };

  const handleFactoryReset = async () => {
    if (!user) return;
    if (!window.confirm(t('factoryResetWarning'))) return;

    const confirmPin = prompt(t('factoryResetPinPrompt') || t('pinPrompt'));
    if (confirmPin !== securityPin && confirmPin !== managerPin && confirmPin !== ownerPin) {
      alert(t('factoryResetIncorrectPin') || t('incorrectPin'));
      return;
    }

    setLoading(true);
    try {
      const collections = ['employees', 'sites', 'attendance', 'payroll', 'inventory', 'sales', 'purchases', 'invoices', 'accounts', 'journal_entries', 'suppliers', 'settings'];

      for (const colName of collections) {
        // We need to fetch all docs to delete them. 
        // Note: Client-side deletion of entire collections is slow for large datasets.
        const q = query(collection(db, colName), where('userId', '==', user.uid));
        const snapshot = await getDocs(q);
        const batch = writeBatch(db);
        let count = 0;

        snapshot.docs.forEach((doc) => {
          batch.delete(doc.ref);
          count++;
        });

        if (count > 0) await batch.commit();
      }

      alert(t('resetComplete'));
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert(t('resetFailed') + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportBackup = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const collections = ['employees', 'sites', 'attendance', 'inventory', 'sales', 'purchases', 'invoices', 'accounts', 'journal_entries', 'suppliers', 'settings'];
      const backup = {
        exportDate: new Date().toISOString(),
        userId: user.uid,
        userEmail: user.email,
        data: {}
      };

      for (const colName of collections) {
        const q = query(collection(db, colName), where('userId', '==', user.uid));
        const snapshot = await getDocs(q);
        backup.data[colName] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      const dataStr = JSON.stringify(backup, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `finnerp-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert(t('backupExported'));
    } catch (err) {
      console.error(err);
      alert(t('exportFailed') + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImportBackup = async (event) => {
    const file = event.target.files[0];
    if (!file || !user) return;

    if (!window.confirm(t('overwriteWarning'))) {
      event.target.value = null;
      return;
    }

    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const backup = JSON.parse(e.target.result);

        if (!backup.data) {
          throw new Error('Invalid backup file format');
        }

        for (const [colName, items] of Object.entries(backup.data)) {
          if (!Array.isArray(items)) continue;

          for (const item of items) {
            const { id, ...data } = item;
            data.userId = user.uid;
            await setDoc(doc(db, colName, id), data);
          }
        }

        alert(t('backupImported'));
        window.location.reload();
      } catch (err) {
        console.error(err);
        alert(t('importFailed') + err.message);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file);
    event.target.value = null;
  };

  const handleLinkGoogle = async () => {
    if (!auth.currentUser) return;
    try {
      const provider = new GoogleAuthProvider();
      await linkWithPopup(auth.currentUser, provider);
      alert(t('linkSuccess'));
    } catch (error) {
      console.error(error);
      if (error.code === 'auth/credential-already-in-use') {
        alert(t('accountLinked'));
      } else {
        alert(t('linkError') + error.message);
      }
    }
  };






  // --- Initial Data (Only used if localStorage is empty) ---



  // --- Firestore Listeners ---
  useEffect(() => {
    if (!user) return;

    // Employees Listener
    const qEmp = query(collection(db, 'employees'), where('userId', '==', user.uid));
    const unsubEmp = onSnapshot(qEmp, (snapshot) => {
      setEmployees(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error("Employees Listener Error:", error);
      setGlobalError("Access Denied: Employees List. " + error.message);
    });

    // Sites Listener
    const qSites = query(collection(db, 'sites'), where('userId', '==', user.uid));
    const unsubSites = onSnapshot(qSites, (snapshot) => {
      setSites(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error("Sites Listener Error:", error);
      setGlobalError("Access Denied: Sites List. " + error.message);
    });

    // Attendance Listener
    const qAtt = query(collection(db, 'attendance'), where('userId', '==', user.uid));
    const unsubAtt = onSnapshot(qAtt, (snapshot) => {
      setAttendance(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error("Attendance Listener Error:", error);
      setGlobalError("Access Denied: Attendance List. " + error.message);
    });

    // Payroll Listener
    const qPay = query(collection(db, 'payroll'), where('userId', '==', user.uid));
    const unsubPay = onSnapshot(qPay, (snapshot) => {
      setPayrolls(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error("Payroll Listener Error:", error);
    });

    return () => {
      unsubEmp();
      unsubSites();
      unsubAtt();
      unsubPay();
    };
  }, [user]);

  // Registrations Listener (Only if User is Owner or Manager to avoid broad reads)
  useEffect(() => {
    if (!user) return;
    const unsubReg = onSnapshot(query(collection(db, 'registrations')), (snapshot) => {
      setPendingRegistrations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubReg();
  }, [user]);

  // --- New Modules State & Listeners ---

  useEffect(() => {
    if (!user) return;

    // Accounts Listener
    const unsubAccounts = onSnapshot(query(collection(db, 'accounts'), where('userId', '==', user.uid)), (snapshot) => {
      setAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Sales Listener
    const unsubSales = onSnapshot(query(collection(db, 'sales'), where('userId', '==', user.uid)), (snapshot) => {
      setSales(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Purchases Listener
    const unsubPurchases = onSnapshot(query(collection(db, 'purchases'), where('userId', '==', user.uid)), (snapshot) => {
      setPurchases(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Inventory Listener
    const unsubInventory = onSnapshot(query(collection(db, 'inventory'), where('userId', '==', user.uid)), (snapshot) => {
      setInventory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Mobile/Shop Settings Listener
    const unsubSettings = onSnapshot(doc(db, 'settings', 'shop_' + user.uid), (doc) => {
      if (doc.exists()) {
        setShopSettings(doc.data());
      }
    });

    return () => {
      unsubAccounts();
      unsubSales();
      unsubPurchases();
      unsubInventory();
      unsubSettings();
    };
  }, [user]);

  // --- Persistence Effects replaced by Firestore Listeners ---

  const shifts = ['Morning (12 Hours)', 'Night (12 Hours)'];

  // Form State
  const [newEmployeeForm, setNewEmployeeForm] = useState({
    name: '', role: '', dept: 'Security', location: '', shift: 'Morning (12 Hours)', salary: 60000, salaryMethod: 'Monthly', bonus: 0, overtime: 0, deductionHours: 0, photo: ''
  });

  const [newSiteForm, setNewSiteForm] = useState({
    name: '', city: '', manager: '', status: 'Operational'
  });



  const [newAttendanceForm, setNewAttendanceForm] = useState({
    employeeId: '', date: new Date().toISOString().split('T')[0], status: 'On Time', replacementFor: '', locationFilter: ''
  });
  const getEmployeeLocation = (employeeName) => {
    const emp = employees.find(e => e.name === employeeName);
    return emp ? emp.location : 'Unknown';
  };

  /* --- Excel Import/Export Handling with exceljs --- */

  /* --- PDF Generation Handling with jsPDF & AutoTable --- */

  const generatePDF = (headers, data, filename, extraMetadata = []) => {
    try {
      const doc = new jsPDF({
        orientation: headers.length > 7 ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;

      // Sanitization: Ensure data is clean (no nulls/undefined)
      const cleanData = data.map(row =>
        row.map(cell => (cell === null || cell === undefined) ? '' : cell)
      );

      // 1. Branding Header

      if (shopSettings.logo) {
        try {
          // Increased logo size and adjusted position to prevent overlap
          doc.addImage(shopSettings.logo, 'PNG', 15, 10, 20, 20);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(22);
          doc.setTextColor(15, 23, 42);
          // Enforce "FINN ERP" as requested
          doc.text('FINN ERP', 40, 20);
        } catch (e) {
          console.error("PDF Logo Error:", e);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(22);
          doc.setTextColor(15, 23, 42);
          doc.text('FINN ERP', 15, 20);
        }
      } else {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(22);
        doc.setTextColor(15, 23, 42);
        doc.text('FINN ERP', 15, 20);
      }

      doc.setFontSize(11);
      doc.setTextColor(59, 130, 246);
      const subTitle = filename.replace('.pdf', '').replace(/_/g, ' ').toUpperCase();
      // Adjusted X to 40 to match the new logo width
      const labelX = shopSettings.logo ? 40 : 15;
      doc.text(subTitle, labelX, 27);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(`${t('date')}: ${new Date().toLocaleString()}`, labelX, 33);

      if (extraMetadata.length > 0) {
        doc.text(extraMetadata.join('  |  '), 15, 38);
        doc.setDrawColor(203, 213, 225);
        doc.line(15, 41, pageWidth - 15, 41);
      } else {
        doc.setDrawColor(203, 213, 225);
        doc.line(15, 36, pageWidth - 15, 36);
      }

      // 2. Table Implementation
      autoTable(doc, {
        head: [headers],
        body: cleanData,
        startY: extraMetadata.length > 0 ? 48 : 42,
        theme: 'striped',
        styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 2.5, valign: 'middle' },
        headStyles: {
          fillColor: [30, 41, 59],
          textColor: [255, 255, 255],
          fontSize: 9.5,
          fontStyle: 'bold',
          halign: 'left',
          lineWidth: 0.1,
          lineColor: [255, 255, 255]
        },
        columnStyles: headers.length > 5 ? {
          0: { cellWidth: 50 }, // Category/Name
          2: { halign: 'center' }, // Qty
          [headers.length - 1]: { halign: 'right', fontStyle: 'bold' } // Final Amount
        } : {
          0: { cellWidth: 60 },
          2: { halign: 'right' }
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didParseCell: (cellData) => {
          const rowData = cellData.row.raw;
          const cellVal = String(cellData.cell.raw || '');
          const firstCellVal = String(rowData[0] || '').toUpperCase();

          // Style Totals
          const isTotal = firstCellVal.includes('TOTAL') || firstCellVal.includes('NET PRO') || firstCellVal.includes('GROSS PRO') || firstCellVal.includes('NET PAYA');
          if (isTotal) {
            cellData.cell.styles.fontStyle = 'bold';
            cellData.cell.styles.fillColor = [239, 246, 255];
            cellData.cell.styles.textColor = [30, 64, 175];
            cellData.cell.styles.fontSize = 9;
          }

          // Style Section Headers
          const nonEmptyCount = rowData.filter(x => x !== '' && x !== null).length;
          const isSection = nonEmptyCount === 1 && isNaN(rowData.find(x => x !== '' && x !== null));

          if (isSection) {
            cellData.cell.styles.fontStyle = 'bold';
            cellData.cell.styles.fillColor = [241, 245, 249];
            cellData.cell.styles.fontSize = 10.5;
            cellData.cell.styles.textColor = [15, 23, 42];
            cellData.cell.styles.cellPadding = 4;
          }

          // Indentation for sub-items (starting with space)
          if (cellVal.startsWith('  ')) {
            cellData.cell.styles.cellPadding = { left: 8 };
            cellData.cell.styles.textColor = [71, 85, 105];
          }

          // Number alignment
          if (typeof cellData.cell.raw === 'number' || (!isNaN(cellVal) && cellVal !== '')) {
            cellData.cell.styles.halign = 'right';
            const numVal = parseFloat(cellVal);
            if (numVal < 0) cellData.cell.styles.textColor = [220, 38, 38];
          }
        },
        margin: { left: 15, right: 15, bottom: 25 },
        didDrawPage: (pData) => {
          doc.setFontSize(7);
          doc.setTextColor(148, 163, 184);
          doc.text(`FINN ERP - ${filename.replace('.pdf', '')} - PRODUCED ON ${new Date().toLocaleDateString()}`, 15, pageHeight - 12);
          const pNum = `${t('page')} ${doc.internal.getNumberOfPages()}`;
          doc.text(pNum, pageWidth - 15 - doc.getTextWidth(pNum), pageHeight - 12);
        }
      });

      doc.save(filename);
    } catch (err) {
      console.error("PDF Generate Error:", err);
      alert("Error generating PDF: " + err.message);
    }
  };

  const handleExportAttendance = () => {
    const headers = [t('name'), t('date'), t('status'), t('replacementFor'), t('shift'), t('location')];
    const data = attendance.map(a => [
      a.name,
      a.date instanceof Date ? a.date.toLocaleDateString() : (a.date?.toDate ? a.date.toDate().toLocaleDateString() : String(a.date)),
      a.status,
      a.replacementFor || '-',
      a.shift || '-',
      a.location || '-'
    ]);

    const metadata = [
      `${t('totalRecords') || 'Total Records'}: ${data.length}`,
      `${t('location')}: ${reportLocationFilter || t('filterAll')}`
    ];

    generatePDF(headers, data, 'Attendance_Report.pdf', metadata);
  };

  const handleExportPayroll = () => {
    const headers = [
      t('name'), t('location'), t('role'),
      t('salary'), t('bonus'), t('overtime'),
      t('advance'), t('deductions'), t('netPay')
    ];

    const data = employees
      .filter(emp => !payrollLocationFilter || emp.location === payrollLocationFilter)
      .map(emp => {
        let baseSalary = Number(emp.salary) || 0;
        let bonus = Number(emp.bonus) || 0;
        let overtime = Number(emp.overtime) || 0;
        let deductionAmount = 0;

        const empAttendance = attendance.filter(a => {
          if (a.name !== emp.name) return false;
          let attendanceMonth;
          if (typeof a.date === 'string') attendanceMonth = a.date.substring(0, 7);
          else if (a.date?.toDate) attendanceMonth = a.date.toDate().toISOString().substring(0, 7);
          else if (a.date instanceof Date) attendanceMonth = a.date.toISOString().substring(0, 7);
          return attendanceMonth === payrollMonthFilter;
        });

        empAttendance.forEach(record => {
          if (record.status === 'Late') {
            const hourlyRate = baseSalary / 360;
            deductionAmount += (Number(record.lateHours) || 0) * hourlyRate;
          }
          if (record.status === 'Absent') {
            deductionAmount += baseSalary / 30;
          }
        });

        const hourlyRate = baseSalary / 360;
        const manualDeduction = (Number(emp.deductionHours) || 0) * hourlyRate;
        const storedRecord = payrolls.find(p => p.employeeId === emp.id && p.month === payrollMonthFilter);

        if (storedRecord) {
          baseSalary = Number(storedRecord.salary) || baseSalary;
          bonus = Number(storedRecord.bonus) || 0;
          overtime = Number(storedRecord.overtime) || 0;
        }

        const effectiveManualDeduction = storedRecord ? (Number(storedRecord.deductions) || 0) : manualDeduction;
        const effectiveAdvance = storedRecord ? (Number(storedRecord.advance) || 0) : (Number(emp.advanceSalary) || 0);

        const totalDeductions = deductionAmount + effectiveManualDeduction + effectiveAdvance;
        const netPay = baseSalary + bonus + overtime - totalDeductions;

        return [
          emp.name,
          emp.location,
          emp.role,
          baseSalary,
          bonus,
          overtime,
          effectiveAdvance,
          totalDeductions,
          netPay
        ];
      });

    const metadata = [
      `${t('payPeriod')}: ${payrollMonthFilter}`,
      `${t('location')}: ${payrollLocationFilter || t('filterAll')}`,
      `${t('totalNetPay') || 'Total Net'}: ${data.reduce((sum, row) => sum + row[8], 0).toFixed(2)}`
    ];

    generatePDF(headers, data, 'Payroll_Report.pdf', metadata);
  };

  const handleImportPayroll = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const buffer = e.target.result;
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);
      const worksheet = workbook.getWorksheet(1); // First sheet

      const batch = writeBatch(db);
      let updatedCount = 0;

      let headerRowIndex = 1;
      worksheet.eachRow((row, rowNumber) => {
        if (row.values.includes('Employee') || row.values.includes('Name')) {
          headerRowIndex = rowNumber;
        }
      });

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber <= headerRowIndex) return;

        const rowVal = row.values;
        // Assume export order: [1]Name, [2]Role, [3]Salary, [4]Bonus, [5]Overtime
        const name = rowVal[1];

        if (!name) return;

        const salary = parseFloat(rowVal[3]);
        const bonus = parseFloat(rowVal[4]);
        const overtime = parseFloat(rowVal[5]);

        const emp = employees.find(e => e.name.toLowerCase() === name.toString().toLowerCase());

        if (emp && !isNaN(salary)) {
          const empRef = doc(db, 'employees', emp.id);
          batch.update(empRef, {
            salary: salary,
            bonus: isNaN(bonus) ? 0 : bonus,
            overtime: isNaN(overtime) ? 0 : overtime
          });
          updatedCount++;
        }
      });

      try {
        await batch.commit();
        alert(t('payrollUpdateSuccess').replace('{0}', updatedCount));
      } catch (err) {
        console.error(err);
        alert(t('payrollUpdateError') + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = null;
  };

  const handleImportAttendance = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const buffer = e.target.result;
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);
      const worksheet = workbook.getWorksheet(1);

      const batch = writeBatch(db);
      let count = 0;

      let headerRowIndex = 1;
      worksheet.eachRow((row, rowNumber) => {
        if (row.values.includes('Employee') || row.values.includes('Name')) headerRowIndex = rowNumber;
      });

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber <= headerRowIndex) return;

        const rowVal = row.values;
        const name = rowVal[1];
        const date = rowVal[2];
        const status = rowVal[4]; // [3] is Location usually

        if (!name || !date) return;

        const newRef = doc(collection(db, 'attendance'));
        batch.set(newRef, {
          name: name,
          date: new Date(date).toISOString().split('T')[0],
          status: status || 'Pending',
          userId: user.uid
        });
        count++;
      });

      try {
        await batch.commit();
        alert(t('importSuccess').replace('{0}', count));
      } catch (err) {
        console.error(err);
        alert(t('importError') + err.message);
      }
    }
    reader.readAsArrayBuffer(file);
    event.target.value = null;
  };

  // --- Payroll Management Logic ---
  const [isManagePayrollModalOpen, setIsManagePayrollModalOpen] = useState(false);
  const [currentPayrollForm, setCurrentPayrollForm] = useState({
    id: '', // Employee ID
    name: '',
    role: '',
    month: '',
    salary: 0,
    bonus: 0,
    overtime: 0,
    advance: 0,
    deductions: 0, // Manual Deductions
    lateDeduction: 0, // For Display/Reference
    absentDeduction: 0 // For Display/Reference
  });

  const handleManagePayroll = (emp, month, calculatedLate, calculatedAbsent) => {
    // Check if record exists for this month
    const existingRecord = payrolls.find(p => p.employeeId === emp.id && p.month === month);

    if (existingRecord) {
      setCurrentPayrollForm({
        id: emp.id,
        name: emp.name,
        role: emp.role,
        month: month,
        salary: Number(existingRecord.salary) || 0,
        bonus: Number(existingRecord.bonus) || 0,
        overtime: Number(existingRecord.overtime) || 0,
        advance: Number(existingRecord.advance) || 0,
        deductions: Number(existingRecord.deductions) || 0,
        lateDeduction: calculatedLate,
        absentDeduction: calculatedAbsent
      });
    } else {
      // Default from Profile
      setCurrentPayrollForm({
        id: emp.id,
        name: emp.name,
        role: emp.role,
        month: month,
        salary: Number(emp.salary) || 0,
        bonus: Number(emp.bonus) || 0,
        overtime: Number(emp.overtime) || 0,
        advance: Number(emp.advanceSalary) || 0,
        deductions: Number(emp.deductionHours) || 0,
        lateDeduction: calculatedLate,
        absentDeduction: calculatedAbsent
      });
    }
    setIsManagePayrollModalOpen(true);
  };

  const handleSavePayroll = async (e) => {
    e.preventDefault();
    if (!user) return;

    try {
      // Create a deterministic ID: employeeId_YYYY-MM
      const docId = `${currentPayrollForm.id}_${currentPayrollForm.month}`;
      const docRef = doc(db, 'payroll', docId);

      await setDoc(docRef, {
        employeeId: currentPayrollForm.id,
        month: currentPayrollForm.month,
        salary: Number(currentPayrollForm.salary),
        bonus: Number(currentPayrollForm.bonus),
        overtime: Number(currentPayrollForm.overtime),
        advance: Number(currentPayrollForm.advance),
        deductions: Number(currentPayrollForm.deductions),
        updatedAt: serverTimestamp(),
        userId: user.uid
      }, { merge: true });

      setIsManagePayrollModalOpen(false);
      // Optional: alert('Saved');
    } catch (err) {
      console.error(err);
      alert("Error saving payroll: " + err.message);
    }
  };

  const handleDeletePayrollRecord = async (employeeId, month) => {
    if (!user) return;

    const confirmDelete = window.confirm(`Reset payroll to default values for ${month}? This will delete any custom adjustments.`);
    if (!confirmDelete) return;

    try {
      const docId = `${employeeId}_${month}`;
      const docRef = doc(db, 'payroll', docId);
      await deleteDoc(docRef);
      setIsManagePayrollModalOpen(false);
      // Optional: alert('Reset to defaults');
    } catch (err) {
      console.error(err);
      alert("Error deleting record: " + err.message);
    }
  };

  // --- Accounts Logic ---
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [newAccountForm, setNewAccountForm] = useState({ name: '', type: 'Asset', balance: 0 });

  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!user) return;
    try {
      await addDoc(collection(db, 'accounts'), {
        ...newAccountForm,
        userId: user.uid,
        createdAt: serverTimestamp()
      });
      setIsAddAccountModalOpen(false);
      setNewAccountForm({ name: '', type: 'Asset', balance: 0 });
    } catch (err) {
      console.error(err);
      alert("Error adding account: " + err.message);
    }
  };

  // --- Inventory Logic ---
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [isPrintBarcodeModalOpen, setIsPrintBarcodeModalOpen] = useState(false);
  const [printConfigs, setPrintConfigs] = useState([]); // Array of { item, qty }
  const [newItemForm, setNewItemForm] = useState({ name: '', quantity: 0, location: '', category: '', buyPrice: 0, sellPrice: 0, photo: '', barcode: '' });
  const [inventorySearch, setInventorySearch] = useState('');

  const [editingItem, setEditingItem] = useState(null); // For Edit Flow

  const handleNewItemImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (editingItem) {
        setEditingItem({ ...editingItem, photo: reader.result });
      } else {
        setNewItemForm({ ...newItemForm, photo: reader.result });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!user) return;

    // Check for duplicates (Name + Location)
    if (inventory.some(item =>
      item.name.trim().toLowerCase() === newItemForm.name.trim().toLowerCase() &&
      item.location === newItemForm.location
    )) {
      alert(t('itemExists') || "Item already exists at this location!");
      return;
    }

    try {
      const invData = {
        ...newItemForm,
        userId: user.uid,
        updatedAt: serverTimestamp()
      };
      await addDoc(collection(db, 'inventory'), invData);

      // Record as a purchase for P&L
      const totalCost = (Number(newItemForm.quantity) || 0) * (Number(newItemForm.buyPrice) || 0);
      if (totalCost > 0) {
        await addDoc(collection(db, 'purchases'), {
          name: newItemForm.name + " (Initial Stock)",
          amount: totalCost,
          quantity: Number(newItemForm.quantity),
          buyPrice: Number(newItemForm.buyPrice),
          location: newItemForm.location,
          category: newItemForm.category,
          userId: user.uid,
          date: new Date().toISOString().split('T')[0],
          createdAt: serverTimestamp(),
          type: 'Inventory Add'
        });
      }

      setIsAddItemModalOpen(false);
      setNewItemForm({ name: '', quantity: 0, location: '', category: '', buyPrice: 0, sellPrice: 0, photo: '', barcode: '' });
    } catch (err) {
      console.error(err);
      alert(t('addItemError') + err.message);
    }
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    if (!user || !editingItem) return;

    // Check for duplicates (excluding current item) - Name + Location unique
    if (inventory.some(item =>
      item.id !== editingItem.id &&
      item.name.trim().toLowerCase() === editingItem.name.trim().toLowerCase() &&
      item.location === editingItem.location
    )) {
      alert(t('itemExists') || "Item already exists at this location!");
      return;
    }

    try {
      const oldItem = inventory.find(i => i.id === editingItem.id);
      const oldQty = oldItem ? Number(oldItem.quantity) || 0 : 0;
      const newQty = Number(editingItem.quantity) || 0;
      const qtyDiff = newQty - oldQty;

      await updateDoc(doc(db, 'inventory', editingItem.id), {
        name: editingItem.name,
        quantity: newQty,
        location: editingItem.location,
        category: editingItem.category,
        buyPrice: Number(editingItem.buyPrice),
        sellPrice: Number(editingItem.sellPrice),
        barcode: editingItem.barcode || '',
        photo: editingItem.photo || '',
        updatedAt: serverTimestamp()
      });

      // If quantity increased, record as a purchase for P&L
      if (qtyDiff > 0) {
        const totalCost = qtyDiff * (Number(editingItem.buyPrice) || 0);
        await addDoc(collection(db, 'purchases'), {
          name: editingItem.name + " (Stock Update)",
          amount: totalCost,
          quantity: qtyDiff,
          buyPrice: Number(editingItem.buyPrice),
          location: editingItem.location,
          category: editingItem.category,
          userId: user.uid,
          date: new Date().toISOString().split('T')[0],
          createdAt: serverTimestamp(),
          type: 'Stock Increase'
        });
      }

      setEditingItem(null);
      setIsAddItemModalOpen(false); // Close generic modal if used, or edit modal
    } catch (err) {
      console.error(err);
      alert(t('updateError') + err.message);
    }
  };

  const handleDeleteWarehouseItem = async (id) => {
    if (!window.confirm(t('confirmDelete') || "Are you sure you want to delete this item?")) return;
    if (!user) return;
    try {
      await deleteDoc(doc(db, 'inventory', id));
      // optional: alert removed for smoother UX, or keep if consistent with other deletes
    } catch (err) {
      console.error(err);
      alert(t('deleteError') + err.message);
    }
  };

  // --- Sales & Purchases (POS) Logic ---
  const [isAddSaleModalOpen, setIsAddSaleModalOpen] = useState(false);
  const [posHistoryDate, setPosHistoryDate] = useState(new Date().toISOString().split('T')[0]);



  // Auto-select first location for Strict Mode
  useEffect(() => {
    if (!posLocationFilter && sites.length > 0) {
      setPosLocationFilter(sites[0].name);
    }
  }, [sites, posLocationFilter]);

  const addToCart = useCallback((item) => {
    // 1. Check if item has any stock at all
    if (item.quantity <= 0) {
      alert(t('outOfStock') || "This item is out of stock!");
      return;
    }

    // 2. Strict Location Rule
    if (cart.length > 0) {
      const cartLocation = cart[0].location;
      if (item.location !== cartLocation) {
        alert(t('mixedLocationError') || "Cannot mix items from different locations! Please clear cart first.");
        return;
      }
    } else {
      if (!posLocationFilter) {
        setPosLocationFilter(item.location);
      }
    }

    // 3. Check current cart quantity against stock
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        if (existing.quantity + 1 > item.quantity) {
          alert(`${t('maxStockReached') || 'Cannot add more. Only'} ${item.quantity} ${t('available') || 'available'}`);
          return prev;
        }
        return prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1, price: Number(item.sellPrice) || 0, barcode: item.barcode || '' }];
    });
  }, [cart, t, posLocationFilter]);





  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(i => i.id !== itemId));
  };

  const updateCartQuantity = (itemId, delta) => {
    // Find the item in the current inventory state to check its stock
    const stockItem = inventory.find(inv => inv.id === itemId);
    const maxStock = stockItem ? Number(stockItem.quantity) : 0;

    setCart(prev => prev.map(i => {
      if (i.id === itemId) {
        const nextQty = i.quantity + delta;
        // Check for upper limit (stock)
        if (delta > 0 && nextQty > maxStock) {
          alert(`${t('maxStockReached') || 'No more stock available! Max:'} ${maxStock}`);
          return i;
        }
        // Lower limit is always 1
        return { ...i, quantity: Math.max(1, nextQty) };
      }
      return i;
    }));
  };

  const calculateTotal = useCallback(() => cart.reduce((sum, item) => sum + (Number(item.price || item.sellPrice || 0) * Number(item.quantity)), 0), [cart]);

  
  const generateSequentialInvoiceId = async (prefix, locName) => {
    try {
      return await runTransaction(db, async (transaction) => {
        const counterRef = doc(db, 'counters', 'daily_invoice');
        const counterDoc = await transaction.get(counterRef);
        const today = new Date().toISOString().split('T')[0];
        const locationName = locName || 'Main';
        const locCode = locationName.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 4);
        const counterKey = `${locCode}_${prefix}`;
        
        let data = counterDoc.exists() ? counterDoc.data() : { date: today };
        if (data.date !== today) { data = { date: today }; }
        
        const count = (data[counterKey] || 0) + 1;
        data[counterKey] = count;
        transaction.set(counterRef, data);
        return `${locCode}-${prefix}-${count.toString().padStart(4, '0')}`;
      });
    } catch (e) {
      console.error("Counter failed", e);
      return prefix + '-' + Date.now().toString().slice(-6);
    }
  };

  const handleCheckout = useCallback(async () => {
    if (!user || cart.length === 0) return;

    if (!salesEmployee) {
      alert(t('selectEmployeeAlert') || "Please select a sales employee!");
      return;
    }
    try {
      calculateTotal();
      const prefix = orderType === 'Walk-in' ? 'W' : 'T';
      let uniqueId;

      uniqueId = await generateSequentialInvoiceId(prefix, posLocationFilter);

      const saleData = {
        invoiceId: uniqueId,
        type: 'sale',
        orderType: orderType,
        paymentMethod: paymentMethod,
        digitalSubMethod: paymentMethod === 'Online' ? digitalSubMethod : null,
        customer: newSaleForm.customer || (orderType === 'Walk-in' ? t('walkInCustomer') : t('takeawayCustomer')),
        customerId: newSaleForm.customerId || null,
        status: 'Completed',
        items: cart.map(i => ({
          id: i.id,
          name: i.name,
          price: i.sellPrice,
          qty: i.quantity
        })),
        amount: Math.max(0, calculateTotal() - cartDiscount), // Apply Discount
        discount: cartDiscount, // Save Discount
        location: posLocationFilter || 'Main',
        userId: user.uid,
        soldBy: salesEmployee ? salesEmployee.name : 'Unknown', // Sales Employee Name
        soldById: salesEmployee ? salesEmployee.id : null, // Sales Employee ID
        date: new Date().toISOString().split('T')[0],
        createdAt: serverTimestamp()
      };

      // 1. Save Sale
      const saleRef = await addDoc(collection(db, 'sales'), saleData);

      // 2. Update Inventory (Decrement Stock)
      const batch = writeBatch(db);
      for (const item of cart) {
        if (item.type === 'service') continue; // Skip service items as they don't have warehouse stock

        const itemRef = doc(db, 'inventory', item.id);
        const currentItem = inventory.find(inv => inv.id === item.id);

        if (currentItem) {
          const newQty = Number(currentItem.quantity) - Number(item.quantity);
          if (newQty < 0) {
            throw new Error(`${t('insufficientStock') || 'Insufficient stock for'} ${item.name}`);
          }
          batch.update(itemRef, { quantity: newQty });
        }
      }
      await batch.commit();

      // 3. Print Invoice (Optional auto-print)
      handlePrintInvoice({ ...saleData, id: saleRef.id }, 'Sales Receipt');

      // Reset
      setCart([]);
      setCartDiscount(0); // Reset Discount
      setNewSaleForm({ customer: 'Walk-in Customer', amount: 0, status: 'Completed', items: '' });
      setSalesEmployee(null);
    } catch (err) {
      console.error(err);
      alert("Checkout Error: " + err.message);
    }
  }, [user, cart, salesEmployee, orderType, t, calculateTotal, posLocationFilter, digitalSubMethod, cartDiscount, inventory, handlePrintInvoice, newSaleForm.customer, newSaleForm.customerId, paymentMethod]);

  // --- Cafe Handlers ---
  const handleStartCafeSession = (room) => {
    setPendingRoom(room);
    setSessionStartForm({ customerName: '', customerPhone: '', sessionType: 'Open' });
    setIsStartSessionModalOpen(true);
  };

  const handleConfirmStartSession = async (e) => {
    e.preventDefault();
    if (!user || !pendingRoom) return;
    try {
      let sessionNo = 'S-' + Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      try {
        sessionNo = await runTransaction(db, async (transaction) => {
          const counterRef = doc(db, 'counters', 'daily_cafe');
          const counterDoc = await transaction.get(counterRef);
          const today = new Date().toISOString().split('T')[0];

          let data = counterDoc.exists() ? counterDoc.data() : { date: today };
          if (data.date !== today) {
            data = { date: today };
          }

          const count = (data['session_count'] || 0) + 1;
          data['session_count'] = count;

          transaction.set(counterRef, data);
          return `S-${count.toString().padStart(4, '0')}`;
        });
      } catch (e) { console.error("Cafe counter failed", e); }

      await addDoc(collection(db, 'cafeSessions'), {
        sessionId: sessionNo,
        roomId: pendingRoom.id,
        roomName: pendingRoom.name,
        roomType: pendingRoom.type,
        hourlyRate: pendingRoom.hourlyPrice || 0,
        startTime: serverTimestamp(),
        status: 'Active',
        orders: [],
        customerName: sessionStartForm.customerName,
        customerPhone: sessionStartForm.customerPhone,
        sessionType: sessionStartForm.sessionType,
        userId: user.uid,
        createdAt: serverTimestamp()
      });
      setIsStartSessionModalOpen(false);
      setPendingRoom(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleStopCafeSession = async (session) => {
    if (!user) return;
    try {
      const end = new Date();
      const start = session.startTime?.seconds ? new Date(session.startTime.seconds * 1000) : new Date();
      const durationMs = Math.max(1, end - start);
      const durationMins = Math.floor(durationMs / 60000);


      const rate = Number(session.hourlyRate);

      // User Logic: Minimum 1 hour charge for any time up to 60 minutes.
      // After 60 minutes, it calculates proportionally.
      let sessionCost;
      if (durationMins <= 60) {
        sessionCost = rate;
      } else {
        // Pro-rated calculation for sessions longer than an hour
        sessionCost = (durationMins / 60) * rate;
      }

      const ordersCost = (session.orders || []).reduce((sum, o) => sum + (Number(o.sellPrice) * Number(o.quantity || 1)), 0);
      const totalAmount = Math.round(sessionCost + ordersCost);

      await updateDoc(doc(db, 'cafeSessions', session.id), {
        status: 'Completed',
        endTime: serverTimestamp(),
        totalCost: totalAmount
      });

      const saleData = {
        invoiceId: session.sessionId || (await generateSequentialInvoiceId('C', 'CAFE')),
        type: 'cafe',
        orderType: 'Dine-in',
        paymentMethod: 'Cash',
        customer: session.roomName,
        status: 'Completed',
        items: [
          { name: `Session Time (${durationMins}m)`, price: sessionCost, qty: 1 },
          ...(session.orders || []).map(o => ({ id: o.id, name: o.name, price: o.sellPrice, qty: o.quantity }))
        ],
        amount: totalAmount,
        location: 'Cafe',
        userId: user.uid,
        date: new Date().toISOString().split('T')[0],
        createdAt: serverTimestamp()
      };
      await addDoc(collection(db, 'sales'), saleData);
      handlePrintInvoice(saleData, 'Cafe Receipt');
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateHistorySale = async (e) => {
    e.preventDefault();
    if (!editingHistoryItem || currentMode !== 'Owner') return;
    try {
      await updateDoc(doc(db, 'sales', editingHistoryItem.id), {
        customer: editingHistoryItem.customer,
        amount: Number(editingHistoryItem.amount),
        paymentMethod: editingHistoryItem.paymentMethod
      });
      setIsEditHistoryModalOpen(false);
      setEditingHistoryItem(null);
      alert(t('saleUpdated') || 'Sale updated successfully!');
    } catch (err) {
      console.error(err);
      alert(t('updateError') + err.message);
    }
  };

  const handleDeleteSale = async (sale) => {
    if (currentMode !== 'Owner') {
      alert(t('permissionDeniedOwner') || 'Permission Denied. Only the Owner can delete sales history.');
      return;
    }

    if (!window.confirm(t('confirmDeleteSale') || 'Are you sure you want to delete this bill? This will also restore stock levels.')) return;

    try {
      const batch = writeBatch(db);

      // 1. Restore Stock if applicable
      if (Array.isArray(sale.items)) {
        sale.items.forEach(item => {
          if (item.id) {
            // Check Warehouse Inventory
            const invItem = inventory.find(i => i.id === item.id);
            if (invItem) {
              const newQty = Number(invItem.quantity) + Number(item.qty || item.quantity);
              batch.update(doc(db, 'inventory', item.id), { quantity: newQty });
            }

            // Check Service Inventory
            const srvItem = serviceInventory.find(i => i.id === item.id);
            if (srvItem) {
              const newQty = Number(srvItem.stock) + Number(item.qty || item.quantity);
              batch.update(doc(db, 'serviceInventory', item.id), { stock: newQty });
            }
          }
        });
      }

      // 2. Delete the Sale Document
      batch.delete(doc(db, 'sales', sale.id));

      await batch.commit();
      alert(t('saleDeleted') || 'Bill deleted and stock restored.');
    } catch (err) {
      console.error("Error deleting sale:", err);
      alert(t('errorDeleting') || 'Error deleting bill.');
    }
  };

  const handleCheckoutCafeOrder = async (sessionId, cartItems) => {
    if (!user) return;
    try {
      const batch = writeBatch(db);
      for (const item of cartItems) {
        // Find recipe or direct item
        const recipe = recipes.find(r => r.id === item.id);
        if (recipe && recipe.ingredients) {
          for (const ing of recipe.ingredients) {
            const ingRef = doc(db, 'inventory', ing.id);
            const invItem = inventory.find(i => i.id === ing.id);
            if (invItem) {
              const consumption = Number(ing.qty) * (Number(item.quantity) || 1);
              const newQty = Number(invItem.quantity) - consumption;
              if (newQty < 0) {
                throw new Error(`${t('insufficientIngredient') || 'Insufficient stock for ingredient'} ${invItem.name} in ${item.name}`);
              }
              batch.update(ingRef, { quantity: newQty });
            }
          }
        } else {
          const itemRef = doc(db, 'inventory', item.id);
          const invItem = inventory.find(i => i.id === item.id);
          if (invItem) {
            const newQty = Number(invItem.quantity) - (Number(item.quantity) || 1);
            if (newQty < 0) {
              throw new Error(`${t('insufficientStock') || 'Insufficient stock for'} ${item.name}`);
            }
            batch.update(itemRef, { quantity: newQty });
          }
        }
      }

      if (sessionId) {
        const session = cafeSessions.find(s => s.id === sessionId);
        if (session) {
          const updatedOrders = [...(session.orders || []), ...cartItems];
          batch.update(doc(db, 'cafeSessions', sessionId), {
            orders: updatedOrders,
            updatedAt: serverTimestamp()
          });
        }
      }
      await batch.commit();
      setCart([]);
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  const handleSaveRoom = async (e) => {
    e.preventDefault();
    if (!user) return;
    try {
      if (roomForm.id) {
        await updateDoc(doc(db, 'cafeRooms', roomForm.id), {
          name: roomForm.name,
          type: roomForm.type,
          hourlyPrice: Number(roomForm.hourlyPrice)
        });
      } else {
        await addDoc(collection(db, 'cafeRooms'), {
          ...roomForm,
          hourlyPrice: Number(roomForm.hourlyPrice),
          userId: user.uid
        });
      }
      setRoomForm({ name: '', type: 'Cafe', hourlyPrice: 0 });
    } catch (err) { console.error(err); }
  };

  const handleDeleteRoom = async (id) => {
    if (!user || !window.confirm(t('confirmDelete'))) return;
    try { await deleteDoc(doc(db, 'cafeRooms', id)); } catch (err) { console.error(err); }
  };

  const handleSaveRecipe = async (e) => {
    e.preventDefault();
    if (!user || !recipeForm.name) return;
    try {
      if (recipeForm.id) {
        await updateDoc(doc(db, 'recipes', recipeForm.id), {
          name: recipeForm.name,
          category: recipeForm.category,
          sellPrice: recipeForm.sellPrice,
          ingredients: recipeForm.ingredients,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'recipes'), { ...recipeForm, userId: user.uid, createdAt: serverTimestamp() });
      }
      setRecipeForm({ name: '', category: 'Hot Drinks', sellPrice: '', ingredients: [] });
    } catch (err) { console.error(err); }
  };

  const handleDeleteRecipe = async (id) => {
    if (!user || !window.confirm(t('confirmDelete'))) return;
    try { await deleteDoc(doc(db, 'recipes', id)); } catch (err) { console.error(err); }
  };

  // Legacy function kept for compatibility if needed, but replaced by handleCheckout
  const handleAddSale = async (e) => {
    e.preventDefault();
    if (!user) return;
    // ... existing logic if still used by modal, but we are moving to POS
  };

  const [isAddPurchaseModalOpen, setIsAddPurchaseModalOpen] = useState(false);
  const [newPurchaseForm, setNewPurchaseForm] = useState({ supplier: '', amount: 0, status: 'Ordered', items: '' });

  const handleAddPurchase = async (e) => {
    e.preventDefault();
    if (!user) return;
    try {
      await addDoc(collection(db, 'purchases'), {
        ...newPurchaseForm,
        userId: user.uid,
        date: new Date().toISOString().split('T')[0],
        createdAt: serverTimestamp()
      });
      setIsAddPurchaseModalOpen(false);
      setNewPurchaseForm({ supplier: '', amount: 0, status: 'Ordered', items: '' });
    } catch (err) {
      console.error(err);
      alert(t('addPurchaseError') + err.message);
    }
  };

  // --- Invoice Logic ---
  const [isAddInvoiceModalOpen, setIsAddInvoiceModalOpen] = useState(false);
  const [newInvoiceForm, setNewInvoiceForm] = useState({ client: '', status: 'Issued', date: new Date().toISOString().split('T')[0], items: [] });
  const [tempInvoiceItem, setTempInvoiceItem] = useState({ itemId: '', quantity: 1 }); // Logic for adding lines

  const handleAddInvoice = async (e) => {
    e.preventDefault();
    if (!user) return;
    try {
      const totalAmount = newInvoiceForm.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      await addDoc(collection(db, 'invoices'), {
        ...newInvoiceForm,
        amount: totalAmount,
        userId: user.uid,
        items: cart.map(i => ({ id: i.id, name: i.name, qty: i.quantity, price: i.price })),
        soldBy: salesEmployee ? salesEmployee.name : (user.email || 'Admin'),
        date: new Date().toISOString().split('T')[0],
        createdAt: serverTimestamp()
      });
      setIsAddInvoiceModalOpen(false);
      setNewInvoiceForm({ client: '', status: 'Issued', date: new Date().toISOString().split('T')[0], items: [] });
    } catch (err) {
      console.error(err);
      alert(t('genInvoiceError') + err.message);
    }
  };



  // Print Payroll Slip
  const handlePrintPayrollSlip = (employee, payrollData, month) => {
    const printWindow = window.open('', '', 'width=800,height=600');
    if (!printWindow) {
      alert('Please allow popups to print payroll slip');
      return;
    }

    // Get translated month name based on current language
    const locale = language === 'ar' ? 'ar-EG' : language === 'hi' ? 'hi-IN' : language === 'zh' ? 'zh-CN' : 'en-US';
    const monthName = new Date(month + '-01').toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    const currentDate = new Date().toLocaleDateString(locale);
    const currentTime = new Date().toLocaleTimeString(locale);

    // Pre-translate all strings (t() function won't work in print window)
    const translatedStrings = {
      payrollSlip: t('payrollSlip'),
      employeeName: t('employeeName'),
      department: t('department'),
      role: t('role'),
      location: t('location'),
      payPeriod: t('payPeriod'),
      description: t('description'),
      amount: t('amount'),
      basicSalary: t('basicSalary'),
      bonus: t('bonus'),
      overtime: t('overtime'),
      grossPay: t('grossPay'),
      lateDeductions: t('lateDeductions'),
      absentDeductions: t('absentDeductions'),
      advanceSalary: t('advanceSalary') || 'Advance Salary',
      totalDeductions: t('totalDeductions'),
      netPay: t('netPay'),
      employeeSignature: t('employeeSignature'),
      authorizedSignature: t('authorizedSignature'),
      computerGenerated: t('computerGenerated'),
      generatedOn: t('generatedOn'),
      at: t('at'),
      phone: t('phone'),
      companyName: shopSettings.name || t('companyName'),
      companyAddress: shopSettings.address || t('companyAddress')
    };

    const styles = `
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Arial', sans-serif; padding: 20px; background: #f5f5f5; }
        .slip { background: white; max-width: 700px; margin: 0 auto; padding: 25px; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
        .header { text-align: center; border-bottom: 2px solid #2563eb; padding-bottom: 12px; margin-bottom: 20px; }
        .header h1 { color: #1e40af; font-size: 22px; margin-bottom: 3px; }
        .header p { color: #64748b; font-size: 12px; margin: 2px 0; }
        .title { text-align: center; color: #1e40af; margin-bottom: 20px; font-size: 18px; font-weight: 700; }
        .info-section { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 20px; }
        .info-box { background: #f8fafc; padding: 10px; border-radius: 6px; border-left: 3px solid #3b82f6; }
        .info-box label { font-size: 10px; color: #64748b; text-transform: uppercase; font-weight: 600; display: block; margin-bottom: 3px; }
        .info-box value { font-size: 13px; color: #1e293b; font-weight: 600; }
        .salary-table { width: 100%; border-collapse: collapse; margin: 15px 0; }
        .salary-table th { background: #f1f5f9; padding: 8px; text-align: left; font-size: 11px; color: #475569; border-bottom: 2px solid #e2e8f0; }
        .salary-table td { padding: 8px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
        .salary-table tr:last-child td { border-bottom: none; }
        .amount { text-align: right; font-family: 'Courier New', monospace; font-weight: 600; }
        .positive { color: #16a34a; }
        .negative { color: #dc2626; }
        .total-row { background: #eff6ff; font-weight: 700; font-size: 14px; }
        .total-row td { padding: 10px 8px; border-top: 2px solid #3b82f6; }
        .footer { margin-top: 25px; padding-top: 12px; border-top: 1px solid #e2e8f0; text-align: center; color: #64748b; font-size: 10px; }
        .signature-section { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 30px; }
        .signature-box { text-align: center; }
        .signature-line { border-top: 1px solid #1e293b; padding-top: 8px; margin-top: 35px; font-weight: 600; color: #475569; font-size: 11px; }
        @media print {
          body { padding: 0; background: white; }
          .slip { box-shadow: none; padding: 15px; }
          .header { padding-bottom: 10px; margin-bottom: 15px; }
          .info-section { margin-bottom: 15px; gap: 10px; }
          .salary-table { margin: 10px 0; }
          .signature-section { margin-top: 20px; }
        }
      </style>
    `;

    const content = `
      <html>
        <head>
          <title>${translatedStrings.payrollSlip} - ${employee.name}</title>
          ${styles}
        </head>
        <body>
          <div class="slip">
            <div class="header">
              <h1>${translatedStrings.companyName}</h1>
              <p>${translatedStrings.companyAddress}</p>
              <p>${translatedStrings.phone}: ${shopSettings.phone || 'N/A'}</p>
            </div>

            <div class="title">${translatedStrings.payrollSlip}</div>

            <div class="info-section">
              <div class="info-box">
                <label>${translatedStrings.employeeName}</label>
                <value>${employee.name}</value>
              </div>
              <div class="info-box">
                <label>${translatedStrings.department}</label>
                <value>${employee.dept}</value>
              </div>
              <div class="info-box">
                <label>${translatedStrings.role}</label>
                <value>${employee.role}</value>
              </div>
              <div class="info-box">
                <label>${translatedStrings.location}</label>
                <value>${employee.location}</value>
              </div>
              <div class="info-box">
                <label>${translatedStrings.payPeriod}</label>
                <value>${monthName}</value>
              </div>
            </div>

            <table class="salary-table">
              <thead>
                <tr>
                  <th>${translatedStrings.description}</th>
                  <th class="amount">${translatedStrings.amount}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>${translatedStrings.basicSalary}</strong></td>
                  <td class="amount">${formatCurrency(payrollData.baseSalary)}</td>
                </tr>
                <tr>
                  <td>${translatedStrings.bonus}</td>
                  <td class="amount positive">+ ${formatCurrency(payrollData.bonus)}</td>
                </tr>
                <tr>
                  <td>${translatedStrings.overtime}</td>
                  <td class="amount positive">+ ${formatCurrency(payrollData.overtime)}</td>
                </tr>
                <tr>
                  <td><strong>${translatedStrings.grossPay}</strong></td>
                  <td class="amount"><strong>${formatCurrency(payrollData.baseSalary + payrollData.bonus + payrollData.overtime)}</strong></td>
                </tr>
                <tr style="height: 8px;"><td colspan="2"></td></tr>
                <tr>
                  <td>${translatedStrings.lateDeductions}</td>
                  <td class="amount negative">- ${formatCurrency(payrollData.lateDeduction)}</td>
                </tr>
                <tr>
                  <td>${translatedStrings.absentDeductions}</td>
                  <td class="amount negative">- ${formatCurrency(payrollData.absentDeduction)}</td>
                </tr>
                <tr>
                  <td>${translatedStrings.advanceSalary}</td>
                  <td class="amount negative">- ${formatCurrency(payrollData.advanceSalary)}</td>
                </tr>
                <tr>
                  <td><strong>${translatedStrings.totalDeductions}</strong></td>
                  <td class="amount negative"><strong>- ${formatCurrency(payrollData.deductionAmount)}</strong></td>
                </tr>
                <tr class="total-row">
                  <td>${translatedStrings.netPay}</td>
                  <td class="amount">${formatCurrency(payrollData.netPay)}</td>
                </tr>
              </tbody>
            </table>

            <div class="signature-section">
              <div class="signature-box">
                <div class="signature-line">${translatedStrings.employeeSignature}</div>
              </div>
              <div class="signature-box">
                <div class="signature-line">${translatedStrings.authorizedSignature}</div>
              </div>
            </div>

            <div class="footer">
              <p>${translatedStrings.computerGenerated}</p>
              <p>${translatedStrings.generatedOn} ${currentDate} ${translatedStrings.at} ${currentTime}</p>
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };



  // --- Barcode Generation & Printing ---
  const generateBarcode = () => {
    const random = Math.floor(10000000 + Math.random() * 90000000); // 8 digit random
    const code = `ITEM-${random}`;
    if (editingItem) {
      setEditingItem({ ...editingItem, barcode: code });
    } else {
      setNewItemForm({ ...newItemForm, barcode: code });
    }
  };

  const handlePrintBarcode = (item, mode = 'sticker') => {
    if (!item.barcode) return;
    const printWindow = window.open('', '', 'width=400,height=400');
    if (!printWindow) return;



    const content = `
      <html>
        <head>
          <title>Barcode - ${item.name}</title>
          <style>
            body { margin: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif; }
            .label { 
              text-align: center; 
              padding: 10px; 
              border: 1px dashed #eee; 
              width: 50mm; 
              height: 30mm; 
              display: flex; 
              flex-direction: column; 
              justify-content: center; 
              align-items: center;
            }
            @media print {
              .label { border: none; }
              body { height: auto; }
            }
            .name { font-size: 10px; font-weight: bold; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
            .price { font-size: 9px; font-weight: bold; margin-top: 2px; }
            #barcode { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>
          <div class="label">
            <div style="font-size: 7px; font-weight: bold; color: #888; margin-bottom: 1px; text-transform: uppercase;">${shopSettings.name || 'FINN ERP'}</div>
            <div class="name">${item.name}</div>
            <svg id="barcode"></svg>
            <div class="price">${formatCurrency(item.sellPrice)}</div>
          </div>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
          <script>
            window.onload = function() {
              if (window.JsBarcode) {
                JsBarcode("#barcode", "${item.barcode}", {
                  format: "CODE128",
                  width: 1.5,
                  height: 35,
                  displayValue: true,
                  fontSize: 10,
                  margin: 2
                });
                window.focus();
                window.print();
              } else {
                // Retry if script not yet applied
                setTimeout(window.onload, 100);
              }
            };
          </script>
        </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(content);
    printWindow.document.close();
  };

  const handlePrintBatchBarcodes = (itemConfigs, mode = barcodePrintMode) => {
    // itemConfigs can be an array of items (legacy) or an array of {item, qty}
    const expandedItems = [];
    itemConfigs.forEach(cfg => {
      if (cfg.item && cfg.qty !== undefined) {
        // New format: {item, qty}
        if (cfg.item.barcode && cfg.qty > 0) {
          for (let i = 0; i < cfg.qty; i++) {
            expandedItems.push(cfg.item);
          }
        }
      } else if (cfg.barcode) {
        // Legacy format: item object
        expandedItems.push(cfg);
      }
    });

    if (expandedItems.length === 0) {
      alert("No items with barcodes to print.");
      return;
    }

    const printWindow = window.open('', '', 'width=800,height=600');
    if (!printWindow) return;

    const isSticker = mode === 'sticker';

    const content = `
    <html>
      <head>
        <title>Batch Barcodes - ${mode.toUpperCase()}</title>
        <style>
          body { margin: 0; padding: ${isSticker ? '0' : '20px'}; font-family: sans-serif; }
          .grid { 
            display: ${isSticker ? 'block' : 'grid'}; 
            grid-template-columns: ${isSticker ? 'none' : 'repeat(4, 1fr)'}; 
            gap: ${isSticker ? '0' : '10px'}; 
          }
          .label { 
            text-align: center; 
            padding: ${isSticker ? '10px' : '15px'}; 
            border: ${isSticker ? 'none' : '1px solid #eee'}; 
            break-inside: avoid;
            ${isSticker ? 'width: 50mm; height: 30mm; display: flex; flex-direction: column; justify-content: center; align-items: center; border-bottom: 1px dashed #ccc;' : ''}
          }
          @media print {
            .label { 
              border: ${isSticker ? 'none' : '1px solid #ddd'}; 
              ${isSticker ? 'page-break-after: always; border-bottom: none;' : ''}
            }
            body { margin: 0; padding: ${isSticker ? '0' : '10mm'}; }
          }
          .name { font-size: ${isSticker ? '10px' : '12px'}; font-weight: bold; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
          .price { font-size: ${isSticker ? '9px' : '10px'}; font-weight: bold; margin-top: 2px; }
          svg { max-width: 100%; height: auto; }
        </style>
      </head>
      <body>
        <div class="grid">
          ${expandedItems.map((item, idx) => `
            <div class="label">
              <div style="font-size: 7px; font-weight: bold; color: #888; margin-bottom: 1px; text-transform: uppercase;">${shopSettings.name || 'FINN ERP'}</div>
              <div class="name">${item.name}</div>
              <svg id="barcode-${idx}"></svg>
              <div class="price">${formatCurrency(item.sellPrice)}</div>
            </div>
          `).join('')}
        </div>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <script>
          window.onload = function() {
            if (window.JsBarcode) {
              ${expandedItems.map((item, idx) => `
                JsBarcode("#barcode-${idx}", "${item.barcode}", {
                  format: "CODE128",
                  width: ${isSticker ? '1.5' : '1.4'},
                  height: ${isSticker ? '35' : '40'},
                  displayValue: true,
                  fontSize: 8,
                  margin: 2
                });
              `).join('')}
              window.focus();
              window.print();
            } else {
              setTimeout(window.onload, 100);
            }
          };
        </script>
      </body>
    </html>
  `;
    printWindow.document.open();
    printWindow.document.write(content);
    printWindow.document.close();
  };


  // --- Image Handling Handlers ---
  const handleNewEmployeeImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewEmployeeForm(prev => ({ ...prev, photo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateEmployeeImage = (id, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleUpdateEmployee(id, 'photo', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCheckoutServiceCart = async (format) => {
    if (serviceCart.length === 0) return;
    const currentId = await generateSequentialInvoiceId('SRV', 'REP');
    const total = serviceCart.reduce((a, b) => a + (Number(b.sellPrice || 0) * Number(b.quantity || 1)), 0);
    const invData = {
      invoiceId: currentId,
      type: 'service_pos',
      client: newSaleForm.customer || 'Service Customer',
      customerId: newSaleForm.customerId || null,
      date: new Date().toISOString().split('T')[0],
      items: serviceCart.map(i => ({ ...i, price: Number(i.sellPrice || 0), qty: Number(i.quantity || 1) })),
      amount: total,
      paymentMethod: paymentMethod,
      soldBy: salesEmployee ? salesEmployee.name : user.email,
      location: 'Repair Shop',
      userId: user.uid,
      createdAt: serverTimestamp(),
      orderType: orderType
    };
    try {
      const batch = writeBatch(db);
      for (const item of serviceCart) {
        if (item.type === 'part') {
          const invItem = inventory.find(i => i.id === item.id);
          if (invItem) {
            const newQty = Number(invItem.quantity) - Number(item.quantity);
            if (newQty < 0) {
              throw new Error(`${t('insufficientStock') || 'Insufficient stock for'} ${item.name}`);
            }
            batch.update(doc(db, 'inventory', item.id), { quantity: newQty });
          }
        }
      }
      const saleRef = await addDoc(collection(db, 'sales'), invData);
      await batch.commit();
      handlePrintInvoice({ ...invData, id: saleRef.id }, format === 'Thermal' ? 'Service Receipt' : 'Service Invoice', format);
      setServiceCart([]);
      setNewSaleForm({ ...newSaleForm, customer: '', customerId: '' });
      alert(t('saleCompleted'));
    } catch (e) {
      console.error(e);
      alert(e.message);
    }
  };

  // --- Actions (Firestore) ---
  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (!user) {
      alert(t('errorNoUser'));
      return;
    }

    // Debug


    try {
      await addDoc(collection(db, 'employees'), {
        ...newEmployeeForm,
        salary: Number(newEmployeeForm.salary) || 0,
        bonus: Number(newEmployeeForm.bonus) || 0,
        overtime: Number(newEmployeeForm.overtime) || 0,
        userId: user.uid,
        status: 'Active',
        createdAt: serverTimestamp()
      });
      alert(t('employeeSaved')); // Debug Confirmation
      setIsAddModalOpen(false);
      setNewEmployeeForm({ name: '', role: '', dept: 'Security', location: '', shift: 'Morning (12 Hours)', salary: 60000, salaryMethod: 'Monthly', bonus: 0, overtime: 0, deductionHours: 0, photo: '' });
    } catch (err) {
      console.error(err);
      alert(t('saveError') + err.message);
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!user) return;
    if (window.confirm(t('deleteConfirm'))) {
      await deleteDoc(doc(db, 'employees', id));
      setSelectedEmployee(null);
    }
  };

  const handleUpdateEmployee = async (id, field, value) => {
    if (!user) return;

    // Name Propagation
    if (field === 'name' && selectedEmployee && selectedEmployee.id === id) {
      const oldName = selectedEmployee.name;
      const newName = value;
      try {
        const batch = writeBatch(db);
        const q = query(collection(db, 'attendance'), where('name', '==', oldName));
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          batch.update(doc.ref, { name: newName });
        });
        await batch.commit();
      } catch (err) {
        console.error("Error propagating name change:", err);
      }
    }

    await updateDoc(doc(db, 'employees', id), { [field]: value });
    setSelectedEmployee(prev => ({ ...prev, [field]: value }));
  };

  const handleAddSite = async (e) => {
    e.preventDefault();
    if (!user) return;
    await addDoc(collection(db, 'sites'), {
      ...newSiteForm,
      address: newSiteForm.address || '',
      phone: newSiteForm.phone || '',
      userId: user.uid,
      createdAt: serverTimestamp()
    });
    setIsAddSiteModalOpen(false);
    setNewSiteForm({ name: '', city: '', manager: '', status: 'Operational', address: '', phone: '' });
  };

  const handleUpdateSite = async (id, field, value) => {
    // Basic validation or formatting if needed
    await updateDoc(doc(db, 'sites', id), { [field]: value });
    setSelectedSite(prev => ({ ...prev, [field]: value }));
  };

  const handleDeleteSite = async (id, siteName) => {
    const assignedEmployees = employees.filter(e => e.location === siteName).length;
    if (assignedEmployees > 0) {
      alert(t('deleteLocationWarning'));
      return;
    }
    if (window.confirm(t('deleteLocationConfirm'))) {
      await deleteDoc(doc(db, 'sites', id));
      setSelectedSite(null);
    }
  };

  const handleAddAttendance = async (e) => {
    e.preventDefault();
    if (!user) return;

    // Get Employee Name
    const emp = employees.find(e => e.id.toString() === newAttendanceForm.employeeId.toString());
    const empName = emp ? emp.name : 'Unknown';

    // Prevent Duplicates
    if (attendance.some(r => r.name === empName && r.date === newAttendanceForm.date)) {
      alert(t('attendanceExists'));
      return;
    }

    await addDoc(collection(db, 'attendance'), {
      name: empName,
      date: newAttendanceForm.date,
      status: newAttendanceForm.status,
      replacementFor: newAttendanceForm.replacementFor || null,
      lateHours: newAttendanceForm.lateHours ? Number(newAttendanceForm.lateHours) : 0,
      userId: user.uid
    });

    setIsAddAttendanceModalOpen(false);
    setIsAddAttendanceModalOpen(false);
    setNewAttendanceForm({ employeeId: '', date: new Date().toISOString().split('T')[0], status: 'On Time', replacementFor: '', locationFilter: '', lateHours: 0 });
  };

  const handleDeleteAttendance = async (id) => {
    if (window.confirm(t('deleteAttendance') + "?")) {
      await deleteDoc(doc(db, 'attendance', id));
    }
  };

  const handleUpdateAttendance = async (e) => {
    e.preventDefault();
    if (!editingAttendance) return;

    await updateDoc(doc(db, 'attendance', editingAttendance.id), {
      date: editingAttendance.date,
      status: editingAttendance.status,
      replacementFor: editingAttendance.replacementFor || null,
      lateHours: editingAttendance.lateHours ? Number(editingAttendance.lateHours) : 0
    });

    setEditingAttendance(null);
  };



  const handleAddBorrowRecord = async (e) => {
    e.preventDefault();
    if (!user) return;
    try {
      await addDoc(collection(db, 'borrowRecords'), {
        ...borrowForm,
        userId: user.uid,
        createdAt: serverTimestamp()
      });
      setIsBorrowModalOpen(false);
      setBorrowForm({ borrowerName: '', itemName: '', borrowDate: new Date().toISOString().split('T')[0], borrowTime: new Date().toTimeString().slice(0,5), returnDate: '', returnTime: '', returned: false, info: '' });
    } catch (err) {
      console.error(err);
      alert('Error adding borrow record: ' + err.message);
    }
  };

  const handleToggleBorrowReturn = async (id, currentStatus) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, 'borrowRecords', id), {
        returned: !currentStatus,
        returnDate: !currentStatus ? new Date().toISOString().split('T')[0] : '',
        returnTime: !currentStatus ? new Date().toTimeString().slice(0,5) : ''
      });
    } catch (err) { console.error(err); }
  };

  const handleDeleteBorrowRecord = async (id) => {
    if (!user || !window.confirm('Delete this record?')) return;
    try { await deleteDoc(doc(db, 'borrowRecords', id)); } catch (err) { console.error(err); }
  };

  const downloadReport = (reportType) => {
    let headers = [], data = [], filename = '', extraMetadata = [];

    // Helper to translate status
    const translateStatus = (s) => {
      if (s === 'On Time') return t('onTime');
      if (s === 'Late') return t('late');
      if (s === 'Absent') return t('absent');
      return s;
    };

    switch (reportType) {
      case 'attendance':
        headers = [t('employeeName'), t('date'), t('location'), t('status')];
        data = attendance
          .filter(r => !reportLocationFilter || getEmployeeLocation(r.name) === reportLocationFilter)
          .map(r => [r.name, r.date, getEmployeeLocation(r.name), translateStatus(r.status)]);
        filename = 'FinnERP_Attendance.pdf';
        break;
      case 'payroll':
        headers = [t('employeeName'), t('role'), t('dept'), `${t('basicSalary')} (${currency})`, `${t('bonus')} (${currency})`, `${t('overtime')} (${currency})`, `${t('total')} (${currency})`];
        const filteredEmployees = employees.filter(e => !reportLocationFilter || e.location === reportLocationFilter);
        data = filteredEmployees.map(e => {
          const salary = Number(e.salary) || 0;
          const bonus = Number(e.bonus) || 0;
          const overtime = Number(e.overtime) || 0;
          const total = salary + bonus + overtime;
          return [e.name, e.role, e.dept, salary, bonus, overtime, total];
        });

        // Add Totals Row
        const totalSalary = filteredEmployees.reduce((sum, e) => sum + (Number(e.salary) || 0), 0);
        const totalBonus = filteredEmployees.reduce((sum, e) => sum + (Number(e.bonus) || 0), 0);
        const totalOvertime = filteredEmployees.reduce((sum, e) => sum + (Number(e.overtime) || 0), 0);
        const totalTotal = totalSalary + totalBonus + totalOvertime;

        // Add empty row then totals
        data.push([]);
        data.push(['', '', t('dashboardTotal').toUpperCase(), totalSalary, totalBonus, totalOvertime, totalTotal]);

        extraMetadata = [`${t('payPeriod')}: ${payrollMonthFilter}`];
        filename = 'FinnERP_Payroll.pdf';
        break;
      case 'turnover':
        headers = [t('employeeName'), t('role'), t('dept'), t('status'), t('location')];
        data = employees
          .filter(e => !reportLocationFilter || e.location === reportLocationFilter)
          .map(e => [e.name, e.role, e.dept, e.status, e.location]);
        filename = 'FinnERP_Staff.pdf';
        break;
      case 'tax':
        headers = [t('employeeName'), `${t('total')} (${currency})`, `${t('tax')} (20%)`, t('netPay')];
        data = employees
          .filter(e => !reportLocationFilter || e.location === reportLocationFilter)
          .map(e => {
            const total = (Number(e.salary) || 0) + (Number(e.bonus) || 0) + (Number(e.overtime) || 0);
            const tax = total * 0.2;
            return [e.name, total, tax, total - tax];
          });
        filename = 'FinnERP_Tax.pdf';
        break;
      case 'weekly_sales':
        headers = [t('date'), t('invoiceId'), t('type'), t('customer'), t('itemsSummary'), t('total')];
        const sevenDaysAgoSales = new Date();
        sevenDaysAgoSales.setDate(sevenDaysAgoSales.getDate() - 7);

        data = sales
          .filter(s => {
            const date = s.createdAt?.seconds ? new Date(s.createdAt.seconds * 1000) : new Date(s.date);
            // Weekly filter AND Location Filter
            return date >= sevenDaysAgoSales &&
              (!reportLocationFilter || s.location === reportLocationFilter);
          })
          .map(s => {
            const date = s.createdAt?.seconds ? new Date(s.createdAt.seconds * 1000) : new Date(s.date);
            const itemsSummary = Array.isArray(s.items) ? s.items.map(i => `${i.qty}x ${i.name}`).join('; ') : t('na');
            const typeLabel = s.orderType === 'Walk-in' ? t('walkIn') : (s.orderType === 'Takeaway' ? t('takeaway') : s.orderType);

            return [
              date.toLocaleString(),
              s.invoiceId || t('na'),
              typeLabel,
              s.customer || t('walkInCustomer'),
              itemsSummary,
              s.amount
            ];
          });
        filename = 'Weekly_Sales_Report.pdf';
        break;
      case 'weekly_buy':
        // For "Buy Report", we look at Inventory items updated recently
        headers = [t('lastUpdated'), t('itemName'), t('location'), t('buyPrice'), t('sellPrice'), t('quantity'), t('stockValueBuy'), t('stockValueSell')];
        const sevenDaysAgoInv = new Date();
        sevenDaysAgoInv.setDate(sevenDaysAgoInv.getDate() - 7);

        data = inventory
          .filter(i => {
            const date = i.updatedAt?.seconds ? new Date(i.updatedAt.seconds * 1000) : new Date();
            // Weekly Filter AND Location Filter
            return date >= sevenDaysAgoInv &&
              (!reportLocationFilter || i.location === reportLocationFilter);
          })
          .map(i => {
            const date = i.updatedAt?.seconds ? new Date(i.updatedAt.seconds * 1000) : new Date();
            return [
              date.toLocaleString(),
              i.name,
              i.location,
              i.buyPrice || 0,
              i.sellPrice || 0,
              i.quantity,
              (i.buyPrice || 0) * i.quantity,
              (i.sellPrice || 0) * i.quantity
            ];
          });
        filename = 'Weekly_Inventory_Buy_Report.pdf';
        break;
      case 'profit_loss':
        headers = [t('category'), t('details'), t('amount')];
        let startDate, endDate, periodLabel;

        if (profitPeriod === 'Daily') {
          startDate = new Date(profitDateFilter);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(profitDateFilter);
          endDate.setHours(23, 59, 59, 999);
          periodLabel = profitDateFilter;
        } else if (profitPeriod === 'Weekly') {
          const selectedDate = new Date(profitDateFilter);
          const day = selectedDate.getDay();
          const diff = selectedDate.getDate() - day + (day === 0 ? -6 : 1); // Monday start
          startDate = new Date(selectedDate.setDate(diff));
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 6);
          endDate.setHours(23, 59, 59, 999);
          periodLabel = `${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`;
        } else if (profitPeriod === 'Yearly') {
          startDate = new Date(profitYearFilter + '-01-01');
          endDate = new Date(profitYearFilter + '-12-31');
          endDate.setHours(23, 59, 59, 999);
          periodLabel = profitYearFilter;
        } else {
          // Monthly
          startDate = new Date(profitMonthFilter + '-01');
          endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
          endDate.setHours(23, 59, 59, 999);
          periodLabel = profitMonthFilter;
        }


        // 1. Revenue Analysis
        const parseSaleDate = (s) => {
          if (s.createdAt?.seconds) return new Date(s.createdAt.seconds * 1000);
          if (!s.date) return new Date(0);
          const d = new Date(s.date);
          if (!isNaN(d.getTime())) return d;
          if (typeof s.date === 'string' && s.date.includes('/')) {
            const parts = s.date.split('/');
            if (parts.length === 3) {
              if (Number(parts[0]) > 12) return new Date(parts[2], Number(parts[1]) - 1, parts[0]);
              return new Date(parts[2], Number(parts[0]) - 1, parts[1]);
            }
          }
          return new Date(0);
        };

        const filteredSales = sales.filter(s => {
          const d = parseSaleDate(s);
          return d >= startDate && d <= endDate && (!reportLocationFilter || s.location === reportLocationFilter);
        }).sort((a, b) => parseSaleDate(a) - parseSaleDate(b));

        const revByMethod = {};
        let totalRevenue = 0;
        filteredSales.forEach(s => {
          const method = s.paymentMethod || 'Cash';
          if (!revByMethod[method]) revByMethod[method] = 0;
          
          // Robust amount calculation
          let amt = Number(s.amount);
          if (isNaN(amt) && Array.isArray(s.items)) {
            // Recalculate from items if amount is missing/invalid
            amt = s.items.reduce((acc, curr) => acc + (Number(curr.price || curr.sellPrice || 0) * (curr.qty || curr.quantity || 1)), 0);
          }
          amt = amt || 0;

          revByMethod[method] += amt;
          totalRevenue += amt;
        });

        // 2. COGS
        let totalCogs = 0;
        filteredSales.forEach(sale => {
          if (Array.isArray(sale.items)) {
            sale.items.forEach(soldItem => {
              const invItem = inventory.find(i => i.name === soldItem.name);
              const cost = invItem ? (invItem.buyPrice || 0) : 0;
              totalCogs += cost * (soldItem.qty || 0);
            });
          }
        });

        const grossProfitVal = totalRevenue - totalCogs;

        // 3. Operating Expenses - Employee Payroll by Department
        const periodEmployees = employees.filter(e => !reportLocationFilter || e.location === reportLocationFilter);

        // Calculate period duration in days
        const periodDays = Math.max(1, Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)));

        // Group employees by department
        const employeesByDept = {};
        let totalPayrollExpenses = 0;

        periodEmployees.forEach(emp => {
          const dept = emp.dept || t('unassigned');
          if (!employeesByDept[dept]) {
            employeesByDept[dept] = [];
          }

          // Calculate employee's total compensation SCALEABLE for the period
          const rawSalary = Number(emp.salary) || 0;
          const salaryMethod = emp.salaryMethod || 'Monthly';
          let proportionalSalary = 0;

          // Scaling Logic based on Salary Method
          if (salaryMethod === 'Monthly') {
            proportionalSalary = (rawSalary / 30) * periodDays;
          } else if (salaryMethod === 'Weekly') {
            proportionalSalary = (rawSalary / 7) * periodDays;
          } else if (salaryMethod === 'Daily') {
            proportionalSalary = rawSalary * periodDays;
          } else if (salaryMethod === 'Hourly') {
            // Heuristic: Assuming 8 hour work day for hourly estimate if not explicitly tracked
            proportionalSalary = (rawSalary * 8) * periodDays;
          }

          const bonus = Number(emp.bonus) || 0;
          const overtime = Number(emp.overtime) || 0;

          // Scale bonus/overtime too if it's a short period? 
          // Usually bonus/overtime in the profile is a periodic estimate. 
          // For simplicity, we scale everything by the same logic to keep budget realistic.
          const totalComp = proportionalSalary + ((bonus + overtime) / 30) * periodDays;

          employeesByDept[dept].push({
            name: emp.name,
            totalComp: totalComp
          });

          totalPayrollExpenses += totalComp;
        });

        // 4. Other Expenses (Inventory Purchases)
        const periodPurchases = purchases.filter(p => {
          const d = p.date ? new Date(p.date) : new Date();
          return d >= startDate && d <= endDate && (!reportLocationFilter || p.location === reportLocationFilter);
        });
        const totalOtherExpenses = periodPurchases.reduce((sum, p) => sum + (p.amount || 0), 0);

        // Total Operating Expenses
        const totalOperatingExpenses = totalPayrollExpenses + totalOtherExpenses;

        // Net profit = Gross Profit - Operating Expenses
        const netProfitVal = grossProfitVal - totalOperatingExpenses;

        // ============================================================
        // P&L: Build a Premium Multi-Table PDF directly (bypass generic generatePDF)
        // ============================================================
        const plFilename = `Profit_Loss_${profitPeriod}_${periodLabel.replace(/\//g, '-')}.pdf`;
        const plMeta = [`${t('period')}: ${t(profitPeriod.toLowerCase())} (${periodLabel})`, `${t('location')}: ${reportLocationFilter || t('filterAll')}`];

        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        const drawPageFooter = () => {
          doc.setFontSize(7);
          doc.setTextColor(148, 163, 184);
          doc.text(`FINN ERP - ${plFilename.replace('.pdf', '')}`, 15, pageHeight - 8);
          const pn = `${t('page')} ${doc.internal.getNumberOfPages()}`;
          doc.text(pn, pageWidth - 15 - doc.getTextWidth(pn), pageHeight - 8);
        };

        const tableDefaults = (startY) => ({
          startY,
          theme: 'striped',
          styles: { font: 'helvetica', fontSize: 8.5, cellPadding: { top: 2, bottom: 2, left: 3, right: 3 } },
          headStyles: { fillColor: [30, 41, 59], textColor: 255, fontSize: 9, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          margin: { left: 15, right: 15, bottom: 18 },
          didDrawPage: drawPageFooter,
          didParseCell: (d) => {
            const v = d.cell.raw;
            if (typeof v === 'number') {
              d.cell.styles.halign = 'right';
              if (v < 0) d.cell.styles.textColor = [220, 38, 38];
              else if (v > 0 && d.column.index > 0) d.cell.styles.textColor = [22, 163, 74];
            }
          }
        });

        // ── DRAW HEADER ─────────────────────────────────────────────
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(20);
        doc.setTextColor(15, 23, 42);
        doc.text((shopSettings.name?.toUpperCase() || 'FINN ERP'), 15, 18);
        doc.setFontSize(10);
        doc.setTextColor(59, 130, 246);
        doc.text(`${t('profit_loss').toUpperCase()} — ${t(profitPeriod.toLowerCase()).toUpperCase()}`, 15, 25);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(100, 116, 139);
        doc.text(`${t('date')}: ${new Date().toLocaleString()}`, 15, 31);
        doc.text(plMeta.join('   |   '), 15, 36);
        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.4);
        doc.line(15, 39, pageWidth - 15, 39);

        // ── SECTION 1: INCOME STATEMENT ─────────────────────────────
        const incomeRows = [
          // Revenue
          ...Object.entries(revByMethod).map(([m, amt]) => [t(m.toLowerCase()) || m, amt]),
          [{ content: t('totalRevenue'), styles: { fontStyle: 'bold', fillColor: [239, 246, 255], textColor: [30, 64, 175] } }, { content: totalRevenue, styles: { fontStyle: 'bold', halign: 'right', fillColor: [239, 246, 255], textColor: [30, 64, 175] } }],
          // COGS
          [t('cogsFull'), -totalCogs],
          [{ content: t('grossProfit'), styles: { fontStyle: 'bold' } }, { content: grossProfitVal, styles: { fontStyle: 'bold', halign: 'right', textColor: grossProfitVal >= 0 ? [22, 163, 74] : [220, 38, 38] } }],
          [t('grossMargin'), ((totalRevenue ? grossProfitVal / totalRevenue : 0) * 100).toFixed(1) + '%'],
          // Expenses
          ...Object.entries(employeesByDept).flatMap(([dept, emps]) => [
            [`  ${translations[language]?.[dept.toLowerCase()] || dept}`, ''],
            ...emps.map(emp => [`    ${emp.name}`, -emp.totalComp])
          ]),
          [t('invPurchases'), -totalOtherExpenses],
          [{ content: t('totalExpenses'), styles: { fontStyle: 'bold', fillColor: [254, 242, 242], textColor: [153, 27, 27] } }, { content: -totalOperatingExpenses, styles: { fontStyle: 'bold', halign: 'right', fillColor: [254, 242, 242], textColor: [153, 27, 27] } }],
          // Net
          [{ content: netProfitVal >= 0 ? t('netProfit') : t('netLoss'), styles: { fontStyle: 'bold', fillColor: netProfitVal >= 0 ? [240, 253, 244] : [254, 242, 242], textColor: netProfitVal >= 0 ? [21, 128, 61] : [153, 27, 27], fontSize: 10 } }, { content: netProfitVal, styles: { fontStyle: 'bold', halign: 'right', fillColor: netProfitVal >= 0 ? [240, 253, 244] : [254, 242, 242], textColor: netProfitVal >= 0 ? [21, 128, 61] : [153, 27, 27], fontSize: 10 } }],
          [t('netMargin'), ((totalRevenue ? netProfitVal / totalRevenue : 0) * 100).toFixed(1) + '%']
        ];

        const incomeHeadRows = [
          [{ content: t('incomeStatement').toUpperCase() + ` (${t(profitPeriod.toLowerCase())})`, colSpan: 2, styles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold', fontSize: 10, halign: 'left' } }],
          [t('description'), t('amount')]
        ];

        autoTable(doc, {
          head: incomeHeadRows,
          body: incomeRows,
          ...tableDefaults(44),
          columnStyles: {
            0: { cellWidth: 130 },
            1: { cellWidth: 40, halign: 'right' }
          },
          didParseCell: (d) => {
            if (d.row.section === 'head') return;
            const v = d.cell.raw;
            if (typeof v === 'number') {
              d.cell.styles.halign = 'right';
              if (v < 0) d.cell.styles.textColor = [220, 38, 38];
            }
            const t0 = String(d.row.raw[0]?.content || d.row.raw[0] || '');
            if (t0.startsWith('    ')) { d.cell.styles.cellPadding = { left: 12 }; d.cell.styles.textColor = [100, 116, 139]; }
            else if (t0.startsWith('  ')) { d.cell.styles.cellPadding = { left: 7 }; d.cell.styles.fontStyle = 'bold'; }
          }
        });

        // ── SECTION 2: SOLD ITEMS ────────────────────────────────────
        const soldRows = [];
        let sumSoldQty2 = 0, sumSoldSales2 = 0, sumSoldCost2 = 0, sumSoldProfit2 = 0;

        filteredSales.forEach(sale => {
          if (Array.isArray(sale.items)) {
            const dateObj = parseSaleDate(sale);
            const dateStr = dateObj.getFullYear() > 1970 ? dateObj.toISOString().split('T')[0] : '-';
            sale.items.forEach(item => {
              const invItem = inventory.find(i => i.name === item.name);
              const ubp = invItem ? (Number(invItem.buyPrice) || 0) : 0;
              const usp = Number(item.price) || 0;
              const qty = Number(item.qty) || 1;
              const ts = usp * qty, tc = ubp * qty, pr = ts - tc;
              sumSoldQty2 += qty; sumSoldSales2 += ts; sumSoldCost2 += tc; sumSoldProfit2 += pr;
              soldRows.push([item.name || t('unknown'), dateStr, qty, usp, ubp, ts, tc, pr]);
            });
          }
        });
        soldRows.push([
          { content: t('totals').toUpperCase(), styles: { fontStyle: 'bold', fillColor: [239, 246, 255], textColor: [30, 64, 175] } },
          '',
          { content: sumSoldQty2, styles: { fontStyle: 'bold', halign: 'right', fillColor: [239, 246, 255], textColor: [30, 64, 175] } },
          { content: '', styles: { fillColor: [239, 246, 255] } },
          { content: '', styles: { fillColor: [239, 246, 255] } },
          { content: sumSoldSales2, styles: { fontStyle: 'bold', halign: 'right', fillColor: [239, 246, 255], textColor: [30, 64, 175] } },
          { content: sumSoldCost2, styles: { fontStyle: 'bold', halign: 'right', fillColor: [239, 246, 255], textColor: [30, 64, 175] } },
          { content: sumSoldProfit2, styles: { fontStyle: 'bold', halign: 'right', fillColor: [239, 246, 255], textColor: sumSoldProfit2 >= 0 ? [21, 128, 61] : [153, 27, 27] } }
        ]);

        const nextY1 = doc.lastAutoTable.finalY + 8;
        autoTable(doc, {
          head: [
            [{ content: t('soldItemsDetail').toUpperCase(), colSpan: 8, styles: { fillColor: [15, 118, 110], textColor: 255, fontStyle: 'bold', fontSize: 10 } }],
            [t('itemName'), t('date'), t('quantity'), t('sellPrice'), t('buyPrice'), t('total'), t('totalCost'), t('profit')]
          ],
          body: soldRows,
          ...tableDefaults(nextY1),
          columnStyles: {
            0: { cellWidth: 40 },
            1: { cellWidth: 25 },
            2: { halign: 'right' },
            3: { halign: 'right' },
            4: { halign: 'right' },
            5: { halign: 'right' },
            6: { halign: 'right' },
            7: { halign: 'right', fontStyle: 'bold' }
          },
          didParseCell: (d) => {
            if (d.row.section === 'head') return;
            const v = d.cell.raw;
            if (typeof v === 'number') {
              d.cell.styles.halign = 'right';
              if (v < 0) d.cell.styles.textColor = [220, 38, 38];
            }
          }
        });

        // ── SECTION 3: BOUGHT ITEMS ──────────────────────────────────
        const boughtRows = [];
        let sumBoughtQty2 = 0, sumBoughtAmt2 = 0;

        periodPurchases.forEach(p => {
          const label = p.name || p.description || p.itemName || '-';
          const dateStr = p.date ? new Date(p.date).toLocaleDateString() : '-';
          const amt = Number(p.amount) || 0;
          const qtyVal = Number(p.quantity) || Number(p.qty) || 1;
          const unitCost = qtyVal > 0 ? (amt / qtyVal).toFixed(2) : amt;
          const qtyDisplay = p.quantity ? p.quantity + (p.unit ? ' ' + p.unit : '') : (p.qty || '-');
          if (!isNaN(qtyVal)) sumBoughtQty2 += qtyVal;
          sumBoughtAmt2 += amt;
          boughtRows.push([label, dateStr, qtyDisplay, unitCost, amt]);
        });
        boughtRows.push([
          { content: t('totals').toUpperCase(), styles: { fontStyle: 'bold', fillColor: [239, 246, 255], textColor: [30, 64, 175] } },
          '',
          { content: sumBoughtQty2, styles: { fontStyle: 'bold', halign: 'right', fillColor: [239, 246, 255] } },
          '',
          { content: sumBoughtAmt2, styles: { fontStyle: 'bold', halign: 'right', fillColor: [239, 246, 255], textColor: [30, 64, 175] } }
        ]);

        const nextY2 = doc.lastAutoTable.finalY + 8;
        autoTable(doc, {
          head: [
            [{ content: t('boughtItemsDetail').toUpperCase(), colSpan: 5, styles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold', fontSize: 10 } }],
            [t('description'), t('date'), t('quantity'), t('unitCost'), t('amount')]
          ],
          body: boughtRows,
          ...tableDefaults(nextY2),
          columnStyles: {
            0: { cellWidth: 60 },
            2: { halign: 'right' },
            3: { halign: 'right' },
            4: { halign: 'right', fontStyle: 'bold' }
          },
          didParseCell: (d) => {
            if (d.row.section === 'head') return;
            const v = d.cell.raw;
            if (typeof v === 'number') {
              d.cell.styles.halign = 'right';
              if (v < 0) d.cell.styles.textColor = [220, 38, 38];
            }
          }
        });

        drawPageFooter();
        doc.save(plFilename);
        return; // skip generic generatePDF call below

      default: return;
    }
    generatePDF(headers, data, filename, extraMetadata);
  };

  // --- High-Speed POS & Scanner Features (Grouped here to avoid TDZ) ---

  const handleHoldCart = useCallback(() => {
    if (cart.length === 0) return;
    setHeldCarts(prev => [...prev, {
      id: Date.now(),
      cart: [...cart],
      cartDiscount,
      salesEmployee,
      customer: newSaleForm.customer,
      orderType,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);
    setCart([]);
    setCartDiscount(0);
    setSalesEmployee(null);
  }, [cart, cartDiscount, salesEmployee, newSaleForm.customer, orderType]);

  const handleResumeCart = (heldId) => {
    const held = heldCarts.find(h => h.id === heldId);
    if (!held) return;
    if (cart.length > 0) {
      if (!window.confirm(t('resumeWarning') || 'Resuming will overwrite current cart. Continue?')) return;
    }
    setCart(held.cart);
    setCartDiscount(held.cartDiscount);
    setSalesEmployee(held.salesEmployee);
    setNewSaleForm(prev => ({ ...prev, customer: held.customer }));
    setOrderType(held.orderType);
    setHeldCarts(prev => prev.filter(h => h.id !== heldId));
  };

  // Barcode Scanner
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const scannerRef = useRef(null);
  const activeTracksRef = useRef([]); // Critical for force-killing camera
  const inventoryRef = useRef(inventory);

  // Keep inventoryRef updated without triggering re-renders of stable callbacks
  useEffect(() => {
    inventoryRef.current = inventory;
  }, [inventory]);

  const onScanSuccess = useCallback(async (decodedText) => {
    const code = decodedText.trim();
    if (scannerMode === 'customerID') {
      setNewSaleForm(prev => ({ ...prev, customerId: code }));
      setIsScannerOpen(false);
      return;
    }

    const item = inventoryRef.current.find(i => i.barcode === code);

    if (!item) {
      alert(t('itemNotFound') || 'Item with barcode: ' + code + ' not found in inventory.');
      return;
    }

    if (scannerMode === 'sell') {
      if (item.quantity > 0) {
        addToCart(item);
        setIsScannerOpen(false);
      } else {
        alert(item.name + " " + (t('outOfStock') || 'is out of stock!'));
      }
    } else if (scannerMode === 'buy') {
      const moreStock = prompt(`${t('quickStockIn') || 'Quick Stock In'}: ${item.name}\n${t('currentQuantity') || 'Current'}: ${item.quantity}\n${t('enterAddedQty') || 'Enter added quantity'}:`, "1");
      if (moreStock !== null && !isNaN(moreStock) && Number(moreStock) > 0) {
        try {
          const itemRef = doc(db, 'inventory', item.id);
          await updateDoc(itemRef, { quantity: Number(item.quantity) + Number(moreStock) });
        } catch (err) {
          alert("Error updating stock: " + err.message);
        }
      }
    }
  }, [addToCart, t, scannerMode, setNewSaleForm, setIsScannerOpen]); // Removed inventory dependency to stop re-creation and flickering

  const onScanError = useCallback((err) => { }, []);

  // --- Barcode / Scanner Buffering (Hardware Scanners) ---
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // If we're typing in a search field, we might still want to catch barcodes
      // but if the user is in a modal or editing another field, maybe not.
      // However, hardware scanners usually act as a keyboard.
      // A common pattern is to only buffer if keys are typed very fast (<50ms apart).

      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTime.current;
      lastKeyTime.current = currentTime;

      // Handle Enter (Common suffix for POS scanners)
      if (e.key === 'Enter') {
        if (barcodeBuffer.current.length >= 3) {
          // It looks like a barcode was scanned
          const scanned = barcodeBuffer.current;
          barcodeBuffer.current = '';

          // If we are in the Sales tab, try to add to cart
          if (activeTab === 'sales_purchases') {
            onScanSuccess(scanned);
            e.preventDefault();
            return;
          }

          // If we are in warehouse and scanning for something
          if (activeTab === 'warehouses' && scannerMode === 'buy') {
            onScanSuccess(scanned);
            e.preventDefault();
            return;
          }
        }
        barcodeBuffer.current = '';
        return;
      }

      // Buffer characters
      if (e.key.length === 1) {
        // If the gap between keys is too long, it's probably manual typing
        if (timeDiff > 50) {
          barcodeBuffer.current = e.key;
        } else {
          barcodeBuffer.current += e.key;
        }
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [activeTab, inventory, scannerMode, onScanSuccess]); // Re-bind when tab, inventory or onScanSuccess changes

  const [hasFlash, setHasFlash] = useState(false);
  const [isFlashOn, setIsFlashOn] = useState(false);
  const [hasZoom, setHasZoom] = useState(false);
  const [zoomRange, setZoomRange] = useState({ min: 1, max: 1, step: 0.1 });
  const [currentZoom, setCurrentZoom] = useState(1);

  useEffect(() => {
    let isMounted = true;
    let html5QrCode = null;
    const originalGetUserMedia = navigator.mediaDevices?.getUserMedia?.bind(navigator.mediaDevices);

    // Intercept all camera requests to ensure we can kill tracks even if the library hangs
    if (navigator.mediaDevices && originalGetUserMedia) {
      navigator.mediaDevices.getUserMedia = async (constraints) => {
        try {
          const stream = await originalGetUserMedia(constraints);
          if (!isMounted) {
            stream.getTracks().forEach(t => t.stop());
          } else {
            activeTracksRef.current = [...activeTracksRef.current, ...stream.getTracks()];
          }
          return stream;
        } catch (err) {
          throw err;
        }
      };
    }

    if (isScannerOpen) {
      // Small safety delay for React modal render
      const initTimeout = setTimeout(() => {
        if (!isMounted) return;
        startScannerLogic();
      }, 500);

      async function startScannerLogic() {
        const element = document.getElementById("reader");
        if (!element || !isMounted) return;

        html5QrCode = new Html5Qrcode("reader");
        scannerRef.current = html5QrCode;

        const startScanner = async (cameraId) => {
          if (!isMounted) return;
          try {
            // Simplified constraints for maximum compatibility
            const constraints = cameraId ? { deviceId: cameraId } : { facingMode: "environment" };

            await html5QrCode.start(
              constraints,
              {
                fps: 25,
                qrbox: { width: 280, height: 160 },
                disableFlip: true,
                formatsToSupport: [
                  Html5QrcodeSupportedFormats.QR_CODE,
                  Html5QrcodeSupportedFormats.UPC_A,
                  Html5QrcodeSupportedFormats.UPC_E,
                  Html5QrcodeSupportedFormats.EAN_13,
                  Html5QrcodeSupportedFormats.EAN_8,
                  Html5QrcodeSupportedFormats.CODE_39,
                  Html5QrcodeSupportedFormats.CODE_128,
                  Html5QrcodeSupportedFormats.ITF,
                ]
              },
              onScanSuccess,
              onScanError
            );

            // Capture the track immediately for robust cleanup
            const track = html5QrCode.getRunningTrack();
            if (track) {
              activeTracksRef.current = [track];
              const capabilities = track.getCapabilities();
              if (capabilities.focusMode?.includes("continuous")) {
                await track.applyConstraints({ focusMode: "continuous" });
              }
              if (capabilities.zoom) {
                setHasZoom(true);
                setZoomRange({
                  min: capabilities.zoom.min || 1,
                  max: capabilities.zoom.max || 1,
                  step: capabilities.zoom.step || 0.1
                });
                setCurrentZoom(track.getSettings().zoom || 1);
              }
              if (capabilities.torch) setHasFlash(true);
            }

            if (!isMounted) {
              html5QrCode.stop().then(() => {
                html5QrCode.clear();
                activeTracksRef.current.forEach(t => t.stop());
                activeTracksRef.current = [];
              }).catch(e => { });
              return;
            }
          } catch (err) {
            console.error("Scanner startup error:", err);
            // Deep Fallback: No constraints
            if (isMounted) {
              try {
                await html5QrCode.start({ facingMode: "environment" }, { fps: 15, qrbox: 250 }, onScanSuccess, onScanError);
                const track = html5QrCode.getRunningTrack();
                if (track) activeTracksRef.current = [track];
                if (!isMounted) {
                  html5QrCode.stop().then(() => {
                    activeTracksRef.current.forEach(t => t.stop());
                    activeTracksRef.current = [];
                  }).catch(e => { });
                }
              } catch (e) { }
            }
          }
        };

        Html5Qrcode.getCameras().then(devices => {
          if (!isMounted) return;
          if (devices && devices.length > 0) {
            setCameras(devices);
            const backCamera = devices.find(d =>
              (d.label?.toLowerCase().includes('back') || d.label?.toLowerCase().includes('rear')) &&
              !d.label?.toLowerCase().includes('wide') && !d.label?.toLowerCase().includes('2')
            ) || devices.find(d => d.label?.toLowerCase().includes('back') || d.label?.toLowerCase().includes('rear')) || devices[0];

            const selectedId = activeCameraId || backCamera.id;
            setActiveCameraId(selectedId);
            startScanner(selectedId);
          } else {
            startScanner();
          }
        }).catch(() => {
          if (isMounted) startScanner();
        });
      }

      return () => {
        isMounted = false;
        clearTimeout(initTimeout);

        // Restore original getUserMedia
        if (originalGetUserMedia) {
          navigator.mediaDevices.getUserMedia = originalGetUserMedia;
        }

        // Immediate hardware kill - doesn't rely on the DOM or library status
        const forceKillHardware = () => {
          activeTracksRef.current.forEach(track => {
            try { track.stop(); } catch (e) { }
          });
          activeTracksRef.current = [];

          // Last resort: search for any stray video elements
          const strayVideos = document.querySelectorAll('video');
          strayVideos.forEach(v => {
            if (v.srcObject) {
              v.srcObject.getTracks().forEach(t => t.stop());
              v.srcObject = null;
            }
          });
        };

        if (html5QrCode) {
          try {
            // Only stop if actually running to avoid "Cannot stop" error
            if (html5QrCode.isScanning) {
              html5QrCode.stop()
                .then(() => {
                  try { html5QrCode.clear(); } catch (e) { }
                  forceKillHardware();
                })
                .catch(() => forceKillHardware());
            } else {
              forceKillHardware();
            }
          } catch (e) {
            forceKillHardware();
          }
        } else {
          forceKillHardware();
        }
        scannerRef.current = null;
      };
    } else {
      setIsFlashOn(false);
      setHasFlash(false);
      setHasZoom(false);
      setCurrentZoom(1);
    }
  }, [isScannerOpen, activeCameraId, onScanSuccess, onScanError]); // Only restart if camera ID or Open state changes explicitly

  const handleZoomChange = async (e) => {
    const value = parseFloat(e.target.value);
    setCurrentZoom(value);
    try {
      const video = document.querySelector('#reader video');
      if (video && video.srcObject) {
        const track = video.srcObject.getVideoTracks()[0];
        if (track) {
          await track.applyConstraints({
            advanced: [{ zoom: value }]
          });
        }
      }
    } catch (err) {
      console.error("Zoom error:", err);
    }
  };

  const toggleFlash = async () => {
    try {
      // Note: html5-qrcode doesn't expose a clean toggle for torch yet in all versions
      // so we use the underlying track if possible
      const video = document.querySelector('#reader video');
      if (video && video.srcObject) {
        const track = video.srcObject.getVideoTracks()[0];
        if (track) {
          const newState = !isFlashOn;
          await track.applyConstraints({
            advanced: [{ torch: newState }]
          });
          setIsFlashOn(newState);
        }
      }
    } catch (err) {
      console.error("Torch error:", err);
    }
  };

  // Keyboard Shortcuts
  useEffect(() => {
    const handleShortcuts = (e) => {
      if (activeTab === 'sales_purchases') {
        if (e.key === 'F1') { e.preventDefault(); document.getElementById('pos-search')?.focus(); }
        if (e.key === 'F2') { e.preventDefault(); handleCheckout(); }
        if (e.key === 'F4') { e.preventDefault(); handleHoldCart(); }
      }
    };
    window.addEventListener('keydown', handleShortcuts);
    return () => window.removeEventListener('keydown', handleShortcuts);
  }, [activeTab, cart, calculateTotal, handleHoldCart, handleCheckout]);

  // UPI QR Auto-hide logic (30 sec)
  useEffect(() => {
    let timer;
    let interval;
    if (showUpiQr && paymentMethod === 'Online' && (serviceCart.length > 0 || cart.length > 0) && (activeTab === 'sales_purchases' || activeTab === 'service')) {
      setUpiQrTimer(30); // reset timer on open
      timer = setTimeout(() => setShowUpiQr(false), 30000);
      interval = setInterval(() => {
        setUpiQrTimer(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    } else if (showUpiQr) {
      setShowUpiQr(false);
    }
  }, [showUpiQr, paymentMethod, cart.length, serviceCart.length, activeTab]);



  // --- Payment Portal (for Customers scanning Printed QR) ---
  if (externalPayment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl border border-gray-100 p-8 text-center animate-in zoom-in-95 duration-500">
          <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <QrCode size={40} className="text-blue-600" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-2 uppercase tracking-tight">{t('digitalPayment')}</h2>
          <p className="text-sm text-gray-500 mb-8 font-medium">{t('invoice')}: <span className="text-blue-600 font-bold">{externalPayment.invoiceId}</span></p>

          {externalPayment.expired ? (
            <div className="bg-rose-50 border border-rose-100 p-6 rounded-3xl mb-8">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={24} className="text-rose-600" />
              </div>
              <h3 className="font-black text-rose-900 uppercase tracking-wide text-sm mb-1">{t('linkExpired')}</h3>
              <p className="text-xs text-rose-600 font-medium">{t('linkExpiredDetail')}</p>
            </div>
          ) : (
            <>
              <div className="bg-blue-50/50 p-6 rounded-3xl mb-8 border border-blue-100/50">
                <p className="text-xs text-blue-600 font-black uppercase tracking-[0.2em] mb-2">{t('totalLabel') || 'Total Amount'}</p>
                <p className="text-4xl font-black text-gray-900 font-mono tracking-tighter">{formatCurrency(externalPayment.amount)}</p>
                <p className="text-[10px] text-gray-400 font-bold uppercase mt-2 tracking-widest">{externalPayment.paymentMethod} Payment</p>
              </div>

              {externalPayment.paymentMethod === 'UPI' ? (
                <a
                  href={`upi://pay?pa=${externalPayment.upiId}&pn=${encodeURIComponent(externalPayment.merchantName)}&am=${externalPayment.amount}&cu=INR`}
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-5 rounded-[2rem] shadow-lg shadow-blue-600/20 transition-all hover:-translate-y-1 active:scale-95 text-lg uppercase tracking-widest"
                >
                  {t('payNow')}
                </a>
              ) : (
                <div className="bg-white border-2 border-dashed border-gray-200 p-6 rounded-[2rem]">
                  <p className="text-xs text-gray-500 font-bold uppercase mb-2 tracking-widest">Pay to ID:</p>
                  <p className="text-xl font-black text-blue-600 break-all">{externalPayment.upiId}</p>
                  <p className="text-[10px] text-gray-400 mt-4 leading-relaxed font-bold uppercase tracking-tighter">
                    Please use your {externalPayment.paymentMethod} app to pay {formatCurrency(externalPayment.amount)} to this ID.
                  </p>
                </div>
              )}

              <p className="mt-6 text-[10px] text-gray-400 font-bold uppercase tracking-widest flex items-center justify-center gap-2">
                <Shield size={12} /> {t('securePayment') || 'Secure Digital Payment'}
              </p>
            </>
          )}

          <button
            onClick={() => {
              setExternalPayment(null);
              const url = new URL(window.location);
              url.search = '';
              window.history.replaceState({}, '', url);
            }}
            className="mt-8 text-xs font-bold text-gray-400 hover:text-gray-600 transition-colors uppercase tracking-[0.2em]"
          >
            {t('goBackToApp')}
          </button>
        </div>
      </div>
    );
  }

  // --- Login Screen ---
  if (authLoading) return <div className="flex h-screen items-center justify-center bg-gray-50"><Loader2 className="animate-spin text-blue-600" size={48} /></div>;

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f172a] p-6 relative animate-in fade-in duration-700 overflow-hidden">
        {/* Architectural Background Elements */}
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-600/20 rounded-full blur-[120px] animate-pulse pointer-events-none"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-indigo-600/20 rounded-full blur-[120px] animate-pulse delay-1000 pointer-events-none"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03] pointer-events-none"></div>

        <div className="w-full max-w-[440px] z-10">
          <div className="bg-white/90 backdrop-blur-3xl rounded-[3rem] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.5)] border border-white/50 overflow-hidden">
            <div className="p-10 sm:p-12">
              <div className="text-center mb-10">
                <div className="inline-block p-5 bg-slate-900 rounded-[2rem] shadow-2xl mb-6 transform hover:rotate-6 transition-all duration-500">
                  <Shield size={40} className="text-blue-500" strokeWidth={2.5} />
                </div>
                <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase italic leading-none">
                  {showForgotPassword ? t('resetPassword') : (authMode === 'login' ? t('welcome') : t('createAccount'))}
                </h1>
                <p className="text-[11px] font-black text-slate-400 uppercase tracking-[0.3em] mt-3 italic opacity-70">
                  {showForgotPassword ? t('enterEmail') : (authMode === 'login' ? t('signInToAccess') : "Verification Required")}
                </p>
              </div>

              {showForgotPassword ? (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">{t('email')}</label>
                    <div className="relative group">
                      <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" size={20} />
                      <input
                        type="email"
                        className="w-full pl-14 pr-6 py-4 bg-slate-50/50 border border-slate-100 rounded-2xl text-sm font-bold text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all placeholder:text-slate-300"
                        placeholder="name@example.com"
                        value={resetEmail}
                        onChange={e => setResetEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <button type="submit" className="w-full bg-slate-900 hover:bg-black text-white font-black py-4 rounded-2xl shadow-xl shadow-slate-200 transition-all flex items-center justify-center gap-3 uppercase tracking-widest text-xs mt-2 hover:scale-[1.02] active:scale-[0.98]">
                    {loading ? <Loader2 className="animate-spin" /> : t('sendResetLink')}
                  </button>
                  <div className="text-center pt-2">
                    <button type="button" onClick={() => setShowForgotPassword(false)} className="text-[11px] font-black text-slate-400 hover:text-blue-600 uppercase tracking-widest transition-colors" title={t('backToLogin')}>
                      {t('backToLogin')}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <form onSubmit={handleAuth} className="space-y-5">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">{t('email')}</label>
                      <div className="relative group">
                        <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" size={20} />
                        <input
                          type="email"
                          className="w-full pl-14 pr-6 py-4 bg-slate-50/50 border border-slate-100 rounded-2xl text-sm font-bold text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all placeholder:text-slate-300"
                          placeholder="name@example.com"
                          value={authForm.email}
                          onChange={e => setAuthForm({ ...authForm, email: e.target.value })}
                          required
                          autoComplete="off"
                          name="new-password-field-hack-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">{t('password')}</label>
                      <div className="relative group">
                        <Shield className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" size={20} />
                        <input
                          type="password"
                          className="w-full pl-14 pr-6 py-4 bg-slate-50/50 border border-slate-100 rounded-2xl text-sm font-bold text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all placeholder:text-slate-300"
                          placeholder="••••••••"
                          value={authForm.password}
                          onChange={e => setAuthForm({ ...authForm, password: e.target.value })}
                          required
                          autoComplete="new-password"
                        />
                      </div>
                    </div>

                    {authMode === 'login' && (
                      <div className="flex justify-between items-center pr-2">
                        <div></div>
                        <button type="button" onClick={() => setShowForgotPassword(true)} className="text-[10px] font-black text-blue-600 hover:text-blue-700 uppercase tracking-widest transition-colors">
                          {t('forgotPassword')}
                        </button>
                      </div>
                    )}

                    {authMode === 'signup' && otpSent && (
                      <div className="animate-in slide-in-from-top-4 duration-500 space-y-4 py-2">
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-black uppercase tracking-widest text-blue-600 ml-4">{t('otp')}</label>
                          <div className="relative group">
                            <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-blue-600/40 group-focus-within:text-blue-600 transition-colors" size={20} />
                            <input
                              type="text"
                              className="w-full pl-14 pr-6 py-4 bg-blue-50/30 border border-blue-100 rounded-2xl text-lg font-black tracking-[0.5em] text-blue-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all placeholder:text-blue-200"
                              placeholder="000000"
                              maxLength={6}
                              value={otpInput}
                              onChange={e => setOtpInput(e.target.value)}
                              required
                            />
                          </div>
                        </div>

                        <div className="flex justify-between items-center px-4">
                           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Did not get code?</p>
                           <button 
                             type="button" 
                             onClick={handleResendOtp}
                             disabled={loading}
                             className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline disabled:opacity-30 transition-all"
                           >
                              {loading ? "..." : "Resend OTP"}
                           </button>
                        </div>
                        
                        {authForm.apiKey && (
                          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between group overflow-hidden relative">
                            <div className="absolute right-0 top-0 h-full w-1 bg-blue-600 transition-all group-hover:w-full opacity-0 group-hover:opacity-[0.03]"></div>
                            <div>
                              <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest mb-0.5">{t('registrationId')}</p>
                              <p className="text-xs font-black text-slate-900 font-mono select-all tracking-tighter">{authForm.apiKey}</p>
                            </div>
                            <div className="p-2 bg-white rounded-xl shadow-sm border border-gray-100 text-blue-600">
                              <Shield size={16} />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-2xl shadow-[0_20px_40px_-10px_rgba(37,99,235,0.4)] transition-all flex items-center justify-center gap-3 uppercase tracking-widest text-xs mt-4 hover:scale-[1.02] active:scale-[0.98]">
                      {loading ? <Loader2 className="animate-spin" /> : (authMode === 'login' ? t('login') : (otpSent ? t('verify') : t('signup')))}
                    </button>
                    
                    {authMode === 'login' && (
                      <div className="pt-2">
                        <div className="relative my-6">
                           <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100"></div></div>
                           <div className="relative flex justify-center text-[10px] font-black uppercase tracking-widest"><span className="px-4 bg-white text-gray-400">{t('orContinue')}</span></div>
                        </div>
                        <button onClick={handleGoogleLogin} type="button" className="w-full bg-white border border-slate-100 text-slate-700 font-black py-3.5 rounded-2xl hover:bg-slate-50 transition-all flex items-center justify-center gap-3 shadow-sm text-xs tracking-wider uppercase mb-3">
                          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5 grayscale opacity-70" alt="Google" />
                          {t('signInGoogle')}
                        </button>

                        <button onClick={handleAnonymousLogin} type="button" className="w-full bg-slate-900 border border-slate-800 text-white font-black py-3.5 rounded-2xl hover:bg-black transition-all flex items-center justify-center gap-3 shadow-md text-xs tracking-wider uppercase">
                          <User size={18} className="text-blue-500" />
                          {t('guestLogin') || 'Guest Login'}
                        </button>
                      </div>
                    )}
                  </form>

                  <div className="mt-8 text-center">
                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{authMode === 'login' ? t('noAccount') : t('haveAccount')}</span>
                    <button
                      onClick={() => {
                        setAuthMode(authMode === 'login' ? 'signup' : 'login');
                        setOtpSent(false);
                        setOtpInput('');
                      }}
                      className="ml-2 text-[11px] font-black text-blue-600 uppercase tracking-widest hover:underline transition-all"
                    >
                      {authMode === 'login' ? t('signup') : t('login')}
                    </button>
                  </div>
                </>
              )}

            <div className="mt-4 flex justify-center gap-4">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="bg-gray-100 border-none text-xs font-medium text-gray-700 px-2 py-1.5 rounded-lg hover:bg-gray-200 cursor:pointer focus:ring-0"
              >
                <option value="en">English</option>
                <option value="hi">हिंदी</option>
                <option value="ar">العربية</option>
                <option value="zh">中文</option>
                <option value="ur">اردو</option>
                <option value="bn">বাংলা</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  }
  const renderKioskView = () => {
    if (kioskTab === 'home') {
      return (
        <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-6 relative overflow-hidden font-sans">
          <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:40px_40px]"></div>
          <div className="relative z-10 w-full max-w-5xl flex flex-col items-center text-center">
            <div className="bg-white/5 backdrop-blur-xl p-10 rounded-[4rem] border border-white/10 mb-12 shadow-2xl flex flex-col items-center">
              {(selectedSite?.logo || shopSettings.logo) ? (
                <img src={selectedSite?.logo || shopSettings.logo} alt="Logo" className="h-24 w-auto mb-6 drop-shadow-2xl" />
              ) : (
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center mb-6 shadow-lg shadow-blue-500/20">
                  <Shield size={40} className="text-white" />
                </div>
              )}
              <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter uppercase mb-4 italic">
                {shopSettings.name || 'FINN ERP'}
              </h1>
              <div className="h-1.5 w-32 bg-blue-500 rounded-full"></div>
            </div>
            <div className="mb-12">
              <p className="text-6xl md:text-8xl font-black text-white tabular-nums tracking-tighter">
                {kioskTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
              </p>
              <p className="text-blue-400 text-xs font-bold uppercase tracking-[0.3em] mt-2">
                {kioskTime.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 w-full">
              {[
                { id: 'pos', label: t('menuSalesPurchases'), icon: <ShoppingCart size={40} />, color: 'blue', tab: 'sales_purchases' },
                { id: 'service', label: t('menuService'), icon: <Wrench size={40} />, color: 'rose', tab: 'service' },
                { id: 'warehouse', label: t('menuWarehouses') || 'Warehouse', icon: <Package size={40} />, color: 'emerald', tab: 'warehouses' },
                { id: 'cafe', label: t('menuCafe') || 'Cafe', icon: <Coffee size={40} />, color: 'orange', tab: 'cafe' },
                { id: 'history', label: t('menuInvoices'), icon: <Clock size={40} />, color: 'purple', tab: 'history' }
              ].map(action => (
                <button
                  key={action.id}
                  onClick={() => { setKioskTab(action.id); setActiveTab(action.tab); }}
                  className="group bg-white p-6 rounded-[2rem] shadow-xl hover:-translate-y-2 transition-all duration-300 text-left border-b-4 border-gray-100 hover:border-blue-500 active:scale-95 flex flex-col items-center text-center"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-slate-50 text-slate-600 group-hover:bg-blue-600 group-hover:text-white transition-all`}>
                    {React.cloneElement(action.icon, { size: 24 })}
                  </div>
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-1 leading-tight">{action.label}</h3>
                  <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">{t('tapToStart') || 'Tap to start'}</p>
                </button>
              ))}
            </div>
            <button
              onDoubleClick={() => setIsKiosk(false)}
              className="mt-20 text-[10px] font-black text-white/20 uppercase tracking-[0.5em] hover:text-white/40 transition-colors"
            >
              Double click to exit kiosk
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  if (isKiosk && kioskTab === 'home') {
    return renderKioskView();
  }

  return (
    <div className={`flex h-screen bg-gray-50 font-sans text-gray-900 overflow-hidden ${isKiosk ? 'kiosk-active' : ''}`}>
      <SpeedInsights />
      <Analytics />
      {/* Sidebar (Responsive & High Z-Index) */}
      {!isKiosk && (
        <aside className={`fixed md:relative inset-y-0 left-0 bg-[#0f172a] text-white flex flex-col shadow-2xl z-[600] transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] overflow-hidden ${isSidebarOpen ? 'w-[280px] translate-x-0' : 'w-0 -translate-x-full md:w-0'}`}>

          <div className="p-7 border-b border-slate-800/50 flex justify-between items-center bg-slate-900/50 backdrop-blur-2xl sticky top-0 z-10">
            <div className="flex items-center gap-3.5">
              <div
                className={`p-1 rounded-lg shadow-sm border ${currentMode === 'Owner' ? 'bg-rose-500/10 border-rose-500/20' : currentMode === 'Manager' ? 'bg-amber-500/10 border-amber-500/20' : 'bg-blue-600/10 border-blue-600/20'} transform hover:scale-110 transition-transform overflow-hidden flex items-center justify-center w-8 h-8 flex-shrink-0 cursor-pointer`}
                onDoubleClick={() => { setPinAction('switchMode'); setIsPinModalOpen(true); }}
              >
                <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                  <Shield size={14} className="text-white" />
                </div>
              </div>
              <div className="overflow-hidden flex-1">
                <h1 className="font-black text-[11px] uppercase tracking-[0.2em] text-white/95">
                  FINN ERP
                </h1>
                <div
                  className={`flex items-center gap-1.5 px-2 py-0.5 rounded-lg transition-all ${currentMode === 'Owner' ? 'text-rose-400' :
                    currentMode === 'Manager' ? 'text-amber-400' :
                      'text-blue-400'
                    }`}
                >
                  <div className={`w-1.5 h-1.5 rounded-full ${currentMode === 'Owner' ? 'bg-rose-500' : currentMode === 'Manager' ? 'bg-amber-500' : 'bg-blue-500'} animate-pulse`}></div>
                  <span className="text-[10px] font-black uppercase tracking-widest">
                    {t(currentMode.toLowerCase() + 'Mode') || currentMode}
                  </span>
                  {currentMode !== 'Cashier' && (
                    <button
                      onClick={() => { setCurrentMode('Cashier'); setShowSensitiveData(false); }}
                      className="p-1 hover:text-white transition-colors"
                      title="Lock Mode"
                    >
                      <Lock size={10} />
                    </button>
                  )}
                </div>
              </div>
            </div>
            <button onClick={() => setIsSidebarOpen(false)} className="md:hidden p-2 hover:bg-slate-800 rounded-2xl text-slate-400 hover:text-white transition-all bg-slate-800/30"><X size={20} /></button>
          </div>

          <nav className="flex-1 overflow-y-auto py-8 px-4 space-y-2 no-scrollbar">
            <SidebarItem icon={<LayoutDashboard size={20} />} label={t('menuDashboard')} active={activeTab === 'dashboard'} onClick={() => { 
                if (!isRegistered) { alert(t('registeredOnly') || "This is an online feature for registered users only."); return; }
                setActiveTab('dashboard'); 
                if (window.innerWidth < 1024) setIsSidebarOpen(false); 
            }} />

            {/* Owner & Manager ERP Tabs */}
            {currentMode !== 'Cashier' && (
              <div className="pt-2 animate-in slide-in-from-left duration-500">
                <div className="px-4 mb-2"><span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{t('management') || 'Management'}</span></div>
                <SidebarItem icon={<Users size={20} />} label={t('menuEmployees')} active={activeTab === 'employees'} onClick={() => { if (!isRegistered) { alert(t('registeredOnly')); return; } setActiveTab('employees'); if (window.innerWidth < 1024) setIsSidebarOpen(false); }} />
                <SidebarItem icon={<MapPin size={20} />} label={t('menuSites')} active={activeTab === 'sites'} onClick={() => { if (!isRegistered) { alert(t('registeredOnly')); return; } setActiveTab('sites'); if (window.innerWidth < 1024) setIsSidebarOpen(false); }} />
                <SidebarItem icon={<Clock size={20} />} label={t('menuAttendance')} active={activeTab === 'attendance'} onClick={() => { if (!isRegistered) { alert(t('registeredOnly')); return; } setActiveTab('attendance'); if (window.innerWidth < 1024) setIsSidebarOpen(false); }} />
                <SidebarItem icon={<DollarSign size={20} />} label={t('menuPayroll')} active={activeTab === 'payroll'} onClick={() => { if (!isRegistered) { alert(t('registeredOnly')); return; } setActiveTab('payroll'); if (window.innerWidth < 1024) setIsSidebarOpen(false); }} />

                <SidebarItem
                  icon={<BarChart3 size={20} />}
                  label={t('menuReports')}
                  active={activeTab === 'reports'}
                  onClick={() => {
                    if (!isRegistered) { alert(t('registeredOnly')); return; }
                    setActiveTab('reports');
                    if (window.innerWidth < 1024) setIsSidebarOpen(false);
                  }}
                />
              </div>
            )}

            <div className="my-6 px-4 border-t border-slate-800/50 pt-6">
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{t('operations') || 'Operations'}</span>
            </div>

            <SidebarItem icon={<ShoppingCart size={20} />} label={t('menuSalesPurchases')} active={activeTab === 'sales_purchases'} onClick={() => { setActiveTab('sales_purchases'); if (window.innerWidth < 1024) setIsSidebarOpen(false); }} />
            <SidebarItem icon={<Coffee size={20} />} label={t('menuCafe')} active={activeTab === 'cafe'} onClick={() => { setActiveTab('cafe'); if (window.innerWidth < 1024) setIsSidebarOpen(false); }} />
            <SidebarItem icon={<Wrench size={20} />} label={t('menuService') || 'Service'} active={activeTab === 'service'} onClick={() => { setActiveTab('service'); if (window.innerWidth < 1024) setIsSidebarOpen(false); }} />
            <SidebarItem icon={<Package size={20} />} label={t('menuWarehouses')} active={activeTab === 'warehouses'} onClick={() => { setActiveTab('warehouses'); if (window.innerWidth < 1024) setIsSidebarOpen(false); }} />
            <SidebarItem icon={<Clock size={20} />} label={t('menuInvoices')} active={activeTab === 'history'} onClick={() => { setActiveTab('history'); if (window.innerWidth < 1024) setIsSidebarOpen(false); }} />
          </nav>

          <div className="p-6 border-t border-slate-800/50 space-y-4 bg-slate-900/20">
            <button onClick={() => setIsKiosk(true)} className="flex items-center w-full px-4 py-3 text-sm font-bold text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-2xl transition-all group">
              <Tablet size={18} className="mr-3 group-hover:scale-110 transition-transform duration-500" />
              {t('menuKiosk') || 'Kiosk Mode'}
            </button>
            <button onClick={() => { if (!isRegistered) { alert(t('registeredOnly')); return; } setShowSettings(true); }} className="flex items-center w-full px-4 py-3 text-sm font-bold text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-2xl transition-all group">
              <Settings size={18} className="mr-3 group-hover:rotate-90 transition-transform duration-500" />
              {t('settings')}
            </button>

            <button onClick={handleLogout} className="flex items-center w-full px-4 py-3 text-sm font-bold text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-2xl transition-all">
              <LogOut size={18} className="mr-3" />
              {t('logout')}
            </button>
          </div>
        </aside>
      )}
      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden relative">
        {/* Mobile Sidebar Overlay with Blur Effect */}
        {isSidebarOpen && <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[550] md:hidden animate-in fade-in duration-300" onClick={() => setIsSidebarOpen(false)}></div>}

        {/* Header */}
        {!isKiosk ? (
          <header className="bg-white/95 backdrop-blur-2xl border-b border-gray-100/50 h-16 sm:h-20 flex items-center justify-between px-4 sm:px-10 sticky top-0 z-[60] transition-all shrink-0">

            <div className="flex items-center gap-4">
              <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className={`p-2.5 -ml-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-2xl transition-all active:scale-90 ${isSidebarOpen ? 'lg:hidden' : 'block'}`}><Menu size={26} /></button>
              <h2 className="text-xl font-black text-gray-900 flex items-center gap-3.5 tracking-tight group">
                <span className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-2xl shadow-lg shadow-blue-200 group-hover:rotate-12 transition-all duration-500">
                  {activeTab === 'dashboard' && <LayoutDashboard size={22} />}
                  {activeTab === 'employees' && <Users size={22} />}
                  {activeTab === 'sites' && <MapPin size={22} />}
                  {activeTab === 'attendance' && <Clock size={22} />}
                  {activeTab === 'payroll' && <DollarSign size={22} />}
                  {activeTab === 'reports' && <BarChart3 size={22} />}
                  {activeTab === 'cafe' && <Coffee size={22} />}
                  {activeTab === 'service' && <Wrench size={22} />}
                  {activeTab === 'sales_purchases' && <ShoppingCart size={22} />}
                  {activeTab === 'warehouses' && <Package size={22} />}
                  {activeTab === 'history' && <Clock size={22} />}
                </span>
                <span className="hidden sm:inline-block animate-in fade-in slide-in-from-left-4 duration-500">
                  {activeTab === 'dashboard' && t('menuDashboard')}
                  {activeTab === 'employees' && t('menuEmployees')}
                  {activeTab === 'sites' && t('menuSites')}
                  {activeTab === 'attendance' && t('menuAttendance')}
                  {activeTab === 'payroll' && t('menuPayroll')}
                  {activeTab === 'reports' && t('menuReports')}
                  {activeTab === 'cafe' && t('menuCafe')}
                  {activeTab === 'service' && (t('menuService') || 'Service')}
                  {activeTab === 'sales_purchases' && t('menuSalesPurchases')}
                  {activeTab === 'warehouses' && t('menuWarehouses')}
                  {activeTab === 'history' && t('menuInvoices')}
                </span>
              </h2>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 h-full">
              {!isRegistered && user && (
                <div className="hidden lg:flex items-center gap-2 px-4 py-1.5 bg-amber-50 border border-amber-100 rounded-2xl text-amber-600 font-bold text-[10px] uppercase tracking-widest animate-pulse">
                  <Shield size={14} /> {t('guestNotice') || "Guest Mode"}
                </div>
              )}
              <div className="hidden md:flex items-center bg-gray-50 p-1 rounded-2xl border border-gray-100 shadow-inner">
                <select
                  value={currency}
                  onChange={(e) => {
                    const newCurrency = e.target.value;
                    setCurrency(newCurrency);
                    saveUserSettings({ currency: newCurrency });
                  }}
                  className="bg-transparent border-none text-[11px] font-black uppercase text-slate-600 px-3 py-1.5 cursor-pointer focus:ring-0 hover:text-blue-600 transition-colors"
                >
                  <option value="EGP">EGP</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="INR">INR</option>
                  <option value="SAR">SAR</option>
                  <option value="AED">AED</option>
                </select>
                <div className="w-px h-4 bg-gray-200 mx-1"></div>
                <select
                  value={language}
                  onChange={(e) => {
                    const newLang = e.target.value;
                    setLanguage(newLang);
                    saveUserSettings({ language: newLang });
                  }}
                  className="bg-transparent border-none text-[11px] font-black uppercase text-slate-600 px-3 py-1.5 cursor-pointer focus:ring-0 hover:text-blue-600 transition-colors"
                >
                  <option value="en">EN</option>
                  <option value="ar">AR</option>
                  <option value="hi">HI</option>
                  <option value="zh">ZH</option>
                  <option value="ur">UR</option>
                  <option value="bn">BN</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button onClick={() => setShowSettings(true)} className="p-2.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-2xl transition-all"><Settings size={22} /></button>
                <div className="w-10 h-10 border-2 border-slate-100 p-0.5 rounded-2xl hover:scale-105 transition-transform cursor-pointer">
                  <div className={`w-full h-full rounded-[0.6rem] flex items-center justify-center font-black text-white text-[12px] shadow-sm ${currentMode === 'Owner' ? 'bg-rose-500' : currentMode === 'Manager' ? 'bg-amber-500' : 'bg-blue-600'}`}>
                    {currentMode[0]}
                  </div>
                </div>
              </div>
            </div>
          </header>
        ) : (
          <header className="bg-slate-900 text-white h-20 flex items-center justify-between px-10 sticky top-0 z-[100] border-b border-white/10 shadow-2xl shrink-0">
            <button onClick={() => setKioskTab('home')} className="flex items-center gap-3 bg-white/10 hover:bg-white/20 px-6 py-2.5 rounded-2xl transition-all font-black uppercase tracking-widest text-sm">
              <ChevronLeft size={20} /> {t('backToKiosk') || 'Back to Kiosk'}
            </button>
            <div className="flex flex-col items-center">
              <h2 className="text-xl font-black uppercase italic tracking-tighter">{shopSettings.name || 'FINN ERP'}</h2>
              <p className="text-[10px] text-blue-400 font-bold uppercase tracking-[0.3em] font-mono whitespace-nowrap">{kioskTime.toLocaleTimeString([], { hour12: false })}</p>
            </div>
            <div className="w-[100px]"></div> {/* Spacer */}
          </header>
        )}



        {/* Global Error Banner */}
        {globalError && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 m-4 mb-0">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 font-bold">
                  {t('connectionError')}
                </p>
              </div>
            </div>
          </div>
        )}


        {/* Views Container - The Heart of the App */}
        <div className="flex-1 overflow-hidden bg-gray-50 flex flex-col relative">

          {/* Scrollable Zone 1: Standard Management Tabs */}
          {['dashboard', 'employees', 'sites', 'attendance', 'payroll', 'reports', 'accounts'].includes(activeTab) && (
            <div className="flex-1 overflow-y-auto no-scrollbar scroll-smooth">
              <div className="p-4 sm:p-6 md:p-8 lg:p-10 pb-[140px] lg:pb-10 max-w-[1600px] mx-auto w-full transition-all duration-500">
                {activeTab === 'dashboard' && (
                  <div className="space-y-8 animate-in fade-in duration-700 max-w-7xl mx-auto">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-8">
                      <div>
                        <h1 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight leading-none mb-2">{t('menuDashboard')}</h1>
                        <p className="text-[10px] sm:text-xs text-gray-400 font-bold uppercase tracking-[0.2em]">{t('welcome')}, {t('admin')}</p>
                      </div>
                      <div className="relative group w-full sm:w-auto">
                        <select
                          className="w-full sm:w-auto appearance-none bg-white border border-gray-100 rounded-2xl px-5 py-3 pr-10 text-sm font-bold text-gray-700 shadow-sm focus:outline-none focus:ring-4 focus:ring-blue-500/10 hover:border-blue-200 transition-all cursor-pointer"
                          value={homeLocationFilter}
                          onChange={e => setHomeLocationFilter(e.target.value)}
                        >
                          <option value="">{t('filterAll') || 'All Locations'}</option>
                          {sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-blue-500 transition-colors" size={16} />
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-[2rem] p-7 text-white shadow-2xl shadow-blue-200 relative overflow-hidden group">
                        <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
                        <div className="flex justify-between items-start mb-6 relative z-10">
                          <div className="p-3 bg-white/20 backdrop-blur-md rounded-2xl"><Users size={28} /></div>
                          <span className="bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">{t('dashboardTotal')}</span>
                        </div>
                        <h3 className="text-4xl font-black tracking-tight mb-1 relative z-10">
                          {employees.filter(e => !homeLocationFilter || e.location === homeLocationFilter).length}
                        </h3>
                        <p className="text-blue-100/80 text-xs font-bold uppercase tracking-widest relative z-10">{t('activeGuards')}</p>
                      </div>

                      <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 rounded-[2rem] p-7 text-white shadow-2xl shadow-emerald-200 relative overflow-hidden group">
                        <div className="absolute bottom-0 left-0 -ml-8 -mb-8 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
                        <div className="flex justify-between items-start mb-6 relative z-10">
                          <div className="p-3 bg-white/20 backdrop-blur-md rounded-2xl"><MapPin size={28} /></div>
                          <span className="bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">{t('active')}</span>
                        </div>
                        <h3 className="text-4xl font-black tracking-tight mb-1 relative z-10">
                          {sites.filter(s => (!homeLocationFilter || s.name === homeLocationFilter) && s.status === 'Operational').length}
                        </h3>
                        <p className="text-emerald-100/80 text-xs font-bold uppercase tracking-widest relative z-10">{t('operationalSites')}</p>
                      </div>

                      <div className="bg-white rounded-[2rem] p-7 shadow-xl shadow-gray-200/50 border border-gray-100/50 relative overflow-hidden group">
                        <div className="flex justify-between items-start mb-6">
                          <div className="p-3 bg-orange-50 text-orange-600 rounded-2xl group-hover:scale-110 transition-transform duration-500"><Clock size={28} /></div>
                          <span className="bg-orange-50 text-orange-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">{t('today')}</span>
                        </div>
                        <h3 className="text-4xl font-black text-gray-900 tracking-tight mb-1">
                          {attendance.filter(a =>
                            a.date === new Date().toISOString().split('T')[0] &&
                            (!homeLocationFilter || getEmployeeLocation(a.name) === homeLocationFilter)
                          ).length}
                        </h3>
                        <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">{t('checkedInToday')}</p>
                      </div>

                      <div className="bg-white rounded-[2rem] p-7 shadow-xl shadow-gray-200/50 border border-gray-100/50 relative overflow-hidden group">
                        <div className="flex justify-between items-start mb-6">
                          <div className="p-3 bg-rose-50 text-rose-600 rounded-2xl group-hover:scale-110 transition-transform duration-500"><AlertCircle size={28} /></div>
                          <span className="bg-rose-50 text-rose-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">{t('lateAbsent')}</span>
                        </div>
                        <h3 className="text-4xl font-black text-gray-900 tracking-tight mb-1">
                          {attendance.filter(a =>
                            (a.status === 'Late' || a.status === 'Absent') &&
                            a.date === new Date().toISOString().split('T')[0] &&
                            (!homeLocationFilter || getEmployeeLocation(a.name) === homeLocationFilter)
                          ).length}
                        </h3>
                        <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">{t('issuesToday')}</p>
                      </div>
                    </div>

                    {/* Quick Links & Status */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div className="lg:col-span-2 bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100/50">
                        <div className="flex justify-between items-center mb-6">
                          <h3 className="font-black text-xl text-gray-900 uppercase tracking-tight">{t('quickActions')}</h3>
                          <div className="w-12 h-1 bg-blue-100 rounded-full"></div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          <button onClick={() => { setActiveTab('employees'); setIsAddModalOpen(true); }} className="p-4 bg-blue-50/50 text-blue-600 rounded-[2rem] font-bold hover:bg-blue-600 hover:text-white transition-all duration-300 flex flex-col items-center gap-3 active:scale-95 group">
                            <div className="p-3 bg-white rounded-2xl shadow-sm group-hover:bg-blue-500"><Users size={24} /></div>
                            <span className="text-xs uppercase tracking-tighter">{t('addStaff')}</span>
                          </button>
                          <button onClick={() => { setActiveTab('sites'); setIsAddSiteModalOpen(true); }} className="p-4 bg-emerald-50/50 text-emerald-600 rounded-[2rem] font-bold hover:bg-emerald-600 hover:text-white transition-all duration-300 flex flex-col items-center gap-3 active:scale-95 group">
                            <div className="p-3 bg-white rounded-2xl shadow-sm group-hover:bg-emerald-500"><MapPin size={24} /></div>
                            <span className="text-xs uppercase tracking-tighter">{t('addSite')}</span>
                          </button>
                          <button onClick={() => { setActiveTab('sales_purchases'); }} className="p-4 bg-indigo-50/50 text-indigo-600 rounded-[2rem] font-bold hover:bg-indigo-600 hover:text-white transition-all duration-300 flex flex-col items-center gap-3 active:scale-95 group">
                            <div className="p-3 bg-white rounded-2xl shadow-sm group-hover:bg-indigo-500"><ShoppingCart size={24} /></div>
                            <span className="text-xs uppercase tracking-tighter">{t('menuSalesPurchases')}</span>
                          </button>
                          <button onClick={() => setShowSettings(true)} className="p-4 bg-gray-50 text-gray-600 rounded-[2rem] font-bold hover:bg-gray-800 hover:text-white transition-all duration-300 flex flex-col items-center gap-3 active:scale-95 group">
                            <div className="p-3 bg-white rounded-2xl shadow-sm group-hover:bg-gray-700"><Settings size={24} /></div>
                            <span className="text-xs uppercase tracking-tighter">{t('settings')}</span>
                          </button>
                        </div>
                      </div>

                      <div className="bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl text-white relative overflow-hidden flex flex-col justify-center">
                        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-blue-600 rounded-full blur-[80px] opacity-40"></div>
                        <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-64 h-64 bg-indigo-600 rounded-full blur-[80px] opacity-30"></div>

                        <div className="relative z-10">
                          <h3 className="font-black text-2xl mb-2 uppercase tracking-tight">{t('systemStatus')}</h3>
                          <p className="text-slate-400 text-sm mb-6 font-medium">{t('systemOperational')}</p>
                          <div className="inline-flex items-center gap-3 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 font-black text-xs uppercase tracking-widest">
                            <span className="w-2.5 h-2.5 bg-emerald-400 rounded-full animate-ping"></span>
                            {t('online')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}


                {activeTab === 'employees' && (
                  <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{t('allEmployees')}</h3>
                        <p className="text-sm text-gray-500">{t('manageStaff')}</p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <select
                          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                          value={employeeLocationFilter}
                          onChange={e => setEmployeeLocationFilter(e.target.value)}
                        >
                          <option value="">{t('filterAll')}</option>
                          {sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                        <input
                          type="month"
                          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                          value={employeeMonthFilter}
                          onChange={e => setEmployeeMonthFilter(e.target.value)}
                          title={t('selectMonthInfo')}
                        />
                        <button onClick={() => setIsAddModalOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 transition-all w-full sm:w-auto">
                          <Plus size={18} /> {t('addEmployee')}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {employees
                        .filter(emp => !employeeLocationFilter || emp.location === employeeLocationFilter)
                        .map(emp => (
                          <div key={emp.id} onClick={() => setSelectedEmployee(emp)} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow cursor-pointer relative overflow-hidden group">
                            <div className={`absolute top-0 left-0 w-1 h-full ${emp.status === 'Active' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-gray-100 overflow-hidden border-2 border-white shadow-sm flex items-center justify-center">
                                  {emp.photo ? <img src={emp.photo} alt={emp.name} className="w-full h-full object-cover" /> : <User size={24} className="text-gray-400" />}
                                </div>
                                <div>
                                  <h4 className="font-bold text-gray-900 leading-tight">{emp.name}</h4>
                                  <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full mt-1 inline-block">{t(emp.role.toLowerCase()) || emp.role}</span>
                                </div>
                              </div>
                              {emp.status === 'Active' ? <CheckCircle size={18} className="text-green-500" /> : <AlertCircle size={18} className="text-gray-400" />}
                            </div>
                            <div className="space-y-2 text-sm text-gray-600">
                              <div className="flex items-center gap-2"><Briefcase size={14} className="text-gray-400" /> {t(emp.dept.toLowerCase()) || emp.dept}</div>
                              <div className="flex items-center gap-2"><MapPin size={14} className="text-gray-400" /> {emp.location || t('unassigned')}</div>
                              <div className="flex items-center gap-2"><Clock size={14} className="text-gray-400" /> {emp.shift === 'Morning (12 Hours)' ? t('morning12') : emp.shift === 'Night (12 Hours)' ? t('night12') : emp.shift}</div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {activeTab === 'sites' && (
                  <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{t('filterAll')}</h3>
                        <p className="text-sm text-gray-500">{t('viewManageLocations')}</p>
                      </div>
                      <button onClick={() => setIsAddSiteModalOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 transition-all w-full sm:w-auto">
                        <Plus size={18} /> {t('addLocation')}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {sites.map(site => {
                        const guardCount = employees.filter(e => e.location === site.name).length;
                        return (
                          <div
                            key={site.id}
                            onClick={() => setSelectedSite(site)}
                            className="group bg-white rounded-3xl shadow-sm border border-gray-100 p-5 hover:shadow-xl transition-all cursor-pointer hover:border-blue-500/30 relative overflow-hidden active-haptic"
                          >
                            <div className={`absolute top-0 left-0 w-1.5 h-full ${(site.status === 'Operational' || site.status === 'Active') ? 'bg-emerald-500' : 'bg-amber-500'}`} />

                            <div className="flex justify-between items-start mb-4 pl-2">
                              <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-2xl ${(site.status === 'Operational' || site.status === 'Active') ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                                  <Building2 size={24} />
                                </div>
                                <div>
                                  <h4 className="font-black text-gray-900 leading-tight uppercase tracking-tight text-sm sm:text-base">{site.name}</h4>
                                  <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest mt-1.5 ${(site.status === 'Operational' || site.status === 'Active') ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                                    {t(site.status.toLowerCase()) || site.status}
                                  </span>
                                </div>
                              </div>
                              <div className="text-gray-300 group-hover:text-blue-600 transition-colors p-2 bg-gray-50 rounded-xl">
                                <ChevronRight size={20} />
                              </div>
                            </div>

                            <div className="space-y-4 pl-2">
                              <div className="flex items-center justify-between text-xs sm:text-sm">
                                <span className="flex items-center gap-2 text-slate-500 font-medium"><MapPin size={14} className="text-blue-500" /> {site.city}</span>
                                {site.phone && (
                                  <span className="flex items-center gap-2 text-emerald-600 font-black"><Phone size={14} /> {site.phone}</span>
                                )}
                              </div>

                              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-50">
                                <div className="flex flex-col gap-1">
                                  <span className="text-[10px] text-slate-400 uppercase font-black tracking-widest">{t('manager')}</span>
                                  <span className="text-gray-900 font-bold truncate text-xs">{site.manager || '-'}</span>
                                </div>
                                <div className="flex flex-col gap-1 text-right">
                                  <span className="text-[10px] text-slate-400 font-black uppercase tracking-widest">{t('siteEmployees')}</span>
                                  <span className="text-blue-600 font-black text-sm">{guardCount}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {activeTab === 'attendance' && (
                  <div className="space-y-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                      <div>
                        <h3 className="font-bold text-gray-900">{t('dailyAttendance')}</h3>
                        <p className="text-xs text-slate-500">{new Date(attendanceDateFilter).toDateString()}</p>
                      </div>

                      {/* Filters */}
                      <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                        <select
                          className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 w-full sm:w-auto"
                          value={attendanceSiteFilter}
                          onChange={(e) => setAttendanceSiteFilter(e.target.value)}
                        >
                          <option value="All">{t('filterAll')}</option>
                          {sites.map(site => (
                            <option key={site.id} value={site.name}>{site.name}</option>
                          ))}
                        </select>

                        <input
                          type="date"
                          className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 w-full sm:w-auto"
                          value={attendanceDateFilter}
                          onChange={(e) => setAttendanceDateFilter(e.target.value)}
                        />

                        <button onClick={() => setIsAddAttendanceModalOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
                          <Plus size={16} /> {t('addAttendance')}
                        </button>

                        <div className="hidden md:block h-6 w-px bg-gray-200 mx-1"></div>

                        <label className="flex items-center gap-2 bg-gray-50 px-3 py-2.5 rounded-lg cursor-pointer border hover:bg-gray-100 transition-colors w-full sm:w-auto justify-center">
                          <Upload size={16} className="text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">{t('import')}</span>
                          <input type="file" accept=".xlsx" onChange={handleImportAttendance} className="hidden" />
                        </label>
                        <button onClick={handleExportAttendance} className="flex items-center justify-center gap-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-2.5 rounded-lg transition-colors w-full sm:w-auto">
                          <Download size={16} /> {t('export')}
                        </button>
                      </div>
                    </div>

                    {/* Desktop Table */}
                    <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                      <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-500 border-b border-gray-100">
                          <tr>
                            <th className="px-6 py-4 font-black uppercase tracking-widest text-[10px]">{t('name')}</th>
                            <th className="px-6 py-4 font-black uppercase tracking-widest text-[10px]">{t('location')}</th>
                            <th className="px-6 py-4 font-black uppercase tracking-widest text-[10px]">{t('selectDate')}</th>
                            <th className="px-6 py-4 font-black uppercase tracking-widest text-[10px]">{t('coveringFor')}</th>
                            <th className="px-6 py-4 font-black uppercase tracking-widest text-[10px]">{t('status')}</th>
                            <th className="px-6 py-4 font-black uppercase tracking-widest text-[10px] text-right">{t('actions')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {attendance.filter(r =>
                            (attendanceSiteFilter === 'All' || getEmployeeLocation(r.name) === attendanceSiteFilter) &&
                            (r.date === attendanceDateFilter)
                          ).map(record => (
                            <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 font-bold text-gray-900">{record.name}</td>
                              <td className="px-6 py-4 text-gray-500 font-medium">{getEmployeeLocation(record.name)}</td>
                              <td className="px-6 py-4 text-gray-400 font-mono text-xs">{record.date}</td>
                              <td className="px-6 py-4 text-gray-400 italic text-xs">{record.replacementFor || '-'}</td>
                              <td className="px-6 py-4">
                                <StatusBadge status={record.status} lateHours={record.lateHours} t={t} />
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <button onClick={() => setEditingAttendance(record)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition-all shadow-sm border border-gray-100 bg-white">
                                    <Edit size={16} />
                                  </button>
                                  <button onClick={() => handleDeleteAttendance(record.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-all shadow-sm border border-gray-100 bg-white">
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-4 pb-20">
                      {attendance.filter(r =>
                        (attendanceSiteFilter === 'All' || getEmployeeLocation(r.name) === attendanceSiteFilter) &&
                        (r.date === attendanceDateFilter)
                      ).map(record => (
                        <div key={record.id} className="bg-white p-5 rounded-[2rem] border border-gray-100 shadow-sm space-y-4 relative overflow-hidden">
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 font-black text-sm">
                                {record.name?.[0].toUpperCase()}
                              </div>
                              <div>
                                <h4 className="font-black text-gray-900 uppercase tracking-tight text-sm">{record.name}</h4>
                                <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-0.5">{getEmployeeLocation(record.name)}</p>
                              </div>
                            </div>
                            <StatusBadge status={record.status} lateHours={record.lateHours} t={t} />
                          </div>

                          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-50">
                            <div className="space-y-1">
                              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">{t('selectDate')}</span>
                              <span className="text-xs font-mono font-bold text-gray-600">{record.date}</span>
                            </div>
                            <div className="space-y-1">
                              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">{t('coveringFor')}</span>
                              <span className="text-xs font-bold text-gray-600 italic truncate block">{record.replacementFor || '-'}</span>
                            </div>
                          </div>

                          <div className="flex gap-2 pt-2">
                            <button onClick={() => setEditingAttendance(record)} className="flex-1 py-2.5 bg-blue-50 text-blue-600 rounded-2xl font-black uppercase tracking-widest text-[9px] flex items-center justify-center gap-2 active-haptic">
                              <Edit size={14} /> {t('edit')}
                            </button>
                            <button onClick={() => handleDeleteAttendance(record.id)} className="flex-1 py-2.5 bg-rose-50 text-rose-600 rounded-2xl font-black uppercase tracking-widest text-[9px] flex items-center justify-center gap-2 active-haptic">
                              <Trash2 size={14} /> {t('delete')}
                            </button>
                          </div>
                        </div>
                      ))}
                      {attendance.length === 0 && (
                        <div className="bg-white p-12 rounded-[2.5rem] border border-dashed border-gray-200 text-center">
                          <Clock size={40} className="mx-auto text-gray-200 mb-4" />
                          <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">{t('noAttendanceToday') || 'No records for today'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'payroll' && (
                  <div className="space-y-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{t('payrollMgmt')}</h3>
                        <p className="text-sm text-slate-500">{t('costAnalysis')}</p>
                      </div>
                      <div className="flex flex-wrap gap-3 w-full md:w-auto">
                        <select
                          className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 w-full sm:w-auto"
                          value={payrollLocationFilter}
                          onChange={(e) => setPayrollLocationFilter(e.target.value)}
                        >
                          <option value="">{t('filterAll')}</option>
                          {sites.map(site => (
                            <option key={site.id} value={site.name}>{site.name}</option>
                          ))}
                        </select>

                        <input
                          type="month"
                          className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 w-full sm:w-auto"
                          value={payrollMonthFilter}
                          onChange={(e) => setPayrollMonthFilter(e.target.value)}
                        />

                        <label className="flex items-center justify-center gap-2 bg-white border border-gray-300 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-gray-50 text-sm font-medium w-full sm:w-auto">
                          <Upload size={16} /> {t('import')}
                          <input type="file" accept=".xlsx" onChange={handleImportPayroll} className="hidden" />
                        </label>
                        <button onClick={handleExportPayroll} className="bg-white border border-gray-300 px-3 py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm font-medium hover:bg-gray-50 w-full sm:w-auto">
                          <Download size={16} /> {t('export')}
                        </button>
                      </div>
                    </div>

                    {/* Month Period Indicator */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-600 rounded-lg p-4 flex items-center gap-3 shadow-sm">
                      <div className="bg-blue-600 p-2 rounded-lg">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-xs text-blue-600 font-semibold uppercase tracking-wide">{t('payPeriod')}</div>
                        <div className="text-base font-bold text-gray-900 mt-0.5">
                          {new Date(payrollMonthFilter + '-01').toLocaleDateString(language === 'ar' ? 'ar-EG' : language === 'hi' ? 'hi-IN' : language === 'zh' ? 'zh-CN' : 'en-US', { month: 'long', year: 'numeric' })}
                        </div>
                      </div>
                    </div>


                    {/* Desktop Table View */}
                    <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                      <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-500 border-b border-gray-100 uppercase tracking-widest text-[10px] font-black">
                          <tr>
                            <th className="px-6 py-4">{t('name')}</th>
                            <th className="px-6 py-4">{t('location')}</th>
                            <th className="px-6 py-4">{t('role')}</th>
                            <th className="px-6 py-4 text-right">{t('salary')}</th>
                            <th className="px-6 py-4 text-right">{t('bonus')}</th>
                            <th className="px-6 py-4">{t('overtime')}</th>
                            <th className="px-6 py-4 text-red-600">{t('advance')}</th>
                            <th className="px-6 py-4 text-right text-amber-600">{t('late')}</th>
                            <th className="px-6 py-4 text-right text-red-600">{t('absent')}</th>
                            <th className="px-6 py-4 text-right text-red-800">{t('deductions')}</th>
                            <th className="px-6 py-4 text-right">{t('netPay')}</th>
                            <th className="px-6 py-4 text-center">{t('actions')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {employees
                            .filter(emp => !payrollLocationFilter || emp.location === payrollLocationFilter)
                            .map(emp => {
                              let baseSalary = Number(emp.salary) || 0;
                              let bonus = Number(emp.bonus) || 0;
                              let overtime = Number(emp.overtime) || 0;
                              let deductionAmount = 0;
                              let lateDeduction = 0;
                              let absentDeduction = 0;

                              const empAttendance = attendance.filter(a => {
                                if (a.name !== emp.name) return false;
                                if (!a.date) return false;
                                let attendanceMonth;
                                if (typeof a.date === 'string') {
                                  attendanceMonth = a.date.substring(0, 7);
                                } else if (a.date.toDate) {
                                  attendanceMonth = a.date.toDate().toISOString().substring(0, 7);
                                } else if (a.date instanceof Date) {
                                  attendanceMonth = a.date.toISOString().substring(0, 7);
                                } else {
                                  return false;
                                }
                                return attendanceMonth === payrollMonthFilter;
                              });

                              empAttendance.forEach(record => {
                                if (record.status === 'Late') {
                                  const hourlyRate = baseSalary / 360;
                                  const lateCost = (Number(record.lateHours) || 0) * hourlyRate;
                                  if (lateCost > 0) {
                                    lateDeduction += lateCost;
                                    deductionAmount += lateCost;
                                  }
                                }
                                if (record.status === 'Absent') {
                                  const cost = baseSalary / 30;
                                  absentDeduction += cost;
                                  deductionAmount += cost;
                                }
                              });

                              const hourlyRate = baseSalary / 360;
                              const manualDeduction = (Number(emp.deductionHours) || 0) * hourlyRate;

                              const storedRecord = payrolls.find(p => p.employeeId === emp.id && p.month === payrollMonthFilter);

                              if (storedRecord) {
                                baseSalary = Number(storedRecord.salary) || baseSalary;
                                bonus = Number(storedRecord.bonus) || 0;
                                overtime = Number(storedRecord.overtime) || 0;
                              }

                              const effectiveManualDeduction = storedRecord ? (Number(storedRecord.deductions) || 0) : manualDeduction;
                              const effectiveAdvance = storedRecord ? (Number(storedRecord.advance) || 0) : (Number(emp.advanceSalary) || 0);

                              deductionAmount = lateDeduction + absentDeduction + effectiveManualDeduction + effectiveAdvance;
                              const netPay = baseSalary + bonus + overtime - deductionAmount;

                              return (
                                <tr key={emp.id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-6 py-4">
                                    <span className="font-black text-gray-900 uppercase tracking-tight block">{emp.name}</span>
                                    <span className="text-[10px] text-blue-600 font-bold uppercase tracking-widest">{emp.dept}</span>
                                  </td>
                                  <td className="px-6 py-4 text-gray-500 font-medium">{emp.location}</td>
                                  <td className="px-6 py-4 text-gray-400 text-xs font-bold uppercase tracking-widest">{t(emp.role.toLowerCase()) || emp.role}</td>
                                  <td className="px-6 py-4 text-right font-mono font-bold text-gray-900">{formatCurrency(baseSalary)}</td>
                                  <td className="px-6 py-4 text-right font-mono text-green-600 font-bold">+{formatCurrency(bonus)}</td>
                                  <td className="px-6 py-4 text-right font-mono text-indigo-600 font-bold">+{formatCurrency(overtime)}</td>
                                  <td className="px-6 py-4 text-right font-mono text-red-500 font-bold">-{formatCurrency(effectiveAdvance)}</td>
                                  <td className="px-6 py-4 text-right font-mono text-amber-600">
                                    {lateDeduction > 0 ? `-${formatCurrency(lateDeduction)}` : '-'}
                                  </td>
                                  <td className="px-6 py-4 text-right font-mono text-red-600">
                                    {absentDeduction > 0 ? `-${formatCurrency(absentDeduction)}` : '-'}
                                  </td>
                                  <td className="px-6 py-4 text-right font-mono text-red-900 font-black">-{formatCurrency(deductionAmount)}</td>
                                  <td className="px-6 py-4 text-right font-black text-blue-600 text-base">{formatCurrency(netPay)}</td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center justify-center gap-2">
                                      <button
                                        onClick={() => handleManagePayroll(emp, payrollMonthFilter, lateDeduction, absentDeduction)}
                                        className={`p-2.5 rounded-xl transition-all shadow-sm border ${storedRecord ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-white text-slate-600 border-gray-100 hover:bg-slate-50'}`}
                                        title={t('manageEditMonth')}
                                      >
                                        <Edit size={16} />
                                      </button>
                                      <button
                                        onClick={() => handlePrintPayrollSlip(emp, {
                                          baseSalary, bonus, overtime, lateDeduction, absentDeduction,
                                          manualDeduction: effectiveManualDeduction, deductionAmount,
                                          advanceSalary: effectiveAdvance, netPay
                                        }, payrollMonthFilter)}
                                        className="p-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm border border-blue-100"
                                        title={t('printPayrollSlip')}
                                      >
                                        <Printer size={16} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-4 pb-20">
                      {employees
                        .filter(emp => !payrollLocationFilter || emp.location === payrollLocationFilter)
                        .map(emp => {
                          let baseSalary = Number(emp.salary) || 0;
                          let bonus = Number(emp.bonus) || 0;
                          let overtime = Number(emp.overtime) || 0;
                          let deductionAmount = 0;
                          let lateDeduction = 0;
                          let absentDeduction = 0;

                          // Filter attendance by selected month
                          const empAttendance = attendance.filter(a => {
                            if (a.name !== emp.name) return false;
                            if (!a.date) return false;
                            let attendanceMonth = typeof a.date === 'string' ? a.date.substring(0, 7) : a.date.toISOString().substring(0, 7);
                            return attendanceMonth === payrollMonthFilter;
                          });

                          empAttendance.forEach(record => {
                            if (record.status === 'Late') {
                              const hourlyRate = baseSalary / 360;
                              const lateCost = (Number(record.lateHours) || 0) * hourlyRate;
                              if (lateCost > 0) {
                                lateDeduction += lateCost;
                                deductionAmount += lateCost;
                              }
                            }
                            if (record.status === 'Absent') {
                              const cost = baseSalary / 30;
                              absentDeduction += cost;
                              deductionAmount += cost;
                            }
                          });

                          const hourlyRate = baseSalary / 360;
                          const manualDeduction = (Number(emp.deductionHours) || 0) * hourlyRate;
                          const storedRecord = payrolls.find(p => p.employeeId === emp.id && p.month === payrollMonthFilter);

                          if (storedRecord) {
                            baseSalary = Number(storedRecord.salary) || baseSalary;
                            bonus = Number(storedRecord.bonus) || 0;
                            overtime = Number(storedRecord.overtime) || 0;
                          }

                          const effectiveManualDeduction = storedRecord ? (Number(storedRecord.deductions) || 0) : manualDeduction;
                          const effectiveAdvance = storedRecord ? (Number(storedRecord.advance) || 0) : (Number(emp.advanceSalary) || 0);

                          deductionAmount = lateDeduction + absentDeduction + effectiveManualDeduction + effectiveAdvance;
                          const netPay = baseSalary + bonus + overtime - deductionAmount;

                          return (
                            <div key={emp.id} className="bg-white p-5 rounded-[2.5rem] border border-gray-100 shadow-sm space-y-4">
                              <div className="flex justify-between items-start">
                                <div className="flex gap-3">
                                  <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 font-black text-sm uppercase">
                                    {emp.name?.[0]}
                                  </div>
                                  <div>
                                    <h4 className="font-black text-gray-900 uppercase tracking-tight text-sm">{emp.name}</h4>
                                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-0.5">{emp.dept} • {emp.location}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('netPay')}</p>
                                  <p className="font-black text-blue-600 text-base">{formatCurrency(netPay)}</p>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-x-4 gap-y-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                <div className="flex justify-between items-center text-[10px]">
                                  <span className="text-slate-500 font-bold uppercase">{t('salary')}</span>
                                  <span className="font-mono font-black text-gray-900">{formatCurrency(baseSalary)}</span>
                                </div>
                                <div className="flex justify-between items-center text-[10px]">
                                  <span className="text-green-600 font-bold uppercase">{t('bonus')}</span>
                                  <span className="font-mono font-black text-green-600">+{formatCurrency(bonus)}</span>
                                </div>
                                <div className="flex justify-between items-center text-[10px]">
                                  <span className="text-red-600 font-bold uppercase">{t('deductions')}</span>
                                  <span className="font-mono font-black text-red-600">-{formatCurrency(deductionAmount)}</span>
                                </div>
                                <div className="flex justify-between items-center text-[10px]">
                                  <span className="text-indigo-600 font-bold uppercase">{t('overtime')}</span>
                                  <span className="font-mono font-black text-indigo-600">+{formatCurrency(overtime)}</span>
                                </div>
                              </div>

                              <div className="flex gap-2">
                                <button onClick={() => handleManagePayroll(emp, payrollMonthFilter, lateDeduction, absentDeduction)} className="flex-1 py-3 bg-white border border-gray-100 text-slate-600 rounded-2xl font-black uppercase tracking-widest text-[9px] flex items-center justify-center gap-2 active-haptic shadow-sm">
                                  <Edit size={14} /> {storedRecord ? t('edit') : t('management')}
                                </button>
                                <button onClick={() => handlePrintPayrollSlip(emp, { baseSalary, bonus, overtime, lateDeduction, absentDeduction, manualDeduction: effectiveManualDeduction, deductionAmount, advanceSalary: effectiveAdvance, netPay }, payrollMonthFilter)} className="flex-1 py-3 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest text-[9px] flex items-center justify-center gap-2 active-haptic shadow-lg shadow-blue-500/20">
                                  <Printer size={14} /> {t('print')}
                                </button>
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  </div>
                )}

                {activeTab === 'reports' && (
                  <div className="space-y-6">
                    <div className="flex justify-end mb-4">
                      <select
                        className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                        value={reportLocationFilter}
                        onChange={e => setReportLocationFilter(e.target.value)}
                      >
                        <option value="">{t('filterAll') || 'All Locations'}</option>
                        {sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {[
                        { id: 'attendance', label: t('attendanceReport'), icon: <Clock />, color: 'text-orange-600', bg: 'bg-orange-50' },
                        { id: 'payroll', label: t('payrollReport'), icon: <DollarSign />, color: 'text-purple-600', bg: 'bg-purple-50' },
                        { id: 'turnover', label: t('staffReport'), icon: <Users />, color: 'text-blue-600', bg: 'bg-blue-50' },
                        { id: 'tax', label: t('taxReport'), icon: <FileText />, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                        { id: 'weekly_sales', label: t('weeklySales'), icon: <ShoppingCart />, color: 'text-indigo-600', bg: 'bg-indigo-50' },
                        { id: 'weekly_buy', label: t('weeklyBuy'), icon: <Package />, color: 'text-teal-600', bg: 'bg-teal-50' },
                      ].map(report => (
                        <button key={report.id} onClick={() => downloadReport(report.id)} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all text-left group">
                          <div className={`w-12 h-12 ${report.bg} ${report.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                            {React.cloneElement(report.icon, { size: 24 })}
                          </div>
                          <h4 className="font-bold text-gray-900 mb-1">{report.label}</h4>
                          <div className="flex items-center text-sm text-gray-500 gap-1 group-hover:text-blue-600">
                            {t('downloadReport')} <Download size={14} />
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Profit & Loss Report Section */}
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div>
                          <h3 className="font-bold text-gray-800 text-lg">{t('profitLossReport')}</h3>
                          <p className="text-sm text-gray-500">{t('plSubtitle')}</p>
                        </div>
                        <div className="flex flex-wrap gap-3 items-center justify-center">
                          <select
                            value={profitPeriod}
                            onChange={e => setProfitPeriod(e.target.value)}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 font-medium"
                          >
                            <option value="Daily">{t('daily')}</option>
                            <option value="Weekly">{t('weekly')}</option>
                            <option value="Monthly">{t('monthly')}</option>
                            <option value="Yearly">{t('yearly')}</option>
                          </select>

                          {profitPeriod === 'Daily' && (
                            <input
                              type="date"
                              value={profitDateFilter}
                              onChange={e => setProfitDateFilter(e.target.value)}
                              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                          )}

                          {profitPeriod === 'Weekly' && (
                            <input
                              type="date"
                              value={profitDateFilter}
                              onChange={e => setProfitDateFilter(e.target.value)}
                              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                              title="Select any day in the target week"
                            />
                          )}

                          {profitPeriod === 'Monthly' && (
                            <input
                              type="month"
                              value={profitMonthFilter}
                              onChange={e => setProfitMonthFilter(e.target.value)}
                              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                          )}

                          {profitPeriod === 'Yearly' && (
                            <select
                              value={profitYearFilter}
                              onChange={e => setProfitYearFilter(e.target.value)}
                              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white font-medium"
                            >
                              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                <option key={year} value={year}>{year}</option>
                              ))}
                            </select>
                          )}

                          <button onClick={() => downloadReport('profit_loss')} className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2 font-medium shadow-lg shadow-emerald-600/20">
                            <Download size={18} /> {t('downloadReport')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'accounts' && (
                  <div className="space-y-6 animate-in fade-in duration-500">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('menuAccounts')}</h2>
                        <p className="text-sm text-gray-500">Track assets, liabilities, and expenses.</p>
                      </div>
                      <button onClick={() => setIsAddAccountModalOpen(true)} className="bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 w-full sm:w-auto">
                        <Plus size={20} /> Add Account
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 items-center justify-between flex">
                        <div>
                          <h3 className="font-bold text-gray-800">{t('taxReport')}</h3>
                          <p className="text-sm text-gray-500">Estimated tax deductions</p>
                        </div>
                        <button onClick={() => downloadReport('tax')} className="bg-orange-100 text-orange-600 p-3 rounded-xl hover:bg-orange-200 transition-colors">
                          <Download size={20} />
                        </button>
                      </div>
                    </div>

                    {/* Desktop View */}
                    <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                      <table className="w-full text-left">
                        <thead className="bg-gray-50 border-b border-gray-100 font-black uppercase tracking-widest text-[10px] text-slate-500">
                          <tr>
                            <th className="px-6 py-4 tracking-widest">{t('accountName') || 'Account Name'}</th>
                            <th className="px-6 py-4 tracking-widest">{t('type') || 'Type'}</th>
                            <th className="px-6 py-4 text-right tracking-widest">{t('balance') || 'Balance'}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {accounts.length === 0 ? (
                            <tr><td colSpan="3" className="px-6 py-12 text-center text-gray-400 font-bold uppercase tracking-widest text-xs">{t('noAccountsFound') || 'No accounts found'}</td></tr>
                          ) : (
                            accounts.map(acc => (
                              <tr key={acc.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 font-black text-gray-900 uppercase tracking-tight">{acc.name}</td>
                                <td className="px-6 py-4">
                                  <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${acc.type === 'Asset' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'}`}>{acc.type}</span>
                                </td>
                                <td className="px-6 py-4 text-right font-mono font-black text-gray-900 text-lg">{formatCurrency(acc.balance)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile View */}
                    <div className="md:hidden space-y-4 pb-20">
                      {accounts.map(acc => (
                        <div key={acc.id} className="bg-white p-5 rounded-[2rem] border border-gray-100 shadow-sm flex items-center justify-between transition-all active-haptic">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${acc.type === 'Asset' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                              <Calculator size={24} />
                            </div>
                            <div>
                              <h4 className="font-black text-gray-900 uppercase tracking-tight text-sm">{acc.name}</h4>
                              <span className={`text-[9px] font-black uppercase tracking-widest ${acc.type === 'Asset' ? 'text-emerald-500' : 'text-rose-500'}`}>{acc.type}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1">{t('balance')}</p>
                            <p className="font-black text-gray-900 text-lg font-mono">{formatCurrency(acc.balance)}</p>
                          </div>
                        </div>
                      ))}
                      {accounts.length === 0 && (
                        <div className="bg-white p-12 rounded-[2.5rem] border border-dashed border-gray-200 text-center">
                          <Calculator size={40} className="mx-auto text-gray-200 mb-4" />
                          <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">{t('noAccountsFound')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Full-Screen Zone: Sales & Purchases */}
          {activeTab === 'sales_purchases' && (
            <div className="flex-1 overflow-hidden h-full flex flex-col bg-slate-50">
              <div className="relative flex flex-col lg:flex-row h-full overflow-hidden bg-gray-50 pb-32 lg:pb-0">
                {/* Left: Product Grid */}
                <div className={`flex-1 flex flex-col h-full overflow-hidden ${isMobileCartOpen ? 'hidden lg:flex' : 'flex'}`}>
                  <div className="p-4 flex-shrink-0 bg-white border-b border-gray-200">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
                      <div className="flex items-center justify-between lg:justify-start gap-4">
                        <h2 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-600 truncate">{shopSettings.name || t('posTerminal')}</h2>
                        <div className="h-6 w-px bg-slate-200 hidden lg:block mx-1"></div>
                      </div>

                      <div className="flex-1 min-w-[200px] group relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-all duration-300 group-focus-within:scale-110" size={18} />
                        <input
                          type="text"
                          placeholder={t('searchInventory') || "SEARCH PRODUCTS OR SERVICES..."}
                          id="pos-search"
                          className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold text-slate-700 focus:bg-white focus:border-blue-400 focus:ring-8 focus:ring-blue-500/5 transition-all shadow-inner outline-none placeholder:text-slate-300"
                          value={inventorySearch}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              // Search Inventory
                              const filteredInv = inventory.filter(item =>
                                (!posLocationFilter || item.location === posLocationFilter) &&
                                ((item.name?.toLowerCase() || '').includes(inventorySearch.toLowerCase()) ||
                                  (item.barcode || '').includes(inventorySearch))
                              );
                              if (filteredInv.length === 1) {
                                addToCart(filteredInv[0]);
                                setInventorySearch('');
                                return;
                              }

                              // Search Repairs
                              if (inventorySearch.length >= 3) {
                                const ticket = serviceTickets.find(t => t.id.toUpperCase().includes(inventorySearch.toUpperCase()) || t.customerPhone === inventorySearch);
                                if (ticket) {
                                  if (!cart.some(i => i.id === 'SRV-' + ticket.id)) {
                                    setCart([...cart, {
                                      id: 'SRV-' + ticket.id,
                                      name: `Repair: ${ticket.brand} ${ticket.model} (${ticket.id.slice(0, 6)})`,
                                      sellPrice: Number(ticket.estimatedCost),
                                      price: Number(ticket.estimatedCost),
                                      quantity: 1,
                                      type: 'service'
                                    }]);
                                    setInventorySearch('');
                                  } else {
                                    alert('Repair already in cart!');
                                  }
                                }
                              }
                            }
                          }}
                          onChange={(e) => setInventorySearch(e.target.value)}
                        />
                      </div>

                      <div className="flex flex-wrap items-center gap-2 lg:gap-3">
                        <button
                          onClick={() => setIsScannerOpen(true)}
                          className="flex items-center gap-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white px-4 py-2.5 rounded-2xl border border-emerald-100 transition-all duration-300 shadow-sm hover:shadow-lg hover:shadow-emerald-200 group"
                        >
                          <Scan size={18} className="group-hover:rotate-90 transition-transform duration-500" />
                          <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">{t('scanBarcode')}</span>
                        </button>

                        <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-2xl border border-slate-100 shadow-sm">
                          <MapPin size={16} className="text-slate-400" />
                          <select
                            className="bg-transparent text-[10px] font-black uppercase tracking-widest text-slate-600 focus:outline-none cursor-pointer min-w-[60px]"
                            value={posLocationFilter}
                            onChange={e => setPosLocationFilter(e.target.value)}
                            disabled={cart.length > 0}
                          >
                            {sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                          </select>
                        </div>

                        <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-2xl border border-slate-100">
                          <Printer size={16} className="text-slate-400" />
                          <select
                            value={printFormat}
                            onChange={(e) => setPrintFormat(e.target.value)}
                            className="text-[10px] font-black uppercase tracking-widest text-slate-600 bg-transparent outline-none cursor-pointer hover:text-blue-600 min-w-[70px]"
                            title={t('printSettings')}
                          >
                            <option value="Thermal">{t('thermal')}</option>
                            <option value="A4">{t('a4')}</option>
                          </select>
                        </div>

                        <div className="flex items-center gap-1.5 p-1 bg-white rounded-2xl border border-slate-100 shadow-sm">
                          <button
                            onClick={() => setPrintDual(!printDual)}
                            className={`w-9 h-9 flex items-center justify-center rounded-xl text-[10px] font-black transition-all ${printDual ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'text-slate-400 hover:bg-slate-50'}`}
                            title={t('dualPrint')}
                          >
                            2X
                          </button>
                          <button
                            onClick={() => setPrintBigOrderNumber(!printBigOrderNumber)}
                            className={`w-9 h-9 flex items-center justify-center rounded-xl text-[10px] font-black transition-all ${printBigOrderNumber ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'text-slate-400 hover:bg-slate-50'}`}
                            title="Big Order Number"
                          >
                            #B
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>


                  <div className="flex-1 overflow-y-auto p-3 sm:p-5 pb-32 lg:pb-6 no-scrollbar">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 sm:gap-6">
                      {inventory
                        .filter(item =>
                          (!posLocationFilter || item.location === posLocationFilter) &&
                          ((item.name?.toLowerCase() || '').includes(inventorySearch.toLowerCase()) ||
                            (item.barcode || '').includes(inventorySearch))
                        )
                        .map(item => (
                          <button
                            key={item.id}
                            onClick={() => addToCart(item)}
                            disabled={item.quantity <= 0}
                            className={`flex flex-col h-[210px] sm:h-[230px] bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all active:scale-95 lg:hover:shadow-xl lg:hover:border-blue-200 lg:hover:-translate-y-1 relative group text-left ${item.quantity <= 0 ? 'opacity-50 grayscale' : 'shadow-sm'}`}
                          >
                            <div className="h-28 sm:h-32 w-full bg-gray-100 relative overflow-hidden">
                              {item.photo ? (
                                <img src={item.photo} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-300">
                                  <Package size={28} />
                                </div>
                              )}
                              <div className="absolute top-2 right-2">
                                <span className={`backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded-lg ${item.quantity <= 5 ? 'bg-red-500/80' : 'bg-black/40'}`}>
                                  {item.quantity}
                                </span>
                              </div>
                              {item.quantity <= 0 && (
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                  <span className="text-white text-[10px] font-black uppercase tracking-widest bg-red-600 px-2 py-1 rounded">Out of Stock</span>
                                </div>
                              )}
                            </div>
                            <div className="p-3 flex flex-col flex-1 justify-between bg-white">
                              <h4 className="font-bold text-gray-800 text-xs sm:text-sm line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors uppercase tracking-tight">{item.name}</h4>
                              <div className="flex justify-between items-end mt-1">
                                <div className="text-blue-600 font-black font-mono text-base sm:text-lg">{formatCurrency(item.sellPrice || 0)}</div>
                                <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-all">
                                  <Plus size={14} />
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                    </div>
                    
                    {/* Daily History Toggle / View - Responsive Card/Table */}
                    <div className="bg-white p-4 mx-0 mt-6 lg:mx-4 mb-4 rounded-2xl shadow-sm border border-gray-100 max-h-64 overflow-y-auto shrink-0 animate-in slide-in-from-bottom-4 duration-500">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-gray-900 flex items-center gap-2 truncate">
                          <Clock size={18} className="text-blue-600" />
                        {posHistoryDate === new Date().toISOString().split('T')[0] ? t('todaysSales') : t('salesOn') + ' ' + posHistoryDate}
                      </h3>
                      <input
                        type="date"
                        value={posHistoryDate}
                        onChange={(e) => setPosHistoryDate(e.target.value)}
                        className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden sm:block">
                      <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                          <tr>
                            <th className="p-3 font-bold text-gray-700">{t('time')}</th>
                            <th className="p-3 font-bold text-gray-700">{t('invoiceId')} #</th>
                            <th className="p-3 font-bold text-gray-700">{t('amount')}</th>
                            <th className="p-3 font-bold text-gray-700">{t('items')}</th>
                            <th className="p-3 font-bold text-gray-700">{t('actions')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {sales
                            .filter(s => s.date === posHistoryDate)
                            .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
                            .map(s => (
                              <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                                <td className="p-3 text-gray-500">{s.createdAt ? new Date(s.createdAt.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Now'}</td>
                                <td className="p-3 font-mono text-xs text-blue-600 font-bold">{s.invoiceId || '-'}</td>
                                <td className="p-3 font-mono font-bold text-gray-900">{formatCurrency(s.amount)}</td>
                                <td className="p-3 text-xs text-gray-600 truncate max-w-[150px]">{Array.isArray(s.items) ? s.items.map(i => `${i.qty}x ${i.name}`).join(', ') : s.items}</td>
                                <td className="p-3">
                                  <button onClick={() => handlePrintInvoice(s, t('receipt'))} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"><Printer size={18} /></button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="sm:hidden space-y-3">
                      {sales
                        .filter(s => s.date === posHistoryDate)
                        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
                        .map(s => (
                          <div key={s.id} className="p-3 border border-gray-100 rounded-xl bg-gray-50/50 flex flex-col gap-2">
                            <div className="flex justify-between items-center">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{s.createdAt ? new Date(s.createdAt.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Now'}</span>
                              <span className="font-mono text-[10px] text-blue-600 font-bold">{s.invoiceId || '-'}</span>
                            </div>
                            <div className="flex justify-between items-end">
                              <div>
                                <div className="text-xs text-gray-500 mb-0.5">{Array.isArray(s.items) ? s.items.map(i => `${i.qty}x ${i.name}`).join(', ') : s.items}</div>
                                <div className="font-mono font-black text-gray-900">{formatCurrency(s.amount)}</div>
                              </div>
                              <button onClick={() => handlePrintInvoice(s, t('receipt'))} className="p-2.5 bg-white border border-gray-200 text-gray-600 rounded-xl shadow-sm active:bg-blue-50 active:text-blue-600 transition-all"><Printer size={20} /></button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                  </div>
                </div>

                {/* Right: Cart Sidebar (Mobile Drawer) */}
                {isMobileCartOpen && <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[75] lg:hidden animate-in fade-in duration-300" onClick={() => setIsMobileCartOpen(false)}></div>}
                <div className={`fixed inset-y-0 right-0 lg:relative lg:inset-auto w-full lg:w-[420px] bg-white lg:border-l border-gray-200 flex flex-col h-full z-[80] lg:z-20 transition-transform duration-300 ease-in-out transform ${isMobileCartOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`}>
                  <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-white">
                    <div className="flex items-center gap-3 text-gray-800">
                      <button onClick={() => setIsMobileCartOpen(false)} className="lg:hidden p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors">
                        <ArrowLeft size={20} />
                      </button>
                      <ShoppingCart size={22} className="text-blue-600" />
                      <h3 className="font-bold text-xl tracking-tight">{t('currentBill')}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleHoldCart}
                        disabled={cart.length === 0}
                        className="p-2 text-amber-600 hover:bg-amber-50 rounded-xl transition-colors border border-amber-100 disabled:opacity-30"
                        title={t('hold') || 'Hold Cart'}
                      >
                        <Clock size={20} />
                      </button>
                      <span className="bg-blue-600 text-white text-xs font-black px-3 py-1.5 rounded-full shadow-lg shadow-blue-200">{cart.reduce((a, b) => a + b.quantity, 0)}</span>
                    </div>
                  </div>

                  {heldCarts.length > 0 && (
                    <div className="bg-amber-50/50 p-2 border-b border-amber-100 overflow-x-auto whitespace-nowrap scrollbar-none">
                      <div className="flex gap-2">
                        {heldCarts.map((h, idx) => (
                          <button
                            key={h.id}
                            onClick={() => handleResumeCart(h.id)}
                            className="bg-white border border-amber-200 px-3 py-1 rounded-full text-[10px] font-bold text-amber-700 shadow-sm hover:border-amber-400 transition-all flex items-center gap-1.5"
                          >
                            <Clock size={10} /> {h.time} - {h.customer || `Cart ${idx + 1}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex-1 overflow-y-auto p-0 scrollbar-thin scrollbar-thumb-gray-200">
                    {cart.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-gray-300 p-8 text-center">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                          <ShoppingCart size={24} className="opacity-50" />
                        </div>
                        <p className="font-medium text-gray-500">{t('cartEmpty')}</p>
                        <p className="text-xs mt-1 text-gray-400">{t('selectItems')}</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {cart.map(item => (
                          <div key={item.id} className="p-2.5 flex items-center gap-2 group hover:bg-gray-50 transition-colors">
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-center mb-0.5">
                                <h4 className="font-bold text-gray-800 text-[11px] sm:text-xs truncate pr-2 uppercase tracking-wide">{item.name}</h4>
                                <span className="font-mono font-black text-gray-900 text-xs">{formatCurrency(item.price * item.quantity)}</span>
                              </div>
                              <div className="text-[10px] text-gray-400 font-medium">{formatCurrency(item.price)} / unit</div>
                            </div>

                            <div className="flex items-center gap-2">
                              <div className="flex items-center border border-gray-200 rounded-lg bg-white h-6 shadow-sm">
                                <button onClick={() => updateCartQuantity(item.id, -1)} className="px-1.5 h-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-l-lg transition-colors font-bold text-xs">-</button>
                                <span className="w-5 text-center text-[10px] font-black text-gray-700">{item.quantity}</span>
                                <button onClick={() => updateCartQuantity(item.id, 1)} className="px-1.5 h-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-r-lg transition-colors font-bold text-xs">+</button>
                              </div>
                              <button onClick={() => removeFromCart(item.id)} className="text-gray-300 hover:text-rose-500 transition-colors p-1 group/del">
                                <X size={12} className="group-active/del:scale-90" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer Section */}
                  <div className="bg-gray-50/50 backdrop-blur-md border-t border-gray-200 p-3.5 space-y-3">
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold">{t('subtotal')}</span>
                        </div>
                        <span className="font-mono">{formatCurrency(calculateTotal())}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <span className="border-b border-dashed border-gray-300 pb-px cursor-help">{t('discount')}</span>
                        <div className="flex items-center bg-white border border-gray-200 rounded px-2 h-6 w-20">
                          <span className="text-gray-400 mr-1">-</span>
                          <input
                            type="number"
                            min="0"
                            className="w-full text-right bg-transparent border-none outline-none text-xs font-mono p-0 focus:ring-0"
                            placeholder="0"
                            value={cartDiscount || ''}
                            onChange={(e) => setCartDiscount(Math.max(0, Number(e.target.value)))}
                          />
                        </div>
                      </div>
                      <div className="pt-2 border-t border-gray-100 flex justify-between items-center text-[11px] font-black uppercase text-slate-400">
                        <span>{t('total')}</span>
                        <span className="text-xl font-black text-gray-900 leading-none">{formatCurrency(Math.max(0, calculateTotal() - cartDiscount))}</span>
                      </div>
                    </div>
                    {/* Sales Employee Selection */}
                    <div className="mb-1">
                      <button
                        onClick={() => { setPinAction('changeSalesEmployee'); setIsPinModalOpen(true); }}
                        className="w-full flex items-center justify-between p-1.5 bg-blue-50 hover:bg-blue-100 rounded-xl border border-blue-100 transition-colors shadow-sm"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white"><User size={16} /></div>
                          <div className="text-left">
                            <div className="text-[9px] text-blue-500 font-black uppercase tracking-widest leading-none mb-1">{t('salesEmployee')}</div>
                            <div className="font-bold text-slate-800 text-[11px] uppercase tracking-wide">{salesEmployee ? salesEmployee.name : t('selectEmployee')}</div>
                          </div>
                        </div>
                        <ChevronDown size={14} className="text-blue-400 mr-1" />
                      </button>
                    </div>

                    {/* Order Type Toggle */}
                    <div className="flex bg-slate-100 p-0.5 rounded-xl mb-1.5">
                      <button
                        onClick={() => {
                          setOrderType('Walk-in');
                          if (!newSaleForm.customer || newSaleForm.customer === t('takeawayCustomer')) {
                            setNewSaleForm(prev => ({ ...prev, customer: t('walkInCustomer') }));
                          }
                        }}
                        className={`flex-1 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${orderType === 'Walk-in' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                      >
                        {t('walkIn')}
                      </button>
                      <button
                        onClick={() => {
                          setOrderType('Takeaway');
                          if (!newSaleForm.customer || newSaleForm.customer === t('walkInCustomer')) {
                            setNewSaleForm(prev => ({ ...prev, customer: t('takeawayCustomer') }));
                          }
                        }}
                        className={`flex-1 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${orderType === 'Takeaway' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                      >
                        {t('takeaway')}
                      </button>
                    </div>

                    <input
                      className="w-full mb-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-medium focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5 transition-all outline-none"
                      placeholder={t('customerNameOptional')}
                      value={newSaleForm.customer}
                      autoComplete="off"
                      onChange={e => setNewSaleForm({ ...newSaleForm, customer: e.target.value })}
                    />

                    <div className="space-y-0.5">
                      <input
                        className="w-full mb-2 px-3 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-medium focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5 transition-all outline-none"
                        placeholder={t('customerId') + " (" + t('optional') + ")"}
                        value={newSaleForm.customerId}
                        autoComplete="off"
                        onChange={e => setNewSaleForm({ ...newSaleForm, customerId: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2 mb-3">
                      <div className="grid grid-cols-3 gap-1.5">
                        {['Cash', 'Visa', 'Online'].map(method => (
                          <button
                            key={method}
                            onClick={() => {
                              setPaymentMethod(method);
                              if (method === 'Online') setShowUpiQr(true);
                            }}
                            className={`py-1.5 text-[9px] font-black uppercase tracking-widest rounded-xl border transition-all duration-300 shadow-sm ${paymentMethod === method ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-400 border-slate-100 hover:border-slate-200'}`}
                          >
                            {method === 'Online' ? 'Digital' : (t(method.toLowerCase()) || method)}
                          </button>
                        ))}
                      </div>

                      {paymentMethod === 'Online' && (
                        <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 rounded-2xl animate-in slide-in-from-top-2 duration-300 border border-gray-100">
                          {['UPI', 'InstaPay', 'Other'].map(sub => (
                            <button
                              key={sub}
                              onClick={() => { setDigitalSubMethod(sub); setShowUpiQr(true); }}
                              className={`px-3 py-1.5 text-[9px] font-black uppercase tracking-tight rounded-lg transition-all ${digitalSubMethod === sub ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-400 border border-gray-100 hover:text-gray-600'}`}
                            >
                              {t(sub.toLowerCase()) || sub}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={handleCheckout}
                      disabled={cart.length === 0}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:brightness-110 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-xl shadow-blue-500/20 flex items-center justify-center gap-3 transition-all"
                    >
                      <CheckCircle size={18} /> {t('checkout')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Scrollable Zone 2: Warehouses, History, Cafe */}
          {['warehouses', 'history', 'cafe'].includes(activeTab) && (
            <div className="flex-1 overflow-y-auto no-scrollbar scroll-smooth">
              <div className="p-4 sm:p-6 md:p-8 lg:p-10 pb-[140px] lg:pb-10 max-w-[1600px] mx-auto w-full transition-all duration-500">
                {activeTab === 'warehouses' && (
                  <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('menuWarehouses')}</h2>
                        <p className="text-sm text-gray-500">{t('inventorySubtitle')}</p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        {currentMode !== 'Cashier' && (
                          <button
                            type="button"
                            onClick={() => {
                              if (showSensitiveData) {
                                setShowSensitiveData(false);
                              } else {
                                setPinAction('showCosts');
                                setIsPinModalOpen(true);
                              }
                            }}
                            className={`px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 font-medium transition-all w-full sm:w-auto ${showSensitiveData ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            {showSensitiveData ? <Shield size={20} /> : <Shield size={20} />} {showSensitiveData ? t('hideCosts') : t('showCosts')}
                          </button>
                        )}
                        {currentMode !== 'Cashier' && (
                          <>
                            <div className="flex bg-gray-100 p-1 rounded-lg">
                              <button
                                onClick={() => setBarcodePrintMode('sticker')}
                                className={`px-3 py-1.5 text-[10px] font-black uppercase rounded-md transition-all ${barcodePrintMode === 'sticker' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                title="Thermal Sticker Printer"
                              > {t('sticker')} </button>
                              <button
                                onClick={() => setBarcodePrintMode('a4')}
                                className={`px-3 py-1.5 text-[10px] font-black uppercase rounded-md transition-all ${barcodePrintMode === 'a4' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                title="Standard A4 Paper Printer"
                              > {t('a4 sheet')} </button>
                            </div>
                            <button
                              onClick={() => {
                                const itemsToConfig = selectedInventoryItems.length > 0
                                  ? inventory.filter(item => selectedInventoryItems.includes(item.id))
                                  : inventory.filter(item =>
                                    (!warehouseLocationFilter || item.location === warehouseLocationFilter) &&
                                    ((item.name?.toLowerCase() || '').includes(inventorySearch.toLowerCase()) ||
                                      (item.barcode?.toLowerCase() || '').includes(inventorySearch.toLowerCase()))
                                  );
                                setPrintConfigs(itemsToConfig.map(item => ({ item, qty: 1 })));
                                setIsPrintBarcodeModalOpen(true);
                              }}
                              className="bg-slate-800 text-white px-4 py-2.5 rounded-lg hover:bg-slate-900 flex items-center justify-center gap-2 w-full sm:w-auto font-medium"
                            >
                              <Printer size={20} /> {selectedInventoryItems.length > 0 ? `${t('printSelected')} (${selectedInventoryItems.length})` : t('printAll')}
                            </button>
                            <button
                              onClick={() => { setScannerMode('buy'); setIsScannerOpen(true); }}
                              className="bg-emerald-600 text-white px-4 py-2.5 rounded-lg hover:bg-emerald-700 flex items-center justify-center gap-2 w-full sm:w-auto font-medium"
                            >
                              <Scan size={20} /> {t('quickStockIn') || 'Stock In'}
                            </button>
                            <button onClick={() => setIsAddItemModalOpen(true)} className="bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 w-full sm:w-auto">
                              <Plus size={20} /> {t('addItem')}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                      <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                        <div className="flex flex-col sm:flex-row gap-3 w-full">
                          <select
                            className="bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto shadow-sm"
                            value={warehouseLocationFilter}
                            onChange={e => setWarehouseLocationFilter(e.target.value)}
                          >
                            <option value="">{t('filterAll') || 'All Locations'}</option>
                            {sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                          </select>
                          <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                              type="text"
                              placeholder={t('searchInventory')}
                              className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                              value={inventorySearch}
                              onChange={(e) => setInventorySearch(e.target.value)}
                              autoComplete="off"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Desktop Table */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-left">
                          <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                              {currentMode !== 'Cashier' && (
                                <th className="px-6 py-4 w-10">
                                  <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    onChange={(e) => {
                                      const filtered = inventory.filter(item =>
                                        (!warehouseLocationFilter || item.location === warehouseLocationFilter) &&
                                        ((item.name?.toLowerCase() || '').includes(inventorySearch.toLowerCase()) ||
                                          (item.barcode?.toLowerCase() || '').includes(inventorySearch.toLowerCase()))
                                      );
                                      if (e.target.checked) {
                                        setSelectedInventoryItems(filtered.map(i => i.id));
                                      } else {
                                        setSelectedInventoryItems([]);
                                      }
                                    }}
                                    checked={(() => {
                                      const filtered = inventory.filter(item =>
                                        (!warehouseLocationFilter || item.location === warehouseLocationFilter) &&
                                        ((item.name?.toLowerCase() || '').includes(inventorySearch.toLowerCase()) ||
                                          (item.barcode?.toLowerCase() || '').includes(inventorySearch.toLowerCase()))
                                      );
                                      return filtered.length > 0 && filtered.every(i => selectedInventoryItems.includes(i.id));
                                    })()}
                                  />
                                </th>
                              )}
                              <th className="px-6 py-4 font-bold text-[10px] uppercase tracking-widest text-gray-500 whitespace-nowrap">{t('image') || 'Image'}</th>
                              <th className="px-6 py-4 font-bold text-[10px] uppercase tracking-widest text-gray-500 whitespace-nowrap">{t('itemName')}</th>
                              <th className="px-6 py-4 font-bold text-[10px] uppercase tracking-widest text-gray-500 whitespace-nowrap">{t('barcode') || 'Barcode'}</th>
                              <th className="px-6 py-4 font-bold text-[10px] uppercase tracking-widest text-gray-500 whitespace-nowrap">{t('location')}</th>
                              {currentMode !== 'Cashier' && <th className="px-6 py-4 font-bold text-[10px] uppercase tracking-widest text-gray-500 whitespace-nowrap text-right">{t('buyPrice')}</th>}
                              <th className="px-6 py-4 font-bold text-[10px] uppercase tracking-widest text-gray-500 whitespace-nowrap text-right">{t('sellPrice')}</th>
                              <th className="px-6 py-4 font-bold text-[10px] uppercase tracking-widest text-gray-500 whitespace-nowrap text-right">{t('quantity')}</th>
                              {currentMode !== 'Cashier' && <th className="px-6 py-4 font-bold text-[10px] uppercase tracking-widest text-gray-500 whitespace-nowrap text-right">{t('actions')}</th>}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {inventory
                              .filter(item =>
                                (!warehouseLocationFilter || item.location === warehouseLocationFilter) &&
                                ((item.name?.toLowerCase() || '').includes(inventorySearch.toLowerCase()) ||
                                  (item.barcode?.toLowerCase() || '').includes(inventorySearch.toLowerCase()))
                              )
                              .map(item => (
                                <tr key={item.id} className={`hover:bg-gray-50 transition-colors ${selectedInventoryItems.includes(item.id) ? 'bg-blue-50/50' : ''}`}>
                                  {currentMode !== 'Cashier' && (
                                    <td className="px-6 py-4">
                                      <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        checked={selectedInventoryItems.includes(item.id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedInventoryItems([...selectedInventoryItems, item.id]);
                                          } else {
                                            setSelectedInventoryItems(selectedInventoryItems.filter(id => id !== item.id));
                                          }
                                        }}
                                      />
                                    </td>
                                  )}
                                  <td className="px-6 py-4">
                                    <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100 overflow-hidden shadow-inner">
                                      {item.photo ? (
                                        <img src={item.photo} alt={item.name} className="w-full h-full object-cover" />
                                      ) : (
                                        <Package size={20} className="text-gray-300" />
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="font-bold text-gray-900 block">{item.name}</span>
                                  </td>
                                  <td className="px-6 py-4 text-xs font-mono text-gray-400">{item.barcode || '-'}</td>
                                  <td className="px-6 py-4 text-xs font-bold text-gray-500">{item.location}</td>
                                  {currentMode !== 'Cashier' && (
                                    <td className="px-6 py-4 text-right font-mono text-gray-500">
                                      {showSensitiveData ? formatCurrency(item.buyPrice || 0) : '****'}
                                    </td>
                                  )}
                                  <td className="px-6 py-4 text-right font-mono font-black text-gray-900">{formatCurrency(item.sellPrice || 0)}</td>
                                  <td className="px-6 py-4 text-right font-mono font-black text-gray-900">
                                    {item.quantity <= 0 ? (
                                      <span className="text-rose-600 text-[10px] font-black uppercase tracking-widest bg-rose-50 px-2 py-1 rounded-lg border border-rose-100 italic">
                                        {t('outOfStock') || 'Out of Stock'}
                                      </span>
                                    ) : (
                                      item.quantity
                                    )}
                                  </td>
                                  {currentMode !== 'Cashier' && (
                                    <td className="px-6 py-4 text-right">
                                      <div className="flex justify-end gap-2">
                                        <button onClick={() => { setPrintConfigs([{ item, qty: 1 }]); setIsPrintBarcodeModalOpen(true); }} disabled={!item.barcode} className="p-2 text-slate-600 hover:bg-slate-50 rounded-xl transition-all shadow-sm bg-white border border-gray-100 disabled:opacity-30" title="Print Barcode"><Printer size={16} /></button>
                                        <button onClick={() => { setEditingItem(item); setIsAddItemModalOpen(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition-all shadow-sm bg-white border border-gray-100"><Edit size={16} /></button>
                                        <button onClick={() => handleDeleteWarehouseItem(item.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-all shadow-sm bg-white border border-gray-100"><Trash2 size={16} /></button>
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Mobile Card View */}
                      <div className="md:hidden p-4 space-y-4 max-h-[60vh] overflow-y-auto bg-gray-50/30">
                        {inventory
                          .filter(item =>
                            (!warehouseLocationFilter || item.location === warehouseLocationFilter) &&
                            ((item.name?.toLowerCase() || '').includes(inventorySearch.toLowerCase()) ||
                              (item.barcode?.toLowerCase() || '').includes(inventorySearch.toLowerCase()))
                          )
                          .map(item => (
                            <div key={item.id} className={`bg-white p-4 rounded-2xl border transition-all duration-300 shadow-sm flex gap-4 animate-in fade-in duration-300 ${selectedInventoryItems.includes(item.id) ? 'border-blue-500 bg-blue-50/20' : 'border-gray-100'}`}>
                              <div className="flex flex-col items-center gap-2 shrink-0">
                                {currentMode !== 'Cashier' && (
                                  <input
                                    type="checkbox"
                                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    checked={selectedInventoryItems.includes(item.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedInventoryItems([...selectedInventoryItems, item.id]);
                                      } else {
                                        setSelectedInventoryItems(selectedInventoryItems.filter(id => id !== item.id));
                                      }
                                    }}
                                  />
                                )}
                                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100 overflow-hidden shadow-inner">
                                  {item.photo ? (
                                    <img src={item.photo} alt={item.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <Package size={20} className="text-gray-300" />
                                  )}
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col justify-between py-1">
                                <div>
                                  <h4 className="font-bold text-gray-900 text-sm uppercase tracking-tight line-clamp-2 leading-tight">{item.name}</h4>
                                  <p className="text-[10px] text-gray-400 font-bold mt-0.5">{item.barcode || '-'}</p>
                                </div>
                                <div className="flex justify-between items-end mt-2">
                                  <div>
                                    <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">{t('sellPrice')}</div>
                                    <div className="font-mono font-black text-blue-600 text-sm">{formatCurrency(item.sellPrice || 0)}</div>
                                  </div>
                                  <div className="text-right flex flex-col items-end">
                                    <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">{t('quantity')}</div>
                                    <div className={`font-mono font-black text-sm ${item.quantity <= 0 ? 'text-rose-500' : 'text-gray-900'}`}>{item.quantity}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'cafe' && (
                  <div className="space-y-6 animate-in fade-in duration-500">
                    {/* Sub-tab Navigation - Swipeable on mobile */}
                    <div className="flex gap-2 bg-white/50 backdrop-blur-md p-1.5 rounded-2xl w-full lg:w-fit border border-gray-200 mb-8 overflow-x-auto no-scrollbar shadow-sm">
                      {[
                        { id: 'board', label: t('liveBoard'), icon: <LayoutDashboard size={14} /> },
                        { id: 'rooms', label: t('manageUnits'), icon: <Plus size={14} /> },
                        { id: 'recipes', label: t('menuRecipes'), icon: <Database size={14} /> },
                        { id: 'history', label: t('history'), icon: <Clock size={14} /> }
                      ].map(tab => (
                        <button
                          key={tab.id}
                          onClick={() => setCafeSubTab(tab.id)}
                          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap active-haptic ${cafeSubTab === tab.id ? 'bg-slate-900 text-white shadow-lg shadow-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-white'}`}
                        >
                          {tab.icon} {tab.label}
                        </button>
                      ))}
                    </div>

                    {cafeSubTab === 'board' && (
                      <div className="animate-in fade-in zoom-in-95 duration-700">
                        <div className="flex justify-between items-end mb-8">
                          <div>
                            <h2 className="text-gray-900 text-2xl font-black uppercase tracking-tight">{t('activeSessions')}</h2>
                            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-[0.1em] mt-1">{t('unitsMonitoring').replace('{0}', cafeRooms.length)}</p>
                          </div>
                          <div className="flex items-center gap-3 px-4 py-2 bg-white rounded-xl border border-gray-100 shadow-sm">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                            <span className="text-[10px] font-black text-gray-600 uppercase tracking-widest">{t('online')}</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                          {cafeRooms.map(room => {
                            const activeSession = cafeSessions.find(s => s.roomId === room.id && s.status === 'Active');
                            const roomIcon = room.type === 'PlayStation' ? <Monitor size={20} /> : room.type === 'Billiards' ? <Disc size={20} /> : <Coffee size={20} />;

                            return (
                              <div key={room.id} className={`p-6 rounded-[2.5rem] border transition-all duration-500 flex flex-col justify-between h-[300px] relative overflow-hidden group ${activeSession ? 'bg-white border-blue-200 shadow-xl shadow-blue-500/5' : 'bg-white border-gray-100 hover:border-blue-500/30 shadow-sm'}`}>

                                <div className="flex justify-between items-start">
                                  <div className={`p-4 rounded-2xl transition-all duration-500 ${activeSession ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-600'}`}>
                                    {roomIcon}
                                  </div>
                                  <div className="text-right">
                                    {activeSession ? (
                                      <div className="animate-in fade-in slide-in-from-top-2 duration-700">
                                        <div className="flex items-center justify-end gap-2 mb-1">
                                          <span className="text-[10px] uppercase font-bold tracking-widest text-blue-600/60">{t('inSession')}</span>
                                          <span className="flex h-2 w-2">
                                            <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-blue-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
                                          </span>
                                        </div>
                                        <span className="text-3xl font-black text-slate-900 tabular-nums tracking-tighter leading-none">
                                          {activeSession.startTime?.seconds ? (() => {
                                            const now = currentTime;
                                            const start = new Date(activeSession.startTime.seconds * 1000);
                                            const diff = Math.max(0, Math.floor((now - start) / 60000));
                                            const h = Math.floor(diff / 60);
                                            const m = diff % 60;
                                            return `${h}h ${m}m`;
                                          })() : '...'}
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="animate-in fade-in duration-700">
                                        <span className="text-[10px] uppercase font-bold tracking-widest text-emerald-500 block mb-1">{t('available')}</span>
                                        <span className="text-lg font-black text-slate-800 transition-colors">{formatCurrency(room.hourlyPrice || 0)}/HR</span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div>
                                  <h3 className="text-2xl font-black uppercase tracking-tight text-slate-900 mb-1 leading-none">{room.name}</h3>
                                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 opacity-80">{room.type}</p>
                                </div>

                                <div className="flex gap-2 mt-6">
                                  {!activeSession ? (
                                    <button onClick={() => handleStartCafeSession(room)} className="flex-1 py-3.5 bg-slate-900 hover:bg-black text-white rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg shadow-slate-200 active:scale-95">{t('startSession')}</button>
                                  ) : (
                                    <>
                                      <button onClick={() => { setActiveCafeSession(activeSession); setIsCafeOrderModalOpen(true); }} className="flex-1 py-3.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 border border-blue-100 italic">{t('addOrder')}</button>
                                      <button onClick={() => handleStopCafeSession(activeSession)} className="flex-1 py-3.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 border border-rose-100 italic">{t('stop')}</button>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {cafeSubTab === 'rooms' && (
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                        <div className="lg:col-span-4 bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl shadow-slate-200/50">
                          <h3 className="text-lg font-black uppercase tracking-tight mb-6 text-slate-800 flex items-center gap-2">
                            <div className="w-1.5 h-6 bg-blue-600 rounded-full"></div>
                            {t('manageUnitsTitle')}
                          </h3>
                          <form onSubmit={handleSaveRoom} className="space-y-5">
                            <div className="flex flex-col gap-1">
                              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-4">{t('roomName')}</label>
                              <input type="text" value={roomForm.name} onChange={e => setRoomForm({ ...roomForm, name: e.target.value })} className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:text-slate-300" placeholder="e.g. PS5 VIP 1" required />
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-4">{t('unitType')}</label>
                              <select value={roomForm.type} onChange={e => setRoomForm({ ...roomForm, type: e.target.value })} className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all appearance-none cursor-pointer">
                                <option value="Cafe">{t('cafeTable')}</option>
                                <option value="PlayStation">{t('playStationRoom')}</option>
                                <option value="Billiards">{t('billiardsTable')}</option>
                              </select>
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-4">{t('hourlyRate')}</label>
                              <input type="number" value={roomForm.hourlyPrice} onChange={e => setRoomForm({ ...roomForm, hourlyPrice: e.target.value })} className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:text-slate-300" placeholder="0.00" required />
                            </div>
                            <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-2xl font-black uppercase tracking-widest text-[11px] transition-all shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 mt-2">
                              <Plus size={18} strokeWidth={3} /> {roomForm.id ? t('updateItem') : t('saveUnit')}
                            </button>
                            {roomForm.id && <button type="button" onClick={() => setRoomForm({ name: '', type: 'Cafe', hourlyPrice: 0 })} className="w-full py-2 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors">{t('cancel')}</button>}
                          </form>
                        </div>

                        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                          {cafeRooms.map(room => (
                            <div key={room.id} className="bg-white p-6 rounded-[2.5rem] border border-gray-100 hover:border-blue-500/30 transition-all group relative overflow-hidden flex flex-col justify-between shadow-sm min-h-[160px]">
                              <div className="flex justify-between items-start">
                                <div className="flex items-center gap-4">
                                  <div className="p-4 bg-slate-50 text-slate-400 rounded-2xl group-hover:bg-blue-600 group-hover:text-white transition-all duration-500">
                                    {room.type === 'PlayStation' ? <Monitor size={20} /> : room.type === 'Billiards' ? <Disc size={20} /> : <Coffee size={20} />}
                                  </div>
                                  <div>
                                    <h4 className="font-black uppercase tracking-tight text-lg text-slate-800 leading-none mb-1">{room.name}</h4>
                                    <div className="flex items-center gap-2">
                                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{room.type}</span>
                                      <div className="w-1 h-1 rounded-full bg-slate-200"></div>
                                      <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest">{formatCurrency(room.hourlyPrice || 0)}/HR</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-x-2 group-hover:translate-x-0">
                                  <button onClick={() => setRoomForm(room)} className="p-3.5 bg-white/5 text-gray-400 hover:text-white hover:bg-blue-600 rounded-2xl transition-all shadow-lg active:scale-90"><Edit size={18} /></button>
                                  <button onClick={() => handleDeleteRoom(room.id)} className="p-3.5 bg-white/5 text-gray-400 hover:text-white hover:bg-rose-600 rounded-2xl transition-all shadow-lg active:scale-90"><Trash2 size={18} /></button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {cafeSubTab === 'recipes' && (
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                        <div className="lg:col-span-4 bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl shadow-slate-200/50">
                          <h3 className="text-lg font-black uppercase tracking-tight mb-6 text-slate-800 flex items-center gap-2">
                            <div className="w-1.5 h-6 bg-indigo-600 rounded-full"></div>
                            {t('recipeBuilder')}
                          </h3>
                          <form onSubmit={handleSaveRecipe} className="space-y-5">
                            <div className="flex flex-col gap-1">
                              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-4">{t('itemName')}</label>
                              <input type="text" value={recipeForm.name} onChange={e => setRecipeForm({ ...recipeForm, name: e.target.value })} className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all placeholder:text-slate-300" placeholder="e.g. Mocha Premium" required />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-4">{t('category')}</label>
                                <select value={recipeForm.category} onChange={e => setRecipeForm({ ...recipeForm, category: e.target.value })} className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all appearance-none cursor-pointer">
                                  <option value="Hot Drinks">{t('hotDrinks')}</option>
                                  <option value="Cold Drinks">{t('coldDrinks')}</option>
                                  <option value="Snacks">{t('snacks')}</option>
                                  <option value="Meals">{t('meals')}</option>
                                </select>
                              </div>
                              <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-4">{t('offerPrice')}</label>
                                <input type="number" value={recipeForm.sellPrice} onChange={e => setRecipeForm({ ...recipeForm, sellPrice: e.target.value })} className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all placeholder:text-slate-300" placeholder="0.00" required />
                              </div>
                            </div>

                            <div className="space-y-3">
                              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-4">{t('composition')}</label>
                              <div className="space-y-2">
                                {recipeForm.ingredients.map((ing, idx) => (
                                  <div key={idx} className="flex gap-2 items-center bg-slate-50 p-3 rounded-xl border border-slate-100 group/ing">
                                    <span className="flex-1 text-[10px] font-bold text-slate-700 truncate uppercase">{ing.name}</span>
                                    <input
                                      type="number"
                                      value={ing.qty}
                                      onChange={e => {
                                        const news = [...recipeForm.ingredients];
                                        news[idx].qty = e.target.value;
                                        setRecipeForm({ ...recipeForm, ingredients: news });
                                      }}
                                      className="w-14 bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-black text-slate-900 text-center"
                                    />
                                    <button type="button" onClick={() => setRecipeForm({ ...recipeForm, ingredients: recipeForm.ingredients.filter((_, i) => i !== idx) })} className="text-slate-300 hover:text-rose-500 transition-colors">
                                      <Minus size={14} strokeWidth={3} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <select
                                onChange={e => {
                                  const item = inventory.find(i => i.id === e.target.value);
                                  if (item && !recipeForm.ingredients.find(ing => ing.id === item.id)) {
                                    setRecipeForm({ ...recipeForm, ingredients: [...recipeForm.ingredients, { id: item.id, name: item.name, qty: 1 }] });
                                  }
                                  e.target.value = "";
                                }}
                                className="w-full bg-white border border-dashed border-slate-200 rounded-2xl px-6 py-4 text-[10px] font-black text-slate-400 text-center cursor-pointer hover:border-indigo-300 hover:text-indigo-400 transition-all font-mono"
                              >
                                <option value="">{t('linkStock')}</option>
                                {inventory.map(i => <option key={i.id} value={i.id}>{i.name} ({i.location})</option>)}
                              </select>
                            </div>

                            <button type="submit" className="w-full bg-slate-900 hover:bg-black text-white py-4 rounded-2xl font-black uppercase tracking-widest text-[11px] transition-all shadow-lg shadow-slate-200 flex items-center justify-center gap-2 mt-4">
                              {recipeForm.id ? t('updateItem') : t('addToMenu')}
                            </button>
                          </form>
                        </div>

                        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                          {recipes.map(recipe => (
                            <div key={recipe.id} className="bg-white p-6 rounded-[2.5rem] border border-gray-100 hover:border-indigo-500/30 transition-all group relative overflow-hidden flex flex-col justify-between shadow-sm cursor-pointer" onClick={() => setRecipeForm(recipe)}>
                              <div className="flex justify-between items-start mb-4">
                                <span className="bg-indigo-50 text-indigo-600 text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-widest border border-indigo-100">{recipe.category}</span>
                                <div className="flex gap-2">
                                  <button onClick={(e) => { e.stopPropagation(); setRecipeForm(recipe); }} className="p-2 text-slate-300 hover:text-indigo-500"><Edit size={16} /></button>
                                  <button onClick={(e) => { e.stopPropagation(); handleDeleteRecipe(recipe.id); }} className="p-2 text-slate-300 hover:text-rose-500"><Trash2 size={16} /></button>
                                </div>
                              </div>

                              <div>
                                <h4 className="font-black uppercase tracking-tight text-xl text-slate-800 mb-1 leading-none">{recipe.name}</h4>
                                <p className="text-lg font-black text-indigo-600 tracking-tight">{formatCurrency(recipe.sellPrice)}</p>

                                <div className="mt-6 flex flex-wrap gap-2">
                                  {(recipe.ingredients || []).map((ing, idx) => (
                                    <span key={idx} className="bg-slate-50 text-[9px] font-bold px-3 py-1.5 rounded-lg uppercase text-slate-500 border border-slate-100">
                                      {ing.qty}× {ing.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {cafeSubTab === 'history' && (
                      <div className="bg-white p-6 rounded-[2.5rem] border border-gray-100 shadow-xl shadow-slate-200/50 animate-in fade-in slide-in-from-bottom-4 duration-700">
                        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 px-2 gap-4">
                          <h2 className="text-gray-900 text-2xl font-black uppercase tracking-tight">{t('cafeHistory')}</h2>
                          <div className="flex items-center gap-3 w-full sm:w-auto">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{t('searchByDate')}</span>
                            <input type="date" value={posHistoryDate} onChange={(e) => setPosHistoryDate(e.target.value)} className="w-full sm:w-auto bg-slate-50 border border-slate-100 rounded-xl px-4 py-2 text-sm font-bold text-slate-900 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all" />
                          </div>
                        </div>
                        <div className="max-h-[600px] overflow-y-auto px-1">
                          <div className="hidden md:block">
                            <table className="w-full text-sm text-left">
                              <thead className="bg-slate-50 border-b border-slate-100 sticky top-0 z-10">
                                <tr>
                                  <th className="p-4 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('time')}</th>
                                  <th className="p-4 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('invoiceId')}</th>
                                  <th className="p-4 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('amount')}</th>
                                  <th className="p-4 font-black text-[10px] text-slate-400 uppercase tracking-widest text-right">{t('actions')}</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-50">
                                {(sales || []).filter(s => s.location === 'Cafe' && s.date === posHistoryDate).map(s => (
                                  <tr key={s.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="p-4 text-slate-500 font-bold">{s.createdAt ? new Date(s.createdAt.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Now'}</td>
                                    <td className="p-4 font-mono text-xs text-blue-600 font-black">{s.invoiceId || '-'}</td>
                                    <td className="p-4 font-mono font-black text-slate-900">{formatCurrency(s.amount)}</td>
                                    <td className="p-4 text-right"><button onClick={() => handlePrintInvoice(s, t('receipt'))} className="p-2 border border-slate-100 text-slate-400 hover:text-blue-600 rounded-lg"><Printer size={16} /></button></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="md:hidden space-y-4">
                            {(sales || []).filter(s => s.location === 'Cafe' && s.date === posHistoryDate).map(s => (
                              <div key={s.id} className="p-4 border border-slate-100 rounded-3xl bg-slate-50/50 flex justify-between items-center">
                                <div>
                                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{s.createdAt ? new Date(s.createdAt.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Now'}</div>
                                  <div className="font-mono font-black text-slate-900">{formatCurrency(s.amount)}</div>
                                </div>
                                <button onClick={() => handlePrintInvoice(s, t('receipt'))} className="p-3 bg-white border border-slate-100 text-slate-600 rounded-2xl shadow-sm"><Printer size={20} /></button>
                              </div>
                            ))}
                          </div>
                          {(sales || []).filter(s => s.location === 'Cafe' && s.date === posHistoryDate).length === 0 && (
                            <div className="p-20 text-center"><Search size={40} className="mx-auto mb-4 text-slate-200" /><p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t('noHistory')}</p></div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {activeTab === 'history' && (
                  <div className="space-y-6 animate-in fade-in duration-500">
                    {/* Header with Filters */}
                    <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
                          <History size={24} />
                        </div>
                        <div>
                          <h2 className="text-xl font-black uppercase tracking-tight text-slate-800">{t('historyLog')}</h2>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('reviewPastSales') || 'Review Past Sales'}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                        <div className="relative flex-1 md:w-48">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                          <input
                            type="text"
                            placeholder={t('searchHistory') || 'Search customer...'}
                            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold outline-none focus:ring-4 focus:ring-blue-500/10 transition-all"
                            onChange={(e) => setHistoryLocationFilter(e.target.value)}
                          />
                        </div>
                        <input
                          type="date"
                          value={historyDateFilter}
                          onChange={(e) => setHistoryDateFilter(e.target.value)}
                          className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-2 text-xs font-bold text-slate-900 outline-none focus:ring-4 focus:ring-blue-500/10 transition-all"
                        />
                        <select
                          value={historyFilter}
                          onChange={(e) => setHistoryFilter(e.target.value)}
                          className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-2 text-xs font-bold text-slate-900 outline-none focus:ring-4 focus:ring-blue-500/10 transition-all cursor-pointer"
                        >
                          <option value="All">{t('allTransactions') || 'All'}</option>
                          <option value="Retail">{t('retail') || 'Sales'}</option>
                          <option value="Warehouse">{t('warehouse') || 'Warehouse/Stock'}</option>
                          <option value="Repair">{t('repair') || 'Repairs'}</option>
                          <option value="Cash">{t('cash')}</option>
                          <option value="Visa">{t('visa')}</option>
                          <option value="Online">{t('onlinePayment')}</option>
                        </select>
                      </div>
                    </div>
                    {/* History List - Responsive */}
                    <div className="bg-white rounded-[2rem] border border-gray-100 shadow-xl shadow-slate-200/50 overflow-hidden">
                      {(() => {
                        const allHistory = [
                          ...sales.map(s => {
                            const isRepair = s.type === 'service_pos' || (Array.isArray(s.items) && s.items.some(i => i.type === 'service'));
                            return {
                              ...s,
                              type: isRepair ? 'Repair' : 'Retail',
                              dateStr: (() => {
                                let d;
                                if (s.createdAt?.seconds) d = new Date(s.createdAt.seconds * 1000);
                                else if (s.date) {
                                  d = new Date(s.date);
                                  if (isNaN(d.getTime()) && s.date.includes('/')) {
                                      const p = s.date.split('/');
                                      if (p.length === 3) d = new Date(p[2], Number(p[0])-1, p[1]);
                                  }
                                }
                                if (!d || isNaN(d.getTime())) return '';
                                const year = d.getFullYear();
                                const month = String(d.getMonth() + 1).padStart(2, '0');
                                const day = String(d.getDate()).padStart(2, '0');
                                return `${year}-${month}-${day}`;
                              })(),
                              customerName: s.client || s.customer || t('walkInCustomer'),
                              itemsSummary: Array.isArray(s.items) ? s.items.map(i => `${i.qty || i.quantity || 1}x ${i.name} @ ${formatCurrency(i.price || i.sellPrice || 0)}`).join(', ') : (s.items || '-'),
                              totalQty: Array.isArray(s.items) ? s.items.reduce((sum, i) => sum + (Number(i.qty || i.quantity || 1)), 0) : 1,
                              invoiceId: s.invoiceId || s.id.slice(0, 6)
                            };
                          }),
                          ...serviceTickets.filter(t => t.status === 'Delivered').map(t => ({
                            ...t,
                            type: 'Repair',
                            dateStr: (() => {
                              const d = (t.createdAt?.seconds ? new Date(t.createdAt.seconds * 1000) : new Date());
                              const year = d.getFullYear();
                              const month = String(d.getMonth() + 1).padStart(2, '0');
                              const day = String(d.getDate()).padStart(2, '0');
                              return `${year}-${month}-${day}`;
                            })(),
                            customerName: t.customerName,
                            itemsSummary: `${t.brand} ${t.model} (${t.issue})`,
                            amount: Number(t.estimatedCost || 0),
                            totalQty: 1,
                            invoiceId: t.id.slice(0, 6)
                          })),
                          ...purchases.map(p => ({
                            ...p,
                            type: 'Warehouse',
                            dateStr: p.date || (p.createdAt?.seconds ? p.date || new Date(p.createdAt.seconds * 1000).toISOString().split('T')[0] : ''),
                            customerName: p.supplier || t('supplier'),
                            itemsSummary: `${p.quantity || 1}x ${p.name || p.itemName || p.items || p.description || '-'} @ ${formatCurrency(p.buyPrice || (p.amount / (p.quantity || 1)))}`,
                            amount: Number(p.amount || 0),
                            totalQty: p.quantity || 1,
                            invoiceId: 'PUR-' + (p.id || '').slice(0, 6),
                            paymentMethod: 'Cash'
                          }))
                        ];

                        const parseHistoryDate = (s) => {
                          if (s.createdAt?.seconds) return new Date(s.createdAt.seconds * 1000);
                          if (s.date) {
                            const d = new Date(s.date);
                            if (!isNaN(d.getTime())) return d;
                            // Fallback for slashes
                            const parts = s.date.split('/');
                            if (parts.length === 3) {
                                if (parts[0].length === 4) return new Date(s.date);
                                return new Date(parts[2], Number(parts[0])-1, parts[1]);
                            }
                          }
                          return new Date(0);
                        };

                        const sortedAllHistory = allHistory.sort((a,b) => parseHistoryDate(b) - parseHistoryDate(a));

                        const filteredHistory = sortedAllHistory
                          .filter(s => {
                            const matchDate = !historyDateFilter || s.dateStr === historyDateFilter;
                            const matchType = historyFilter === 'All' || s.paymentMethod === historyFilter || s.type === historyFilter;
                            const matchSearch = !historyLocationFilter ||
                              (s.customerName && s.customerName.toLowerCase().includes(historyLocationFilter.toLowerCase())) ||
                              (s.invoiceId && s.invoiceId.toLowerCase().includes(historyLocationFilter.toLowerCase())) ||
                               (s.itemsSummary && s.itemsSummary.toLowerCase().includes(historyLocationFilter.toLowerCase()));
                            return matchDate && matchType && matchSearch;
                          })
                          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

                        return (
                          <>
                            {/* Desktop View Table */}
                            <div className="hidden lg:block overflow-x-auto">
                              <table className="w-full text-left">
                                <thead className="bg-slate-50 border-b border-slate-100">
                                  <tr>
                                    <th className="p-5 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('time')}</th>
                                    <th className="p-5 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('invoiceId')}</th>
                                    <th className="p-5 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('customer')}</th>
                                    <th className="p-5 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('qty')}</th>
                                    <th className="p-5 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('unitPrice') || 'Unit Price'}</th>
                                    <th className="p-5 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('payment')}</th>
                                    <th className="p-5 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('amount')}</th>
                                    <th className="p-5 font-black text-[10px] text-slate-400 uppercase tracking-widest text-right">{t('actions')}</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                  {filteredHistory.map(item => (
                                    <tr key={item.id} className="hover:bg-slate-50/50 transition-colors group">
                                      <td className="p-5">
                                        <span className="text-[10px] font-black text-slate-400 uppercase">{item.time || (item.createdAt?.seconds ? new Date(item.createdAt.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '')}</span>
                                      </td>
                                      <td className="p-5">
                                        <div className="flex items-center gap-2">
                                          <div className={`w-1.5 h-1.5 rounded-full ${item.type === 'Repair' ? 'bg-orange-500' : item.type === 'Warehouse' ? 'bg-emerald-500' : 'bg-blue-500'}`}></div>
                                          <span className="text-xs font-black text-slate-900 font-mono tracking-tighter">#{item.invoiceId}</span>
                                        </div>
                                      </td>
                                      <td className="p-5">
                                        <div className="flex flex-col">
                                          <span className="text-xs font-bold text-slate-700">{item.customerName}</span>
                                          <div className="flex items-center gap-1.5 mt-0.5">
                                            {item.type === 'Repair' ? <Wrench size={8} className="text-orange-400" /> : item.type === 'Warehouse' ? <Package size={8} className="text-emerald-400" /> : <ShoppingCart size={8} className="text-blue-400" />}
                                            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-tight truncate max-w-[250px]">
                                              {item.itemsSummary}
                                            </p>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="p-5">
                                        <span className="text-xs font-black text-slate-900 font-mono">{item.totalQty}</span>
                                      </td>
                                      <td className="p-5">
                                        <span className="text-xs font-black text-blue-600 font-mono">
                                          {item.type === 'Retail' && Array.isArray(item.items) && item.items.length === 1 
                                            ? formatCurrency(item.items[0].price || item.items[0].sellPrice || 0)
                                            : item.type === 'Warehouse' 
                                              ? formatCurrency(item.buyPrice || (item.amount / (item.totalQty || 1)))
                                              : item.type === 'Repair'
                                                ? formatCurrency(item.amount)
                                                : '-'}
                                        </span>
                                      </td>
                                      <td className="p-5">
                                        <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider ${item.paymentMethod === 'Cash' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                                          item.paymentMethod === 'Visa' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                                            'bg-indigo-50 text-indigo-600 border border-indigo-100'
                                          }`}>
                                          {t((item.paymentMethod || 'Cash').toLowerCase()) || (item.paymentMethod || 'Cash')}
                                        </span>
                                      </td>
                                      <td className="p-5">
                                        <span className="text-sm font-black text-slate-900 font-mono">{formatCurrency(item.amount)}</span>
                                      </td>
                                      <td className="p-5 text-right">
                                        <div className="flex items-center justify-end gap-2 transition-all">
                                          <button
                                            onClick={() => handlePrintInvoice(item, item.type === 'Repair' ? 'Service Receipt' : 'retail')}
                                            className="p-2.5 bg-slate-50 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all border border-slate-100 shadow-sm active:scale-95"
                                            title={t('reprint')}
                                          >
                                            <Printer size={16} />
                                          </button>
                                          {currentMode === 'Owner' && (
                                            <button
                                              onClick={() => item.type === 'Repair' ? alert('Delete via Service Active Jobs') : handleDeleteSale(item)}
                                              className="p-2.5 bg-slate-50 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all border border-slate-100 shadow-sm active:scale-95"
                                              title={t('delete')}
                                            >
                                              <Trash2 size={16} />
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {/* Mobile View Card List */}
                            <div className="lg:hidden divide-y divide-slate-100 no-scrollbar overflow-y-auto max-h-[60vh] pb-[100px]">
                              {filteredHistory.map(item => (
                                <div key={item.id} className="p-5 hover:bg-slate-50 active:bg-slate-100 transition-all group flex items-center justify-between gap-4">
                                  <div className="flex items-center gap-4 flex-1 min-w-0">
                                    <div className={`shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center border ${item.type === 'Repair' ? 'bg-orange-50 border-orange-100 text-orange-600' : item.type === 'Warehouse' ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-blue-50 border-blue-100 text-blue-600'}`}>
                                      {item.type === 'Repair' ? <Wrench size={22} /> : item.type === 'Warehouse' ? <Package size={22} /> : <Receipt size={22} />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-black text-slate-400 font-mono">#{item.invoiceId}</span>
                                        <span className="text-[10px] text-slate-300 font-black uppercase">
                                          {item.time || (item.createdAt?.seconds ? new Date(item.createdAt.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '')}
                                        </span>
                                      </div>
                                      <h4 className="text-sm font-black text-slate-900 uppercase tracking-tight truncate">{item.customerName}</h4>
                                      <p className="text-[10px] text-slate-400 truncate mt-0.5 line-clamp-1 italic">{item.itemsSummary} (Qty: {item.totalQty})</p>
                                    </div>
                                  </div>
                                  <div className="text-right shrink-0 flex flex-col items-end gap-2">
                                    <span className="text-base font-black text-slate-900 font-mono">{formatCurrency(item.amount)}</span>
                                    <div className="flex items-center gap-2">
                                      <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest ${item.paymentMethod === 'Cash' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                                        {t((item.paymentMethod || 'Cash').toLowerCase()) || (item.paymentMethod || 'Cash')}
                                      </span>
                                      <button
                                        onClick={() => handlePrintInvoice(item, item.type === 'Repair' ? 'Service Receipt' : 'retail')}
                                        className="p-2 bg-slate-50 text-slate-400 hover:text-blue-600 rounded-xl active:scale-95 transition-all border border-slate-100 shadow-sm"
                                      >
                                        <Printer size={18} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {filteredHistory.length === 0 && (
                                <div className="p-12 text-center">
                                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <History size={24} className="text-slate-300" />
                                  </div>
                                  <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{t('noRecentActivity')}</p>
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    {(() => {
                      const count = [
                        ...sales,
                        ...serviceTickets.filter(t => t.status === 'Delivered'),
                        ...purchases
                      ].filter(s => {
                        const dateStr = s.date || (s.createdAt?.seconds ? new Date(s.createdAt.seconds * 1000).toISOString().split('T')[0] : '');
                        const matchDate = !historyDateFilter || dateStr === historyDateFilter;
                        const matchType = historyFilter === 'All' || s.paymentMethod === historyFilter;
                        return matchDate && matchType;
                      }).length;

                      return count === 0 && (
                        <div className="p-20 text-center">
                          <div className="w-16 h-16 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-slate-100">
                            <History size={32} className="text-slate-200" />
                          </div>
                          <p className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">{t('noHistoryInPeriod') || 'No records found for this period'}</p>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Full-Screen Zone: Service & Repairs */}
          {activeTab === 'service' && (
            <div className="flex-1 overflow-hidden h-full flex flex-col bg-white">
              <div className="flex-1 overflow-y-auto no-scrollbar pb-[140px] lg:pb-0">
                <div className={serviceSubTab === 'sell' ? 'p-0' : 'p-4 md:p-6 lg:p-8'}>
                  {/* Sub Tab Navigation - ERP Style */}
                  <div className={`flex gap-2 p-1.5 bg-white/50 backdrop-blur-md border border-gray-200 rounded-2xl w-full lg:w-fit overflow-x-auto no-scrollbar shadow-sm ${serviceSubTab === 'sell' ? 'mx-4 my-3 mb-1' : 'mb-8'}`}>
                    {[
                      { id: 'board', label: t('dashboard'), icon: <LayoutDashboard size={14} /> },
                      { id: 'sell', label: t('sales') || 'Sales', icon: <ShoppingCart size={14} /> },
                      { id: 'active', label: t('activeJobs'), icon: <Wrench size={14} /> },
                      { id: 'new', label: t('newTicket'), icon: <Plus size={14} /> },
                      { id: 'history', label: t('history') || 'History', icon: <History size={14} /> },
                      { id: 'borrow', label: t('borrow') || 'Borrow', icon: <Package size={14} /> },
                      { id: 'reports', label: t('menuReports'), icon: <BarChart3 size={14} /> }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setServiceSubTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-tight transition-all ${serviceSubTab === tab.id ? 'bg-slate-900 text-white shadow-md' : 'text-slate-400 hover:text-slate-900 hover:bg-slate-50'}`}
                      >
                        {tab.icon} {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Sub Tab: BOARD (DASHBOARD) */}
                  {serviceSubTab === 'board' && (
                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-6 px-0 sm:px-0">
                        {[
                          { label: t('todaysRevenue') || "Today's Revenue", value: formatCurrency(serviceTickets.filter(t => t.status === 'Delivered' && t.createdAt?.seconds && new Date(t.createdAt.seconds * 1000).toDateString() === new Date().toDateString()).reduce((acc, curr) => acc + Number(curr.estimatedCost || 0), 0)), icon: <Zap className="text-amber-500" />, bg: 'bg-amber-50', color: 'text-amber-600' },
                          { label: t('pendingRepairs'), value: serviceTickets.filter(t => t.status === 'Received' || t.status === 'In Progress').length, icon: <Clock className="text-orange-500" />, bg: 'bg-orange-50', color: 'text-orange-600' },
                          { label: t('readyPickup'), value: serviceTickets.filter(t => t.status === 'Ready').length, icon: <CheckSquare className="text-emerald-500" />, bg: 'bg-emerald-50', color: 'text-emerald-600' },
                          { label: t('lowStock'), value: (inventory || []).filter(i => i.quantity <= (i.minStock || 5)).length, icon: <AlertTriangle className="text-rose-500" />, bg: 'bg-rose-50', color: 'text-rose-600' },
                          { label: t('pendingPayments'), value: serviceTickets.filter(t => t.paymentStatus === 'Unpaid' || t.paymentStatus === 'Partial').length, icon: <CreditCard className="text-blue-500" />, bg: 'bg-blue-50', color: 'text-blue-600' }
                        ].map((stat, i) => (
                          <div key={i} className={`bg-white p-4 sm:p-6 rounded-[2rem] border border-gray-100 shadow-sm flex flex-col items-start gap-4 hover:shadow-md transition-shadow active:scale-[0.98] cursor-default group`}>
                            <div className={`p-3 sm:p-4 rounded-2xl ${stat.bg} group-hover:scale-110 transition-transform`}>{stat.icon}</div>
                            <div>
                              <p className="text-[9px] sm:text-[10px] font-black uppercase text-slate-400 tracking-[0.15em] leading-tight mb-1">{stat.label}</p>
                              <h3 className={`text-xl sm:text-2xl font-black ${stat.color} leading-none font-mono`}>{stat.value}</h3>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Quick Actions & Recent Activity */}
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-32">
                        <div className="lg:col-span-2 space-y-8">
                          {/* Quick Actions Grid */}
                          <div className="bg-white p-6 sm:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
                            <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em] mb-8 flex items-center gap-3">
                              <Zap size={16} className="text-amber-500 fill-amber-500" /> {t('quickActions')}
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                              {[
                                { label: t('newTicket'), icon: <Plus size={28} />, color: 'bg-blue-600', shadow: 'shadow-blue-200', action: () => setServiceSubTab('new') },
                                { label: t('activeJobs'), icon: <Activity size={28} />, color: 'bg-emerald-600', shadow: 'shadow-emerald-200', action: () => setServiceSubTab('active') },
                                { label: t('menuReports'), icon: <BarChart3 size={28} />, color: 'bg-rose-500', shadow: 'shadow-rose-200', action: () => setServiceSubTab('reports') },
                                { label: t('sales') || 'Sales', icon: <ShoppingCart size={28} />, color: 'bg-slate-900', shadow: 'shadow-slate-200', action: () => setServiceSubTab('sell') }
                              ].map((btn, i) => (
                                <button
                                  key={i}
                                  onClick={btn.action}
                                  className="group flex flex-col items-center gap-3 p-4 rounded-[2rem] hover:bg-slate-50 transition-all border border-transparent hover:border-slate-100 active:scale-95"
                                >
                                  <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl ${btn.color} text-white flex items-center justify-center shadow-2xl ${btn.shadow} transform group-hover:-translate-y-1 transition-transform`}>
                                    {btn.icon}
                                  </div>
                                  <span className="text-[10px] font-black uppercase text-slate-600 tracking-widest text-center leading-tight">{btn.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Recent Activity List */}
                          <div className="bg-white p-6 sm:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
                            <div className="flex justify-between items-center mb-8">
                              <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em] flex items-center gap-3">
                                <History size={16} className="text-blue-500" /> {t('recentActivity')}
                              </h3>
                              <button onClick={() => setServiceSubTab('active')} className="text-[10px] font-black uppercase text-blue-600 hover:underline tracking-widest">{t('viewAll') || 'View All'}</button>
                            </div>
                            <div className="space-y-3">
                              {serviceTickets.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds).slice(0, 5).map(ticket => (
                                <div
                                  key={ticket.id}
                                  onClick={() => { setEditingTicket(ticket); setIsTicketModalOpen(true); }}
                                  className="flex items-center gap-4 p-4 rounded-3xl hover:bg-slate-50 transition-all border border-transparent hover:border-slate-100 group cursor-pointer active:scale-[0.99]"
                                >
                                  <div className={`p-3 rounded-2xl transition-colors ${ticket.status === 'Ready' ? 'bg-emerald-50 text-emerald-600' :
                                    ticket.status === 'In Progress' ? 'bg-blue-50 text-blue-600' :
                                      'bg-slate-100 text-slate-400'
                                    } group-hover:bg-white border border-transparent group-hover:border-slate-100 group-hover:shadow-sm`}>
                                    {ticket.deviceType === 'Mobile' ? <Smartphone size={20} /> : <Laptop size={20} />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-start">
                                      <h4 className="text-sm font-black text-gray-900 uppercase tracking-tight truncate pr-2">{ticket.customerName}</h4>
                                      <span className="text-[8px] font-black text-slate-300 font-mono">#{ticket.id.slice(0, 6)}</span>
                                    </div>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5 truncate">{ticket.brand} {ticket.model} • {ticket.status}</p>
                                  </div>
                                  <div className="p-2 text-slate-300 group-hover:text-blue-600 transition-colors">
                                    <ChevronRight size={20} />
                                  </div>
                                </div>
                              ))}
                              {serviceTickets.length === 0 && (
                                <div className="text-center py-16 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-100">
                                  <Search size={40} className="mx-auto mb-4 text-slate-200" />
                                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t('noRecentActivity') || 'No Recent Activity'}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Side Section: Priority & Low Stock */}
                        <div className="space-y-8">
                          <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white shadow-xl shadow-slate-200">
                            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] mb-6 text-slate-400">{t('urgent')} & {t('high')}</h3>
                            <div className="space-y-4">
                              {serviceTickets.filter(t => t.priority === 'Urgent' || t.priority === 'High').slice(0, 3).map(ticket => (
                                <div key={ticket.id} className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/5 group hover:bg-white/20 transition-all cursor-pointer" onClick={() => { setEditingTicket(ticket); setIsTicketModalOpen(true); }}>
                                  <div className="flex justify-between items-start mb-2">
                                    <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded ${ticket.priority === 'Urgent' ? 'bg-red-500 text-white' : 'bg-orange-400 text-slate-900'}`}>{ticket.priority}</span>
                                    <span className="text-[8px] font-mono text-slate-500">#{ticket.id.slice(0, 6)}</span>
                                  </div>
                                  <h4 className="text-xs font-bold truncate">{ticket.customerName}</h4>
                                  <p className="text-[10px] text-slate-400 mt-1 line-clamp-1">{ticket.issue}</p>
                                </div>
                              ))}
                              {serviceTickets.filter(t => t.priority === 'Urgent' || t.priority === 'High').length === 0 && (
                                <p className="text-[10px] text-slate-500 font-bold uppercase py-4">All high priority jobs cleared!</p>
                              )}
                            </div>
                          </div>

                          <div className="bg-white p-8 rounded-[2.5rem] border border-rose-100 shadow-sm border-dashed">
                            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] mb-6 text-rose-500 flex items-center gap-2">
                              <AlertTriangle size={14} /> {t('lowStockAlerts')}
                            </h3>
                            <div className="space-y-4">
                              {serviceInventory.filter(i => i.stock <= i.minStock).slice(0, 4).map(item => (
                                <div key={item.id} className="flex justify-between items-center group">
                                  <div>
                                    <h4 className="text-xs font-bold text-gray-800 uppercase tracking-tight">{item.name}</h4>
                                    <p className="text-[9px] text-gray-400 font-bold uppercase mt-0.5">{item.category}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs font-black text-rose-600 tabular-nums">{item.stock} / {item.minStock}</p>
                                    <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest">{t('lowStock')}</p>
                                  </div>
                                </div>
                              ))}
                              {serviceInventory.filter(i => i.stock <= i.minStock).length === 0 && (
                                <p className="text-[10px] text-slate-300 font-bold uppercase py-4">Inventory is healthy</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sub Tab: SELL (SERVICE POS) */}
                  {serviceSubTab === 'sell' && (
                    <div className="flex flex-col lg:flex-row h-full bg-gray-50 overflow-hidden relative min-h-screen">
                      {/* Left Side: Search & Items Grid */}
                      <div className={`flex-1 flex flex-col min-h-0 border-r border-gray-100 ${isMobileCartOpen ? 'hidden lg:flex' : 'flex'}`}>
                        {/* Top Action Bar - Optimized Header */}
                        <div className="p-4 bg-white border-b border-gray-100">
                          <div className="flex flex-col md:flex-row gap-4 justify-between items-center max-w-7xl mx-auto">
                            <div className="flex items-center gap-4">
                              <div className="flex flex-col text-center md:text-left">
                                <h2 className="text-lg font-black text-slate-800 tracking-tight leading-none uppercase">{t('servicePOS') || 'Service POS'}</h2>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em] mt-1.5">{t('terminal') || 'Terminal'}</p>
                              </div>
                              <div className="h-10 w-px bg-gray-100 mx-2 hidden md:block"></div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    const name = prompt(t('enterItemName') || 'Enter Item Name:');
                                    if (!name) return;
                                    const price = prompt(t('enterItemPrice') || 'Enter Item Price:');
                                    if (!price || isNaN(price)) return;
                                    setServiceCart([...serviceCart, { id: 'CUSTOM-' + Date.now(), name, sellPrice: Number(price), quantity: 1, type: 'custom' }]);
                                    if (window.innerWidth < 1024) setIsMobileCartOpen(true);
                                  }}
                                  className="flex items-center gap-2 bg-amber-500 text-white px-5 py-2.5 rounded-xl hover:bg-amber-600 text-[10px] font-black uppercase tracking-widest transition-all shadow-lg shadow-amber-200 active:scale-95"
                                >
                                  <Plus size={14} strokeWidth={3} /> {t('customItem')}
                                </button>
                                <button
                                  onClick={() => { setScannerMode('productBarcode'); setIsScannerOpen(true); }}
                                  className="flex items-center gap-2 bg-blue-50 text-blue-600 border border-blue-100 px-5 py-2.5 rounded-xl hover:bg-blue-100 text-[10px] font-black uppercase tracking-widest transition-all active:scale-95"
                                >
                                  <Scan size={14} strokeWidth={3} /> {t('scan')}
                                </button>
                              </div>
                            </div>

                            <div className="relative flex-1 w-full max-w-md group">
                              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={16} />
                              <input type="text" placeholder={t('searchRepairsAndStock') || 'Search Repairs or Stock...'} className="w-full pl-11 pr-4 py-3 bg-slate-50 border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all text-sm font-bold shadow-sm" value={serviceInventorySearch} onChange={e => setServiceInventorySearch(e.target.value)} />
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 lg:overflow-y-auto p-4 sm:p-6 md:p-8 space-y-8 sm:space-y-10 pb-28 sm:pb-32">
                          {/* 1. Active Repairs Section */}
                          <section className="space-y-4 sm:space-y-6">
                            <div className="flex items-center justify-between px-1 sm:px-2">
                              <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] flex items-center gap-2 sm:gap-3">
                                <div className="p-1.5 bg-blue-50 rounded-lg text-blue-600 shrink-0"><Wrench size={12} /></div>
                                <span className="truncate">{t('activeRepairs') || 'Active Repairs'}</span>
                              </h3>
                              <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-3 sm:px-4 py-1.5 rounded-full border border-blue-100/50 uppercase tracking-widest shrink-0">
                                {serviceTickets.filter(t => t.status !== 'Completed' && t.status !== 'Delivered').length} Jobs
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-5">
                              {serviceTickets
                                .filter(ticket => (ticket.status !== 'Completed' && ticket.status !== 'Delivered') &&
                                  (ticket.customerName.toLowerCase().includes(serviceInventorySearch.toLowerCase()) ||
                                    ticket.id.toLowerCase().includes(serviceInventorySearch.toLowerCase()) ||
                                    ticket.brand.toLowerCase().includes(serviceInventorySearch.toLowerCase())))
                                .map(ticket => (
                                  <button
                                    key={ticket.id}
                                    onClick={() => {
                                      const laborPrice = Number(ticket.estimatedCost || 0);
                                      const items = [
                                        { id: 'SRV-' + ticket.id + '-LB', name: `${t('repairLabor') || 'Repair Labor'}: ${ticket.brand} ${ticket.model} (${ticket.id.slice(0, 6)})`, sellPrice: laborPrice, price: laborPrice, quantity: 1, type: 'service' },
                                        ...(ticket.partsUsed || []).map(p => ({ id: p.id || 'man-' + Date.now(), name: `${t('part') || 'Part'}: ${p.name}`, sellPrice: p.price, price: p.price, quantity: p.quantity, type: 'part' }))
                                      ];
                                      setServiceCart([...serviceCart, ...items]);
                                      if (window.innerWidth < 1024) setIsMobileCartOpen(true);
                                    }}
                                    className="text-left bg-white p-4 sm:p-6 rounded-xl sm:rounded-[2rem] border border-slate-100 hover:border-blue-500 hover:shadow-2xl transition-all active:scale-[0.98] group relative overflow-hidden shadow-sm flex flex-col gap-3 sm:gap-4 min-h-[88px] touch-manipulation"
                                  >
                                    <div className="absolute top-0 right-0 p-6 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity -rotate-12 translate-x-4 -translate-y-4">
                                      <Wrench size={64} />
                                    </div>
                                    <div className="relative z-10 flex flex-col h-full justify-between gap-4">
                                      <div className="space-y-1">
                                        <div className="flex justify-between items-start gap-2">
                                          <p className="text-sm font-black text-slate-800 uppercase tracking-tight line-clamp-1">{ticket.customerName}</p>
                                          <span className={`text-[8px] font-black uppercase px-2 py-1 rounded-lg shrink-0 ${ticket.status === 'Ready' ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200' : 'bg-amber-100 text-amber-700'}`}>
                                            {ticket.status === 'Ready' ? t('readyPickup') :
                                              ticket.status === 'InProgress' ? t('inProgress') :
                                                ticket.status === 'WaitingParts' ? t('waitingParts') :
                                                  ticket.status === 'Received' ? t('received') :
                                                    ticket.status === 'Delivered' ? t('delivered') :
                                                      ticket.status}
                                          </span>
                                        </div>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{ticket.brand} {ticket.model}</p>
                                      </div>
                                      <div className="flex justify-between items-center bg-slate-50 group-hover:bg-blue-50 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-100/50 group-hover:border-blue-200/50 transition-all min-h-[48px]">
                                        <div className="text-base sm:text-xl font-black text-blue-600 font-mono tracking-tighter leading-none">{formatCurrency(ticket.estimatedCost)}</div>
                                        <div className="p-2.5 sm:p-2 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-200 group-active:scale-90 transition-all min-w-[44px] min-h-[44px] flex items-center justify-center">
                                          <Plus size={16} strokeWidth={3} />
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                ))}
                            </div>
                          </section>

                          {/* 2. Parts & Inventory Section */}
                          <section className="space-y-4 sm:space-y-6">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 px-1 sm:px-2">
                              <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] flex items-center gap-2 sm:gap-3 shrink-0">
                                <div className="p-1.5 bg-indigo-50 rounded-lg text-indigo-600"><Package size={12} /></div>
                                <span className="truncate">{t('inventoryParts') || 'Inventory Parts'}</span>
                              </h3>
                              <div className="flex gap-2 overflow-x-auto no-scrollbar py-1 -mx-1 px-1 scroll-smooth touch-pan-x">
                                {[
                                  { id: 'all', label: t('all'), val: '' },
                                  { id: 'phoneParts', label: t('phoneParts'), val: 'Phone Parts' },
                                  { id: 'pcComponents', label: t('pcComponents'), val: 'PC Components' },
                                  { id: 'accessories', label: t('accessories'), val: 'Accessories' }
                                ].map(cat => (
                                  <button
                                    key={cat.id}
                                    onClick={() => setServiceInventorySearch(cat.val)}
                                    className={`text-[9px] font-black uppercase px-4 py-2.5 sm:py-2 rounded-full transition-all whitespace-nowrap shadow-sm border min-h-[40px] touch-manipulation flex items-center ${serviceInventorySearch === cat.val ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-200' : 'bg-white text-slate-400 border-slate-100 hover:border-slate-300 active:scale-95'}`}
                                  >
                                    {cat.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                              {inventory
                                .filter(item => item.name.toLowerCase().includes(serviceInventorySearch.toLowerCase()) || (item.category || '').toLowerCase().includes(serviceInventorySearch.toLowerCase()))
                                .map(item => (
                                  <button
                                    key={item.id}
                                    onClick={() => {
                                      const stock = Number(item.quantity) || 0;
                                      if (stock <= 0) { return alert(t('outOfStock') || "Out of stock!"); }
                                      const existing = serviceCart.find(c => c.id === item.id);
                                      if (existing) {
                                        if (existing.quantity + 1 > stock) {
                                          return alert(`${t('maxStockReached') || 'Max stock reached:'} ${stock}`);
                                        }
                                        setServiceCart(serviceCart.map(c => c.id === item.id ? { ...c, quantity: (c.quantity || 1) + 1 } : c));
                                      } else {
                                        setServiceCart([...serviceCart, { id: item.id, name: item.name, sellPrice: Number(item.sellPrice), quantity: 1, type: 'part' }]);
                                      }
                                      if (window.innerWidth < 1024) setIsMobileCartOpen(true);
                                    }}
                                    className="bg-white p-3 sm:p-4 rounded-xl sm:rounded-[2rem] border border-slate-100 hover:border-indigo-500 hover:shadow-2xl transition-all active:scale-[0.98] group flex flex-col gap-2 sm:gap-4 text-left shadow-sm overflow-hidden touch-manipulation"
                                  >
                                    <div className="bg-slate-50 aspect-square rounded-xl sm:rounded-[1.5rem] overflow-hidden relative border border-slate-100/50">
                                      {item.photo ? (
                                        <img src={item.photo} alt={item.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center text-slate-200">
                                          <Package size={24} strokeWidth={1.5} />
                                        </div>
                                      )}
                                      <div className={`absolute bottom-1.5 right-1.5 sm:bottom-2 sm:right-2 px-1.5 sm:px-2 py-0.5 sm:py-1 backdrop-blur-md rounded-lg text-[8px] font-black ${Number(item.quantity) <= 0 ? 'bg-rose-600 text-white' : 'bg-white/90 text-slate-900 border border-slate-100'}`}>
                                        {Number(item.quantity) <= 0 ? 'OUT' : `QTY: ${item.quantity}`}
                                      </div>
                                    </div>
                                    <div className="space-y-1.5 sm:space-y-2 flex-1 flex flex-col justify-between min-h-0">
                                      <h4 className="text-[10px] sm:text-[11px] font-black text-slate-800 uppercase tracking-tight line-clamp-2 leading-tight group-hover:text-indigo-600 transition-colors">{item.name}</h4>
                                      <div className="flex justify-between items-center bg-slate-50 group-hover:bg-indigo-50 p-2 sm:p-2 rounded-lg sm:rounded-xl transition-all border border-transparent group-hover:border-indigo-100 min-h-[44px]">
                                        <p className="text-[10px] sm:text-xs font-black text-indigo-600 font-mono tracking-tighter pl-0.5 sm:pl-1">{formatCurrency(item.sellPrice)}</p>
                                        <div className="p-2 sm:p-1.5 bg-indigo-600 text-white rounded-lg shadow-md shadow-indigo-100 group-active:scale-90 transition-all min-w-[40px] min-h-[40px] flex items-center justify-center">
                                          <Plus size={14} strokeWidth={3} />
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                ))}
                            </div>
                          </section>
                        </div>

                        {/* Mobile: Sticky "View cart" bar when cart has items */}
                        {serviceCart.length > 0 && (
                          <div className="lg:hidden fixed bottom-[90px] left-0 right-0 z-[120] px-4 pb-2 pointer-events-none">
                            <button
                              onClick={() => setIsMobileCartOpen(true)}
                              className="pointer-events-auto w-full py-4 px-5 bg-slate-900 text-white rounded-2xl shadow-2xl shadow-slate-900/30 flex items-center justify-between gap-4 active:scale-[0.98] transition-transform touch-manipulation border border-slate-700"
                            >
                              <span className="flex items-center gap-2 text-left">
                                <ShoppingBag size={22} strokeWidth={2.5} className="text-blue-300 shrink-0" />
                                <span className="text-sm font-black uppercase tracking-tight">
                                  {t('viewCart') || 'View cart'} ({serviceCart.reduce((a, b) => a + (b.quantity || 1), 0)})
                                </span>
                              </span>
                              <span className="text-lg font-black font-mono tracking-tighter text-blue-300 shrink-0">
                                {formatCurrency(serviceCart.reduce((a, b) => a + (b.sellPrice * b.quantity), 0))}
                              </span>
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Right Side: Cart Sidebar (Fixed) */}
                      {isMobileCartOpen && serviceSubTab === 'sell' && <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[1040] lg:hidden animate-in fade-in duration-300" onClick={() => setIsMobileCartOpen(false)}></div>}
                      <div className={`fixed inset-y-0 right-0 lg:relative lg:inset-auto w-full lg:w-[420px] bg-white lg:border-l border-gray-200 flex flex-col h-full z-[1050] lg:z-20 transition-transform duration-300 ease-in-out transform ${isMobileCartOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`}>
                        <div className="p-3 border-b border-gray-100 flex justify-between items-center bg-white">
                          <div className="flex items-center gap-3 text-gray-800">
                            <button onClick={() => setIsMobileCartOpen(false)} className="lg:hidden p-2 -ml-2 hover:bg-rose-50 hover:text-rose-600 rounded-full transition-colors">
                              <X size={24} />
                            </button>
                            <ShoppingBag size={20} className="text-blue-600" />
                            <h3 className="font-bold text-lg tracking-tight">{t('currentBill')}</h3>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="bg-blue-600 text-white text-[10px] font-black px-2 py-1 rounded-full shadow-lg shadow-blue-200">{serviceCart.reduce((a, b) => a + (b.quantity || 1), 0)}</span>
                          </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-0 scrollbar-thin scrollbar-thumb-gray-200">
                          {serviceCart.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-gray-300 p-6 text-center">
                              <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                                <ShoppingBag size={20} className="opacity-50" />
                              </div>
                              <p className="font-bold text-gray-500 text-xs">{t('cartEmpty')}</p>
                              <p className="text-[10px] mt-0.5 text-gray-400">{t('selectItems')}</p>
                            </div>
                          ) : (
                            <div className="divide-y divide-gray-100">
                              {serviceCart.map((item, i) => (
                                <div key={`${item.id}-${i}`} className="p-2.5 flex items-center gap-2 group hover:bg-gray-50 transition-colors">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-center mb-0.5">
                                      <h4 className="font-bold text-gray-800 text-[11px] sm:text-xs truncate pr-2 uppercase tracking-wide">{item.name}</h4>
                                      <span className="font-mono font-black text-gray-900 text-xs">{formatCurrency(item.sellPrice * (item.quantity || 1))}</span>
                                    </div>
                                    <div className="text-[10px] text-gray-400 font-medium">{formatCurrency(item.sellPrice)} / unit</div>
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <div className="flex items-center border border-gray-200 rounded-lg bg-white h-6 shadow-sm">
                                      <button onClick={() => {
                                        const newCart = [...serviceCart];
                                        if (newCart[i].quantity > 1) {
                                          newCart[i].quantity -= 1;
                                          setServiceCart(newCart);
                                        } else {
                                          setServiceCart(serviceCart.filter((_, idx) => idx !== i));
                                        }
                                      }} className="px-1.5 h-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-l-lg transition-colors font-bold text-xs">-</button>
                                      <span className="w-5 text-center text-[10px] font-black text-gray-700">{item.quantity}</span>
                                      <button onClick={() => {
                                        const newCart = [...serviceCart];
                                        newCart[i].quantity += 1;
                                        setServiceCart(newCart);
                                      }} className="px-1.5 h-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-r-lg transition-colors font-bold text-xs">+</button>
                                    </div>
                                    <button onClick={() => setServiceCart(serviceCart.filter((_, idx) => idx !== i))} className="text-gray-300 hover:text-rose-500 transition-colors p-1 group/del">
                                      <X size={12} className="group-active/del:scale-90" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="bg-gray-50/50 backdrop-blur-md border-t border-gray-200 p-3.5 space-y-3">
                          <div className="space-y-2">
                            <div className="flex justify-between items-center text-xs text-gray-500">
                              <span>{t('subtotal')}</span>
                              <span className="font-mono">{formatCurrency(serviceCart.reduce((a, b) => a + (b.sellPrice * (b.quantity || 1)), 0))}</span>
                            </div>
                            <div className="pt-2 border-t border-gray-100 flex justify-between items-center text-[11px] font-black uppercase text-slate-400">
                              <span>{t('total')}</span>
                              <span className="text-xl font-black text-gray-900 leading-none">{formatCurrency(serviceCart.reduce((a, b) => a + (b.sellPrice * (b.quantity || 1)), 0))}</span>
                            </div>
                          </div>

                          <div className="mb-1">
                            <button
                              onClick={() => { setPinAction('changeSalesEmployee'); setIsPinModalOpen(true); }}
                              className="w-full flex items-center justify-between p-1.5 bg-blue-50 hover:bg-blue-100 rounded-xl border border-blue-100 transition-colors shadow-sm"
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white"><User size={16} /></div>
                                <div className="text-left">
                                  <div className="text-[9px] text-blue-500 font-black uppercase tracking-widest leading-none mb-1">{t('salesEmployee')}</div>
                                  <div className="font-bold text-slate-800 text-[11px] uppercase tracking-wide">{salesEmployee ? salesEmployee.name : t('selectEmployee')}</div>
                                </div>
                              </div>
                              <ChevronDown size={14} className="text-blue-400 mr-1" />
                            </button>
                          </div>

                          <div className="flex bg-slate-100 p-0.5 rounded-xl mb-1.5">
                            {['Walk-in', 'Takeaway'].map(type => (
                              <button
                                key={type}
                                onClick={() => setOrderType(type)}
                                className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${orderType === type ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'}`}
                              >
                                {type === 'Walk-in' ? t('walkIn') : t('takeaway')}
                              </button>
                            ))}
                          </div>

                          <div className="flex flex-col gap-2">
                            <div className="relative group">
                              <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" />
                              <input
                                className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 outline-none"
                                placeholder={t('customerId') || 'Customer ID'}
                                value={newSaleForm.customerId}
                                onChange={e => setNewSaleForm({ ...newSaleForm, customerId: e.target.value })}
                              />
                            </div>
                            <input
                              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 outline-none"
                              placeholder={t('customerNameOptional') || 'Customer Name'}
                              value={newSaleForm.customer}
                              onChange={e => setNewSaleForm({ ...newSaleForm, customer: e.target.value })}
                            />
                          </div>

                          <div className="space-y-2">
                            <div className="grid grid-cols-3 gap-2">
                              {['Cash', 'Visa', 'Online'].map(method => (
                                <button
                                  key={method}
                                  onClick={() => {
                                    setPaymentMethod(method);
                                    if (method === 'Online') setShowUpiQr(true);
                                  }}
                                  className={`py-2 flex flex-col items-center gap-1 rounded-xl border transition-all ${paymentMethod === method ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-100' : 'bg-white text-gray-400 border-gray-100'}`}
                                >
                                  {method === 'Cash' && <Banknote size={16} />}
                                  {method === 'Visa' && <CreditCard size={16} />}
                                  {method === 'Online' && <Smartphone size={16} />}
                                  <span className="text-[9px] font-black uppercase">{t(method.toLowerCase()) || method}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {paymentMethod === 'Online' && (
                            <div className="grid grid-cols-3 gap-2 animate-in slide-in-from-top-2 duration-300">
                              {['UPI', 'InstaPay', 'Other'].map(sub => (
                                <button
                                  key={sub}
                                  onClick={() => { setDigitalSubMethod(sub); setShowUpiQr(true); }}
                                  className={`py-2.5 sm:py-2 text-[8px] font-black uppercase tracking-widest rounded-xl border transition-all active:scale-95 touch-manipulation min-h-[40px] ${digitalSubMethod === sub ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg' : 'bg-slate-50 text-slate-400 border-slate-100'}`}
                                >
                                  {t(sub.toLowerCase()) || sub}
                                </button>
                              ))}
                            </div>
                          )}

                          <button
                            onClick={() => handleCheckoutServiceCart(printFormat)}
                            disabled={serviceCart.length === 0}
                            className="w-full py-4 sm:py-5 bg-blue-600 text-white rounded-xl sm:rounded-[2rem] font-black uppercase tracking-[0.2em] text-xs shadow-2xl shadow-blue-200 hover:bg-blue-700 disabled:opacity-50 disabled:shadow-none transition-all active:scale-[0.98] flex items-center justify-center gap-3 sm:gap-4 touch-manipulation min-h-[52px]"
                          >
                            <CreditCard size={20} strokeWidth={3} className="shrink-0" />
                            <span className="truncate">{t('checkout') || 'Checkout'} - {formatCurrency(serviceCart.reduce((a, b) => a + (Number(b.sellPrice || 0) * Number(b.quantity || 1)), 0))}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {serviceSubTab === 'new' && (
                    <div className="animate-in fade-in slide-in-from-bottom-6 duration-700 max-w-5xl mx-auto pb-32">
                      <div className="flex items-center justify-between mb-8 px-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-[1.5rem] flex items-center justify-center shadow-2xl shadow-blue-200 ring-4 ring-white">
                            <Plus size={28} strokeWidth={3} />
                          </div>
                          <div>
                            <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight uppercase leading-none">{t('createNewRepairTicket') || 'New Ticket'}</h1>
                            <div className="text-[10px] md:text-[11px] text-slate-400 font-extrabold uppercase tracking-[0.2em] mt-2 flex items-center gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div> {t('serviceModule') || 'Repair Center'} • {new Date().toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </div>

                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        setLoading(true);
                        try {
                          const ticketData = {
                            ...serviceForm,
                            userId: user.uid,
                            createdAt: serverTimestamp(),
                            statusLogs: [{
                              status: 'Received',
                              date: new Date().toISOString(),
                              by: user?.email || 'System'
                            }]
                          };
                          await addDoc(collection(db, 'serviceTickets'), ticketData);
                          setServiceForm({
                            customerName: '', customerPhone: '', customerEmail: '', customerAddress: '',
                            deviceType: 'Mobile', brand: '', model: '', serialNo: '',
                            issue: '', priority: 'Normal', technician: '', estimatedCost: '', status: 'Received',
                            notes: '', diagnostics: '', partsUsed: [], paymentStatus: 'Unpaid', amountPaid: 0, paymentMethod: 'Cash', laborCost: 0,
                            photos: []
                          });
                          setServiceSubTab('active');
                        } catch (err) {
                          console.error(err);
                          alert("Error creating ticket: " + err.message);
                        } finally {
                          setLoading(false);
                        }
                      }} className="space-y-6 px-4 md:px-0">

                        {/* Main Grid Layout for Forms */}
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                          {/* Left Column: Customer & Device (Wider) */}
                          <div className="lg:col-span-8 space-y-6">
                            {/* Customer Profile Card */}
                            <div className="bg-white rounded-[2.5rem] p-6 md:p-10 shadow-xl shadow-slate-200/40 border border-slate-100 flex flex-col gap-8 relative overflow-hidden group">
                              <div className="absolute top-0 right-0 p-10 opacity-[0.02] group-hover:opacity-[0.05] transition-opacity -rotate-12 translate-x-6 -translate-y-6 pointer-events-none">
                                <User size={120} />
                              </div>
                              <div className="relative z-10 flex items-center gap-3">
                                <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl"><User size={20} weight="bold" /></div>
                                <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em]">{t('customerInfo')}</h3>
                              </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div className="space-y-2 relative group">
                                    <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest ml-1">{t('customerName')}</label>
                                    <div className="relative">
                                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-blue-500 transition-colors" size={18} />
                                      <input
                                        className="w-full pl-12 pr-6 py-5 bg-slate-50/50 hover:bg-slate-50 border border-transparent focus:border-blue-500/20 rounded-[1.5rem] text-sm font-black focus:ring-8 focus:ring-blue-500/5 transition-all outline-none placeholder:text-slate-300"
                                        placeholder={t('enterCustomerName') || 'Search or Enter Name...'}
                                        value={serviceForm.customerName}
                                        onChange={e => setServiceForm({ ...serviceForm, customerName: e.target.value })}
                                        required
                                      />
                                    </div>
                                    {serviceForm.customerName.length > 1 && serviceCustomers.filter(c => c.name.toLowerCase().includes(serviceForm.customerName.toLowerCase())).length > 0 && (
                                      <div className="absolute top-full left-0 right-0 bg-white/95 backdrop-blur-xl border border-slate-100 rounded-[2rem] shadow-2xl z-50 mt-3 p-2 max-h-64 overflow-y-auto animate-in slide-in-from-top-4 duration-300">
                                        {serviceCustomers.filter(c => c.name.toLowerCase().includes(serviceForm.customerName.toLowerCase())).slice(0, 5).map(c => (
                                          <button
                                            key={c.id}
                                            type="button"
                                            onClick={() => setServiceForm({ ...serviceForm, customerName: c.name, customerPhone: c.phone })}
                                            className="w-full text-left p-4 hover:bg-blue-50 rounded-2xl transition-all border border-transparent hover:border-blue-100/50 flex items-center gap-4 group/item"
                                          >
                                            <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center font-black text-sm uppercase group-hover/item:scale-110 transition-transform">{c.name[0]}</div>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-black text-slate-900 uppercase tracking-tight truncate">{c.name}</p>
                                              <p className="text-[10px] font-mono text-slate-400 mt-0.5 tracking-tighter">{c.phone}</p>
                                            </div>
                                            <ChevronRight size={18} className="text-slate-200 group-hover/item:text-blue-400 group-hover/item:translate-x-1 transition-all" />
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                <div className="space-y-2 group">
                                  <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest ml-1">{t('phone')}</label>
                                  <div className="relative">
                                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-blue-500 transition-colors" size={18} />
                                    <input
                                      className="w-full pl-12 pr-6 py-5 bg-slate-50/50 hover:bg-slate-50 border border-transparent focus:border-blue-500/20 rounded-[1.5rem] text-sm font-black focus:ring-8 focus:ring-blue-500/5 transition-all outline-none placeholder:text-slate-300"
                                      placeholder={t('phonePlaceholder') || '05X XXX XXXX'}
                                      value={serviceForm.customerPhone}
                                      onChange={e => setServiceForm({ ...serviceForm, customerPhone: e.target.value })}
                                      required
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Device Details Card */}
                            <div className="bg-white rounded-[2.5rem] p-6 md:p-10 shadow-xl shadow-slate-200/40 border border-slate-100 space-y-8 relative overflow-hidden group">
                              <div className="absolute top-0 right-0 p-10 opacity-[0.02] group-hover:opacity-[0.05] transition-opacity rotate-12 translate-x-6 -translate-y-6 pointer-events-none">
                                <Smartphone size={120} />
                              </div>
                              <div className="relative z-10 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl"><Smartphone size={20} /></div>
                                  <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em]">{t('deviceDetails')}</h3>
                                </div>
                                <span className="text-[9px] font-black text-indigo-500 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-100 uppercase tracking-widest">Select Category</span>
                              </div>

                              <div className="grid grid-cols-3 gap-3 md:gap-6">
                                {[
                                  { id: 'Mobile', icon: <Smartphone size={24} />, label: t('mobile') || 'Mobile' },
                                  { id: 'Tablet', icon: <Tablet size={24} />, label: t('tablet') || 'Tablet' },
                                  { id: 'PC', icon: <Laptop size={24} />, label: t('pc') || 'Laptop/PC' }
                                ].map(type => (
                                  <button
                                    key={type.id}
                                    type="button"
                                    onClick={() => setServiceForm({ ...serviceForm, deviceType: type.id })}
                                    className={`flex flex-col items-center gap-3 p-5 md:p-8 rounded-[2rem] border-2 transition-all active:scale-95 group/btn ${serviceForm.deviceType === type.id
                                      ? 'bg-indigo-600 border-indigo-600 text-white shadow-2xl shadow-indigo-200 ring-4 ring-indigo-50 scale-[1.02]'
                                      : 'bg-slate-50/50 border-transparent hover:bg-white hover:border-indigo-200 text-slate-400 hover:text-indigo-600'
                                      }`}
                                  >
                                    <div className={`transition-transform duration-500 ${serviceForm.deviceType === type.id ? 'scale-110 drop-shadow-md' : 'group-hover/btn:scale-110'}`}>{type.icon}</div>
                                    <span className={`text-[10px] md:text-sm font-black uppercase tracking-widest transition-opacity ${serviceForm.deviceType === type.id ? 'opacity-100' : 'opacity-60'}`}>{type.label}</span>
                                  </button>
                                ))}
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                                <div className="space-y-2 group">
                                  <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest ml-1">{t('brand')}</label>
                                  <input
                                    className="w-full px-6 py-5 bg-slate-50/50 hover:bg-slate-50 border border-transparent focus:border-indigo-500/20 rounded-[1.5rem] text-sm font-black focus:ring-8 focus:ring-indigo-500/5 transition-all outline-none"
                                    placeholder="e.g. Apple, Samsung"
                                    value={serviceForm.brand}
                                    onChange={e => setServiceForm({ ...serviceForm, brand: e.target.value })}
                                    required
                                  />
                                </div>
                                <div className="space-y-2 group">
                                  <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest ml-1">{t('model')}</label>
                                  <input
                                    className="w-full px-6 py-5 bg-slate-50/50 hover:bg-slate-50 border border-transparent focus:border-indigo-500/20 rounded-[1.5rem] text-sm font-black focus:ring-8 focus:ring-indigo-500/5 transition-all outline-none"
                                    placeholder="e.g. iPhone 15 Pro, MacBook M3"
                                    value={serviceForm.model}
                                    onChange={e => setServiceForm({ ...serviceForm, model: e.target.value })}
                                    required
                                  />
                                </div>
                                <div className="md:col-span-2 space-y-2 group">
                                  <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest ml-1">{t('devicePhotos') || 'Device Photos'}</label>
                                  <div className="flex flex-wrap gap-4 p-6 bg-slate-50/50 rounded-[1.5rem] border-2 border-dashed border-slate-100 min-h-[120px] items-center">
                                    {serviceForm.photos && serviceForm.photos.map((url, i) => (
                                      <div key={i} className="relative w-20 h-20 rounded-2xl overflow-hidden border-2 border-white shadow-xl animate-in zoom-in-75">
                                        <img src={url} alt="" className="w-full h-full object-cover" />
                                        <button type="button" onClick={() => setServiceForm({ ...serviceForm, photos: serviceForm.photos.filter((_, idx) => idx !== i) })} className="absolute top-1 right-1 bg-rose-500 text-white p-1 rounded-full shadow-lg"><X size={12} /></button>
                                      </div>
                                    ))}
                                    <label className="w-20 h-20 rounded-2xl bg-indigo-50 border-2 border-dashed border-indigo-200 flex flex-col items-center justify-center text-indigo-400 hover:text-indigo-600 hover:bg-white hover:border-indigo-400 transition-all cursor-pointer group/upload">
                                      <Camera size={24} className="mb-1 group-hover/upload:scale-110 transition-transform" />
                                      <span className="text-[8px] font-black uppercase">{t('add')}</span>
                                      <input
                                        type="file"
                                        className="hidden"
                                        accept="image/*"
                                        multiple
                                        onChange={async (e) => {
                                          const files = Array.from(e.target.files);
                                          setLoading(true);
                                          const newUrls = [];
                                          for (const file of files) {
                                            const storageRef = ref(storage, `service/${Date.now()}_${file.name}`);
                                            const snapshot = await uploadBytes(storageRef, file);
                                            const url = await getDownloadURL(snapshot.ref);
                                            newUrls.push(url);
                                          }
                                          setServiceForm({ ...serviceForm, photos: [...(serviceForm.photos || []), ...newUrls] });
                                          setLoading(false);
                                        }}
                                      />
                                    </label>
                                  </div>
                                </div>
                                <div className="md:col-span-2 space-y-2 group">
                                  <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest ml-1">{t('serialNo')} / IMEI</label>
                                  <div className="relative">
                                    <QrCode className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                                    <input
                                      className="w-full pl-12 pr-6 py-5 bg-slate-50/50 hover:bg-slate-50 border-2 border-dashed border-slate-100 focus:border-indigo-500/20 rounded-[1.5rem] text-[10px] md:text-xs font-black focus:ring-8 focus:ring-indigo-500/5 transition-all outline-none uppercase tracking-widest placeholder:text-slate-300"
                                      placeholder="Scan or Type SN/IMEI"
                                      value={serviceForm.serialNo}
                                      onChange={e => setServiceForm({ ...serviceForm, serialNo: e.target.value })}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Right Column: Problem & Actions (Narrower) */}
                          <div className="lg:col-span-4 space-y-6">
                            {/* Issue Detail Card */}
                            <div className="bg-white rounded-[2.5rem] p-6 md:p-8 shadow-xl shadow-slate-200/40 border border-slate-100 flex flex-col h-full group">
                              <div className="flex items-center gap-3 mb-6">
                                <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl"><AlertTriangle size={20} /></div>
                                <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em]">{t('issueDescription')}</h3>
                              </div>

                              <div className="flex-1 space-y-6">
                                <div className="space-y-2 group">
                                  <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest ml-1">{t('describeIssue')}</label>
                                  <textarea
                                    className="w-full px-6 py-5 bg-amber-50/30 hover:bg-amber-50 border border-transparent focus:border-amber-500/20 rounded-[2rem] text-sm font-bold focus:ring-8 focus:ring-amber-500/5 transition-all outline-none min-h-[160px] md:min-h-[220px] resize-none"
                                    placeholder="What's wrong with the device?"
                                    value={serviceForm.issue}
                                    onChange={e => setServiceForm({ ...serviceForm, issue: e.target.value })}
                                    required
                                  ></textarea>
                                </div>

                                <div className="space-y-6 bg-slate-50/50 p-6 rounded-[2rem] border border-slate-100">
                                  <div className="space-y-3">
                                    <label className="text-[10px] font-black uppercase text-rose-500 tracking-widest block px-1">{t('priority')}</label>
                                    <div className="grid grid-cols-2 gap-2">
                                      {['Low', 'Normal', 'High', 'Urgent'].map(p => {
                                        const colors = {
                                          Low: 'bg-slate-100 text-slate-600 border-transparent',
                                          Normal: 'bg-blue-50 text-blue-600 border-transparent',
                                          High: 'bg-orange-50 text-orange-600 border-transparent',
                                          Urgent: 'bg-red-50 text-red-600 border-transparent'
                                        };
                                        const activeColors = {
                                          Low: 'bg-slate-600 text-white border-slate-600',
                                          Normal: 'bg-blue-600 text-white border-blue-600',
                                          High: 'bg-orange-500 text-white border-orange-500',
                                          Urgent: 'bg-red-600 text-white border-red-600'
                                        };
                                        return (
                                          <button
                                            key={p}
                                            type="button"
                                            onClick={() => setServiceForm({ ...serviceForm, priority: p })}
                                            className={`py-3.5 rounded-[1.25rem] text-[10px] font-black uppercase tracking-widest border transition-all active:scale-95 ${serviceForm.priority === p ? `${activeColors[p]} shadow-lg` : `${colors[p]}`}`}
                                          >
                                            {t(p.toLowerCase()) || p}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  <div className="space-y-3">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block px-1">{t('assignedTechnician')}</label>
                                    <select className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl text-[11px] font-black uppercase tracking-tight focus:ring-4 focus:ring-blue-500/10 transition-all outline-none appearance-none" value={serviceForm.technician} onChange={e => setServiceForm({ ...serviceForm, technician: e.target.value })}>
                                      <option value="">{t('unassigned')}</option>
                                      {employees.filter(e => e.dept === 'IT' || e.dept === 'Service' || e.role?.toLowerCase().includes('tech')).map(emp => (
                                        <option key={emp.id} value={emp.name}>{emp.name}</option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="space-y-3">
                                    <label className="text-[10px] font-black uppercase text-emerald-600 tracking-widest block px-1">{t('estimatedCost')} ({currency})</label>
                                    <div className="relative">
                                      <DollarSign size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500" />
                                      <input type="number" className="w-full pl-12 pr-6 py-4 bg-white border border-emerald-100 rounded-2xl text-lg font-black text-emerald-600 font-mono focus:ring-8 focus:ring-emerald-500/5 transition-all outline-none" placeholder="0.00" value={serviceForm.estimatedCost} onChange={e => setServiceForm({ ...serviceForm, estimatedCost: e.target.value })} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Form Action */}
                            <div className="lg:pt-6">
                              <button
                                disabled={loading}
                                type="submit"
                                className={`w-full py-6 md:py-8 bg-slate-900 text-white rounded-[2.5rem] hover:bg-blue-600 font-black uppercase tracking-[0.25em] text-sm shadow-2xl shadow-slate-300 transition-all flex items-center justify-center gap-4 group active:scale-[0.97] ${loading ? 'opacity-50 pointer-events-none' : ''}`}
                              >
                                {loading ? <Loader2 size={24} className="animate-spin" /> : <Save size={24} />}
                                {t('createTicket') || 'Create Repair Ticket'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Sub Tab: ACTIVE JOBS */}
                  {serviceSubTab === 'active' && (
                    <div className="space-y-6 animate-in fade-in zoom-in-95 duration-700">
                      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="flex bg-gray-100 p-1 rounded-xl w-full md:w-auto overflow-x-auto">
                          {['All', 'Received', 'In Progress', 'Waiting for Parts', 'Ready', 'Delivered'].map(stat => {
                            const labelMap = { 'All': t('filterAll'), 'Received': t('received'), 'In Progress': t('inProgress'), 'Waiting for Parts': t('waitingParts'), 'Ready': t('readyPickup'), 'Delivered': t('delivered') };
                            return (
                              <button
                                key={stat}
                                onClick={() => setServiceStatusFilter(stat === 'All' ? '' : stat)}
                                className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg flex-1 md:flex-none whitespace-nowrap transition-all ${serviceStatusFilter === (stat === 'All' ? '' : stat) ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                              >
                                {labelMap[stat] || stat}
                              </button>
                            );
                          })}
                        </div>
                        <div className="relative w-full md:w-64">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                          <input
                            type="text"
                            placeholder={t('searchTickets') || 'Search customer/device...'}
                            className="w-full pl-9 pr-4 py-2 bg-gray-50 border-none rounded-xl text-sm font-bold focus:ring-2 focus:ring-blue-500/20 transition-all"
                            value={serviceSearch}
                            onChange={e => setServiceSearch(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-0 sm:px-0">
                        {serviceTickets
                          .filter(t => t.status !== 'Delivered')
                          .filter(t => !serviceStatusFilter || t.status === serviceStatusFilter)
                          .filter(t => !serviceSearch || t.customerName.toLowerCase().includes(serviceSearch.toLowerCase()) || t.brand?.toLowerCase().includes(serviceSearch.toLowerCase()) || t.model?.toLowerCase().includes(serviceSearch.toLowerCase()))
                          .map(ticket => (
                            <div
                              key={ticket.id}
                              onClick={() => { setEditingTicket(ticket); setIsTicketModalOpen(true); }}
                              className="bg-white rounded-[2.5rem] p-5 shadow-sm border border-gray-100/50 hover:shadow-xl hover:border-blue-200 transition-all flex flex-col group relative overflow-hidden active:scale-[0.98] cursor-pointer"
                            >
                              {/* Priority Indicator */}
                              {(ticket.priority === 'Urgent' || ticket.priority === 'High') && (
                                <div className={`absolute top-0 right-0 px-4 py-1.5 rounded-bl-2xl text-[8px] font-black uppercase tracking-widest text-white ${ticket.priority === 'Urgent' ? 'bg-red-500 animate-pulse' : 'bg-orange-500'}`}>
                                  {ticket.priority}
                                </div>
                              )}

                              <div className="flex justify-between items-start mb-4">
                                <div className="flex-1 pr-12">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[10px] font-black text-blue-600/40 uppercase tracking-[0.15em] font-mono">#{ticket.id.slice(0, 6)}</span>
                                  </div>
                                  <h3 className="font-black text-gray-900 leading-tight text-base uppercase tracking-tight">{ticket.customerName}</h3>
                                  <p className="text-xs text-blue-600 font-bold mt-1.5 flex items-center gap-1.5">
                                    <Phone size={12} fill="currentColor" className="opacity-20" /> {ticket.customerPhone}
                                  </p>
                                </div>
                              </div>

                              <div className="bg-slate-50/50 rounded-3xl p-4 mb-4 flex-1 border border-slate-100/50 backdrop-blur-sm">
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="p-2.5 bg-white rounded-xl shadow-sm text-blue-500">
                                    {ticket.deviceType === 'Mobile' ? <Smartphone size={18} /> :
                                      ticket.deviceType === 'PC' || ticket.deviceType === 'Tablet' ? <Laptop size={18} /> :
                                        <Database size={18} />}
                                  </div>
                                  <div>
                                    <p className="text-xs font-black text-slate-800 uppercase tracking-tight">{ticket.brand} {ticket.model}</p>
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-0.5">{ticket.serialNo || 'N/A'}</p>
                                  </div>
                                </div>
                                <p className="text-xs text-slate-500 font-medium line-clamp-2 leading-relaxed bg-white/50 p-2.5 rounded-xl border border-white italic">
                                  "{ticket.issue}"
                                </p>
                                {ticket.photos && ticket.photos.length > 0 && (
                                  <div className="mt-3 flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                                    {ticket.photos.map((url, i) => (
                                      <div key={i} className="relative w-10 h-10 rounded-xl overflow-hidden border-2 border-white shadow-sm shrink-0">
                                        <img src={url} alt="" className="w-full h-full object-cover" />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-black shadow-lg shadow-blue-200">
                                    {ticket.technician ? ticket.technician[0].toUpperCase() : '?'}
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-0.5">{t('technician')}</span>
                                    <span className="text-[10px] font-black text-gray-900 truncate max-w-[80px]">{ticket.technician || t('unassigned')}</span>
                                  </div>
                                </div>

                                <div className="text-right">
                                  <div className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest inline-block mb-1.5 ${ticket.status === 'Ready' ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200' :
                                    ticket.status === 'In Progress' ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' :
                                      ticket.status === 'Waiting for Parts' ? 'bg-rose-100 text-rose-600' :
                                        'bg-slate-100 text-slate-500'
                                    }`}>
                                    {t(ticket.status.toLowerCase().replace(/ /g, '')) || ticket.status}
                                  </div>
                                  {ticket.estimatedCost && (
                                    <div className="text-sm font-black text-slate-900 font-mono tracking-tighter block">{formatCurrency(ticket.estimatedCost)}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}

                        {serviceTickets.filter(t => t.status !== 'Delivered' && (!serviceStatusFilter || t.status === serviceStatusFilter)).length === 0 && (
                          <div className="col-span-full py-20 flex flex-col items-center justify-center bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
                            <CheckSquare size={48} className="text-gray-300 mb-4" />
                            <h3 className="text-lg font-black text-gray-400 uppercase tracking-widest">{t('noActiveJobs') || 'No Active Repair Jobs'}</h3>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Sub Tab: CUSTOMERS */}
                  {serviceSubTab === 'customers' && (
                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-6 duration-700 pb-32">
                      <div className="flex flex-col md:flex-row gap-6 items-center justify-between bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                        <div className="relative flex-1 w-full max-w-2xl group">
                          <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-blue-500 transition-colors" size={20} />
                          <input
                            type="text"
                            placeholder={t('search') || 'Search customers by name or phone...'}
                            className="w-full pl-14 pr-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none"
                            onChange={e => setServiceSearch(e.target.value)}
                          />
                        </div>
                        <button
                          onClick={() => { setSelectedServiceCustomer(null); setIsCustomerModalOpen(true); }}
                          className="w-full md:w-auto px-8 py-4 bg-slate-900 text-white rounded-2xl hover:bg-black font-black uppercase tracking-widest text-[10px] shadow-xl shadow-slate-200 transition-all flex items-center justify-center gap-3 active:scale-95"
                        >
                          <UserPlus size={20} /> {t('addCustomer')}
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {serviceCustomers
                          .filter(c => !serviceSearch || (c.name?.toLowerCase().includes(serviceSearch.toLowerCase())) || (c.phone?.includes(serviceSearch)))
                          .map(customer => (
                            <div key={customer.id} className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-2xl hover:shadow-blue-500/5 transition-all group flex flex-col relative overflow-hidden">
                              <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity -rotate-12 translate-x-4 -translate-y-4">
                                <User size={80} />
                              </div>

                              <div className="flex justify-between items-start mb-6">
                                <div className="w-16 h-16 bg-blue-50 rounded-[1.5rem] flex items-center justify-center text-blue-600 font-black text-2xl uppercase border border-blue-100/50 shadow-sm shadow-blue-50">
                                  {customer.name?.slice(0, 1)}
                                </div>
                                <div className="flex gap-2">
                                  <button onClick={() => { setSelectedServiceCustomer(customer); setServiceCustomerForm(customer); setIsCustomerModalOpen(true); }} className="p-3 bg-slate-50 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all active:scale-90"><Edit size={18} /></button>
                                  <button onClick={async () => {
                                    if (window.confirm('Delete this customer?')) {
                                      await deleteDoc(doc(db, 'serviceCustomers', customer.id));
                                    }
                                  }} className="p-3 bg-slate-50 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all active:scale-90"><Trash2 size={18} /></button>
                                </div>
                              </div>

                              <div className="space-y-1">
                                <h3 className="text-xl font-black text-slate-800 tracking-tight uppercase leading-tight">{customer.name}</h3>
                                <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">{t('customerInformation') || 'Client Profile'}</p>
                              </div>

                              <div className="space-y-4 mt-8 pt-8 border-t border-slate-50">
                                <div className="flex items-center gap-4 text-slate-600 bg-slate-50/50 p-3 rounded-2xl border border-transparent hover:border-slate-100 transition-colors">
                                  <div className="p-2 bg-white rounded-lg shadow-sm"><Phone size={14} className="text-blue-500" /></div>
                                  <span className="text-xs font-black font-mono tracking-tight">{customer.phone}</span>
                                </div>
                                {customer.email && (
                                  <div className="flex items-center gap-4 text-slate-600 bg-slate-50/50 p-3 rounded-2xl border border-transparent hover:border-slate-100 transition-colors">
                                    <div className="p-2 bg-white rounded-lg shadow-sm"><Mail size={14} className="text-indigo-500" /></div>
                                    <span className="text-xs font-bold truncate">{customer.email}</span>
                                  </div>
                                )}
                                {customer.address && (
                                  <div className="flex items-center gap-4 text-slate-600 bg-slate-50/50 p-3 rounded-2xl border border-transparent hover:border-slate-100 transition-colors">
                                    <div className="p-2 bg-white rounded-lg shadow-sm"><MapPin size={14} className="text-emerald-500" /></div>
                                    <span className="text-[10px] font-bold truncate leading-relaxed">{customer.address}</span>
                                  </div>
                                )}
                              </div>

                              <div className="mt-8 pt-6 border-t border-slate-50 flex justify-between items-center bg-slate-50 -mx-8 -mb-8 px-8 py-6 rounded-b-[2.5rem]">
                                <div className="flex flex-col">
                                  <span className="text-[8px] font-black uppercase text-slate-400 tracking-widest mb-1">{t('repairHistory')}</span>
                                  <span className="text-xs font-black text-blue-600 bg-blue-50 px-3 py-1 rounded-lg border border-blue-100/50">
                                    {serviceTickets.filter(t => t.customerPhone === customer.phone).length} JOBS
                                  </span>
                                </div>
                                <button
                                  onClick={() => { setServiceForm({ ...serviceForm, customerName: customer.name, customerPhone: customer.phone }); setServiceSubTab('new'); }}
                                  className="p-4 bg-white text-blue-600 rounded-2xl shadow-sm border border-slate-100 hover:bg-blue-600 hover:text-white transition-all active:scale-95 group/btn"
                                >
                                  <Plus size={20} strokeWidth={3} className="group-hover/btn:rotate-90 transition-transform" />
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Sub Tab: INVENTORY */}
                  {serviceSubTab === 'inventory' && (
                    <div className="space-y-6 animate-in fade-in zoom-in-95 duration-700">
                      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm">
                        <div className="relative flex-1 max-w-lg">
                          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                          <input
                            type="text"
                            placeholder={t('searchInventory') || 'Search spare parts...'}
                            className="w-full pl-12 pr-6 py-4 bg-gray-50 border-none rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 transition-all"
                            onChange={e => setServiceSearch(e.target.value)}
                          />
                        </div>
                        <button
                          onClick={() => setIsServiceInventoryModalOpen(true)}
                          className="px-8 py-4 bg-slate-900 text-white rounded-2xl hover:bg-black font-black uppercase tracking-widest text-[10px] shadow-lg shadow-slate-200 transition-all flex items-center gap-2"
                        >
                          <Plus size={18} /> {t('addInventoryItem')}
                        </button>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 pb-32 sm:pb-0">
                        {inventory
                          .filter(i => !serviceSearch || i.name?.toLowerCase().includes(serviceSearch.toLowerCase()))
                          .map(item => (
                            <div key={item.id} className="bg-white p-4 rounded-[2rem] border border-gray-100 shadow-sm flex flex-col group relative active:scale-[0.98] transition-all">
                              {item.quantity <= (item.minStock || 5) && (
                                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-0.5 bg-rose-50 text-rose-500 rounded-full border border-rose-100 animate-pulse">
                                  <AlertTriangle size={8} />
                                  <span className="text-[7px] font-black uppercase tracking-widest leading-none">Low</span>
                                </div>
                              )}

                              <div className="w-full aspect-square bg-slate-50 rounded-2xl mb-4 overflow-hidden flex items-center justify-center text-slate-300 relative group-hover:bg-blue-50 transition-colors">
                                {item.photo ? (
                                  <img src={item.photo} alt={item.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                ) : (
                                  <Package size={32} strokeWidth={1.5} />
                                )}
                              </div>

                              <h4 className="text-[10px] font-black uppercase text-gray-900 tracking-tight line-clamp-2 min-h-[28px] leading-tight mb-1">{item.name}</h4>
                              <p className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">{item.category || (t('sparePart') || 'Spare Part')}</p>

                              <div className="mt-auto flex justify-between items-end bg-slate-50 p-3 rounded-2xl border border-slate-100/50">
                                <div>
                                  <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5">{t('units')}</p>
                                  <p className={`text-xs font-black tabular-nums ${item.quantity <= (item.minStock || 5) ? 'text-rose-500' : 'text-slate-900'}`}>{item.quantity}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-[7px] font-black text-blue-400 uppercase tracking-widest mb-0.5">{currency}</p>
                                  <p className="text-sm font-black text-blue-600 font-mono tracking-tighter leading-none">{formatCurrency(item.sellPrice).replace(/[^\d.]/g, '')}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Sub Tab: HISTORY */}
                  {serviceSubTab === 'history' && (
                    <div className="bg-white p-6 rounded-[2.5rem] border border-gray-100 shadow-xl shadow-slate-200/50 animate-in fade-in slide-in-from-bottom-4 duration-700">
                      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 px-2 gap-4">
                        <h3 className="text-gray-900 text-2xl font-black uppercase tracking-tight flex items-center gap-2">
                          <Clock size={24} className="text-blue-600" />
                          {serviceHistoryDateFilter === new Date().toISOString().split('T')[0] ? (t('todaysServices') || "Today's Services") : (t('servicesOn') || 'Services on') + ' ' + serviceHistoryDateFilter}
                        </h3>
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                          <input
                            type="date"
                            value={serviceHistoryDateFilter}
                            onChange={(e) => setServiceHistoryDateFilter(e.target.value)}
                            className="w-full sm:w-auto bg-slate-50 border border-slate-100 rounded-xl px-4 py-2 text-sm font-bold text-slate-900 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all"
                          />
                        </div>
                      </div>

                      <div className="overflow-x-auto px-1">
                        {(() => {
                          const historyItems = [
                            ...serviceTickets
                              .filter(t => t.status === 'Delivered')
                              .map(t => ({
                                id: t.id,
                                item: t,
                                isTicket: true,
                                date: t.createdAt?.seconds ? new Date(t.createdAt.seconds * 1000) : new Date(),
                                invoiceId: t.id.slice(0, 6),
                                amount: t.estimatedCost || 0,
                                customer: t.customerName,
                                details: `${t.brand} ${t.model}`,
                                soldBy: t.technician || '-'
                              })),
                            ...sales
                              .filter(s => s.location === 'Repair Shop')
                              .map(s => ({
                                id: s.id,
                                item: s,
                                isTicket: false,
                                date: s.createdAt?.seconds ? new Date(s.createdAt.seconds * 1000) : (s.date ? new Date(s.date) : new Date()),
                                invoiceId: s.invoiceId || s.id.slice(0, 6),
                                amount: s.amount,
                                customer: s.client || s.customer || 'Walk-in',
                                details: Array.isArray(s.items) ? s.items.map(i => `${i.qty}x ${i.name}`).join(', ') : 'Service POS',
                                soldBy: s.soldBy || '-'
                              }))
                          ]
                            .filter(h => !serviceHistoryDateFilter || h.date.toISOString().split('T')[0] === serviceHistoryDateFilter)
                            .sort((a, b) => b.date - a.date);

                          if (historyItems.length === 0) {
                            return (
                              <div className="p-20 text-center">
                                <History size={40} className="mx-auto mb-4 text-slate-200" />
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t('noHistory')}</p>
                              </div>
                            );
                          }

                          return (
                            <>
                              {/* Desktop View */}
                              <div className="hidden md:block">
                                <table className="w-full text-sm text-left">
                                  <thead className="bg-slate-50 border-b border-slate-100 sticky top-0 z-10">
                                    <tr>
                                      <th className="p-4 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('time')}</th>
                                      <th className="p-4 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('invoiceId')} #</th>
                                      <th className="p-4 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('cost')}</th>
                                      <th className="p-4 font-black text-[10px] text-slate-400 uppercase tracking-widest">{t('details') || 'Details'}</th>
                                      <th className="p-4 font-black text-[10px] text-slate-400 uppercase tracking-widest text-right">{t('actions')}</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-50">
                                    {historyItems.map(h => (
                                      <tr key={h.id} className="hover:bg-slate-50/50 transition-colors group">
                                        <td className="p-4 text-slate-500 font-bold">{h.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                                        <td className="p-4 font-mono text-xs text-blue-600 font-black">#{h.invoiceId}</td>
                                        <td className="p-4 font-mono font-black text-slate-900">{formatCurrency(h.amount)}</td>
                                        <td className="p-4">
                                          <div className="text-xs font-bold text-gray-900">{h.customer}</div>
                                          <div className="text-[10px] text-slate-400 font-medium truncate max-w-[200px]">{h.details}</div>
                                          <div className="text-[8px] text-blue-500 font-black uppercase mt-1 tracking-widest">BY: {h.soldBy}</div>
                                        </td>
                                        <td className="p-4 text-right flex items-center justify-end gap-2">
                                          <button
                                            onClick={() => handlePrintInvoice(h.item, h.isTicket ? 'Service Invoice' : 'Service Receipt')}
                                            className="p-2 border border-slate-100 text-slate-400 hover:text-blue-600 rounded-lg transition-all shadow-sm"
                                          >
                                            <Printer size={16} />
                                          </button>
                                          {!h.isTicket && currentMode === 'Owner' && (
                                            <div className="flex gap-2">
                                              <button
                                                onClick={() => { setEditingHistoryItem(h.item); setIsEditHistoryModalOpen(true); }}
                                                className="p-2 border border-slate-100 rounded-lg transition-all shadow-sm text-blue-400 hover:text-blue-600 hover:bg-blue-50"
                                                title="Edit Sale"
                                              >
                                                <Edit size={16} />
                                              </button>
                                              <button
                                                onClick={() => handleDeleteSale(h.item)}
                                                className="p-2 border border-slate-100 rounded-lg transition-all shadow-sm text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                                                title="Delete Sale"
                                              >
                                                <Trash2 size={16} />
                                              </button>
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              {/* Mobile View */}
                              <div className="md:hidden space-y-4 px-0 pb-32 sm:pb-0">
                                {historyItems.map(h => (
                                  <div key={h.id} className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm flex flex-col gap-5 relative overflow-hidden active:scale-[0.98] transition-all group">
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg transform -rotate-6 ${h.isTicket ? 'bg-blue-600 text-white shadow-blue-100' : 'bg-indigo-600 text-white shadow-indigo-100'}`}>
                                            {h.isTicket ? <Wrench size={18} strokeWidth={2.5} /> : <ShoppingBag size={18} strokeWidth={2.5} />}
                                          </div>
                                          <div>
                                            <span className="text-[10px] font-mono font-black text-slate-300 block leading-none mb-1">#{h.invoiceId}</span>
                                            <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em]">{h.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                          </div>
                                          <div className="ml-auto text-right">
                                            <span className="text-[10px] font-black text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg border border-emerald-100">{formatCurrency(h.amount)}</span>
                                          </div>
                                        </div>
                                        <h4 className="text-sm font-black text-slate-900 uppercase tracking-tight mb-1">{h.customer}</h4>
                                        <p className="text-[10px] text-slate-400 font-bold line-clamp-2 leading-relaxed italic">"{h.details}"</p>
                                      </div>
                                    </div>

                                    <div className="flex gap-2 pt-2 border-t border-slate-50">
                                      <button
                                        onClick={() => handlePrintInvoice(h.item, h.isTicket ? 'Service Invoice' : 'Service Receipt')}
                                        className="flex-1 py-4 bg-slate-50 text-slate-900 rounded-2xl font-black uppercase tracking-widest text-[9px] flex items-center justify-center gap-2 border border-slate-100 active:scale-95 transition-all"
                                      >
                                        <Printer size={16} /> {t('print')}
                                      </button>
                                      {!h.isTicket && currentMode === 'Owner' && (
                                        <button
                                          onClick={() => handleDeleteSale(h.item)}
                                          className="p-4 bg-rose-50 text-rose-500 rounded-2xl border border-rose-100 active:scale-95 transition-all"
                                        >
                                          <Trash2 size={16} />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          )
                        })()}
                      </div>
                    </div>
                  )}


                  {/* Sub Tab: BORROW */}
                  {serviceSubTab === 'borrow' && (
                    <div className="space-y-8 animate-in fade-in zoom-in-95 duration-700 pb-32">
                      <div className="flex justify-between items-center bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm">
                        <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight flex items-center gap-2">
                          <Package size={24} className="text-blue-500" /> {t('borrowItems') || 'Borrowed Items'}
                        </h3>
                        <button onClick={() => setIsBorrowModalOpen(true)} className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-all font-black uppercase text-[10px] tracking-widest shadow-lg shadow-blue-200">
                          <Plus size={16} strokeWidth={3} /> {t('addRecord') || 'Add Record'}
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {borrowRecords.length === 0 ? (
                          <div className="col-span-full py-16 text-center bg-white rounded-[2rem] border border-gray-100 border-dashed">
                            <Package size={40} className="mx-auto text-slate-200 mb-4" />
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{t('noBorrowRecords') || 'No Borrow Records'}</p>
                          </div>
                        ) : (
                          borrowRecords.map(record => (
                            <div key={record.id} className={`p-6 rounded-[2rem] border transition-all ${record.returned ? 'bg-slate-50 border-gray-100 opacity-60' : 'bg-white border-blue-100 shadow-sm'}`}>
                              <div className="flex justify-between items-start mb-4">
                                <div>
                                  <span className={`text-[9px] font-black uppercase px-2 py-1 rounded tracking-widest ${record.returned ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                    {record.returned ? (t('returned') || 'Returned') : (t('borrowed') || 'Borrowed')}
                                  </span>
                                </div>
                                <button onClick={() => handleDeleteBorrowRecord(record.id)} className="text-slate-300 hover:text-red-500 transition-colors">
                                  <Trash2 size={16} />
                                </button>
                              </div>
                              <h4 className="font-black text-lg text-gray-900 leading-tight">{record.itemName}</h4>
                              <p className="text-sm font-bold text-blue-600 mb-4">{t('borrower') || 'Borrower'}: {record.borrowerName}</p>
                              <div className="space-y-1 mb-4 border-t border-gray-50 pt-4">
                                <p className="text-[10px] uppercase font-bold text-slate-400 flex items-center justify-between">
                                  <span>{t('dateBorrowed') || 'Borrowed'}:</span> <span className="text-slate-700 font-mono">{record.borrowDate} {record.borrowTime}</span>
                                </p>
                                {record.returned && (
                                  <p className="text-[10px] uppercase font-bold text-emerald-500 flex items-center justify-between">
                                    <span>{t('dateReturned') || 'Returned'}:</span> <span className="font-mono">{record.returnDate} {record.returnTime}</span>
                                  </p>
                                )}
                              </div>
                              {record.info && (
                                <p className="text-xs text-slate-500 italic mb-4 bg-slate-50 p-3 rounded-xl line-clamp-2">"{record.info}"</p>
                              )}
                              <button
                                onClick={() => handleToggleBorrowReturn(record.id, record.returned)}
                                className={`w-full py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${record.returned ? 'bg-gray-100 text-slate-500 hover:bg-amber-50 hover:text-amber-600' : 'bg-blue-600 text-white shadow-lg shadow-blue-200 hover:bg-blue-700'}`}
                              >
                                {record.returned ? (t('markAsBorrowed') || 'Mark as Borrowed') : (t('markAsReturned') || 'Mark as Returned')}
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* Sub Tab: REPORTS */}
                  {serviceSubTab === 'reports' && (
                    <div className="space-y-8 animate-in fade-in zoom-in-95 duration-700 pb-32">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-0 sm:px-0">
                        {/* Revenue Summary */}
                        <div className="bg-white p-6 sm:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
                          <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em] mb-8 flex items-center gap-3">
                            <CreditCard className="text-blue-600" size={20} /> {t('monthlyServiceRevenue')}
                          </h3>
                          <div className="space-y-3">
                            {[
                              { label: t('totalRevenue') || 'Total Revenue', value: formatCurrency(serviceTickets.reduce((acc, curr) => acc + Number(curr.estimatedCost || 0), 0)), color: 'text-blue-600', bg: 'bg-blue-50' },
                              { label: t('collected') || 'Collected', value: formatCurrency(serviceTickets.filter(t => t.paymentStatus === 'Paid').reduce((acc, curr) => acc + Number(curr.estimatedCost || 0), 0)), color: 'text-emerald-600', bg: 'bg-emerald-50' },
                              { label: t('unpaid') || 'Unpaid', value: formatCurrency(serviceTickets.filter(t => t.paymentStatus === 'Unpaid').reduce((acc, curr) => acc + Number(curr.estimatedCost || 0), 0)), color: 'text-rose-600', bg: 'bg-rose-50' }
                            ].map((stat, i) => (
                              <div key={i} className={`flex justify-between items-center p-4 ${stat.bg}/30 rounded-2xl border border-${stat.color.split('-')[1]}-100/50`}>
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{stat.label}</span>
                                <span className={`text-sm font-black mono ${stat.color}`}>{stat.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Top Brands */}
                        <div className="bg-white p-6 sm:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
                          <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em] mb-8 flex items-center gap-3">
                            <Smartphone className="text-orange-600" size={20} /> {t('topRepairs')}
                          </h3>
                          <div className="space-y-4">
                            {Object.entries(serviceTickets.reduce((acc, t) => {
                              acc[t.brand] = (acc[t.brand] || 0) + 1;
                              return acc;
                            }, {})).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([brand, count], i) => (
                              <div key={i} className="flex flex-col gap-2 group">
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-black text-slate-600 uppercase tracking-tight">{brand || 'Unknown'}</span>
                                  <span className="text-xs font-black text-slate-900">{count}</span>
                                </div>
                                <div className="h-2 bg-slate-50 rounded-full w-full overflow-hidden border border-slate-100 italic">
                                  <div className="h-full bg-blue-500 rounded-full transition-all duration-1000 shadow-[0_0_10px_rgba(59,130,246,0.5)]" style={{ width: `${(count / (serviceTickets.length || 1)) * 100}%` }}></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Tech Performance */}
                        <div className="bg-white p-6 sm:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
                          <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em] mb-8 flex items-center gap-3">
                            <User className="text-indigo-600" size={20} /> {t('technicianRevenue')}
                          </h3>
                          <div className="space-y-3">
                            {Object.entries(serviceTickets.reduce((acc, t) => {
                              if (t.technician) {
                                acc[t.technician] = (acc[t.technician] || 0) + Number(t.estimatedCost || 0);
                              }
                              return acc;
                            }, {})).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([tech, revenue], i) => (
                              <div key={i} className="flex justify-between items-center p-4 bg-slate-50 rounded-2xl border border-slate-100/50 group hover:border-indigo-200 transition-all">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[10px] font-black text-indigo-600 border border-indigo-100 shadow-sm">
                                    {tech[0].toUpperCase()}
                                  </div>
                                  <span className="text-xs font-black text-slate-600 uppercase tracking-tight">{tech}</span>
                                </div>
                                <span className="text-xs font-black text-emerald-600 font-mono">{formatCurrency(revenue)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

      {/* --- Modals --- */}

      {/* Service / Repair Ticket Edit Modal */}
      {
        isTicketModalOpen && editingTicket && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[600] lg:p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[2.5rem] w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col border border-gray-100 bottom-sheet max-h-[90vh]">
              {/* Mobile Pull Indicator */}
              <div className="lg:hidden w-full pt-4 shrink-0">
                <div className="pull-indicator"></div>
              </div>

              <div className="p-6 md:p-8 flex justify-between items-center border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-50 rounded-2xl text-blue-600">
                    <Wrench size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-slate-800 tracking-tight uppercase leading-none">{t('editTicket')}</h3>
                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] mt-1.5">{t('ticketId') || 'Ticket'}: #{editingTicket.id.slice(0, 8)}</p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsTicketModalOpen(false);
                    setEditingTicket(null);
                  }}
                  className="p-3 bg-slate-50 text-slate-400 hover:bg-rose-50 hover:text-rose-600 rounded-2xl transition-all active:scale-95 border border-slate-100 flex items-center justify-center cursor-pointer group"
                >
                  <X size={24} className="group-hover:rotate-90 transition-transform duration-300" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8 no-scrollbar pb-32 lg:pb-8">
                {/* Customer Snapshot */}
                <div className="flex items-center justify-between bg-slate-50 p-6 rounded-[2rem] border border-slate-100/50">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-blue-600 shadow-sm border border-slate-100">
                      <User size={24} />
                    </div>
                    <div>
                      <h4 className="font-black text-gray-900 uppercase tracking-tight text-base">{editingTicket.customerName}</h4>
                      <p className="text-xs text-blue-600 font-bold">{editingTicket.customerPhone}</p>
                    </div>
                  </div>
                  <button onClick={() => window.open(`tel:${editingTicket.customerPhone}`)} className="p-3 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-200 active:scale-90 transition-all">
                    <Phone size={20} />
                  </button>
                </div>

                {/* Status & Assignment */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">{t('status')}</label>
                    <select className={`w-full px-5 py-4 rounded-2xl text-sm font-black uppercase tracking-widest border-2 transition-all outline-none appearance-none ${editingTicket.status === 'Ready' ? 'bg-emerald-50 border-emerald-100 text-emerald-600 focus:ring-emerald-500/20' :
                      editingTicket.status === 'In Progress' ? 'bg-blue-50 border-blue-100 text-blue-600 focus:ring-blue-500/20' :
                        'bg-slate-50 border-slate-100 text-slate-600 focus:ring-slate-500/20'
                      }`} value={editingTicket.status} onChange={e => setEditingTicket({ ...editingTicket, status: e.target.value })}>
                      <option value="Received">{t('received')}</option>
                      <option value="In Progress">{t('inProgress')}</option>
                      <option value="Waiting for Parts">{t('waitingParts')}</option>
                      <option value="Ready">{t('readyPickup')}</option>
                      <option value="Delivered">{t('delivered')} ({t('closed') || 'Closed'})</option>
                    </select>
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">{t('assignedTechnician') || 'Technician'}</label>
                    <select className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={editingTicket.technician || ''} onChange={e => setEditingTicket({ ...editingTicket, technician: e.target.value })}>
                      <option value="">{t('unassigned') || 'Unassigned'}</option>
                      {employees.filter(e => e.dept === 'IT' || e.dept === 'Service' || e.role?.toLowerCase().includes('tech')).map(emp => (
                        <option key={emp.id} value={emp.name}>{emp.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">{t('issueDescription') || 'Issue'}</label>
                  <div className="relative">
                    <textarea
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-[1.5rem] text-sm font-bold text-gray-700 min-h-[100px] focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none resize-none"
                      value={editingTicket.issue}
                      onChange={e => setEditingTicket({ ...editingTicket, issue: e.target.value })}
                    ></textarea>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">{t('priority') || 'Priority'}</label>
                    <div className="grid grid-cols-4 gap-2">
                      {['Low', 'Normal', 'High', 'Urgent'].map(p => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setEditingTicket({ ...editingTicket, priority: p })}
                          className={`py-2.5 rounded-xl text-[8px] font-black uppercase tracking-tight border transition-all ${editingTicket.priority === p ?
                            (p === 'Urgent' ? 'bg-rose-600 text-white border-rose-600 shadow-lg shadow-rose-200' :
                              p === 'High' ? 'bg-orange-500 text-white border-orange-500 shadow-lg shadow-orange-200' :
                                p === 'Normal' ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-200' :
                                  'bg-slate-900 text-white border-slate-900 shadow-lg shadow-slate-200') :
                            'bg-white text-slate-400 border-slate-100 hover:border-slate-300'}`}
                        >
                          {t(p.toLowerCase()) || p}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black uppercase text-emerald-600 tracking-widest pl-2">{t('estimatedCost')} ({currency})</label>
                    <div className="relative">
                      <input type="number" className="w-full px-5 py-4 bg-emerald-50/50 border border-emerald-100 rounded-2xl text-xl font-black text-emerald-600 font-mono focus:bg-white focus:ring-4 focus:ring-emerald-500/10 transition-all outline-none" value={editingTicket.estimatedCost} onChange={e => setEditingTicket({ ...editingTicket, estimatedCost: e.target.value })} />
                      <div className="absolute right-5 top-1/2 -translate-y-1/2 text-emerald-300">
                        <CreditCard size={20} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-gray-100">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">{t('paymentStatus') || 'Payment Status'}</label>
                    <select className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={editingTicket.paymentStatus || 'Unpaid'} onChange={e => setEditingTicket({ ...editingTicket, paymentStatus: e.target.value })}>
                      <option value="Unpaid">{t('unpaid')}</option>
                      <option value="Partial">{t('partial')}</option>
                      <option value="Paid">{t('paid')}</option>
                    </select>
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">{t('paymentMethod') || 'Payment Method'}</label>
                    <select className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={editingTicket.paymentMethod || 'Cash'} onChange={e => setEditingTicket({ ...editingTicket, paymentMethod: e.target.value })}>
                      <option value="Cash">{t('cash')}</option>
                      <option value="Card">{t('creditCard')}</option>
                      <option value="Digital Wallet">{t('digitalPayment')}</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-5 bg-slate-50/80 p-6 rounded-[2rem] border border-slate-100 outline-none">
                  <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest flex justify-between items-center px-2">
                    <span>{t('partsUsed') || 'Spare Parts Used'}</span>
                    <span className="bg-white px-2 py-0.5 rounded-full text-[8px] border border-slate-200">{(editingTicket.partsUsed || []).length} items</span>
                  </label>
                  <div className="relative group">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                        <input
                          type="text"
                          id="part-search-input"
                          placeholder={t('searchInventory') || "Search parts..."}
                          className="w-full bg-white border border-slate-100 rounded-2xl pl-12 pr-4 py-4 text-sm font-bold focus:ring-4 focus:ring-blue-500/10 outline-none transition-all"
                          autoComplete="off"
                          onChange={(e) => {
                            const val = e.target.value.toLowerCase();
                            const results = val ? inventory.filter(i => (i.name?.toLowerCase().includes(val) || i.barcode?.includes(val)) && i.quantity > 0).slice(0, 5) : [];
                            setPartSearchResults(results);
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const val = document.getElementById('part-search-input').value;
                          if (!val) return;
                          const price = prompt(`${t('enterItemPrice')} for ${val}:`);
                          if (price) {
                            setEditingTicket({ ...editingTicket, partsUsed: [...(editingTicket.partsUsed || []), { id: 'man-' + Date.now(), name: val, price: Number(price), quantity: 1, fromInventory: false }] });
                            document.getElementById('part-search-input').value = '';
                            setPartSearchResults([]);
                          }
                        }}
                        className="bg-slate-900 text-white px-6 rounded-2xl text-[10px] font-black uppercase tracking-widest active:scale-95 transition-all shadow-lg hover:bg-black transition-colors"
                      >
                        {t('manual')}
                      </button>
                    </div>

                    {/* Search Results Dropdown */}
                    {partSearchResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-slate-100 rounded-[1.5rem] shadow-2xl z-[210] mt-2 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300">
                        {partSearchResults.map(item => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => {
                              setEditingTicket({ ...editingTicket, partsUsed: [...(editingTicket.partsUsed || []), { id: item.id, name: item.name, price: item.sellPrice, quantity: 1, fromInventory: true }] });
                              document.getElementById('part-search-input').value = '';
                              setPartSearchResults([]);
                            }}
                            className="w-full text-left p-4 hover:bg-blue-50 border-b border-slate-50 flex justify-between items-center group transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 group-hover:bg-blue-100 group-hover:text-blue-600">
                                <Package size={20} />
                              </div>
                              <div>
                                <p className="text-xs font-black text-gray-900 uppercase tracking-tight">{item.name}</p>
                                <p className="text-[9px] text-slate-400 font-mono tracking-widest uppercase">{item.barcode || 'NO BARCODE'}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-black text-blue-600 font-mono">{formatCurrency(item.sellPrice)}</p>
                              <p className="text-[9px] font-bold text-emerald-500 uppercase tracking-widest">{item.quantity} in stock</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    {(editingTicket.partsUsed || []).map((part, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-white p-4 rounded-2xl border border-slate-100 text-xs shadow-sm hover:border-blue-200 transition-all group">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-slate-50 rounded-xl text-slate-400"><Tag size={12} /></div>
                          <span className="font-black text-slate-700 uppercase tracking-tight">{part.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-blue-600 font-black font-mono text-sm">{formatCurrency(part.price)}</span>
                          <button
                            onClick={() => setEditingTicket({ ...editingTicket, partsUsed: editingTicket.partsUsed.filter((_, i) => i !== idx) })}
                            className="w-8 h-8 rounded-full bg-rose-50 text-rose-400 hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                    {(editingTicket.partsUsed || []).length > 0 && (
                      <div className="flex justify-between items-center px-5 py-4 bg-blue-600 rounded-2xl text-xs font-black text-white shadow-xl shadow-blue-100">
                        <span className="uppercase tracking-[0.2em]">{t('partsTotal') || 'Parts Total'}</span>
                        <span className="font-mono text-lg tracking-tighter">{formatCurrency((editingTicket.partsUsed || []).reduce((s, p) => s + (Number(p.price) * (p.quantity || 1)), 0))}</span>
                      </div>
                    )}
                    {(editingTicket.partsUsed || []).length === 0 && (
                      <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-2xl">
                        <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">{t('noPartsUsed') || 'No parts used yet'}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">{t('internalNotes') || 'Internal Technician Notes'}</label>
                  <textarea className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-[1.5rem] text-xs font-bold text-gray-700 min-h-[80px] focus:bg-white focus:ring-4 focus:ring-slate-500/10 transition-all outline-none resize-none border-dashed" placeholder="Diagnoses details, parts used, etc..." value={editingTicket.notes || ''} onChange={e => setEditingTicket({ ...editingTicket, notes: e.target.value })}></textarea>
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest flex justify-between items-center px-2">
                    {t('attachPhotos') || 'Device Photos'}
                    {photoUploading && <Loader2 size={12} className="animate-spin text-blue-600" />}
                  </label>
                  <div className="flex flex-wrap gap-4">
                    <label className="w-24 h-24 border-2 border-dashed border-slate-200 rounded-[1.5rem] flex flex-col items-center justify-center text-slate-300 hover:text-blue-600 hover:border-blue-200 transition-all cursor-pointer bg-slate-50 active:scale-95 group">
                      <Camera size={24} className="group-hover:scale-110 transition-transform" />
                      <span className="text-[8px] font-black uppercase mt-1.5 tracking-widest">{t('add')}</span>
                      <input type="file" className="hidden" accept="image/*" onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const url = await handleUploadRepairPhoto(file, editingTicket.id);
                          if (url) {
                            setEditingTicket({ ...editingTicket, photos: [...(editingTicket.photos || []), url] });
                          }
                        }
                      }} />
                    </label>
                    {(editingTicket.photos || []).map((url, i) => (
                      <div key={i} className="relative w-24 h-24 rounded-[1.5rem] overflow-hidden group border-2 border-white shadow-md">
                        <img src={url} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                        <button onClick={() => setEditingTicket({ ...editingTicket, photos: editingTicket.photos.filter((_, idx) => idx !== i) })} className="absolute inset-0 bg-rose-600/60 text-white opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center backdrop-blur-sm">
                          <Trash2 size={24} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6 md:p-8 border-t border-gray-100 bg-white shrink-0 sticky bottom-0 z-20 shadow-[0_-20px_40px_rgba(255,255,255,0.95)] rounded-t-[2.5rem] lg:rounded-none">
                <div className="grid grid-cols-2 lg:flex lg:flex-row gap-3 w-full items-center">
                  <div className="col-span-2 lg:col-auto lg:flex-1">
                    {editingTicket.status === 'Delivered' && (
                      <button onClick={async () => {
                        if (window.confirm('Delete this ticket entirely?')) {
                          await deleteDoc(doc(db, 'serviceTickets', editingTicket.id));
                          setIsTicketModalOpen(false); setEditingTicket(null);
                        }
                      }} className="w-full lg:w-16 h-14 bg-rose-50 text-rose-500 rounded-2xl hover:bg-rose-500 hover:text-white transition-all active:scale-95 shadow-sm border border-rose-100 flex items-center justify-center">
                        <Trash2 size={24} />
                      </button>
                    )}
                    {!(editingTicket.status === 'Delivered') && (
                      <button onClick={() => {
                        const laborPrice = Number(editingTicket.estimatedCost || 0);
                        const repairItems = [
                          { id: 'SRV-' + editingTicket.id + '-LB', name: `Repair Labor: ${editingTicket.brand} ${editingTicket.model} (${editingTicket.id.slice(0, 6)})`, sellPrice: laborPrice, price: laborPrice, quantity: 1, type: 'service' },
                          ...(editingTicket.partsUsed || []).map(p => ({ id: p.id || 'man-' + Date.now(), name: `Part: ${p.name}`, sellPrice: p.price, price: p.price, quantity: p.quantity, type: 'part' }))
                        ];
                        setServiceCart([...serviceCart, ...repairItems]);
                        setServiceSubTab('sell');
                        setIsTicketModalOpen(false);
                        setEditingTicket(null);
                      }} className="w-full px-6 h-14 bg-slate-900 text-white rounded-2xl hover:bg-black font-black uppercase tracking-widest text-[9px] transition-all shadow-xl shadow-slate-200 flex items-center justify-center gap-3 whitespace-nowrap active:scale-95">
                        <ShoppingCart size={18} strokeWidth={3} /> {t('billToPOS') || 'Bill to POS'}
                      </button>
                    )}
                  </div>

                  <button type="button" onClick={() => { setIsTicketModalOpen(false); setEditingTicket(null); }} className="px-6 h-14 bg-gray-50 text-slate-400 rounded-2xl hover:bg-gray-100 font-black uppercase tracking-widest text-[9px] border border-slate-100 transition-all active:scale-95">{t('cancel')}</button>
                  <button onClick={async () => {
                    try {
                      const origTicket = serviceTickets.find(t => t.id === editingTicket.id);
                      let newLogs = editingTicket.statusLogs || [];
                      if (origTicket && origTicket.status !== editingTicket.status) {
                        newLogs = [...newLogs, { status: editingTicket.status, date: new Date().toISOString(), by: user.email || 'System' }];

                        if (editingTicket.status === 'Delivered' && origTicket.status !== 'Delivered') {
                          const batch = writeBatch(db);
                          (editingTicket.partsUsed || []).forEach(part => {
                            const invRef = doc(db, 'inventory', part.id);
                            const currentInv = inventory.find(i => i.id === part.id);
                            if (currentInv) {
                              const newQty = Math.max(0, Number(currentInv.quantity) - (part.quantity || 1));
                              batch.update(invRef, { quantity: newQty });
                            }
                          });
                          await batch.commit();
                        }
                      }
                      await updateDoc(doc(db, 'serviceTickets', editingTicket.id), { ...editingTicket, statusLogs: newLogs });
                      setIsTicketModalOpen(false); setEditingTicket(null);
                    } catch (err) { console.error(err); }
                  }} className="flex-2 px-12 py-4 bg-blue-600 text-white rounded-[1.5rem] hover:bg-blue-700 font-black uppercase tracking-widest text-[10px] shadow-2xl shadow-blue-200 transition-all flex items-center justify-center gap-3 active:scale-95">
                    <Save size={18} strokeWidth={3} /> {t('saveChanges')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* --- Service Customer Modal --- */}
      {isCustomerModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[200] lg:p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden flex flex-col border border-gray-100 bottom-sheet">
            {/* Mobile Pull Indicator */}
            <div className="lg:hidden w-full pt-4 shrink-0">
              <div className="pull-indicator"></div>
            </div>

            <div className="p-8 flex justify-between items-center border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-50 rounded-2xl text-blue-600 shadow-sm border border-blue-100/50">
                  <UserPlus size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-800 tracking-tight uppercase leading-none">{selectedServiceCustomer ? t('editCustomer') : t('addCustomer')}</h3>
                  <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] mt-1.5">{t('customerInformation') || 'Customer Profile'}</p>
                </div>
              </div>
              <button onClick={() => { setIsCustomerModalOpen(false); setSelectedServiceCustomer(null); setServiceCustomerForm({ name: '', phone: '', email: '', address: '', notes: '' }); }} className="p-2 text-slate-300 hover:text-rose-600 transition-all active:scale-95"><X size={24} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6 no-scrollbar pb-32 lg:pb-8">
              <form id="customer-form" onSubmit={async (e) => {
                e.preventDefault();
                try {
                  if (selectedServiceCustomer) {
                    await updateDoc(doc(db, 'serviceCustomers', selectedServiceCustomer.id), serviceCustomerForm);
                  } else {
                    await addDoc(collection(db, 'serviceCustomers'), { ...serviceCustomerForm, userId: user.uid, createdAt: serverTimestamp() });
                  }
                  setIsCustomerModalOpen(false);
                  setSelectedServiceCustomer(null);
                  setServiceCustomerForm({ name: '', phone: '', email: '', address: '', notes: '' });
                } catch (err) { console.error(err); }
              }} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 pl-4">{t('customerName')}</label>
                  <input type="text" className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={serviceCustomerForm.name} onChange={e => setServiceCustomerForm({ ...serviceCustomerForm, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 pl-4">{t('phone')}</label>
                  <input type="text" className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={serviceCustomerForm.phone} onChange={e => setServiceCustomerForm({ ...serviceCustomerForm, phone: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 pl-4">{t('email')}</label>
                  <input type="email" className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={serviceCustomerForm.email} onChange={e => setServiceCustomerForm({ ...serviceCustomerForm, email: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 pl-4">{t('address')}</label>
                  <textarea className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none min-h-[100px] resize-none" value={serviceCustomerForm.address} onChange={e => setServiceCustomerForm({ ...serviceCustomerForm, address: e.target.value })}></textarea>
                </div>
              </form>
            </div>

            <div className="p-8 border-t border-gray-100 bg-white lg:bg-gray-50 flex gap-4 shrink-0 sticky bottom-0 rounded-t-[2.5rem] lg:rounded-none shadow-[0_-20px_40px_rgba(255,255,255,0.8)]">
              <button type="button" onClick={() => { setIsCustomerModalOpen(false); setSelectedServiceCustomer(null); }} className="flex-1 px-8 py-4 bg-white text-slate-400 rounded-2xl hover:bg-slate-50 font-black uppercase tracking-widest text-[10px] border border-slate-100 transition-all active:scale-95">{t('cancel')}</button>
              <button form="customer-form" type="submit" className="flex-[2] bg-slate-900 hover:bg-black text-white py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all shadow-xl shadow-slate-200 flex items-center justify-center gap-2 active:scale-95">
                <Save size={18} /> {selectedServiceCustomer ? t('saveChanges') : t('addCustomer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Service Inventory Modal --- */}
      {isServiceInventoryModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[200] lg:p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden flex flex-col border border-gray-100 bottom-sheet">
            {/* Mobile Pull Indicator */}
            <div className="lg:hidden w-full pt-4 shrink-0">
              <div className="pull-indicator"></div>
            </div>

            <div className="p-8 flex justify-between items-center border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-50 rounded-2xl text-emerald-600 shadow-sm border border-emerald-100/50">
                  <Package size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-800 tracking-tight uppercase leading-none">{editingServiceInventory ? t('editItem') : t('addStock')}</h3>
                  <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] mt-1.5">{t('inventoryManagement') || 'Part Details'}</p>
                </div>
              </div>
              <button onClick={() => { setIsServiceInventoryModalOpen(false); setEditingServiceInventory(null); setServiceInventoryForm({ name: '', category: 'Phone Parts', stock: 0, minStock: 5, buyPrice: 0, sellPrice: 0 }); }} className="p-2 text-slate-300 hover:text-rose-600 transition-all active:scale-95"><X size={24} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6 no-scrollbar pb-32 lg:pb-8">
              <form id="inventory-form" onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const finalData = {
                    ...serviceInventoryForm,
                    quantity: Number(serviceInventoryForm.stock),
                    updatedAt: serverTimestamp()
                  };
                  delete finalData.stock;

                  if (editingServiceInventory) {
                    await updateDoc(doc(db, 'inventory', editingServiceInventory.id), finalData);
                  } else {
                    await addDoc(collection(db, 'inventory'), { ...finalData, userId: user.uid, createdAt: serverTimestamp() });
                  }
                  setIsServiceInventoryModalOpen(false);
                  setEditingServiceInventory(null);
                  setServiceInventoryForm({ name: '', category: 'Phone Parts', stock: 0, minStock: 5, buyPrice: 0, sellPrice: 0 });
                } catch (err) { console.error(err); }
              }} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 pl-4">{t('itemName')}</label>
                  <input type="text" className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={serviceInventoryForm.name} onChange={e => setServiceInventoryForm({ ...serviceInventoryForm, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 pl-4">{t('category')}</label>
                  <select className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none appearance-none" value={serviceInventoryForm.category} onChange={e => setServiceInventoryForm({ ...serviceInventoryForm, category: e.target.value })}>
                    <option value="Phone Parts">{t('phoneParts')}</option>
                    <option value="PC Components">{t('pcComponents')}</option>
                    <option value="Accessories">{t('accessories')}</option>
                    <option value="Other">{t('other')}</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 pl-4">{t('quantity')}</label>
                    <input type="number" className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-black font-mono focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={serviceInventoryForm.stock} onChange={e => setServiceInventoryForm({ ...serviceInventoryForm, stock: Number(e.target.value) })} required />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 pl-4">{t('minStock')}</label>
                    <input type="number" className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-black font-mono focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={serviceInventoryForm.minStock} onChange={e => setServiceInventoryForm({ ...serviceInventoryForm, minStock: Number(e.target.value) })} required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 pl-4">{t('buyPrice')}</label>
                    <input type="number" step="0.01" className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-black font-mono focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={serviceInventoryForm.buyPrice} onChange={e => setServiceInventoryForm({ ...serviceInventoryForm, buyPrice: Number(e.target.value) })} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 pl-4">{t('sellPrice')}</label>
                    <input type="number" step="0.01" className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-black font-mono focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={serviceInventoryForm.sellPrice} onChange={e => setServiceInventoryForm({ ...serviceInventoryForm, sellPrice: Number(e.target.value) })} required />
                  </div>
                </div>
              </form>
            </div>

            <div className="p-8 border-t border-gray-100 bg-white lg:bg-gray-50 flex gap-4 shrink-0 sticky bottom-0 rounded-t-[2.5rem] lg:rounded-none shadow-[0_-20px_40px_rgba(255,255,255,0.8)]">
              <button type="button" onClick={() => { setIsServiceInventoryModalOpen(false); setEditingServiceInventory(null); }} className="flex-1 px-8 py-4 bg-white text-slate-400 rounded-2xl hover:bg-slate-50 font-black uppercase tracking-widest text-[10px] border border-slate-100 transition-all active:scale-95">{t('cancel')}</button>
              <button form="inventory-form" type="submit" className="flex-[2] bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all shadow-xl shadow-emerald-200 flex items-center justify-center gap-2 active:scale-95">
                <Save size={18} /> {editingServiceInventory ? t('saveChanges') : t('addStock')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barcode Print Configuration Modal */}
      {
        isPrintBarcodeModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[200] backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[2rem] w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-white/20">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 className="font-bold text-lg text-gray-900">{t('printWizard')}</h3>
                <button onClick={() => setIsPrintBarcodeModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
              </div>
              <div className="p-6 space-y-4">
                <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-2">
                  {printConfigs.map((cfg, idx) => (
                    <div key={cfg.item.id} className="flex items-center gap-4 bg-gray-50 p-3 rounded-xl border border-gray-100">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center border border-gray-200 overflow-hidden">
                        {cfg.item.photo ? <img src={cfg.item.photo} alt="" className="w-full h-full object-cover" /> : <Package size={20} className="text-gray-300" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-gray-900 truncate">{cfg.item.name}</p>
                        <p className="text-[10px] font-mono text-gray-400">{cfg.item.barcode || t('noBarcode') || 'No Barcode'}</p>
                      </div>
                      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1">
                        <button
                          type="button"
                          onClick={() => {
                            const newConfigs = [...printConfigs];
                            newConfigs[idx].qty = Math.max(0, (newConfigs[idx].qty || 0) - 1);
                            setPrintConfigs(newConfigs);
                          }}
                          className="p-1 hover:bg-gray-100 rounded text-gray-500"
                        >
                          <ChevronDown size={14} />
                        </button>
                        <input
                          type="number"
                          className="w-12 text-center text-sm font-bold bg-transparent outline-none"
                          value={cfg.qty}
                          onChange={(e) => {
                            const newConfigs = [...printConfigs];
                            newConfigs[idx].qty = parseInt(e.target.value) || 0;
                            setPrintConfigs(newConfigs);
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newConfigs = [...printConfigs];
                            newConfigs[idx].qty = (newConfigs[idx].qty || 0) + 1;
                            setPrintConfigs(newConfigs);
                          }}
                          className="p-1 hover:bg-gray-100 rounded text-gray-500"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-2xl border border-blue-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-600 text-white rounded-lg">
                      <Printer size={20} />
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase text-blue-600 tracking-widest">{t('totalLabels')}</p>
                      <p className="text-xl font-black text-blue-900">{printConfigs.reduce((sum, c) => sum + (c.qty || 0), 0)}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <p className="text-[10px] font-black uppercase text-blue-600 tracking-widest">{t('layoutMode')}</p>
                    <p className="text-sm font-bold text-blue-900 uppercase">{t(barcodePrintMode.toLowerCase()) || barcodePrintMode}</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setIsPrintBarcodeModalOpen(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-bold transition-all"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={() => {
                      handlePrintBatchBarcodes(printConfigs);
                      setIsPrintBarcodeModalOpen(false);
                    }}
                    className="flex-1 px-4 py-3 bg-slate-900 text-white rounded-xl hover:bg-black font-bold shadow-lg shadow-slate-900/20 transition-all flex items-center justify-center gap-2"
                  >
                    <Printer size={20} /> {t('printNow')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Employee Modal (Premium) */}
      {
        isAddModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[120] backdrop-blur-md p-0 sm:p-4 animate-in fade-in duration-300">
            <div className="bg-white sm:rounded-[2.5rem] w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-white/20 bottom-sheet flex flex-col">
              <div className="lg:hidden h-1.5 w-12 bg-gray-200 rounded-full mx-auto mt-3 mb-1 shrink-0"></div>
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-black text-lg text-slate-900 uppercase tracking-tight">{t('addEmployee')}</h3>
                <button onClick={() => setIsAddModalOpen(false)} className="p-2 text-slate-400 hover:text-rose-600 active:scale-90 transition-all"><X size={24} /></button>
              </div>
              <form onSubmit={handleAddEmployee} className="p-6 space-y-4 overflow-y-auto no-scrollbar pb-32 lg:pb-6">
                <div className="flex justify-center mb-4">
                  <label className="relative cursor-pointer group">
                    <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300 group-hover:border-blue-500 transition-colors">
                      {newEmployeeForm.photo ? <img src={newEmployeeForm.photo} alt="Preview" className="w-full h-full object-cover" /> : <Camera className="text-gray-400 group-hover:text-blue-500" size={32} />}
                    </div>
                    <input type="file" accept="image/*" onChange={handleNewEmployeeImage} className="hidden" />
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <input className="input-field" placeholder={t('name')} value={newEmployeeForm.name} onChange={e => setNewEmployeeForm({ ...newEmployeeForm, name: e.target.value })} required />
                  <input className="input-field" placeholder={t('role')} value={newEmployeeForm.role} onChange={e => setNewEmployeeForm({ ...newEmployeeForm, role: e.target.value })} required />
                </div>

                <select className="input-field" value={newEmployeeForm.dept} onChange={e => setNewEmployeeForm({ ...newEmployeeForm, dept: e.target.value })}>
                  {(shopSettings.departments || ['Security', 'Operations', 'HR', 'IT', 'Sales', 'Marketing', 'Kitchen', 'Service', 'Bar', 'Retail', 'Warehouse', 'Inventory', 'Accounts', 'Management', 'Cleaning', 'Maintenance']).map(d => (
                    <option key={d} value={d}>{translations[language]?.[d.toLowerCase()] || d}</option>
                  ))}
                </select>

                <select className="input-field" value={newEmployeeForm.location} onChange={e => setNewEmployeeForm({ ...newEmployeeForm, location: e.target.value })} required>
                  {sites.length === 0 ? (
                    <option value="">{t('noLocations') || 'No locations available - Please create a location first'}</option>
                  ) : (
                    <>
                      <option value="">{t('selectLocation') || 'Select Location'}</option>
                      {sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </>
                  )}
                </select>

                <select className="input-field" value={newEmployeeForm.shift} onChange={e => setNewEmployeeForm({ ...newEmployeeForm, shift: e.target.value })}>
                  {shifts.map(s => <option key={s} value={s}>{s === 'Morning (12 Hours)' ? t('morning12') : s === 'Night (12 Hours)' ? t('night12') : s}</option>)}
                </select>

                <div className="grid grid-cols-2 gap-4">
                  <input type="number" className="input-field" placeholder={t('salary')} value={newEmployeeForm.salary} onChange={e => setNewEmployeeForm({ ...newEmployeeForm, salary: Number(e.target.value) })} required />
                  <select className="p-2 border rounded-xl text-xs font-bold bg-white" value={newEmployeeForm.salaryMethod || 'Monthly'} onChange={e => setNewEmployeeForm({ ...newEmployeeForm, salaryMethod: e.target.value })}>
                    <option value="Monthly">{t('monthly')}</option>
                    <option value="Weekly">{t('weekly')}</option>
                    <option value="Daily">{t('daily')}</option>
                    <option value="Hourly">Hourly</option>
                  </select>
                </div>

                <div className="pt-2 grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-4 py-3 bg-slate-50 text-slate-400 rounded-2xl hover:bg-slate-100 font-black uppercase tracking-widest text-[9px] border border-slate-100 transition-all active:scale-95">{t('cancel')}</button>
                  <button type="submit" className="px-4 py-3 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 font-black uppercase tracking-widest text-[9px] shadow-lg shadow-blue-600/20 transition-all active:scale-95">{t('addEmployee')}</button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Site Modal (Premium) */}
      {
        isAddSiteModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[120] backdrop-blur-md p-0 sm:p-4 animate-in fade-in duration-300">
            <div className="bg-white sm:rounded-[2.5rem] w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-white/20 bottom-sheet flex flex-col">
              <div className="lg:hidden h-1.5 w-12 bg-gray-200 rounded-full mx-auto mt-3 mb-1 shrink-0"></div>
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-black text-lg text-slate-900 uppercase tracking-tight">{t('createLocation')}</h3>
                <button onClick={() => setIsAddSiteModalOpen(false)} className="p-2 text-slate-400 hover:text-rose-600 active:scale-90 transition-all"><X size={24} /></button>
              </div>
              <form onSubmit={handleAddSite} className="p-6 space-y-4 overflow-y-auto no-scrollbar pb-32 lg:pb-6">
                <input className="input-field" placeholder={t('siteName')} value={newSiteForm.name} onChange={e => setNewSiteForm({ ...newSiteForm, name: e.target.value })} required />
                <input className="input-field" placeholder={t('city')} value={newSiteForm.city} onChange={e => setNewSiteForm({ ...newSiteForm, city: e.target.value })} required />
                <input className="input-field" placeholder={t('manager')} value={newSiteForm.manager} onChange={e => setNewSiteForm({ ...newSiteForm, manager: e.target.value })} />
                <input className="input-field" placeholder={t('phone') || 'Phone'} value={newSiteForm.phone || ''} onChange={e => setNewSiteForm({ ...newSiteForm, phone: e.target.value })} />

                <select className="input-field" value={newSiteForm.status} onChange={e => setNewSiteForm({ ...newSiteForm, status: e.target.value })}>
                  <option value="Operational">{t('operational')}</option>
                  <option value="Renovating">{t('renovating')}</option>
                  <option value="Active">{t('active')}</option>
                  <option value="Closed">{t('closed')}</option>
                </select>

                <div className="pt-2 grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setIsAddSiteModalOpen(false)} className="px-4 py-3 bg-slate-50 text-slate-400 rounded-2xl hover:bg-slate-100 font-black uppercase tracking-widest text-[9px] border border-slate-100 transition-all active:scale-95">{t('cancel')}</button>
                  <button type="submit" className="px-4 py-3 bg-emerald-600 text-white rounded-2xl hover:bg-emerald-700 font-black uppercase tracking-widest text-[9px] shadow-lg shadow-emerald-600/20 transition-all active:scale-95">{t('createLocation')}</button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Detail Sidebar (Employee/Site) */}
      {
        (selectedEmployee || selectedSite) && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md z-[1040] md:hidden animate-in fade-in duration-300" onClick={() => { setSelectedEmployee(null); setSelectedSite(null); }}></div>
        )
      }
      {
        (selectedEmployee || selectedSite) && (
          <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-2xl z-[1050] transform transition-transform duration-500 ease-out border-l border-gray-100 flex flex-col translate-x-0">
            <div className="p-6 border-b-2 border-slate-100 flex justify-between items-center shrink-0 bg-white sticky top-0 z-[1060]">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-3 h-3 rounded-full bg-blue-600 animate-pulse shadow-lg shadow-blue-200"></div>
                <h3 className="font-black text-slate-800 uppercase tracking-[0.2em] text-xs">
                  {selectedEmployee ? t('employeeDetails') : t('locationDetails')}
                </h3>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedEmployee(null);
                  setSelectedSite(null);
                }}
                className="w-14 h-14 flex items-center justify-center bg-rose-600 text-white hover:bg-rose-700 rounded-2xl shadow-xl transition-all active:scale-90 cursor-pointer group hover:rotate-90"
                aria-label="Close"
              >
                <X size={28} strokeWidth={3} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar p-6 pb-32 space-y-8">
              {selectedEmployee && (
                <div className="space-y-8">
                  <div className="flex flex-col items-center">
                    <div className="relative group p-1 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-4">
                      <div className="w-24 h-24 rounded-full bg-white overflow-hidden border-2 border-white shadow-inner">
                        {selectedEmployee.photo ? <img src={selectedEmployee.photo} alt={selectedEmployee.name} className="w-full h-full object-cover" /> : <User size={40} className="text-slate-200 m-auto mt-6" />}
                      </div>
                      <label className="absolute bottom-1 right-1 bg-white p-2 rounded-full text-blue-600 shadow-lg cursor-pointer hover:bg-blue-50 transition-all border border-blue-100 active:scale-90">
                        <Camera size={14} />
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleUpdateEmployeeImage(selectedEmployee.id, e)} />
                      </label>
                    </div>
                    <input
                      className="text-xl font-black text-slate-900 text-center bg-transparent border-b-2 border-transparent focus:border-blue-500 focus:outline-none transition-all w-full uppercase tracking-tight"
                      value={selectedEmployee.name}
                      onChange={(e) => handleUpdateEmployee(selectedEmployee.id, 'name', e.target.value)}
                    />
                    <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest mt-2 px-3 py-1 bg-blue-50 rounded-full">{t(selectedEmployee.role.toLowerCase()) || selectedEmployee.role}</span>
                  </div>

                  <div className="space-y-4">
                    <div className="p-5 bg-slate-50 rounded-[2rem] border border-slate-100 space-y-4">
                      <div className="flex justify-between items-center py-1">
                        <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{t('dept')}</span>
                        <select
                          className="text-xs font-black bg-transparent text-right outline-none text-slate-700"
                          value={selectedEmployee.dept || ''}
                          onChange={(e) => handleUpdateEmployee(selectedEmployee.id, 'dept', e.target.value)}
                        >
                          {(shopSettings.departments || ['Security', 'Operations', 'HR', 'IT', 'Sales', 'Marketing', 'Kitchen', 'Service', 'Bar', 'Retail', 'Warehouse', 'Inventory', 'Accounts', 'Management', 'Cleaning', 'Maintenance']).map(d => (
                            <option key={d} value={d}>{translations[language]?.[d.toLowerCase()] || d}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{t('location')}</span>
                        <select className="text-xs font-black bg-transparent text-right outline-none text-slate-700" value={selectedEmployee.location || ''} onChange={(e) => handleUpdateEmployee(selectedEmployee.id, 'location', e.target.value)}>
                          <option value="">{t('selectLocation')}</option>
                          {sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{t('shift')}</span>
                        <select className="text-xs font-black bg-transparent text-right outline-none text-slate-700" value={selectedEmployee.shift} onChange={(e) => handleUpdateEmployee(selectedEmployee.id, 'shift', e.target.value)}>
                          {shifts.map(s => <option key={s} value={s}>{s === 'Morning (12 Hours)' ? t('morning12') : s === 'Night (12 Hours)' ? t('night12') : s}</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="p-5 bg-blue-50/50 rounded-[2rem] border border-blue-100 space-y-4">
                      <h4 className="text-[10px] font-black text-blue-900 uppercase tracking-widest flex items-center gap-2 mb-2"><DollarSign size={14} /> {t('compensation')}</h4>
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-blue-700">{t('salary')}</span>
                        <input type="number" className="w-24 text-right bg-white rounded-lg px-2 py-1.5 text-xs font-mono border border-blue-100" value={selectedEmployee.salary} onChange={(e) => handleUpdateEmployee(selectedEmployee.id, 'salary', Number(e.target.value))} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-red-500">{t('advance')}</span>
                        <input type="number" className="w-24 text-right bg-white rounded-lg px-2 py-1.5 text-xs font-mono border border-red-100 text-red-600" value={selectedEmployee.advanceSalary} onChange={(e) => handleUpdateEmployee(selectedEmployee.id, 'advanceSalary', Number(e.target.value))} />
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleDeleteEmployee(selectedEmployee.id)}
                    className="w-full bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white py-4 rounded-3xl font-black uppercase tracking-widest text-[10px] transition-all flex items-center justify-center gap-2 group"
                  >
                    <Trash2 size={16} className="group-hover:animate-bounce" /> {t('terminateService')}
                  </button>
                </div>
              )}

              {selectedSite && !selectedEmployee && (
                <div className="space-y-8 animate-in slide-in-from-right-10 duration-500">
                  <div className="space-y-6">
                    <div className="p-6 bg-slate-50/80 rounded-[2rem] border border-slate-100 space-y-5">
                      <div className="flex flex-col gap-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">{t('siteName')}</label>
                        <input className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl text-sm font-black uppercase tracking-tight focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={selectedSite.name} onChange={(e) => handleUpdateSite(selectedSite.id, 'name', e.target.value)} />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">{t('city')}</label>
                        <input className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl text-sm font-black uppercase tracking-tight focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={selectedSite.city} onChange={(e) => handleUpdateSite(selectedSite.id, 'city', e.target.value)} />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">{t('manager')}</label>
                        <input className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl text-sm font-black uppercase tracking-tight focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={selectedSite.manager} onChange={(e) => handleUpdateSite(selectedSite.id, 'manager', e.target.value)} />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">{t('operationalStatus')}</label>
                        <select className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl text-sm font-black uppercase tracking-widest focus:ring-4 focus:ring-blue-500/10 transition-all outline-none" value={selectedSite.status} onChange={(e) => handleUpdateSite(selectedSite.id, 'status', e.target.value)}>
                          <option value="Operational">{t('operational')}</option>
                          <option value="Renovating">{t('renovating')}</option>
                          <option value="Active">{t('active')}</option>
                          <option value="Closed">{t('closed')}</option>
                        </select>
                      </div>
                    </div>

                    <div className="p-6 bg-emerald-50/50 rounded-[2rem] border border-emerald-100 space-y-4">
                      <h4 className="text-[10px] font-black text-emerald-900 uppercase tracking-widest flex items-center gap-2 mb-2"><Users size={16} /> {t('assignedStaff') || 'Assigned Staff'}</h4>
                      <div className="space-y-3">
                        {employees.filter(e => e.location === selectedSite.name).map(e => (
                          <div key={e.id} className="flex items-center justify-between bg-white/80 p-3 rounded-xl border border-emerald-50 shadow-sm">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-[10px] font-black text-emerald-700">{e.name?.[0].toUpperCase()}</div>
                              <span className="text-xs font-black text-slate-700 uppercase tracking-tight">{e.name}</span>
                            </div>
                            <span className="text-[8px] font-black text-emerald-500 uppercase px-2 py-0.5 bg-emerald-50 rounded-full">{t(e.role?.toLowerCase()) || e.role}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={() => handleDeleteSite(selectedSite.id, selectedSite.name)}
                      className="w-full bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white py-4 rounded-3xl font-black uppercase tracking-widest text-[10px] transition-all flex items-center justify-center gap-2"
                    >
                      <Trash2 size={16} /> {t('deleteLocation')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      }

      {/* Settings Modal (Premium with Scroll Padding) */}
      {
        showSettings && (
          <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-[150] backdrop-blur-xl p-0 sm:p-4 animate-in fade-in duration-300">
            <div className="bg-white sm:rounded-[2.5rem] w-full max-w-2xl h-full sm:h-[85vh] shadow-2xl overflow-hidden flex flex-col border border-white/20 relative">

              <div className="flex-1 overflow-y-auto p-6 sm:p-8 pb-32 sm:pb-8">
                {/* Header - Fixed */}
                <div className="p-6 border-b border-gray-100 flex-shrink-0 flex justify-between items-center bg-white z-10">
                  <h3 className="text-xl font-bold flex items-center gap-2 text-gray-800">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Settings className="text-slate-600" size={24} />
                    </div>
                    {t('settings')}
                  </h3>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Scrollable Content */}
                <div className="p-6 overflow-y-auto custom-scrollbar">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* Column 1: Profile & Localization */}
                    <div className="space-y-6">
                      {/* My Profile */}
                      <div className="p-5 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                        <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b border-gray-50 pb-2">
                          <User size={18} className="text-blue-600" /> {t('myProfile')}
                        </h4>
                        <div className="space-y-3 mb-4">
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('emailLabel')}</span>
                            <span className="text-sm font-medium text-gray-900 break-all">{user?.email}</span>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('userIdLabel')}</span>
                            <span className="text-xs font-mono bg-gray-50 px-2 py-1 rounded border border-gray-200 break-all select-all text-gray-600">{user?.uid}</span>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('accountTypeLabel')}</span>
                            <span className="self-start bg-blue-100 text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-bold">{t('admin')}</span>
                          </div>
                        </div>

                        <div className="pt-4 border-t border-gray-50">
                          <h5 className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">{t('accountHandling')}</h5>
                          <button onClick={handleLinkGoogle} className="w-full bg-white border border-gray-300 text-gray-700 font-bold py-2.5 rounded-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2 mb-2 shadow-sm">
                            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" alt="Google" />
                            {t('connectGoogle')}
                          </button>
                          <p className="text-[10px] text-gray-400 text-center px-2">{t('linkGoogleDesc')}</p>
                        </div>
                      </div>

                      {/* Localization */}
                      <div className="p-5 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                        <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b border-gray-50 pb-2">
                          <Globe size={18} className="text-indigo-600" /> {t('localization')}
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">{t('language')}</label>
                            <select
                              value={language}
                              onChange={(e) => {
                                const newLang = e.target.value;
                                setLanguage(newLang);
                                saveUserSettings({ language: newLang });
                              }}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                            >
                              <option value="en">English</option>
                              <option value="hi">हिंदी</option>
                              <option value="ar">العربية</option>
                              <option value="zh">中文</option>
                              <option value="ur">اردو</option>
                              <option value="bn">বাংলা</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">{t('currency')}</label>
                            <select
                              value={currency}
                              onChange={(e) => {
                                const newCurrency = e.target.value;
                                setCurrency(newCurrency);
                                saveUserSettings({ currency: newCurrency });
                              }}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                            >
                              <option value="EGP">EGP</option>
                              <option value="USD">USD</option>
                              <option value="EUR">EUR</option>
                              <option value="GBP">GBP</option>
                              <option value="SAR">SAR</option>
                              <option value="AED">AED</option>
                              <option value="INR">INR</option>
                              <option value="CNY">CNY</option>
                              <option value="JPY">JPY</option>
                              <option value="CAD">CAD</option>
                              <option value="AUD">AUD</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Column 2: Shop, Security, Backup, Danger */}
                    <div className="space-y-6">
                      {/* Shop Settings */}
                      <div className="p-5 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                        <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b border-gray-50 pb-2">
                          <Building2 size={18} className="text-orange-600" /> {t('shopSettings')}
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">{t('shopName')}</label>
                            <input
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                              placeholder="My Shop"
                              value={shopSettings.name}
                              autoComplete="off"
                              name="shop_name_no_autofill"
                              onChange={(e) => setShopSettings({ ...shopSettings, name: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">{t('shopAddress')}</label>
                            <input
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                              placeholder="123 Main St"
                              value={shopSettings.address}
                              autoComplete="off"
                              name="shop_address_no_autofill"
                              onChange={(e) => setShopSettings({ ...shopSettings, address: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">{t('shopPhone')}</label>
                            <input
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                              placeholder="+1 234..."
                              value={shopSettings.phone}
                              autoComplete="off"
                              name="shop_phone_no_autofill"
                              onChange={(e) => setShopSettings({ ...shopSettings, phone: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">{t('upiId') || 'UPI ID'} (for India)</label>
                            <input
                              className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-blue-50/30"
                              placeholder="example@upi"
                              value={shopSettings.upiId || ''}
                              onChange={(e) => setShopSettings({ ...shopSettings, upiId: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">{t('instapayId') || 'InstaPay ID'}</label>
                            <input
                              className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-blue-50/30"
                              placeholder="InstaPay ID"
                              value={shopSettings.instapayId || ''}
                              onChange={(e) => setShopSettings({ ...shopSettings, instapayId: e.target.value })}
                            />
                          </div>
                          <div className="pt-2">
                            <div className="mb-4 bg-white p-5 rounded-2xl border border-gray-100 shadow-sm transition-all hover:border-blue-200">
                              <label className="flex items-center justify-between cursor-pointer group">
                                <div className="flex items-center gap-3">
                                  <div className={`p-2 rounded-xl transition-colors ${shopSettings.gstEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                                    <Calculator size={18} />
                                  </div>
                                  <div>
                                    <span className="text-sm font-black text-gray-800 tracking-tight uppercase block">{t('enableGst')}</span>
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{t('taxableAmtMgmt')}</span>
                                  </div>
                                </div>
                                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${shopSettings.gstEnabled ? 'bg-blue-600' : 'bg-gray-300'}`}>
                                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${shopSettings.gstEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                  <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={shopSettings.gstEnabled || false}
                                    onChange={(e) => setShopSettings({ ...shopSettings, gstEnabled: e.target.checked })}
                                  />
                                </div>
                              </label>

                              {shopSettings.gstEnabled && (
                                <div className="mt-5 grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 focus-within:border-blue-300 focus-within:ring-4 focus-within:ring-blue-50 transition-all">
                                    <label className="block text-[10px] uppercase font-black tracking-widest text-slate-400 mb-2">{t('gstNumber')}</label>
                                    <input
                                      className="w-full bg-transparent text-sm font-bold text-slate-800 placeholder-slate-300 outline-none"
                                      placeholder={t('gstPlaceholder')}
                                      value={shopSettings.gstNumber || ''}
                                      onChange={(e) => setShopSettings({ ...shopSettings, gstNumber: e.target.value })}
                                    />
                                  </div>
                                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 focus-within:border-blue-300 focus-within:ring-4 focus-within:ring-blue-50 transition-all">
                                    <label className="block text-[10px] uppercase font-black tracking-widest text-slate-400 mb-2">{t('gstPercent')}</label>
                                    <div className="flex items-center">
                                      <input
                                        type="number"
                                        className="w-full bg-transparent text-sm font-bold text-slate-800 placeholder-slate-300 outline-none"
                                        placeholder="18"
                                        value={shopSettings.gstPercent || ''}
                                        onChange={(e) => setShopSettings({ ...shopSettings, gstPercent: Number(e.target.value) })}
                                      />
                                      <span className="text-slate-400 font-bold ml-2">%</span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">{t('shopLogo') || 'Shop Logo'}</label>
                            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                              <div className="h-16 w-16 bg-white rounded-2xl flex items-center justify-center overflow-hidden border border-slate-100 shadow-sm shrink-0">
                                {shopSettings.logo ? (
                                  <img src={shopSettings.logo} alt="Logo preview" className="w-full h-full object-contain" />
                                ) : (
                                  <Shield size={20} className="text-slate-300" />
                                )}
                              </div>
                              <div className="flex-1 space-y-2">
                                <input
                                  type="file"
                                  id="shop-logo-upload"
                                  className="hidden"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      if (file.size > 2 * 1024 * 1024) {
                                        alert(t('logoSizeError'));
                                        return;
                                      }
                                      const reader = new FileReader();
                                      reader.onloadend = () => {
                                        setShopSettings({ ...shopSettings, logo: reader.result });
                                      };
                                      reader.readAsDataURL(file);
                                    }
                                  }}
                                />
                                <label htmlFor="shop-logo-upload" className="block text-center py-2 px-4 bg-white border border-slate-200 text-slate-700 text-[10px] font-black uppercase tracking-widest rounded-lg cursor-pointer hover:bg-slate-50 hover:border-blue-300 transition-all">
                                  {shopSettings.logo ? (t('changeLogo') || 'Change Logo') : (t('uploadLogo') || 'Upload Logo')}
                                </label>
                                {shopSettings.logo && (
                                  <button
                                    type="button"
                                    onClick={() => setShopSettings({ ...shopSettings, logo: '' })}
                                    className="w-full text-[9px] font-black text-rose-500 uppercase tracking-widest hover:underline"
                                  >
                                    {t('removeLogo') || 'Remove Logo'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              if (!user) return;
                              try {
                                await setDoc(doc(db, 'settings', 'shop_' + user.uid), shopSettings);
                                alert(t('shopSettingsSaved'));
                              } catch (e) {
                                console.error(e);
                                alert(t('shopSettingsError'));
                              }
                            }}
                            className="w-full bg-gray-900 text-white font-bold py-2 rounded-lg hover:bg-black transition-colors text-sm mt-2"
                          >
                            {t('updateSettings')}
                          </button>
                        </div>
                      </div>

                      {/* Security */}
                      <div className="p-5 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                        <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b border-gray-50 pb-2">
                          <Shield size={18} className="text-teal-600" /> {t('changePin')} & {t('setSecurityQuestion')}
                        </h4>

                        {/* Multi-Mode PIN Management */}
                        <div className="space-y-4 pt-2 border-t border-gray-100">
                          <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{t('pinManagement') || 'PIN Management'}</h4>

                          {/* Cashier PIN */}
                          <div>
                            <label className="block text-[10px] font-black uppercase text-gray-500 mb-1">{t('cashierPin') || 'Cashier PIN'}</label>
                            <input
                              type="password"
                              placeholder={securityPin === '1234' ? t('setNewPin') : '****'}
                              maxLength={4}
                              className="w-full px-3 py-2 border border-blue-100 rounded-lg text-sm bg-blue-50/30 focus:ring-2 focus:ring-blue-500 outline-none"
                              onBlur={(e) => {
                                if (e.target.value.length === 4) {
                                  if (window.confirm("Update Cashier PIN?")) {
                                    setSecurityPin(e.target.value);
                                    saveUserSettings({ securityPin: e.target.value });
                                    e.target.value = '';
                                    alert(t('pinChanged'));
                                  }
                                }
                              }}
                            />
                          </div>

                          {/* Manager & Owner PINs (Owner Only) */}
                          {currentMode === 'Owner' ? (
                            <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-500">
                              <div>
                                <label className="block text-[10px] font-black uppercase text-amber-600 mb-1">{t('managerPin') || 'Manager PIN'}</label>
                                <input
                                  type="password"
                                  placeholder={managerPin === '8888' ? "Set Manager PIN" : "****"}
                                  maxLength={4}
                                  className="w-full px-3 py-2 border border-amber-100 rounded-lg text-sm bg-amber-50/30 focus:ring-2 focus:ring-amber-500 outline-none"
                                  onBlur={(e) => {
                                    if (e.target.value.length === 4) {
                                      setManagerPin(e.target.value);
                                      saveUserSettings({ managerPin: e.target.value });
                                      e.target.value = '';
                                      alert("Manager PIN Restricted.");
                                    }
                                  }}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-black uppercase text-rose-600 mb-1">{t('ownerPin') || 'Owner PIN'}</label>
                                <input
                                  type="password"
                                  placeholder={ownerPin === '0000' ? "Set Owner PIN" : "****"}
                                  maxLength={4}
                                  className="w-full px-3 py-2 border border-rose-100 rounded-lg text-sm bg-rose-50/30 focus:ring-2 focus:ring-rose-500 outline-none"
                                  onBlur={(e) => {
                                    if (e.target.value.length === 4) {
                                      setOwnerPin(e.target.value);
                                      saveUserSettings({ ownerPin: e.target.value });
                                      e.target.value = '';
                                      alert("Owner PIN Restricted.");
                                    }
                                  }}
                                />
                              </div>
                               {/* Pending Registrations [Admin View] */}
                               <div className="pt-4 border-t border-gray-100 mt-6 bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                                 <h4 className="text-[10px] font-bold text-slate-500 mb-2 flex items-center gap-1 uppercase tracking-tight">
                                   <Users size={12} /> {t('pendingRepairs') || "Pending Registrations"}
                                 </h4>
                                 <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                                    {pendingRegistrations.length === 0 ? (
                                      <p className="text-[9px] text-gray-400 italic font-medium px-1">No pending registrations found.</p>
                                    ) : pendingRegistrations.map((reg) => (
                                      <div key={reg.id} className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm flex flex-col gap-1 transition-all hover:border-blue-300">
                                        <div className="flex justify-between items-center whitespace-nowrap overflow-hidden">
                                          <span className="text-[10px] font-black text-slate-800 break-all">{reg.userEmail}</span>
                                          <span className="bg-blue-100 text-blue-800 text-[8px] px-1.5 py-0.5 rounded-full font-black uppercase">{reg.otp}</span>
                                        </div>
                                        <div className="flex justify-between items-end gap-1">
                                          <span className="text-[8px] text-slate-400 font-mono opacity-80 select-all truncate">#{reg.registrationId}</span>
                                          <button
                                            onClick={async () => {
                                              if (window.confirm("Delete this registration record?")) {
                                                try { await deleteDoc(doc(db, 'registrations', reg.id)); } catch (e) { alert(e.message); }
                                              }
                                            }}
                                            className="text-[8px] font-black text-rose-500 uppercase tracking-widest hover:underline whitespace-nowrap"
                                          >
                                            Dismiss
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                 </div>
                               </div>
                            </div>
                          ) : (
                            <div className="mt-4 p-4 bg-slate-50 border border-slate-100 rounded-xl italic text-[10px] text-slate-400">
                              {t('ownerOnlyPins') || 'Switch to Owner Mode to manage Manager/Owner PINs.'}
                            </div>
                          )}
                        </div>

                        {/* Security Question */}
                        <div className="space-y-2 pt-2 border-t border-gray-100">
                          <select
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-teal-500 outline-none"
                            value={securityQuestion}
                            onChange={(e) => setSecurityQuestion(e.target.value)}
                          >
                            <option value="">{t('securityQuestion')}</option>
                            <option value="secQ_pet">{t('secQ_pet')}</option>
                            <option value="secQ_mother">{t('secQ_mother')}</option>
                            <option value="secQ_city">{t('secQ_city')}</option>
                            <option value="secQ_school">{t('secQ_school')}</option>
                          </select>
                          <input
                            type="text"
                            placeholder={t('securityAnswer')}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                            value={securityAnswer}
                            onChange={(e) => setSecurityAnswer(e.target.value)}
                          />
                          <button
                            onClick={() => {
                              if (!securityQuestion || !securityAnswer) {
                                alert(t('Please select a question and answer!'));
                                return;
                              }
                              saveUserSettings({ securityQuestion, securityAnswer });
                              alert(t('saveSettings') + ' Success!');
                            }}
                            className="w-full bg-teal-600 text-white py-2 rounded-lg text-sm font-bold hover:bg-teal-700 shadow-sm"
                          >
                            {t('saveSettings')}
                          </button>
                        </div>
                      </div>

                      {/* Backup & Danger Zone Group */}
                      <div className="grid grid-cols-1 gap-4">
                        {/* Backup */}
                        <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                          <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2 text-sm">
                            <Database size={16} /> {t('backupRestore')}
                          </h4>
                          <div className="flex gap-2">
                            <button onClick={handleExportBackup} disabled={loading} className="flex-1 bg-white text-blue-700 border border-blue-200 font-bold py-1.5 rounded-lg hover:bg-blue-100 flex items-center justify-center gap-1 text-xs shadow-sm">
                              <Download size={14} /> {t('export')}
                            </button>
                            <label className="flex-1 bg-blue-600 text-white font-bold py-1.5 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-1 text-xs cursor-pointer shadow-sm">
                              <Upload size={14} /> {t('import')}
                              <input type="file" accept=".json" onChange={handleImportBackup} disabled={loading} className="hidden" />
                            </label>
                          </div>
                        </div>

                        {/* Danger */}
                        <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                          <h4 className="font-bold text-red-900 mb-2 flex items-center gap-2 text-sm">
                            <AlertCircle size={16} /> {t('dangerZone')}
                          </h4>
                          <button onClick={handleFactoryReset} className="w-full bg-white text-red-600 border border-red-200 font-bold py-2 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-2 text-xs shadow-sm">
                            <Trash2 size={14} /> {t('factoryReset')}
                          </button>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Add Attendance Modal */}
      {
        isAddAttendanceModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[130] backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 className="font-bold text-lg text-gray-900">{t('addAttendance')}</h3>
                <button onClick={() => setIsAddAttendanceModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
              </div>
              <form onSubmit={handleAddAttendance} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('location')}</label>
                  <select
                    className="input-field"
                    value={newAttendanceForm.locationFilter || ''}
                    onChange={e => setNewAttendanceForm({ ...newAttendanceForm, locationFilter: e.target.value, employeeId: '' })}
                  >
                    <option value="">{t('filterAll')}</option>
                    {sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('selectEmployee')}</label>
                  <select className="input-field" value={newAttendanceForm.employeeId} onChange={e => setNewAttendanceForm({ ...newAttendanceForm, employeeId: e.target.value })} required>
                    <option value="">{t('selectEmployee')}</option>
                    {employees
                      .filter(e => !newAttendanceForm.locationFilter || e.location === newAttendanceForm.locationFilter)
                      .map(e => <option key={e.id} value={e.id}>{e.name} ({e.location})</option>)
                    }
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('selectDate')}</label>
                  <input type="date" className="input-field" value={newAttendanceForm.date} onChange={e => setNewAttendanceForm({ ...newAttendanceForm, date: e.target.value })} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('selectStatus')}</label>
                  <select className="input-field" value={newAttendanceForm.status} onChange={e => setNewAttendanceForm({ ...newAttendanceForm, status: e.target.value })}>
                    <option value="On Time">On Time</option>
                    <option value="Late">Late</option>
                    <option value="Absent">Absent</option>
                  </select>
                </div>

                {newAttendanceForm.status === 'Late' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('lateHours')}</label>
                    <input
                      type="number"
                      className="input-field"
                      min="0"
                      step="0.5"
                      value={newAttendanceForm.lateHours || ''}
                      onChange={e => setNewAttendanceForm({ ...newAttendanceForm, lateHours: e.target.value })}
                      required
                    />
                  </div>
                )}

                {/* Daily Replacement Dropdown */}
                {newAttendanceForm.employeeId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('replacementFor')} ({t('optional')})</label>
                    <select
                      className="input-field"
                      value={newAttendanceForm.replacementFor || ''}
                      onChange={e => setNewAttendanceForm({ ...newAttendanceForm, replacementFor: e.target.value })}
                    >
                      <option value="">{t('none')}</option>
                      {(() => {
                        const selectedEmp = employees.find(e => e.id.toString() === newAttendanceForm.employeeId.toString());
                        if (!selectedEmp) return null;

                        const candidates = employees.filter(e => e.id !== selectedEmp.id);
                        const locations = [...new Set(candidates.map(e => e.location))].sort();

                        return locations.map(loc => (
                          <optgroup key={loc} label={loc}>
                            {candidates.filter(e => e.location === loc).map(e => (
                              <option key={e.id} value={e.name}>{e.name}</option>
                            ))}
                          </optgroup>
                        ));
                      })()}
                    </select>
                  </div>
                )}

                <div className="pt-2 flex gap-3">
                  <button type="button" onClick={() => setIsAddAttendanceModalOpen(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">{t('cancel')}</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-lg shadow-blue-600/20">{t('save')}</button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Edit Attendance Modal */}
      {
        editingAttendance && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[130] backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 className="font-bold text-lg text-gray-900">{t('editAttendance')}</h3>
                <button onClick={() => setEditingAttendance(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
              </div>
              <form onSubmit={handleUpdateAttendance} className="p-6 space-y-4">
                <div>
                  <h4 className="text-lg font-bold text-gray-800">{editingAttendance.name}</h4>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('selectDate')}</label>
                  <input type="date" className="input-field" value={editingAttendance.date} onChange={e => setEditingAttendance({ ...editingAttendance, date: e.target.value })} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('selectStatus')}</label>
                  <select className="input-field" value={editingAttendance.status} onChange={e => setEditingAttendance({ ...editingAttendance, status: e.target.value })}>
                    <option value="On Time">{t('onTimeLabel')}</option>
                    <option value="Late">{t('lateLabel')}</option>
                    <option value="Absent">{t('absentLabel')}</option>
                  </select>
                </div>

                {editingAttendance.status === 'Late' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('lateHours')}</label>
                    <input
                      type="number"
                      className="input-field"
                      min="0"
                      step="0.5"
                      value={editingAttendance.lateHours || ''}
                      onChange={e => setEditingAttendance({ ...editingAttendance, lateHours: e.target.value })}
                      required
                    />
                  </div>
                )}

                {/* Edit Replacement */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('replacementFor')} ({t('optional') || 'Optional'})</label>
                  <select
                    className="input-field"
                    value={editingAttendance.replacementFor || ''}
                    onChange={e => setEditingAttendance({ ...editingAttendance, replacementFor: e.target.value })}
                  >
                    <option value="">{t('none')}</option>
                    {(() => {
                      const recName = editingAttendance.name;
                      const candidates = employees.filter(e => e.name !== recName);
                      const locations = [...new Set(candidates.map(e => e.location))].sort();

                      return locations.map(loc => (
                        <optgroup key={loc} label={loc}>
                          {candidates.filter(e => e.location === loc).map(e => (
                            <option key={e.id} value={e.name}>{e.name}</option>
                          ))}
                        </optgroup>
                      ));
                    })()}
                  </select>
                </div>

                <div className="pt-2 flex gap-3">
                  <button type="button" onClick={() => setEditingAttendance(null)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">{t('cancel')}</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-lg shadow-blue-600/20">{t('save')}</button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Add Account Modal */}
      {
        isAddAccountModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[130] backdrop-blur-md p-0 sm:p-4 animate-in fade-in duration-300">
            <div className="bg-white sm:rounded-[2.5rem] w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-white/20 bottom-sheet flex flex-col">
              <div className="lg:hidden h-1.5 w-12 bg-gray-200 rounded-full mx-auto mt-3 mb-1 shrink-0"></div>
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-black text-lg text-slate-900 uppercase tracking-tight">{t('addAccount')}</h3>
                <button onClick={() => setIsAddAccountModalOpen(false)} className="p-2 text-slate-400 hover:text-rose-600 active:scale-90 transition-all"><X size={24} /></button>
              </div>
              <form onSubmit={handleAddAccount} className="p-6 space-y-4 overflow-y-auto no-scrollbar pb-32 lg:pb-6">
                <input className="input-field" placeholder={t('accountName')} value={newAccountForm.name} onChange={e => setNewAccountForm({ ...newAccountForm, name: e.target.value })} required />

                <select className="input-field" value={newAccountForm.type} onChange={e => setNewAccountForm({ ...newAccountForm, type: e.target.value })}>
                  <option value="Asset">Asset</option>
                  <option value="Liability">Liability</option>
                  <option value="Equity">Equity</option>
                  <option value="Revenue">Revenue</option>
                  <option value="Expense">Expense</option>
                </select>

                <input type="number" className="input-field" placeholder={t('initialBalance')} value={newAccountForm.balance} onChange={e => setNewAccountForm({ ...newAccountForm, balance: Number(e.target.value) })} />

                <div className="pt-2 flex gap-3">
                  <button type="button" onClick={() => setIsAddAccountModalOpen(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">{t('cancel')}</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-lg shadow-blue-600/20">Create Account</button>
                </div>
              </form>
            </div>
          </div>
        )
      }
      {/* Add / Edit Item Modal */}
      {
        isAddItemModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[130] backdrop-blur-md p-0 sm:p-4 animate-in fade-in duration-300">
            <div className="bg-white sm:rounded-[2.5rem] w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-white/20 bottom-sheet flex flex-col">
              <div className="lg:hidden h-1.5 w-12 bg-gray-200 rounded-full mx-auto mt-3 mb-1 shrink-0"></div>
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-black text-lg text-slate-900 uppercase tracking-tight">{editingItem ? t('editItem') : t('addInventoryItem')}</h3>
                <button onClick={() => { setIsAddItemModalOpen(false); setEditingItem(null); }} className="p-2 text-slate-400 hover:text-rose-600 active:scale-90 transition-all"><X size={24} /></button>
              </div>
              <form onSubmit={editingItem ? handleUpdateItem : handleAddItem} className="p-6 space-y-4 overflow-y-auto no-scrollbar pb-32 lg:pb-6">
                {/* Image Upload */}
                <div className="flex justify-center mb-4">
                  <label className="relative cursor-pointer group">
                    <div className="w-24 h-24 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300 group-hover:border-blue-500 transition-colors">
                      {(editingItem ? editingItem.photo : newItemForm.photo) ? (
                        <img src={editingItem ? editingItem.photo : newItemForm.photo} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <Camera className="text-gray-400 group-hover:text-blue-500" size={32} />
                      )}
                    </div>
                    <input type="file" accept="image/*" onChange={handleNewItemImage} className="hidden" />
                  </label>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">{t('itemName')}</label>
                  <input className="input-field" placeholder={t('itemName')} value={editingItem ? editingItem.name : newItemForm.name} onChange={e => editingItem ? setEditingItem({ ...editingItem, name: e.target.value }) : setNewItemForm({ ...newItemForm, name: e.target.value })} required />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">{t('location')}</label>
                  <select className="input-field" value={editingItem ? editingItem.location : newItemForm.location} onChange={e => editingItem ? setEditingItem({ ...editingItem, location: e.target.value }) : setNewItemForm({ ...newItemForm, location: e.target.value })} required>
                    <option value="">{t('selectLocation')}</option>
                    {sites.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('buyPrice')}</label>
                    <input type="number" className="input-field" placeholder="0.00" value={editingItem ? editingItem.buyPrice : newItemForm.buyPrice} onChange={e => editingItem ? setEditingItem({ ...editingItem, buyPrice: Number(e.target.value) }) : setNewItemForm({ ...newItemForm, buyPrice: Number(e.target.value) })} required />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('sellPrice')}</label>
                    <input type="number" className="input-field" placeholder="0.00" value={editingItem ? editingItem.sellPrice : newItemForm.sellPrice} onChange={e => editingItem ? setEditingItem({ ...editingItem, sellPrice: Number(e.target.value) }) : setNewItemForm({ ...newItemForm, sellPrice: Number(e.target.value) })} required />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">{t('quantity')}</label>
                  <input type="number" className="input-field" placeholder="0" value={editingItem ? editingItem.quantity : newItemForm.quantity} onChange={e => editingItem ? setEditingItem({ ...editingItem, quantity: Number(e.target.value) }) : setNewItemForm({ ...newItemForm, quantity: Number(e.target.value) })} required />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">{t('barcode')}</label>
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <input className="input-field flex-1" placeholder={t('barcode')} value={editingItem ? (editingItem.barcode || '') : (newItemForm.barcode || '')} onChange={e => editingItem ? setEditingItem({ ...editingItem, barcode: e.target.value }) : setNewItemForm({ ...newItemForm, barcode: e.target.value })} />
                      <button type="button" onClick={() => setIsScannerOpen(true)} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"><Scan size={20} /></button>
                      <button type="button" onClick={generateBarcode} className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 text-xs font-bold uppercase tracking-tighter" title={t('generate')}>{t('generate')}</button>
                    </div>
                    {(editingItem?.barcode || newItemForm.barcode) && (
                      <button
                        type="button"
                        onClick={() => handlePrintBarcode(editingItem || newItemForm)}
                        className="w-full flex items-center justify-center gap-2 py-2 bg-blue-50 text-blue-600 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-100 transition-colors border border-blue-100"
                      >
                        <Printer size={16} /> {t('printBarcodeLabel')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="pt-2 flex gap-3">
                  <button type="button" onClick={() => { setIsAddItemModalOpen(false); setEditingItem(null); }} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">{t('cancel')}</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-lg shadow-blue-600/20">{editingItem ? t('update') || 'Update' : t('addItem')}</button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Edit History Sale Modal */}
      {
        isEditHistoryModalOpen && editingHistoryItem && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[250] backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden border border-gray-100">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <div className="flex items-center gap-2">
                  <Edit className="text-blue-600" size={18} />
                  <h3 className="font-bold text-lg text-gray-900">{t('editSale') || 'Edit Sale Entry'}</h3>
                </div>
                <button onClick={() => { setIsEditHistoryModalOpen(false); setEditingHistoryItem(null); }} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
              </div>
              <form onSubmit={handleUpdateHistorySale} className="p-6 space-y-4">
                <div>
                  <label className="block text-[10px] uppercase font-black tracking-widest text-slate-400 mb-1">{t('customerName')}</label>
                  <input
                    className="input-field"
                    value={editingHistoryItem.customer}
                    onChange={e => setEditingHistoryItem({ ...editingHistoryItem, customer: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-black tracking-widest text-slate-400 mb-1">{t('totalAmount')}</label>
                  <input
                    type="number"
                    className="input-field font-mono"
                    value={editingHistoryItem.amount}
                    onChange={e => setEditingHistoryItem({ ...editingHistoryItem, amount: Number(e.target.value) })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-black tracking-widest text-slate-400 mb-1">{t('paymentMethod')}</label>
                  <select
                    className="input-field"
                    value={editingHistoryItem.paymentMethod}
                    onChange={e => setEditingHistoryItem({ ...editingHistoryItem, paymentMethod: e.target.value })}
                  >
                    <option value="Cash">{t('cash')}</option>
                    <option value="Visa">{t('visa')}</option>
                    <option value="Online">{t('onlinePayment')}</option>
                  </select>
                </div>

                <div className="pt-4 flex gap-3">
                  <button type="button" onClick={() => { setIsEditHistoryModalOpen(false); setEditingHistoryItem(null); }} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-bold text-xs uppercase tracking-widest">{t('cancel')}</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold text-xs uppercase tracking-widest shadow-lg shadow-blue-600/20">{t('update')}</button>
                </div>
              </form>
            </div>
          </div>
        )
      }
      {
        isAddSaleModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[130] backdrop-blur-md p-0 sm:p-4 animate-in fade-in duration-300">
            <div className="bg-white sm:rounded-[2.5rem] w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-white/20 bottom-sheet flex flex-col">
              <div className="lg:hidden h-1.5 w-12 bg-gray-200 rounded-full mx-auto mt-3 mb-1 shrink-0"></div>
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-black text-lg text-slate-900 uppercase tracking-tight">{t('newSale')}</h3>
                <button onClick={() => setIsAddSaleModalOpen(false)} className="p-2 text-slate-400 hover:text-rose-600 active:scale-90 transition-all"><X size={24} /></button>
              </div>
              <form onSubmit={handleAddSale} className="p-6 space-y-4 overflow-y-auto no-scrollbar pb-32 lg:pb-6">
                <input className="input-field" placeholder={t('customerName')} value={newSaleForm.customer} onChange={e => setNewSaleForm({ ...newSaleForm, customer: e.target.value })} required />
                <input type="number" className="input-field" placeholder={t('totalAmount')} value={newSaleForm.amount} onChange={e => setNewSaleForm({ ...newSaleForm, amount: Number(e.target.value) })} required />
                <select className="input-field" value={newSaleForm.status} onChange={e => setNewSaleForm({ ...newSaleForm, status: e.target.value })}>
                  <option value="Pending">{t('pendingLabel')}</option>
                  <option value="Completed">{t('completed')}</option>
                  <option value="Shipped">{t('shipped')}</option>
                </select>
                <input className="input-field" placeholder={t('itemsSummaryLabel')} value={newSaleForm.items} onChange={e => setNewSaleForm({ ...newSaleForm, items: e.target.value })} />

                <div className="pt-2 flex gap-3">
                  <button type="button" onClick={() => setIsAddSaleModalOpen(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">{t('cancel')}</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium shadow-lg shadow-emerald-600/20">Create Sale</button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Add Purchase Modal */}
      {
        isAddPurchaseModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[130] backdrop-blur-md p-0 sm:p-4 animate-in fade-in duration-300">
            <div className="bg-white sm:rounded-[2.5rem] w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-white/20 bottom-sheet flex flex-col">
              <div className="lg:hidden h-1.5 w-12 bg-gray-200 rounded-full mx-auto mt-3 mb-1 shrink-0"></div>
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-black text-lg text-slate-900 uppercase tracking-tight">{t('newPurchase')}</h3>
                <button onClick={() => setIsAddPurchaseModalOpen(false)} className="p-2 text-slate-400 hover:text-rose-600 active:scale-90 transition-all"><X size={24} /></button>
              </div>
              <form onSubmit={handleAddPurchase} className="p-6 space-y-4 overflow-y-auto no-scrollbar pb-32 lg:pb-6">
                <input className="input-field" placeholder={t('supplierName')} value={newPurchaseForm.supplier} onChange={e => setNewPurchaseForm({ ...newPurchaseForm, supplier: e.target.value })} required />
                <input type="number" className="input-field" placeholder={t('totalCost')} value={newPurchaseForm.amount} onChange={e => setNewPurchaseForm({ ...newPurchaseForm, amount: Number(e.target.value) })} required />
                <select className="input-field" value={newPurchaseForm.status} onChange={e => setNewPurchaseForm({ ...newPurchaseForm, status: e.target.value })}>
                  <option value="Ordered">Ordered</option>
                  <option value="Received">Received</option>
                  <option value="Paid">Paid</option>
                </select>
                <input className="input-field" placeholder="Items (Summary)" value={newPurchaseForm.items} onChange={e => setNewPurchaseForm({ ...newPurchaseForm, items: e.target.value })} />

                <div className="pt-2 flex gap-3">
                  <button type="button" onClick={() => setIsAddPurchaseModalOpen(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">{t('cancel')}</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-lg shadow-blue-600/20">Create Order</button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Add Invoice Modal */}
      {
        isAddInvoiceModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[130] backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 className="font-bold text-lg text-gray-900">Create Invoice</h3>
                <button onClick={() => setIsAddInvoiceModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
              </div>
              <form onSubmit={handleAddInvoice} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <input className="input-field" placeholder="Client / Customer" value={newInvoiceForm.client} onChange={e => setNewInvoiceForm({ ...newInvoiceForm, client: e.target.value })} required />
                  <input type="date" className="input-field" value={newInvoiceForm.date} onChange={e => setNewInvoiceForm({ ...newInvoiceForm, date: e.target.value })} required />
                </div>

                {/* Line Items Section */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="font-bold text-sm text-gray-700 mb-2">Invoice Items</h4>
                  {newInvoiceForm.items.length > 0 && (
                    <ul className="mb-3 space-y-2">
                      {newInvoiceForm.items.map((item, idx) => (
                        <li key={idx} className="flex justify-between items-center text-sm bg-white p-2 rounded border border-gray-200 shadow-sm">
                          <span>{item.name} x {item.quantity}</span>
                          <span className="font-mono font-bold text-gray-900">{formatCurrency(item.price * item.quantity)}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="text-xs font-semibold text-gray-500 mb-1 block">Item</label>
                      <select
                        className="input-field text-sm"
                        value={tempInvoiceItem.itemId}
                        onChange={e => setTempInvoiceItem({ ...tempInvoiceItem, itemId: e.target.value })}
                      >
                        <option value="">Select Item...</option>
                        {inventory.map(i => <option key={i.id} value={i.id}>{i.name} ({formatCurrency(i.sellPrice || 0)})</option>)}
                      </select>
                    </div>
                    <div className="w-20">
                      <label className="text-xs font-semibold text-gray-500 mb-1 block">Qty</label>
                      <input
                        type="number"
                        className="input-field text-sm"
                        min="1"
                        value={tempInvoiceItem.quantity}
                        onChange={e => setTempInvoiceItem({ ...tempInvoiceItem, quantity: Number(e.target.value) })}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const selectedInvItem = inventory.find(i => i.id === tempInvoiceItem.itemId);
                        if (selectedInvItem && tempInvoiceItem.quantity > 0) {
                          setNewInvoiceForm({
                            ...newInvoiceForm,
                            items: [...newInvoiceForm.items, {
                              id: selectedInvItem.id,
                              name: selectedInvItem.name,
                              price: selectedInvItem.sellPrice || 0,
                              quantity: tempInvoiceItem.quantity
                            }]
                          });
                          setTempInvoiceItem({ itemId: '', quantity: 1 });
                        }
                      }}
                      className="bg-gray-900 text-white px-3 py-2 rounded-lg hover:bg-gray-800 text-sm h-[42px]"
                    >Add</button>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-2 px-2">
                  <span className="font-bold text-gray-500">Total Amount:</span>
                  <span className="font-mono text-2xl font-bold text-indigo-600">
                    {formatCurrency(newInvoiceForm.items.reduce((sum, item) => sum + (item.price * item.quantity), 0))}
                  </span>
                </div>

                <select className="input-field" value={newInvoiceForm.status} onChange={e => setNewInvoiceForm({ ...newInvoiceForm, status: e.target.value })}>
                  <option value="Issued">Issued</option>
                  <option value="Paid">Paid</option>
                  <option value="Cancelled">Cancelled</option>
                </select>

                <div className="pt-2 flex gap-3">
                  <button type="button" onClick={() => setIsAddInvoiceModalOpen(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">{t('cancel')}</button>
                  <button type="submit" disabled={newInvoiceForm.items.length === 0} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium shadow-lg shadow-indigo-600/20 disabled:opacity-50 disabled:cursor-not-allowed">Generate Invoice</button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Select Sales Employee Modal */}
      {
        isSelectSalesEmployeeModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[130] backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 className="font-bold text-lg text-gray-900">Select Sales Employee</h3>
                <button onClick={() => setIsSelectSalesEmployeeModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
              </div>
              <div className="p-2 max-h-[60vh] overflow-y-auto">
                <div className="space-y-1">
                  {/* Filtered Employees: Assigned to Location OR Covering Today */}
                  {employees.filter(emp => {
                    if (!posLocationFilter || currentMode !== 'Cashier') return true; // Safety check
                    const isAssigned = emp.location === posLocationFilter;
                    const today = new Date().toISOString().split('T')[0];
                    const isCovering = attendance.some(a =>
                      a.name === emp.name &&
                      a.date === today &&
                      (a.locationFilter === posLocationFilter || a.site === posLocationFilter) // check both just in case
                    );
                    return isAssigned || isCovering;
                  }).map(emp => (
                    <button
                      key={emp.id}
                      onClick={() => { setSalesEmployee(emp); setIsSelectSalesEmployeeModalOpen(false); }}
                      className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 hover:bg-gray-50 transition-colors ${salesEmployee?.id === emp.id ? 'bg-blue-50 border border-blue-100' : ''}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-100 overflow-hidden">
                        {emp.photo ? <img src={emp.photo} alt={emp.name} className="w-full h-full object-cover" /> : <User size={20} className="text-gray-400 m-auto mt-2" />}
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">{emp.name}</div>
                        <div className="text-xs text-gray-500">{t(emp.role.toLowerCase()) || emp.role}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Manage Payroll Modal */}
      {
        isManagePayrollModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[130] backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <div>
                  <h3 className="font-bold text-lg text-gray-900">{t('managePayroll')}</h3>
                  <p className="text-xs text-slate-500">{currentPayrollForm.name} • {currentPayrollForm.month}</p>
                </div>
                <button onClick={() => setIsManagePayrollModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
              </div>
              <form onSubmit={handleSavePayroll} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">

                {/* Fixed Fields */}
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">{t('basicSalary')}</label>
                    <input type="number" className="input-field bg-white" value={currentPayrollForm.salary} onChange={e => setCurrentPayrollForm({ ...currentPayrollForm, salary: e.target.value })} required title={t('basicSalary')} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">{t('advanceTaken')}</label>
                    <input type="number" className="input-field bg-white text-red-600" value={currentPayrollForm.advance} onChange={e => setCurrentPayrollForm({ ...currentPayrollForm, advance: e.target.value })} title={t('advance')} />
                  </div>
                </div>

                {/* Variable Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">{t('bonus')} (+)</label>
                    <input type="number" className="input-field text-green-600 font-bold" value={currentPayrollForm.bonus} onChange={e => setCurrentPayrollForm({ ...currentPayrollForm, bonus: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">{t('overtime')} (+)</label>
                    <input type="number" className="input-field text-orange-600 font-bold" value={currentPayrollForm.overtime} onChange={e => setCurrentPayrollForm({ ...currentPayrollForm, overtime: e.target.value })} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">{t('manualDeductions')} (-)</label>
                    <input type="number" className="input-field text-red-600 font-bold" value={currentPayrollForm.deductions} onChange={e => setCurrentPayrollForm({ ...currentPayrollForm, deductions: e.target.value })} />
                  </div>
                  <div className="opacity-70 pointer-events-none">
                    <label className="text-xs font-semibold text-gray-400 block mb-1">{t('lateAbsentAuto')}</label>
                    <input type="number" className="input-field bg-gray-100 text-gray-500" value={(currentPayrollForm.lateDeduction + currentPayrollForm.absentDeduction).toFixed(2)} readOnly />
                  </div>
                </div>

                {/* Summary Box */}
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mt-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-blue-800">{t('grossEarnings')}:</span>
                    <span className="font-mono font-bold text-blue-900">{formatCurrency(Number(currentPayrollForm.salary) + Number(currentPayrollForm.bonus) + Number(currentPayrollForm.overtime))}</span>
                  </div>
                  <div className="flex justify-between items-center mb-1 text-red-700">
                    <span className="text-sm font-medium">{t('totalDeductions')}:</span>
                    <span className="font-mono font-bold">-{formatCurrency(Number(currentPayrollForm.advance) + Number(currentPayrollForm.deductions) + Number(currentPayrollForm.lateDeduction) + Number(currentPayrollForm.absentDeduction))}</span>
                  </div>
                  <div className="h-px bg-blue-200 my-2"></div>
                  <div className="flex justify-between items-center">
                    <span className="text-base font-bold text-blue-900">{t('netPayable')}:</span>
                    <span className="text-xl font-bold text-blue-700 font-mono">
                      {formatCurrency(
                        (Number(currentPayrollForm.salary) + Number(currentPayrollForm.bonus) + Number(currentPayrollForm.overtime)) -
                        (Number(currentPayrollForm.advance) + Number(currentPayrollForm.deductions) + Number(currentPayrollForm.lateDeduction) + Number(currentPayrollForm.absentDeduction))
                      )}
                    </span>
                  </div>
                </div>

                <div className="pt-2 flex gap-3">
                  <button type="button" onClick={() => setIsManagePayrollModalOpen(false)} className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-bold transition-colors">{t('cancel')}</button>
                  {payrolls.find(p => p.employeeId === currentPayrollForm.id && p.month === currentPayrollForm.month) && (
                    <button
                      type="button"
                      onClick={() => handleDeletePayrollRecord(currentPayrollForm.id, currentPayrollForm.month)}
                      className="flex-1 px-4 py-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 font-bold transition-all flex items-center justify-center gap-2 border border-red-200"
                      title="Delete custom record and reset to employee defaults"
                    >
                      <X size={18} /> {t('reset')}
                    </button>
                  )}
                  <button type="submit" className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-bold shadow-lg shadow-blue-600/20 transition-all flex items-center justify-center gap-2">
                    <Save size={18} /> {t('saveRecord')}
                  </button>
                </div>

              </form>
            </div>
          </div>
        )
      }

      {/* PIN Verification Modal */}
      {
        isPinModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-[130] backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
              <div className="p-6 text-center">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                  <Shield size={32} className="text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('securityCheck')}</h3>
                <p className="text-sm text-gray-500 mb-6">
                  {pinAction === 'changeSalesEmployee' ? t('changeUserPrompt') :
                    pinAction === 'switchMode' ? t('switchModePrompt') :
                      t('pinPrompt')}
                </p>

                <div className="flex justify-center gap-3 mb-6">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className={`w-4 h-4 rounded-full transition-all duration-300 ${pinInput.length > i ? 'bg-blue-600 scale-110' : 'bg-gray-200'}`} />
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                    <button
                      key={num}
                      onClick={() => {
                        const newPin = pinInput + num;
                        if (newPin.length <= 4) {
                          setPinInput(newPin);
                        }
                      }}
                      className="h-12 rounded-xl bg-gray-50 hover:bg-gray-100 text-lg font-bold text-gray-700 transition-colors flex items-center justify-center active:scale-95"
                    >
                      {num}
                    </button>
                  ))}
                  <button onClick={() => setPinInput('')} className="h-12 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 font-bold flex items-center justify-center transition-colors active:scale-95">C</button>
                  <button
                    onClick={() => {
                      const newPin = pinInput + '0';
                      if (newPin.length <= 4) {
                        setPinInput(newPin);
                      }
                    }}
                    className="h-12 rounded-xl bg-gray-50 hover:bg-gray-100 text-lg font-bold text-gray-700 transition-colors flex items-center justify-center active:scale-95"
                  >
                    0
                  </button>
                  <button onClick={() => setIsPinModalOpen(false)} className="h-12 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-500 font-bold flex items-center justify-center transition-colors active:scale-95"><X size={20} /></button>
                </div>

                {/* Forgot PIN Link */}
                <button
                  onClick={() => {
                    if (!securityQuestion || !securityAnswer) {
                      alert(t('setSecurityQuestion') + " in Settings first!");
                      return;
                    }
                    const ans = prompt(`${t('securityQuestion')}: ${t(securityQuestion) || securityQuestion}\n${t('enterSecurityAnswer')}:`);
                    if (ans && ans.toLowerCase().trim() === securityAnswer.toLowerCase().trim()) {
                      const resetPin = '1234';
                      setSecurityPin(resetPin);
                      saveUserSettings({ securityPin: resetPin });
                      alert(t('pinResetSuccess'));
                    } else {
                      alert(t('incorrectAnswer'));
                    }
                  }}
                  className="text-sm text-blue-500 hover:text-blue-700 underline mt-2"
                >
                  {t('forgotPin')}
                </button>
              </div>
            </div>
          </div>
        )
      }







      {/* Scanner Modal */}
      {
        isScannerOpen && (
          <div className="fixed inset-0 bg-slate-950/90 flex flex-col items-center justify-center z-[150] p-4 backdrop-blur-sm">
            <style>{`
              @keyframes scan {
                0% { top: 10%; }
                100% { top: 90%; }
              }
              .scanner-overlay {
                position: absolute;
                inset: 0;
                background: linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0) 40%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.3));
                pointer-events: none;
                z-index: 5;
              }
              #reader video {
                width: 100%;
                height: 100%;
                object-fit: cover;
              }
              .scanning-line {
                position: absolute;
                left: 10%;
                right: 10%;
                height: 2px;
                background: #ef4444;
                box-shadow: 0 0 15px #ef4444, 0 0 5px #ef4444;
                animation: scan 2s linear infinite;
                z-index: 20;
              }
            `}</style>

            <div className="bg-white rounded-[2.5rem] w-full max-w-lg overflow-hidden relative shadow-2xl border border-white/20 animate-in zoom-in-95 duration-300">
              <div className="p-5 border-b flex justify-between items-center bg-gray-50/50 backdrop-blur-xl">
                <div className="flex flex-col flex-1 text-left">
                  <h3 className="font-black text-gray-900 flex items-center gap-2 uppercase tracking-tight text-sm"><Scan size={18} className="text-blue-600" /> {t('scanBarcode')}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    {cameras.length > 1 && (
                      <select
                        className="text-[10px] bg-white border border-gray-100 rounded-full px-3 py-1.5 outline-none font-black text-gray-400 hover:text-blue-600 transition-colors uppercase tracking-widest shadow-sm"
                        value={activeCameraId || ''}
                        onChange={(e) => setActiveCameraId(e.target.value)}
                      >
                        {cameras.map(cam => (
                          <option key={cam.id} value={cam.id}>{cam.label || `Camera ${cam.id}`}</option>
                        ))}
                      </select>
                    )}
                    {hasFlash && (
                      <button
                        onClick={toggleFlash}
                        className={`p-2 rounded-full border transition-all shadow-sm ${isFlashOn ? 'bg-amber-100 border-amber-300 text-amber-600' : 'bg-white border-gray-100 text-gray-400'}`}
                        title="Toggle Flash"
                      >
                        <Zap size={16} fill={isFlashOn ? "currentColor" : "none"} />
                      </button>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setIsScannerOpen(false)}
                  className="p-3 hover:bg-red-50 rounded-full text-gray-400 hover:text-red-600 transition-all ml-2 bg-gray-100/50"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Retry Overlay if black screen */}
              <div className="absolute top-24 right-6 z-[60]">
                <button
                  onClick={() => {
                    setIsScannerOpen(false);
                    setTimeout(() => setIsScannerOpen(true), 100);
                  }}
                  className="px-3 py-1.5 bg-blue-600/80 backdrop-blur-md text-white text-[10px] font-black uppercase rounded-full shadow-lg border border-white/20 hover:bg-blue-700 transition-all flex items-center gap-2"
                >
                  <RefreshCw size={12} className="animate-spin-slow" /> {t('retryCamera')}
                </button>
              </div>

              <div className="relative overflow-hidden bg-black aspect-square sm:aspect-video flex items-center justify-center">
                <div id="reader" className="w-full h-full"></div>

                {/* Manual Focus Trigger Overlay */}
                <button
                  onClick={async () => {
                    try {
                      const track = scannerRef.current?.getRunningTrack();
                      if (track) {
                        await track.applyConstraints({ focusMode: "continuous" });
                        // Visual focus pulse animation
                        const focusUI = document.getElementById('focus-pulse');
                        if (focusUI) {
                          focusUI.classList.remove('hidden');
                          setTimeout(() => focusUI.classList.add('hidden'), 500);
                        }
                      }
                    } catch (e) { }
                  }}
                  className="absolute inset-0 z-40 cursor-crosshair flex items-center justify-center"
                >
                  <div id="focus-pulse" className="hidden w-20 h-20 border border-blue-400 rounded-full animate-ping"></div>
                </button>

                {/* Visual Scanning Effects */}
                <div className="scanner-overlay"></div>
                <div className="scanning-line"></div>

                {/* Corner Frame */}
                <div className="absolute top-10 left-10 w-10 h-10 border-t-4 border-l-4 border-blue-500 rounded-tl-xl z-30"></div>
                <div className="absolute top-10 right-10 w-10 h-10 border-t-4 border-r-4 border-blue-500 rounded-tr-xl z-30"></div>
                <div className="absolute bottom-10 left-10 w-10 h-10 border-b-4 border-l-4 border-blue-500 rounded-bl-xl z-30"></div>
                <div className="absolute bottom-10 right-10 w-10 h-10 border-b-4 border-r-4 border-blue-500 rounded-br-xl z-30"></div>

                {/* Zoom Control UI overlayed on video */}
                {hasZoom && zoomRange.max > zoomRange.min && (
                  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-3/4 bg-black/60 backdrop-blur-xl px-4 py-3 rounded-full flex items-center gap-4 z-50 border border-white/20 shadow-lg">
                    <span className="text-white text-[10px] font-black opacity-50 uppercase">1x</span>
                    <input
                      type="range"
                      min={zoomRange.min}
                      max={zoomRange.max}
                      step={zoomRange.step}
                      value={currentZoom}
                      onChange={handleZoomChange}
                      className="flex-1 h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                    <span className="text-white text-[10px] font-black opacity-50 uppercase">{Math.round(zoomRange.max)}x</span>
                  </div>
                )}
              </div>

              <div className="p-6 bg-white text-center">
                <p className="text-gray-900 font-bold text-sm tracking-tight mb-1">
                  {hasZoom ? t('scanFocusInfo') : t('tapToFocus')}
                </p>
                <p className="text-[10px] text-gray-400 uppercase tracking-widest font-black leading-relaxed">
                  {t('centerCodeFrame')}<br />
                  <span className="text-blue-500">{t('holdAwayTap')}</span>
                </p>
              </div>
            </div>
          </div>
        )
      }

      {/* UPI QR Payment Modal (Improved with Close and Overlap fix) */}
      {
        (activeTab === 'sales_purchases' || activeTab === 'service') && !showSettings && !isPinModalOpen && showUpiQr && paymentMethod === 'Online' && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 w-[280px] bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,.1)] border border-blue-50 p-6 animate-in slide-in-from-bottom-10 duration-500 z-[100]">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-3 text-blue-800">
                <div className="p-2 bg-blue-50 rounded-xl"><QrCode size={20} /></div>
                <span className="font-black text-xs uppercase tracking-widest">
                  {digitalSubMethod === 'UPI' ? t('payWithUPI') : digitalSubMethod === 'InstaPay' ? t('payWithInstapay') : (t('otherPayment') || 'Other Payment')}
                </span>
              </div>
              <button
                onClick={() => setShowUpiQr(false)}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-900 transition-all"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex justify-center bg-gray-50 p-5 rounded-3xl mb-4 border border-gray-100 shadow-inner">
              {digitalSubMethod === 'UPI' && shopSettings.upiId ? (
                <QRCodeSVG
                  value={`upi://pay?pa=${shopSettings.upiId}&pn=${shopSettings.name}&am=${(activeTab === 'service' ? serviceCart.reduce((a, b) => a + (Number(b.sellPrice) * Number(b.quantity)), 0) : (calculateTotal() - cartDiscount)).toFixed(2)}&cu=${currency === 'INR' ? 'INR' : 'INR'}`}
                  size={180}
                  level="H"
                  includeMargin={true}
                />
              ) : digitalSubMethod === 'InstaPay' && shopSettings.instapayId ? (
                <div className="text-center">
                  <div className="bg-white p-2 rounded-lg inline-block border border-gray-100 shadow-sm mb-2">
                    <QRCodeSVG value={shopSettings.instapayId} size={150} />
                  </div>
                  <p className="text-[10px] font-bold text-gray-500">InstaPay ID: {shopSettings.instapayId}</p>
                </div>
              ) : digitalSubMethod === 'Other' ? (
                <div className="text-center py-4 flex flex-col items-center gap-3">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600">
                    <QrCode size={32} />
                  </div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('useExternalTerminal') || 'Use External Terminal or QR'}</p>
                </div>
              ) : (
                <div className="p-4 text-center text-xs text-red-500 italic">
                  {t('paymentMethodNotConfigured') || 'Details not configured in Settings'}
                </div>
              )}
            </div>
            <div className="text-center mb-6">
              <p className="text-[10px] text-gray-400 uppercase font-black tracking-[0.2em] mb-1">
                {digitalSubMethod === 'Other' ? '' : (digitalSubMethod === 'UPI' ? shopSettings.upiId : shopSettings.instapayId)}
              </p>
              <p className="text-lg font-black text-gray-900 font-mono tracking-tight">
                {formatCurrency(activeTab === 'service' ? serviceCart.reduce((a, b) => a + (Number(b.sellPrice) * Number(b.quantity)), 0) : (calculateTotal() - cartDiscount))}
              </p>
            </div>

            <div className="space-y-2">
              <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all duration-1000 ease-linear rounded-full"
                  style={{ width: `${(upiQrTimer / 30) * 100}%` }}
                ></div>
              </div>
              <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                <span className="text-blue-600">{t('autoHide') || 'Auto-hide'}</span>
                <span className="text-gray-400">{upiQrTimer}{t('seconds') || 's'}</span>
              </div>
            </div>
          </div>
        )
      }


      {/* Borrow Modal */}
      {isBorrowModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[130] p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-8 py-6 border-b border-gray-50 flex justify-between items-center bg-blue-50/30">
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                <Package size={20} className="text-blue-500" /> {t('addBorrowRecord') || 'Borrow Record'}
              </h3>
              <button onClick={() => setIsBorrowModalOpen(false)} className="text-slate-400 hover:text-slate-600 bg-white p-2 rounded-full shadow-sm"><X size={20} /></button>
            </div>
            <form onSubmit={handleAddBorrowRecord} className="p-8 space-y-5">
              <div>
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2 block">{t('borrowerName') || 'Borrower Name'}</label>
                <input required type="text" className="w-full px-5 py-3 rounded-xl bg-slate-50 border-0 focus:ring-2 focus:ring-blue-500 text-sm font-bold" value={borrowForm.borrowerName} onChange={e => setBorrowForm({ ...borrowForm, borrowerName: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2 block">{t('itemName') || 'Item Borrowed'}</label>
                <input required type="text" className="w-full px-5 py-3 rounded-xl bg-slate-50 border-0 focus:ring-2 focus:ring-blue-500 text-sm font-bold" value={borrowForm.itemName} onChange={e => setBorrowForm({ ...borrowForm, itemName: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2 block">{t('borrowDate') || 'Borrow Date'}</label>
                  <input required type="date" className="w-full px-5 py-3 rounded-xl bg-slate-50 border-0 focus:ring-2 focus:ring-blue-500 text-sm font-bold" value={borrowForm.borrowDate} onChange={e => setBorrowForm({ ...borrowForm, borrowDate: e.target.value })} />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2 block">{t('borrowTime') || 'Time'}</label>
                  <input required type="time" className="w-full px-5 py-3 rounded-xl bg-slate-50 border-0 focus:ring-2 focus:ring-blue-500 text-sm font-bold" value={borrowForm.borrowTime} onChange={e => setBorrowForm({ ...borrowForm, borrowTime: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2 block">{t('additionalInfo') || 'Additional Info (Optional)'}</label>
                <textarea className="w-full px-5 py-3 rounded-xl bg-slate-50 border-0 focus:ring-2 focus:ring-blue-500 text-sm" rows="2" value={borrowForm.info} onChange={e => setBorrowForm({ ...borrowForm, info: e.target.value })}></textarea>
              </div>
              <button type="submit" className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-blue-200 mt-4 active:scale-95 transition-all">
                {t('saveRecord') || 'Save Record'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Cafe Order Modal */}
      {
        isCafeOrderModalOpen && activeCafeSession && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[130] p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[2.5rem] w-full max-w-5xl max-h-[90vh] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-100 flex flex-col relative">
              <div className="px-8 py-6 border-b border-gray-50 flex justify-between items-center bg-white/50 backdrop-blur-xl">
                <div>
                  <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
                    <div className="w-1.5 h-6 bg-blue-600 rounded-full"></div>
                    {t('addOrder')} <span className="text-slate-300 font-normal">/</span> {activeCafeSession.roomName}
                  </h3>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">{t('appendSession')}</p>
                </div>
                <button
                  onClick={() => { setIsCafeOrderModalOpen(false); setCart([]); }}
                  className="p-3 hover:bg-rose-50 rounded-full text-slate-300 hover:text-rose-600 transition-all active:scale-95 bg-slate-50"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
                {/* Left: Menu */}
                <div className="flex-1 p-8 overflow-y-auto flex flex-col gap-8 bg-slate-50/30">
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    {['All', 'Hot Drinks', 'Cold Drinks', 'Snacks', 'Meals'].map(cat => (
                      <button
                        key={cat}
                        onClick={() => setActiveCafeCategory(cat)}
                        className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap border ${activeCafeCategory === cat ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-500/20' : 'bg-white text-slate-400 border-gray-100 hover:border-blue-500/30 hover:text-blue-600'}`}
                      >
                        {cat === 'All' ? t('all') : cat === 'Hot Drinks' ? t('hotDrinks') : cat === 'Cold Drinks' ? t('coldDrinks') : cat === 'Snacks' ? t('snacks') : t('meals')}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {recipes
                      .filter(r => activeCafeCategory === 'All' || r.category === activeCafeCategory)
                      .map(item => (
                        <button
                          key={item.id}
                          onClick={() => {
                            const stock = Number(item.quantity) || 0;
                            const isRecipe = item.ingredients;

                            // For recipes, we just allow adding and check at checkout OR perform a shallow check here if we want to be fancy.
                            // For now, let's just do direct item check.
                            if (!isRecipe && stock <= 0) { return alert(t('outOfStock') || "Out of stock!"); }

                            const existing = cart.find(c => c.id === item.id);
                            if (existing) {
                              if (!isRecipe && Number(existing.quantity) + 1 > stock) {
                                return alert(`${t('maxStockReached') || 'Max stock reached:'} ${stock}`);
                              }
                              setCart(cart.map(c => c.id === item.id ? { ...c, quantity: Number(c.quantity) + 1 } : c));
                            } else {
                              setCart([...cart, { ...item, quantity: 1 }]);
                            }
                          }}
                          className="p-5 bg-white rounded-2xl border border-gray-100 hover:border-blue-500/30 hover:shadow-xl hover:shadow-slate-200/50 transition-all group relative text-left flex flex-col justify-between min-h-[130px] shadow-sm"
                        >
                          <div>
                            <p className="font-black text-slate-800 text-sm mb-1 uppercase tracking-tight leading-tight">{item.name}</p>
                            <p className="text-blue-600 font-bold text-xs tracking-widest">{formatCurrency(item.sellPrice)}</p>
                          </div>
                          <div className="w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center text-slate-300 group-hover:bg-blue-600 group-hover:text-white transition-all transform group-hover:rotate-12">
                            <Plus size={16} strokeWidth={3} />
                          </div>
                        </button>
                      ))}
                  </div>

                  {recipes.filter(r => activeCafeCategory === 'All' || r.category === activeCafeCategory).length === 0 && (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-12 opacity-30">
                      <Package size={48} className="mb-4 text-slate-300" />
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t('menuItemsMissingTitle')}</p>
                    </div>
                  )}
                </div>

                {/* Right: Cart & History */}
                <div className="w-full md:w-[380px] bg-white border-l border-gray-100 p-8 flex flex-col h-full overflow-hidden">
                  <div className="flex-1 overflow-y-auto space-y-8 pr-2 custom-scrollbar">
                    {(() => {
                      const currentSession = cafeSessions.find(s => s.id === activeCafeSession.id);
                      const history = currentSession?.orders || [];
                      if (history.length === 0) return null;
                      return (
                        <div className="bg-slate-50 p-6 rounded-3xl border border-gray-100/50">
                          <h4 className="font-black text-[9px] uppercase tracking-widest text-blue-600 mb-4 flex items-center gap-2">
                            <History size={14} /> {t('orderHistory')}
                          </h4>
                          <div className="space-y-3">
                            {history.map((item, idx) => (
                              <div key={idx} className="flex justify-between items-center px-1">
                                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-tight truncate max-w-[150px]">{item.name}</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-[10px] font-black text-slate-300">x{item.quantity}</span>
                                  <span className="text-[11px] font-mono font-bold text-slate-700">{formatCurrency(Number(item.sellPrice) * Number(item.quantity))}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-center">
                            <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{t('subtotalLabel')}</span>
                            <span className="text-sm font-black text-slate-900">{formatCurrency(history.reduce((sum, h) => sum + (Number(h.sellPrice) * Number(h.quantity)), 0))}</span>
                          </div>
                        </div>
                      );
                    })()}

                    <div>
                      <h4 className="font-black text-[9px] uppercase tracking-widest text-slate-400 mb-4 px-1">{t('newSelection')}</h4>
                      <div className="space-y-3">
                        {cart.length === 0 ? (
                          <div className="py-8 flex flex-col items-center justify-center opacity-20">
                            <ShoppingCart size={32} className="mb-2 text-slate-400" />
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t('trayEmpty')}</p>
                          </div>
                        ) : (
                          cart.map((item, idx) => (
                            <div key={idx} className="bg-white p-4 rounded-2xl border border-gray-100 flex items-center gap-3 animate-in slide-in-from-right-4 duration-300 shadow-sm">
                              <div className="flex-1 overflow-hidden">
                                <p className="font-bold text-[11px] text-slate-800 truncate uppercase tracking-tight mb-0.5">{item.name}</p>
                                <p className="text-[10px] text-blue-600 font-black tracking-widest">{formatCurrency(item.sellPrice)}</p>
                              </div>
                              <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-1.5 border border-gray-100">
                                <button onClick={() => setCart(cart.map(c => c.id === item.id ? { ...c, quantity: Math.max(0, Number(c.quantity) - 1) } : c).filter(c => c.quantity > 0))} className="p-1.5 hover:bg-white hover:text-rose-600 rounded-lg text-slate-400 transition-all"><Minus size={12} /></button>
                                <span className="text-[11px] font-black text-slate-700 tabular-nums min-w-[20px] text-center">{item.quantity}</span>
                                <button
                                  onClick={() => {
                                    const invItem = inventory.find(i => i.id === item.id);
                                    const isRecipe = recipes.find(r => r.id === item.id);
                                    if (isRecipe) {
                                      setCart(cart.map(c => c.id === item.id ? { ...c, quantity: Number(c.quantity) + 1 } : c));
                                    } else if (invItem && Number(item.quantity) < Number(invItem.quantity)) {
                                      setCart(cart.map(c => c.id === item.id ? { ...c, quantity: Number(c.quantity) + 1 } : c));
                                    } else {
                                      alert(t('outOfStock') || "No more stock available!");
                                    }
                                  }}
                                  className="p-1.5 hover:bg-white hover:text-blue-600 rounded-lg text-slate-400 transition-all"
                                >
                                  <Plus size={12} />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="pt-8 border-t border-gray-100 mt-auto">
                    <div className="flex justify-between items-end mb-6">
                      <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{t('trayTotal')}</span>
                      <span className="text-2xl font-black text-slate-900 tracking-tight">{formatCurrency(cart.reduce((sum, i) => sum + (Number(i.sellPrice) * Number(i.quantity)), 0))}</span>
                    </div>
                    <button
                      disabled={cart.length === 0}
                      onClick={() => {
                        handleCheckoutCafeOrder(activeCafeSession.id, cart);
                        setIsCafeOrderModalOpen(false);
                      }}
                      className="w-full py-4 bg-slate-900 hover:bg-black text-white rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all shadow-lg shadow-slate-200 disabled:opacity-20 flex items-center justify-center gap-2 transform active:scale-95"
                    >
                      <CheckCircle size={18} strokeWidth={3} /> {t('confirmOrder')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Start Session Modal */}
      {
        isStartSessionModalOpen && pendingRoom && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[130] p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-100 flex flex-col p-8">
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-50 rounded-2xl text-blue-600">
                    <Play size={24} fill="currentColor" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-slate-800 tracking-tight uppercase">{t('startSessionTitle')}</h3>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{pendingRoom.name}</p>
                  </div>
                </div>
                <button onClick={() => setIsStartSessionModalOpen(false)} className="p-2 text-slate-300 hover:text-rose-600 transition-all active:scale-95"><X size={24} /></button>
              </div>

              <form onSubmit={handleConfirmStartSession} className="space-y-5">
                <div className="bg-slate-50 p-6 rounded-2xl border border-gray-100 flex justify-between items-center group transition-all">
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">{t('hourlyRateLabel')}</span>
                    <span className="text-xl font-black text-slate-900 tabular-nums">{formatCurrency(pendingRoom.hourlyPrice)}</span>
                  </div>
                  <div className="p-3 bg-white rounded-xl shadow-sm border border-gray-100 group-hover:bg-blue-600 group-hover:text-white transition-all text-slate-400">
                    <DollarSign size={20} />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 pl-4">{t('sessionTypeLabel')}</label>
                  <select
                    value={sessionStartForm.sessionType}
                    onChange={e => setSessionStartForm({ ...sessionStartForm, sessionType: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all appearance-none cursor-pointer"
                  >
                    <option value="Open">{t('openSession')}</option>
                    <option value="Fixed">{t('fixedDuration')}</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 pl-4">{t('customerPhone')}</label>
                  <input
                    type="text"
                    value={sessionStartForm.customerPhone}
                    onChange={e => setSessionStartForm({ ...sessionStartForm, customerPhone: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:text-slate-300"
                    placeholder={t('phonePlaceholder')}
                    required
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 pl-4">{t('customerName')}</label>
                  <input
                    type="text"
                    value={sessionStartForm.customerName}
                    onChange={e => setSessionStartForm({ ...sessionStartForm, customerName: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:text-slate-300"
                    placeholder={t('guestName')}
                  />
                </div>

                <button type="submit" className="w-full bg-slate-900 hover:bg-black text-white py-4 rounded-2xl font-black uppercase tracking-widest text-[11px] transition-all shadow-lg shadow-slate-200 flex items-center justify-center gap-2 mt-4 hover:scale-[1.02] active:scale-[0.98]">
                  <Play size={18} fill="currentColor" /> {t('startSession')}
                </button>
              </form>
            </div>
          </div>
        )
      }
      </div>
    </main>

          {/* Service Module Specific Mobile Navigation */ }
  {/* Global Ethereal Command Center - Native Phone UI Core */ }
  {
    !isKiosk && !isPinModalOpen && !showSettings && !isTicketModalOpen && !isAddModalOpen && !isAddSiteModalOpen && 
    !isAddAttendanceModalOpen && !isAddItemModalOpen && !isAddSaleModalOpen && !isAddPurchaseModalOpen && 
    !isAddInvoiceModalOpen && !isAddAccountModalOpen && !isScannerOpen && !isManagePayrollModalOpen && !isMobileCartOpen && (
      <div className="lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 w-[92%] max-w-sm bg-white/80 backdrop-blur-3xl border border-white/20 p-2.5 flex justify-around items-center z-[500] rounded-[2.5rem] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] animate-in slide-in-from-bottom-10 duration-700">
        {(() => {
          const navs = {
            dashboard: [
              { id: 'home', label: t('menuDashboard'), icon: <LayoutDashboard size={22} />, onClick: () => setActiveTab('dashboard') },
              { id: 'settings', label: t('settings'), icon: <Settings size={22} />, onClick: () => setShowSettings(true) },
            ],
            employees: [
              { id: 'all', label: t('allEmployees'), icon: <Users size={22} />, onClick: () => setActiveTab('employees') },
              { id: 'add', isFab: true, icon: <Plus size={24} strokeWidth={3} />, onClick: () => setIsAddModalOpen(true) },
              { id: 'sites', label: t('menuSites'), icon: <MapPin size={22} />, onClick: () => setActiveTab('sites') }
            ],
            sites: [
              { id: 'all', label: t('filterAll'), icon: <MapPin size={22} />, onClick: () => setActiveTab('sites') },
              { id: 'add', isFab: true, icon: <Plus size={24} strokeWidth={3} />, onClick: () => setIsAddSiteModalOpen(true) },
              { id: 'dashboard', label: t('dashboard'), icon: <LayoutDashboard size={22} />, onClick: () => setActiveTab('dashboard') }
            ],
            attendance: [
              { id: 'daily', label: t('menuAttendance'), icon: <Clock size={22} />, onClick: () => setActiveTab('attendance') },
              { id: 'add', isFab: true, icon: <Plus size={24} strokeWidth={3} />, onClick: () => setIsAddAttendanceModalOpen(true) },
              { id: 'export', label: t('export'), icon: <Download size={22} />, onClick: handleExportAttendance }
            ],
            payroll: [
              { id: 'mgmt', label: t('menuPayroll'), icon: <DollarSign size={22} />, onClick: () => setActiveTab('payroll') },
              { id: 'add', isFab: true, icon: <Plus size={24} strokeWidth={3} />, onClick: () => { /* No direct add yet */ } },
              { id: 'export', label: t('export'), icon: <Download size={22} />, onClick: handleExportPayroll }
            ],
            reports: [
              { id: 'dashboard', label: t('summary'), icon: <BarChart3 size={22} />, onClick: () => setActiveTab('reports') },
              { id: 'download', isFab: true, icon: <Download size={24} strokeWidth={3} />, onClick: () => downloadReport('all') },
              { id: 'pl', label: t('profit_loss'), icon: <DollarSign size={22} />, onClick: () => setProfitPeriod('Monthly') }
            ],
            sales_purchases: [
              { id: 'pos', label: t('products'), icon: <ShoppingCart size={22} />, onClick: () => { setActiveTab('sales_purchases'); setIsMobileCartOpen(false); } },
                            {
                id: 'cart', isFab: true, icon: (
                  <div className="relative">
                    <ShoppingCart size={24} strokeWidth={3} />
                    {cart.length > 0 && (
                      <span className="absolute -top-3 -right-3 bg-red-500 text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full border-2 border-white">{cart.reduce((a, b) => a + b.quantity, 0)}</span>
                    )}
                  </div>
                ), onClick: () => setIsMobileCartOpen(true)
              },
              { id: 'history', label: t('history'), icon: <Clock size={22} />, onClick: () => setActiveTab('history') }
            ],
            cafe: [
              { id: 'board', label: t('dashboard'), icon: <Coffee size={22} />, onClick: () => setCafeSubTab('board') },
              { id: 'rooms', label: t('rooms'), icon: <Monitor size={22} />, onClick: () => setCafeSubTab('rooms') },
              { id: 'add', isFab: true, icon: <Plus size={24} strokeWidth={3} />, onClick: () => setCafeSubTab('rooms') },
              { id: 'recipes', label: t('recipes'), icon: <History size={22} />, onClick: () => setCafeSubTab('recipes') },
              { id: 'history', label: t('history'), icon: <Clock size={22} />, onClick: () => setCafeSubTab('history') }
            ],
            service: [
              { id: 'board', label: t('dashboard'), icon: <LayoutDashboard size={22} />, onClick: () => setServiceSubTab('board') },
              {
                id: 'sell', label: t('sell'), icon: (
                  <div className="relative">
                    <ShoppingCart size={22} />
                    {serviceCart.length > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[7px] w-4 h-4 flex items-center justify-center rounded-full border border-white">
                        {serviceCart.reduce((a, b) => a + (b.quantity || 1), 0)}
                      </span>
                    )}
                  </div>
                ), onClick: () => {
                  if (serviceSubTab === 'sell' && serviceCart.length > 0) setIsMobileCartOpen(!isMobileCartOpen);
                  else setServiceSubTab('sell');
                }
              },
              { id: 'new', isFab: true, icon: <Plus size={24} strokeWidth={3} />, onClick: () => setServiceSubTab('new') },
              { id: 'active', label: t('activeJobs'), icon: <Wrench size={22} />, onClick: () => setServiceSubTab('active') },
              { id: 'history', label: t('history'), icon: <Clock size={22} />, onClick: () => setServiceSubTab('history') }
            ],
            warehouses: [
              { id: 'stock', label: t('inventory'), icon: <Package size={22} />, onClick: () => setActiveTab('warehouses') },
              { id: 'add', isFab: true, icon: <Plus size={24} strokeWidth={3} />, onClick: () => setIsAddItemModalOpen(true) },
              { id: 'history', label: t('history'), icon: <Clock size={22} />, onClick: () => setActiveTab('history') }
            ],
            history: [
              { id: 'invoices', label: t('menuInvoices'), icon: <Receipt size={22} />, onClick: () => setActiveTab('history') },
              { id: 'dashboard', label: t('dashboard'), icon: <LayoutDashboard size={22} />, onClick: () => setActiveTab('dashboard') }
            ],
            accounts: [
              { id: 'all', label: t('menuAccounts'), icon: <Calculator size={22} />, onClick: () => setActiveTab('accounts') },
              { id: 'add', isFab: true, icon: <Plus size={24} strokeWidth={3} />, onClick: () => setIsAddAccountModalOpen(true) },
              { id: 'dashboard', label: t('dashboard'), icon: <LayoutDashboard size={22} />, onClick: () => setActiveTab('dashboard') }
            ]
          };

          const currentNav = navs[activeTab] || navs.dashboard;
          return currentNav.map(tab => {
            const isActive = tab.isFab ? false : (
              (activeTab === 'service' && serviceSubTab === tab.id) ||
              (activeTab === 'cafe' && cafeSubTab === tab.id) ||
              (activeTab === 'sales_purchases' && tab.id === 'pos' && !isMobileCartOpen) ||
              (activeTab === 'sales_purchases' && tab.id === 'cart' && isMobileCartOpen) ||
              (activeTab === tab.id && !['service', 'cafe', 'sales_purchases'].includes(activeTab))
            );

            return (
              <button
                key={tab.id}
                onClick={() => {
                  tab.onClick();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`flex flex-col items-center gap-1 transition-all ${tab.isFab ? '' : (isActive ? 'text-blue-600' : 'text-gray-400')}`}
              >
                {tab.isFab ? (
                  <div className="bg-blue-600 text-white p-5 rounded-[2rem] shadow-xl shadow-blue-200 transform -translate-y-8 border-4 border-white active:scale-90 transition-all">
                    {tab.icon}
                  </div>
                ) : (
                  <div className={`p-2 rounded-xl transition-all ${isActive ? 'bg-blue-50' : ''}`}>
                    {tab.icon}
                  </div>
                )}
                {tab.label && !tab.isFab && <span className="text-[9px] font-black uppercase tracking-widest">{tab.label}</span>}
              </button>
            );
          });
        })()}
      </div>
    )
  }

    </div>
  );
}


